import { IPermissonCodeKey, ESORT_TYPE, IPermissonCode } from '@monorepo/types';
import * as React from 'react';
import React__default, { useRef, useMemo, useEffect, useState, useCallback, createContext, useContext } from 'react';
import { MARS_CRM_PATH, DEFAULT_SORTER, HEAD_QUARTERS, ZONE, DISTRIBUTION_CENTER, NORMAL_SHOP, TABLE_COL_WIDTH, MAX_DATA_LIMIT, MAX_PRICE_LIMIT, MAX_MONEY_LIMIT } from '@monorepo/shared';
import { HttpRequest } from '@monorepo/services';
import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';
import { $t } from '@monorepo/i18n';
export { $t } from '@monorepo/i18n';
import BigNumber from 'bignumber.js';
import { Subject, Modal, Space, Row, Col, message } from '@lemon-fe/kits';
import shallowEqual from 'shallowequal';
import ReactDOM from 'react-dom';
import camelCase from 'lodash/camelCase';
import { findIndex, cloneDeep, isUndefined, debounce as debounce$1 } from 'lodash';
import { saveSystemLog as saveSystemLog$1, batchSaveSystemLog } from '@monorepo/services/system/system.service';
import moment from 'moment';
import dayjs from 'dayjs';
import html2canvas from 'html2canvas';
import { getLocationShopsList } from '@monorepo/services/appSettings/locationShops/locationShops.service';
import { getCustomColumn, updateCustomColumn, getOrderJumpType, saveDomainCookieData, getDomainCookieData } from '@monorepo/services/common';
import { getSettings } from '@monorepo/services/document/settings/settings.service';
import { filterUpdate, filterDelete, filterCreate, filterList, generalFilterUpdate, generalFilterDelete, generalFilterCreate, generalFilterList } from '@monorepo/services/filter/filter.services';
import { parse } from 'querystring';
import { getProductByKitsId } from '@monorepo/services/document/virtualItem/virtualItem.service';

const isCentralizedStore = () => window.localStorage.getItem('management_style') === 'false';
const isDecentralizedStore = () => window.localStorage.getItem('management_style') === 'true';
// 使用真实 window 对象（绕过 qiankun 沙箱），确保所有应用共享同一状态
const getRealWindow$1 = () => {
    // 在 qiankun 沙箱环境中，window 可能是 Proxy
    // 需要获取真实的 window 对象
    if (window.rawWindow) {
        return window.rawWindow;
    }
    // 尝试通过 Function 获取真实 window
    return new Function('return this')();
};
const getGlobalState = () => {
    const realWindow = getRealWindow$1();
    if (!realWindow.__MARS_GLOBAL_STATE__) {
        realWindow.__MARS_GLOBAL_STATE__ = {
            scopeType: '',
            isEarthAuth: false,
            isCardType: false,
        };
    }
    return realWindow.__MARS_GLOBAL_STATE__;
};
/**设置商户实体卡类型 */
const setCardType = (isCard) => {
    getGlobalState().isCardType = isCard;
};
/**获取商户实体卡类型 */
const getCardType = () => getGlobalState().isCardType;
// 设置earth授权标记
const setEarthAuth = (isAuth) => {
    getGlobalState().isEarthAuth = isAuth;
};
const getEarthAuth = (isEarthModule = false) => {
    const isEarthAuth = getGlobalState().isEarthAuth;
    // 如果是earth模块，则授权即显示，不授权不显示
    if (isEarthModule) {
        return isEarthAuth;
    }
    // 如果不是earth模块，则不授权即显示，授权不显示
    return !isEarthAuth;
};
const earthAuthis = () => getGlobalState().isEarthAuth;
class PermissionHandler {
    constructor() {
        /** 存值 */
        this.setPermission = (permissonList, appName) => {
            const source = appName || 'unknown';
            permissonList.forEach((permission) => {
                const { code_key, permission_item_list, scope_type } = permission;
                const permissions = permission_item_list.map((item) => item.code);
                this.permissonMap.set(code_key, permissions);
                this._permissionSource.set(code_key, source);
                getGlobalState().scopeType = scope_type;
            });
        };
        /** 判断是否有操作权限 */
        this.hasOpteratePermission = (code, key) => {
            const scopeType = getGlobalState().scopeType;
            if (Array.isArray(code)) {
                return code.some((codeItem) => {
                    const permissionItem = this.permissonMap.get([scopeType, codeItem].join('_'));
                    return !!permissionItem?.includes(key);
                });
            }
            const permissionItem = this.permissonMap.get([scopeType, code].join('_'));
            return !!permissionItem?.includes(key);
        };
        /** 判断是否有读取权限 */
        this.canRead = (code) => {
            // 如果没有设置权限，一律通过
            if (!code) {
                return true;
            }
            if (Array.isArray(code)) {
                return (code.filter((codeItem) => this.hasOpteratePermission(codeItem, IPermissonCodeKey.QUERY))
                    .length !== 0);
            }
            return this.hasOpteratePermission(code, IPermissonCodeKey.QUERY);
        };
        this.permissonMap = new Map();
        this._permissionSource = new Map();
    }
}
/** 区分菜单是否为earth授权 */
const routeCheck = (list) => {
    if (getGlobalState().isEarthAuth) {
        // 区分跳转earth授权单据
        const earthList = [
            'PERMISSION_CHAIN_TRANSFER_IN_ORDER',
            'PERMISSION_CHAIN_INNER_APPLY_ORDER',
            'PERMISSION_CHAIN_TRANSFER_OUT_ORDER',
            'PERMISSION_CHAIN_REQUEST_ORDER',
        ];
        return list.map((ele) => {
            if (earthList.includes(ele.code_key.split('HEADQUARTERS_')[1]) ||
                earthList.includes(ele.code_key)) {
                ele.code_key = `${ele?.code_key}_EARTH`;
            }
            return ele;
        });
    }
    return list;
};
// 使用真实 window 的全局单例，确保在 qiankun 微前端环境中所有应用共享同一个实例
const getPermissionHandler = () => {
    const realWindow = getRealWindow$1();
    if (!realWindow.__MARS_PERMISSION_HANDLER__) {
        realWindow.__MARS_PERMISSION_HANDLER__ = new PermissionHandler();
    }
    return realWindow.__MARS_PERMISSION_HANDLER__;
};
const permissionHandler = getPermissionHandler();
const { setPermission, hasOpteratePermission, canRead } = permissionHandler;
const getScopeType = () => getGlobalState().scopeType;
/** 是否为总部视角 */
const isHeadquarters = () => getGlobalState().scopeType === 'HEADQUARTERS';
/** 是否为门店视角 */
const isBranch = () => getGlobalState().scopeType === 'BRANCH';
/** 是否为区域视角 */
const isZone = () => getGlobalState().scopeType === 'ZONE';
/** 门店商品属性页面在 【分散式门店视角】下才有操作权限 */
const showOperationOfAttr = (pageRouter) => pageRouter !== 'storeAttribute' ||
    (getGlobalState().scopeType === 'BRANCH' && isDecentralizedStore());

var createUpdateEffect = function (hook) { return function (effect, deps) {
    var isMounted = useRef(false);
    // for react-refresh
    hook(function () {
        return function () {
            isMounted.current = false;
        };
    }, []);
    hook(function () {
        if (!isMounted.current) {
            isMounted.current = true;
        }
        else {
            return effect();
        }
    }, deps);
}; };

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}

function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var isFunction$1 = function (value) {
    return typeof value === 'function';
};

var isDev = process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test';

var useMemoizedFn = function (fn) {
    if (isDev) {
        if (!isFunction$1(fn)) {
            console.error("useMemoizedFn expected parameter is a function, got ".concat(typeof fn));
        }
    }
    var fnRef = useRef(fn);
    // why not write `fnRef.current = fn`?
    // https://github.com/alibaba/hooks/issues/728
    fnRef.current = useMemo(function () { return fn; }, [fn]);
    var memoizedFn = useRef(undefined);
    if (!memoizedFn.current) {
        memoizedFn.current = function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            return fnRef.current.apply(this, args);
        };
    }
    return memoizedFn.current;
};

var useUpdateEffect = createUpdateEffect(useEffect);

// support refreshDeps & ready
var useAutoRunPlugin = function (fetchInstance, _a) {
    var manual = _a.manual, _b = _a.ready, ready = _b === void 0 ? true : _b, _c = _a.defaultParams, defaultParams = _c === void 0 ? [] : _c, _d = _a.refreshDeps, refreshDeps = _d === void 0 ? [] : _d, refreshDepsAction = _a.refreshDepsAction;
    var hasAutoRun = useRef(false);
    hasAutoRun.current = false;
    useUpdateEffect(function () {
        if (!manual && ready) {
            hasAutoRun.current = true;
            fetchInstance.run.apply(fetchInstance, __spreadArray([], __read(defaultParams), false));
        }
    }, [ready]);
    useUpdateEffect(function () {
        if (hasAutoRun.current) {
            return;
        }
        if (!manual) {
            hasAutoRun.current = true;
            if (refreshDepsAction) {
                refreshDepsAction();
            }
            else {
                fetchInstance.refresh();
            }
        }
    }, __spreadArray([], __read(refreshDeps), false));
    return {
        onBefore: function () {
            if (!ready) {
                return {
                    stopNow: true,
                };
            }
        },
    };
};
useAutoRunPlugin.onInit = function (_a) {
    var _b = _a.ready, ready = _b === void 0 ? true : _b, manual = _a.manual;
    return {
        loading: !manual && ready,
    };
};

function depsAreSame(oldDeps, deps) {
    if (oldDeps === deps) {
        return true;
    }
    for (var i = 0; i < oldDeps.length; i++) {
        if (!Object.is(oldDeps[i], deps[i])) {
            return false;
        }
    }
    return true;
}

var useCreation = function (factory, deps) {
    var current = useRef({
        deps: deps,
        obj: undefined,
        initialized: false,
    }).current;
    if (current.initialized === false || !depsAreSame(current.deps, deps)) {
        current.deps = deps;
        current.obj = factory();
        current.initialized = true;
    }
    return current.obj;
};

function useLatest(value) {
    var ref = useRef(value);
    ref.current = value;
    return ref;
}

var useUnmount = function (fn) {
    if (isDev) {
        if (!isFunction$1(fn)) {
            console.error("useUnmount expected parameter is a function, got ".concat(typeof fn));
        }
    }
    var fnRef = useLatest(fn);
    useEffect(function () { return function () {
        fnRef.current();
    }; }, []);
};

var cache = new Map();
var setCache = function (key, cacheTime, cachedData) {
    var currentCache = cache.get(key);
    if (currentCache === null || currentCache === void 0 ? void 0 : currentCache.timer) {
        clearTimeout(currentCache.timer);
    }
    var timer = undefined;
    if (cacheTime > -1) {
        // if cache out, clear it
        timer = setTimeout(function () {
            cache.delete(key);
        }, cacheTime);
    }
    cache.set(key, __assign(__assign({}, cachedData), { timer: timer }));
};
var getCache = function (key) {
    return cache.get(key);
};

var cachePromise = new Map();
var getCachePromise = function (cacheKey) {
    return cachePromise.get(cacheKey);
};
var setCachePromise = function (cacheKey, promise) {
    // Should cache the same promise, cannot be promise.finally
    // Because the promise.finally will change the reference of the promise
    cachePromise.set(cacheKey, promise);
    // no use promise.finally for compatibility
    promise
        .then(function (res) {
        cachePromise.delete(cacheKey);
        return res;
    })
        .catch(function () {
        cachePromise.delete(cacheKey);
    });
};

var listeners$2 = {};
var trigger = function (key, data) {
    if (listeners$2[key]) {
        listeners$2[key].forEach(function (item) { return item(data); });
    }
};
var subscribe$2 = function (key, listener) {
    if (!listeners$2[key]) {
        listeners$2[key] = [];
    }
    listeners$2[key].push(listener);
    return function unsubscribe() {
        var index = listeners$2[key].indexOf(listener);
        listeners$2[key].splice(index, 1);
    };
};

var useCachePlugin = function (fetchInstance, _a) {
    var cacheKey = _a.cacheKey, _b = _a.cacheTime, cacheTime = _b === void 0 ? 5 * 60 * 1000 : _b, _c = _a.staleTime, staleTime = _c === void 0 ? 0 : _c, customSetCache = _a.setCache, customGetCache = _a.getCache;
    var unSubscribeRef = useRef(undefined);
    var currentPromiseRef = useRef(undefined);
    var _setCache = function (key, cachedData) {
        if (customSetCache) {
            customSetCache(cachedData);
        }
        else {
            setCache(key, cacheTime, cachedData);
        }
        trigger(key, cachedData.data);
    };
    var _getCache = function (key, params) {
        if (params === void 0) { params = []; }
        if (customGetCache) {
            return customGetCache(params);
        }
        return getCache(key);
    };
    useCreation(function () {
        if (!cacheKey) {
            return;
        }
        // get data from cache when init
        var cacheData = _getCache(cacheKey);
        if (cacheData && Object.hasOwnProperty.call(cacheData, 'data')) {
            fetchInstance.state.data = cacheData.data;
            fetchInstance.state.params = cacheData.params;
            if (staleTime === -1 || Date.now() - cacheData.time <= staleTime) {
                fetchInstance.state.loading = false;
            }
        }
        // subscribe same cachekey update, trigger update
        unSubscribeRef.current = subscribe$2(cacheKey, function (data) {
            fetchInstance.setState({ data: data });
        });
    }, []);
    useUnmount(function () {
        var _a;
        (_a = unSubscribeRef.current) === null || _a === void 0 ? void 0 : _a.call(unSubscribeRef);
    });
    if (!cacheKey) {
        return {};
    }
    return {
        onBefore: function (params) {
            var cacheData = _getCache(cacheKey, params);
            if (!cacheData || !Object.hasOwnProperty.call(cacheData, 'data')) {
                return {};
            }
            // If the data is fresh, stop request
            if (staleTime === -1 || Date.now() - cacheData.time <= staleTime) {
                return {
                    loading: false,
                    data: cacheData === null || cacheData === void 0 ? void 0 : cacheData.data,
                    error: undefined,
                    returnNow: true,
                };
            }
            else {
                // If the data is stale, return data, and request continue
                return {
                    data: cacheData === null || cacheData === void 0 ? void 0 : cacheData.data,
                    error: undefined,
                };
            }
        },
        onRequest: function (service, args) {
            var servicePromise = getCachePromise(cacheKey);
            // If has servicePromise, and is not trigger by self, then use it
            if (servicePromise && servicePromise !== currentPromiseRef.current) {
                return { servicePromise: servicePromise };
            }
            servicePromise = service.apply(void 0, __spreadArray([], __read(args), false));
            currentPromiseRef.current = servicePromise;
            setCachePromise(cacheKey, servicePromise);
            return { servicePromise: servicePromise };
        },
        onSuccess: function (data, params) {
            var _a;
            if (cacheKey) {
                // cancel subscribe, avoid trgger self
                (_a = unSubscribeRef.current) === null || _a === void 0 ? void 0 : _a.call(unSubscribeRef);
                _setCache(cacheKey, {
                    data: data,
                    params: params,
                    time: Date.now(),
                });
                // resubscribe
                unSubscribeRef.current = subscribe$2(cacheKey, function (d) {
                    fetchInstance.setState({ data: d });
                });
            }
        },
        onMutate: function (data) {
            var _a;
            if (cacheKey) {
                // cancel subscribe, avoid trigger self
                (_a = unSubscribeRef.current) === null || _a === void 0 ? void 0 : _a.call(unSubscribeRef);
                _setCache(cacheKey, {
                    data: data,
                    params: fetchInstance.state.params,
                    time: Date.now(),
                });
                // resubscribe
                unSubscribeRef.current = subscribe$2(cacheKey, function (d) {
                    fetchInstance.setState({ data: d });
                });
            }
        },
    };
};

var useDebouncePlugin = function (fetchInstance, _a) {
    var debounceWait = _a.debounceWait, debounceLeading = _a.debounceLeading, debounceTrailing = _a.debounceTrailing, debounceMaxWait = _a.debounceMaxWait;
    var debouncedRef = useRef(undefined);
    var options = useMemo(function () {
        var ret = {};
        if (debounceLeading !== undefined) {
            ret.leading = debounceLeading;
        }
        if (debounceTrailing !== undefined) {
            ret.trailing = debounceTrailing;
        }
        if (debounceMaxWait !== undefined) {
            ret.maxWait = debounceMaxWait;
        }
        return ret;
    }, [debounceLeading, debounceTrailing, debounceMaxWait]);
    useEffect(function () {
        if (debounceWait) {
            var _originRunAsync_1 = fetchInstance.runAsync.bind(fetchInstance);
            debouncedRef.current = debounce(function (callback) {
                callback();
            }, debounceWait, options);
            // debounce runAsync should be promise
            // https://github.com/lodash/lodash/issues/4400#issuecomment-834800398
            fetchInstance.runAsync = function () {
                var args = [];
                for (var _i = 0; _i < arguments.length; _i++) {
                    args[_i] = arguments[_i];
                }
                return new Promise(function (resolve, reject) {
                    var _a;
                    (_a = debouncedRef.current) === null || _a === void 0 ? void 0 : _a.call(debouncedRef, function () {
                        _originRunAsync_1.apply(void 0, __spreadArray([], __read(args), false)).then(resolve)
                            .catch(reject);
                    });
                });
            };
            return function () {
                var _a;
                (_a = debouncedRef.current) === null || _a === void 0 ? void 0 : _a.cancel();
                fetchInstance.runAsync = _originRunAsync_1;
            };
        }
    }, [debounceWait, options]);
    if (!debounceWait) {
        return {};
    }
    return {
        onCancel: function () {
            var _a;
            (_a = debouncedRef.current) === null || _a === void 0 ? void 0 : _a.cancel();
        },
    };
};

var useLoadingDelayPlugin = function (fetchInstance, _a) {
    var loadingDelay = _a.loadingDelay, ready = _a.ready;
    var timerRef = useRef(undefined);
    if (!loadingDelay) {
        return {};
    }
    var cancelTimeout = function () {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
    };
    return {
        onBefore: function () {
            cancelTimeout();
            // Two cases:
            // 1. ready === undefined
            // 2. ready === true
            if (ready !== false) {
                timerRef.current = setTimeout(function () {
                    fetchInstance.setState({
                        loading: true,
                    });
                }, loadingDelay);
            }
            return {
                loading: false,
            };
        },
        onFinally: function () {
            cancelTimeout();
        },
        onCancel: function () {
            cancelTimeout();
        },
    };
};

var isBrowser = !!(typeof window !== 'undefined' &&
    window.document &&
    window.document.createElement);

function isDocumentVisible() {
    if (isBrowser) {
        return document.visibilityState !== 'hidden';
    }
    return true;
}

var listeners$1 = new Set();
function subscribe$1(listener) {
    listeners$1.add(listener);
    return function unsubscribe() {
        listeners$1.has(listener) && listeners$1.delete(listener);
    };
}
if (isBrowser) {
    var revalidate$1 = function () {
        if (!isDocumentVisible())
            return;
        listeners$1.forEach(function (listener) { return listener(); });
    };
    window.addEventListener('visibilitychange', revalidate$1, false);
}

var usePollingPlugin = function (fetchInstance, _a) {
    var pollingInterval = _a.pollingInterval, _b = _a.pollingWhenHidden, pollingWhenHidden = _b === void 0 ? true : _b, _c = _a.pollingErrorRetryCount, pollingErrorRetryCount = _c === void 0 ? -1 : _c;
    var timerRef = useRef(undefined);
    var unsubscribeRef = useRef(undefined);
    var countRef = useRef(0);
    var stopPolling = function () {
        var _a;
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        (_a = unsubscribeRef.current) === null || _a === void 0 ? void 0 : _a.call(unsubscribeRef);
    };
    useUpdateEffect(function () {
        if (!pollingInterval) {
            stopPolling();
        }
    }, [pollingInterval]);
    if (!pollingInterval) {
        return {};
    }
    return {
        onBefore: function () {
            stopPolling();
        },
        onError: function () {
            countRef.current += 1;
        },
        onSuccess: function () {
            countRef.current = 0;
        },
        onFinally: function () {
            if (pollingErrorRetryCount === -1 ||
                // When an error occurs, the request is not repeated after pollingErrorRetryCount retries
                (pollingErrorRetryCount !== -1 && countRef.current <= pollingErrorRetryCount)) {
                timerRef.current = setTimeout(function () {
                    // if pollingWhenHidden = false && document is hidden, then stop polling and subscribe revisible
                    if (!pollingWhenHidden && !isDocumentVisible()) {
                        unsubscribeRef.current = subscribe$1(function () {
                            fetchInstance.refresh();
                        });
                    }
                    else {
                        fetchInstance.refresh();
                    }
                }, pollingInterval);
            }
            else {
                countRef.current = 0;
            }
        },
        onCancel: function () {
            stopPolling();
        },
    };
};

function limit(fn, timespan) {
    var pending = false;
    return function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        if (pending)
            return;
        pending = true;
        fn.apply(void 0, __spreadArray([], __read(args), false));
        setTimeout(function () {
            pending = false;
        }, timespan);
    };
}

var isOnline = function () {
    if (isBrowser && typeof navigator.onLine !== 'undefined') {
        return navigator.onLine;
    }
    return true;
};

// from swr
var listeners = new Set();
function subscribe(listener) {
    listeners.add(listener);
    return function unsubscribe() {
        listeners.has(listener) && listeners.delete(listener);
    };
}
if (isBrowser) {
    var revalidate = function () {
        if (!isDocumentVisible() || !isOnline())
            return;
        listeners.forEach(function (listener) { return listener(); });
    };
    window.addEventListener('visibilitychange', revalidate, false);
    window.addEventListener('focus', revalidate, false);
}

var useRefreshOnWindowFocusPlugin = function (fetchInstance, _a) {
    var refreshOnWindowFocus = _a.refreshOnWindowFocus, _b = _a.focusTimespan, focusTimespan = _b === void 0 ? 5000 : _b;
    var unsubscribeRef = useRef(undefined);
    var stopSubscribe = function () {
        var _a;
        (_a = unsubscribeRef.current) === null || _a === void 0 ? void 0 : _a.call(unsubscribeRef);
    };
    useEffect(function () {
        if (refreshOnWindowFocus) {
            var limitRefresh_1 = limit(fetchInstance.refresh.bind(fetchInstance), focusTimespan);
            unsubscribeRef.current = subscribe(function () {
                limitRefresh_1();
            });
        }
        return function () {
            stopSubscribe();
        };
    }, [refreshOnWindowFocus, focusTimespan]);
    useUnmount(function () {
        stopSubscribe();
    });
    return {};
};

var useRetryPlugin = function (fetchInstance, _a) {
    var retryInterval = _a.retryInterval, retryCount = _a.retryCount;
    var timerRef = useRef(undefined);
    var countRef = useRef(0);
    var triggerByRetry = useRef(false);
    if (!retryCount) {
        return {};
    }
    return {
        onBefore: function () {
            if (!triggerByRetry.current) {
                countRef.current = 0;
            }
            triggerByRetry.current = false;
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        },
        onSuccess: function () {
            countRef.current = 0;
        },
        onError: function () {
            countRef.current += 1;
            if (retryCount === -1 || countRef.current <= retryCount) {
                // Exponential backoff
                var timeout = retryInterval !== null && retryInterval !== void 0 ? retryInterval : Math.min(1000 * Math.pow(2, countRef.current), 30000);
                timerRef.current = setTimeout(function () {
                    triggerByRetry.current = true;
                    fetchInstance.refresh();
                }, timeout);
            }
            else {
                countRef.current = 0;
            }
        },
        onCancel: function () {
            countRef.current = 0;
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        },
    };
};

var useThrottlePlugin = function (fetchInstance, _a) {
    var throttleWait = _a.throttleWait, throttleLeading = _a.throttleLeading, throttleTrailing = _a.throttleTrailing;
    var throttledRef = useRef(undefined);
    var options = {};
    if (throttleLeading !== undefined) {
        options.leading = throttleLeading;
    }
    if (throttleTrailing !== undefined) {
        options.trailing = throttleTrailing;
    }
    useEffect(function () {
        if (throttleWait) {
            var _originRunAsync_1 = fetchInstance.runAsync.bind(fetchInstance);
            throttledRef.current = throttle(function (callback) {
                callback();
            }, throttleWait, options);
            // throttle runAsync should be promise
            // https://github.com/lodash/lodash/issues/4400#issuecomment-834800398
            fetchInstance.runAsync = function () {
                var args = [];
                for (var _i = 0; _i < arguments.length; _i++) {
                    args[_i] = arguments[_i];
                }
                return new Promise(function (resolve, reject) {
                    var _a;
                    (_a = throttledRef.current) === null || _a === void 0 ? void 0 : _a.call(throttledRef, function () {
                        _originRunAsync_1.apply(void 0, __spreadArray([], __read(args), false)).then(resolve)
                            .catch(reject);
                    });
                });
            };
            return function () {
                var _a;
                fetchInstance.runAsync = _originRunAsync_1;
                (_a = throttledRef.current) === null || _a === void 0 ? void 0 : _a.cancel();
            };
        }
    }, [throttleWait, throttleLeading, throttleTrailing]);
    if (!throttleWait) {
        return {};
    }
    return {
        onCancel: function () {
            var _a;
            (_a = throttledRef.current) === null || _a === void 0 ? void 0 : _a.cancel();
        },
    };
};

var useMount = function (fn) {
    if (isDev) {
        if (!isFunction$1(fn)) {
            console.error("useMount: parameter `fn` expected to be a function, but got \"".concat(typeof fn, "\"."));
        }
    }
    useEffect(function () {
        var result = fn === null || fn === void 0 ? void 0 : fn();
        // If fn returns a Promise, don't return it as cleanup function
        if (result && typeof result === 'object' && typeof result.then === 'function') {
            return;
        }
        return result;
    }, []);
};

var useUpdate = function () {
    var _a = __read(useState({}), 2), setState = _a[1];
    return useCallback(function () { return setState({}); }, []);
};

var Fetch = /** @class */ (function () {
    function Fetch(serviceRef, options, subscribe, initState) {
        if (initState === void 0) { initState = {}; }
        this.serviceRef = serviceRef;
        this.options = options;
        this.subscribe = subscribe;
        this.initState = initState;
        this.count = 0;
        this.state = {
            loading: false,
            params: undefined,
            data: undefined,
            error: undefined,
        };
        this.state = __assign(__assign(__assign({}, this.state), { loading: !options.manual }), initState);
    }
    Fetch.prototype.setState = function (s) {
        if (s === void 0) { s = {}; }
        this.state = __assign(__assign({}, this.state), s);
        this.subscribe();
    };
    Fetch.prototype.runPluginHandler = function (event) {
        var rest = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            rest[_i - 1] = arguments[_i];
        }
        // @ts-ignore
        var r = this.pluginImpls.map(function (i) { var _a; return (_a = i[event]) === null || _a === void 0 ? void 0 : _a.call.apply(_a, __spreadArray([i], __read(rest), false)); }).filter(Boolean);
        return Object.assign.apply(Object, __spreadArray([{}], __read(r), false));
    };
    Fetch.prototype.runAsync = function () {
        var params = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            params[_i] = arguments[_i];
        }
        return __awaiter(this, void 0, void 0, function () {
            var currentCount, _a, _b, stopNow, _c, returnNow, state, servicePromise, res, error_1;
            var _d;
            var _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
            return __generator(this, function (_q) {
                switch (_q.label) {
                    case 0:
                        this.count += 1;
                        currentCount = this.count;
                        _a = this.runPluginHandler('onBefore', params), _b = _a.stopNow, stopNow = _b === void 0 ? false : _b, _c = _a.returnNow, returnNow = _c === void 0 ? false : _c, state = __rest(_a, ["stopNow", "returnNow"]);
                        // stop request
                        if (stopNow) {
                            return [2 /*return*/, new Promise(function () { })];
                        }
                        this.setState(__assign({ loading: true, params: params }, state));
                        // return now
                        if (returnNow) {
                            return [2 /*return*/, Promise.resolve(state.data)];
                        }
                        (_f = (_e = this.options).onBefore) === null || _f === void 0 ? void 0 : _f.call(_e, params);
                        _q.label = 1;
                    case 1:
                        _q.trys.push([1, 3, , 4]);
                        servicePromise = this.runPluginHandler('onRequest', this.serviceRef.current, params).servicePromise;
                        if (!servicePromise) {
                            servicePromise = (_d = this.serviceRef).current.apply(_d, __spreadArray([], __read(params), false));
                        }
                        return [4 /*yield*/, servicePromise];
                    case 2:
                        res = _q.sent();
                        if (currentCount !== this.count) {
                            // prevent run.then when request is canceled
                            return [2 /*return*/, new Promise(function () { })];
                        }
                        // const formattedResult = this.options.formatResultRef.current ? this.options.formatResultRef.current(res) : res;
                        this.setState({
                            data: res,
                            error: undefined,
                            loading: false,
                        });
                        (_h = (_g = this.options).onSuccess) === null || _h === void 0 ? void 0 : _h.call(_g, res, params);
                        this.runPluginHandler('onSuccess', res, params);
                        (_k = (_j = this.options).onFinally) === null || _k === void 0 ? void 0 : _k.call(_j, params, res, undefined);
                        if (currentCount === this.count) {
                            this.runPluginHandler('onFinally', params, res, undefined);
                        }
                        return [2 /*return*/, res];
                    case 3:
                        error_1 = _q.sent();
                        if (currentCount !== this.count) {
                            // prevent run.then when request is canceled
                            return [2 /*return*/, new Promise(function () { })];
                        }
                        this.setState({
                            error: error_1,
                            loading: false,
                        });
                        (_m = (_l = this.options).onError) === null || _m === void 0 ? void 0 : _m.call(_l, error_1, params);
                        this.runPluginHandler('onError', error_1, params);
                        (_p = (_o = this.options).onFinally) === null || _p === void 0 ? void 0 : _p.call(_o, params, undefined, error_1);
                        if (currentCount === this.count) {
                            this.runPluginHandler('onFinally', params, undefined, error_1);
                        }
                        throw error_1;
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    Fetch.prototype.run = function () {
        var _this = this;
        var params = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            params[_i] = arguments[_i];
        }
        this.runAsync.apply(this, __spreadArray([], __read(params), false)).catch(function (error) {
            if (!_this.options.onError) {
                console.error(error);
            }
        });
    };
    Fetch.prototype.cancel = function () {
        this.count += 1;
        this.setState({
            loading: false,
        });
        this.runPluginHandler('onCancel');
    };
    Fetch.prototype.refresh = function () {
        // @ts-ignore
        this.run.apply(this, __spreadArray([], __read((this.state.params || [])), false));
    };
    Fetch.prototype.refreshAsync = function () {
        // @ts-ignore
        return this.runAsync.apply(this, __spreadArray([], __read((this.state.params || [])), false));
    };
    Fetch.prototype.mutate = function (data) {
        var targetData = isFunction$1(data) ? data(this.state.data) : data;
        this.runPluginHandler('onMutate', targetData);
        this.setState({
            data: targetData,
        });
    };
    return Fetch;
}());

function useRequestImplement(service, options, plugins) {
    if (options === void 0) { options = {}; }
    if (plugins === void 0) { plugins = []; }
    var _a = options.manual, manual = _a === void 0 ? false : _a, _b = options.ready, ready = _b === void 0 ? true : _b, rest = __rest(options, ["manual", "ready"]);
    if (isDev) {
        if (options.defaultParams && !Array.isArray(options.defaultParams)) {
            console.warn("expected defaultParams is array, got ".concat(typeof options.defaultParams));
        }
    }
    var fetchOptions = __assign({ manual: manual, ready: ready }, rest);
    var serviceRef = useLatest(service);
    var update = useUpdate();
    var fetchInstance = useCreation(function () {
        var initState = plugins.map(function (p) { var _a; return (_a = p === null || p === void 0 ? void 0 : p.onInit) === null || _a === void 0 ? void 0 : _a.call(p, fetchOptions); }).filter(Boolean);
        return new Fetch(serviceRef, fetchOptions, update, Object.assign.apply(Object, __spreadArray([{}], __read(initState), false)));
    }, []);
    fetchInstance.options = fetchOptions;
    // run all plugins hooks
    fetchInstance.pluginImpls = plugins.map(function (p) { return p(fetchInstance, fetchOptions); });
    useMount(function () {
        if (!manual && ready) {
            // useCachePlugin can set fetchInstance.state.params from cache when init
            var params = fetchInstance.state.params || options.defaultParams || [];
            // @ts-ignore
            fetchInstance.run.apply(fetchInstance, __spreadArray([], __read(params), false));
        }
    });
    useUnmount(function () {
        fetchInstance.cancel();
    });
    return {
        loading: fetchInstance.state.loading,
        data: fetchInstance.state.data,
        error: fetchInstance.state.error,
        params: fetchInstance.state.params || [],
        cancel: useMemoizedFn(fetchInstance.cancel.bind(fetchInstance)),
        refresh: useMemoizedFn(fetchInstance.refresh.bind(fetchInstance)),
        refreshAsync: useMemoizedFn(fetchInstance.refreshAsync.bind(fetchInstance)),
        run: useMemoizedFn(fetchInstance.run.bind(fetchInstance)),
        runAsync: useMemoizedFn(fetchInstance.runAsync.bind(fetchInstance)),
        mutate: useMemoizedFn(fetchInstance.mutate.bind(fetchInstance)),
    };
}

// function useRequest<TData, TParams extends any[], TFormated, TTFormated extends TFormated = any>(
//   service: Service<TData, TParams>,
//   options: OptionsWithFormat<TData, TParams, TFormated, TTFormated>,
//   plugins?: Plugin<TData, TParams>[],
// ): Result<TFormated, TParams>
// function useRequest<TData, TParams extends any[]>(
//   service: Service<TData, TParams>,
//   options?: OptionsWithoutFormat<TData, TParams>,
//   plugins?: Plugin<TData, TParams>[],
// ): Result<TData, TParams>
function useRequest(service, options, plugins) {
    return useRequestImplement(service, options, __spreadArray(__spreadArray([], __read(([])), false), [
        useDebouncePlugin,
        useLoadingDelayPlugin,
        usePollingPlugin,
        useRefreshOnWindowFocusPlugin,
        useThrottlePlugin,
        useAutoRunPlugin,
        useCachePlugin,
        useRetryPlugin,
    ], false));
}

function getDefaultExportFromCjs (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

/*
 * @Author: 赵泽文
 * @Date: 2024-04-04 16:44:44
 * @LastEditors: nhsoft.zhangxh
 * @LastEditTime: 2025-05-15 17:34:27
 * @Description: tabs全局状态管理
 */
const useRemberListIndex = () => {
    /**
     * 添加缓存页面
     * @param listName 需要缓存的列表页面
     * @param value  列表当前进入详情的下标
     */
    const addRemberListIndex = useMemoizedFn((listName, value) => {
        const CacheRemberList = sessionStorage.getItem('cacheRemberList')
            ? JSON.parse(sessionStorage.getItem('cacheRemberList') || '')
            : {};
        CacheRemberList[listName] = `${value}`;
        sessionStorage.setItem('cacheRemberList', JSON.stringify(CacheRemberList));
    });
    // 删除缓存页面
    const deleteRemberListIndex = useMemoizedFn((deleteName) => {
        const CacheRemberList = sessionStorage.getItem('cacheRemberList')
            ? JSON.parse(sessionStorage.getItem('cacheRemberList') || '')
            : {};
        if (CacheRemberList[deleteName]) {
            delete CacheRemberList[deleteName];
            sessionStorage.setItem('cacheRemberList', JSON.stringify(CacheRemberList));
        }
    });
    const getRemberListIndex = useMemoizedFn((name) => {
        const CacheRemberList = sessionStorage.getItem('cacheRemberList')
            ? JSON.parse(sessionStorage.getItem('cacheRemberList') || '')
            : {};
        if (CacheRemberList[name]) {
            return CacheRemberList[name];
        }
        return 0;
    });
    return {
        addRemberListIndex,
        deleteRemberListIndex,
        getRemberListIndex,
    };
};

/**
 * 获取指定抵用券
 */
const useGetVoucherList = () => useRequest((params) => HttpRequest({
    url: `${MARS_CRM_PATH}/discount_voucher/list_voucher_name.json`,
    params,
    method: 'GET',
}), {
    manual: true,
});
/**
 * 获取指定抵用券的id和名称
 */
const useGetVoucherIdAndNameList = () => useRequest((params) => HttpRequest({
    url: `${MARS_CRM_PATH}/discount_voucher/list_voucher_name_and_id.json`,
    params,
    method: 'GET',
}), {
    manual: true,
});

const CACHE_FILTER_KEY = 'CACHE_FILTER_KEY';
// 所有缓存的key
const CACHE_KEY_SET = new Set([CACHE_FILTER_KEY]);
/** 缓存数据 */
const setCacheData = (name, data, sessionName) => {
    if (!name)
        return;
    if (data && Object.keys(data)?.length > 0) {
        const parsedData = JSON.stringify({
            name,
            query: data,
        });
        sessionStorage.setItem(sessionName || CACHE_FILTER_KEY, parsedData);
    }
};
/** 获取缓存数据 */
const getCachedData = (name, clear, sessionName) => {
    if (!name)
        return;
    const cachedData = sessionStorage.getItem(sessionName || CACHE_FILTER_KEY);
    let result;
    if (cachedData) {
        const parsedData = JSON.parse(cachedData);
        if (parsedData && parsedData.name === name) {
            result = parsedData.query;
        }
        {
            sessionStorage.setItem(sessionName || CACHE_FILTER_KEY, '');
        }
    }
    return result;
};
/**
 * @param name 模块name
 * @param data 需要缓存的数据
 * @param handler 重新进入页面时的回调，获取之前缓存的数据
 * @param sessionName 自定义sessionName
 */
const useCacheHandler = (name, data, handler, sessionName) => {
    useMount(() => {
        const result = getCachedData(name, true, sessionName);
        if (result) {
            handler(result);
        }
        else {
            sessionStorage.setItem(sessionName || CACHE_FILTER_KEY, '');
        }
    });
    useUnmount(() => {
        setCacheData(name, data, sessionName);
        if (sessionName) {
            CACHE_KEY_SET.add(sessionName);
        }
    });
};

/*
 * @Author: 张帮辉
 * @LastEditors: 王仁涛 wangrt@cnlemon.net
 * @Date: 2022-02-27 11:25:22
 * @LastEditTime: 2026-07-14 14:35:40
 * @Description: file content
 */
const numberOperator = (firstNum, secondNum, type = 'plus', precision = 8, toNumberOrString) => {
    const num1 = new BigNumber(firstNum || 0);
    const num2 = new BigNumber(secondNum || 0);
    let result;
    switch (type) {
        case 'plus':
            result = num1.plus(num2);
            break;
        case 'minus':
            result = num1.minus(num2);
            break;
        case 'mult':
            result = num1.multipliedBy(num2);
            break;
        case 'divide':
            if (num2.isEqualTo(0)) {
                result = num1;
            }
            else {
                result = num1.dividedBy(num2);
            }
            break;
        case 'mod':
            result = num1.modulo(num2);
            break;
        default:
            result = num1.plus(num2);
            break;
    }
    if (result.isNaN()) {
        return (toNumberOrString === 'number' ? 0 : '0');
    }
    const processedResult = result.dp(precision);
    return (toNumberOrString === 'number' ? processedResult.toNumber() : processedResult.toString());
};
/**
 * 保存小数
 * @param number 数值
 * @param precision 位数，默认保留两位小数
 * @returns
 */
const numberFixed = (number = 0, precision = 2) => {
    const calcNumber = new BigNumber(number);
    return calcNumber.isNaN() ? '0' : calcNumber.dp(precision).toString();
};
// 处理极小位数小数转为字符串方法
const toExactString = (num) => {
    let str = num?.toString() ?? '';
    // 处理科学计数法（如 '2e-5' → '0.00002'）
    if (str.includes('e')) {
        const [coefficient, exponent] = str.split('e');
        const decimalPlaces = Math.abs(parseInt(exponent));
        // 根据指数生成对应的零填充字符串
        str = '0.' + '0'.repeat(decimalPlaces - 1) + coefficient.replace('.', '');
    }
    // 去除末尾多余的零和小数点（如 '0.2000' → '0.2'）
    return str.replace(/\.?0+$/, '');
};
/**
 * 数字格式化，每隔三位用逗号分割，展示2位小数
 * @param number
 * @returns
 */
const numberFormat = (number, precision = 2) => {
    let intNum = Number(number ?? '');
    let isFloat = toExactString(intNum).includes('.');
    if (isFloat) {
        intNum = new BigNumber(intNum).dp(precision).toNumber();
        isFloat = toExactString(intNum).includes('.');
    }
    if (!isFloat) {
        const strNum = (+intNum).toLocaleString();
        return precision ? `${strNum}.${''.padEnd(precision, '0')}` : `${strNum}`;
    }
    const [intPart, floatPart] = toExactString(intNum).split('.');
    if (floatPart?.length > precision) {
        return `${(+intPart).toLocaleString()}.${floatPart.slice(0, precision)}`;
    }
    else if (floatPart?.length < precision) {
        return `${(+intPart).toLocaleString()}.${floatPart.padEnd(precision, '0')}`;
    }
    return `${(+intPart).toLocaleString()}.${floatPart}`;
};
/**
 * 获取小数位的值
 * @param value 需要处理的值
 * @returns
 */
const getFloatValue = (value) => {
    if (!value)
        return '';
    return value.split('.')[1] || '';
};
/**
 * 根据小数点位数格式化数字
 * @param value 需要格式化的值
 * @param precision 保留小数位数
 * @param isFiexed 是否需要补齐小数位数(默认补齐)
 * @param round 四舍五入
 */
const precisionLimit = (value, precision = 2, isFixed = true, round) => {
    if (!value || value === 'null' || value === 'undefined' || Number(value) === 0)
        return '0.00';
    const stringVaue = String(value);
    const floatNum = getFloatValue(stringVaue);
    if (floatNum.length > precision || isFixed)
        return new BigNumber(stringVaue).toFixed(precision, !round ? undefined : 1);
    return stringVaue;
};
/**
 * 数字格式化formatter
 * @param number
 * @returns
 */
const inputFormatterRule = (precision = 8) => {
    return {
        validator: (_rule, value) => {
            const decimalLength = String(value)?.split('.')?.[1]?.length || 0;
            if (decimalLength > precision) {
                return Promise.reject(`最多输入${precision}位小数`);
            }
            return Promise.resolve();
        },
    };
};
/**
 * 单价、数量formmater
 * @param number
 * @returns
 */
const priceQuantityFormatter = (value = 0, styleFormatter = false) => {
    const num = new BigNumber(value || 0);
    if (num.isNaN())
        return value?.toString();
    const decimalLength = toExactString(value)?.split('.')?.[1]?.length || 0;
    if (decimalLength < 8) {
        return styleFormatter ? numberFormat(String(+value), decimalLength) : String(+value);
    }
    return styleFormatter ? numberFormat(numberFixed(value, 8), 8) : numberFixed(value, 8) + '';
};
const getCurrencyPrecision = () => {
    const currency = localStorage.getItem('currency') || 'CNY';
    if (['JPY', 'IDR'].includes(currency)) {
        return 0;
    }
    return 2;
};
// 处理 MARS-54628 小数位精度
const moneyFormatterWithCurrencyPrecision = (value = 0, styleFormatter = false, precision) => {
    const _precision = precision || getCurrencyPrecision();
    const num = new BigNumber(value || 0);
    if (num.isNaN())
        return value.toString();
    if (_precision >= 0) {
        return styleFormatter
            ? numberFormat(numberFixed(value, _precision), _precision)
            : numberFixed(value, _precision) + '';
    }
    return styleFormatter ? Number(value).toLocaleString() : Number(value) + '';
};
/**
 * 除人民币外的货币，输入框最大值为999999999.xx，小数位数根据货币精度确定
 * @param maxValue 人民币最大值
 * @returns
 */
const getMoneyMaxValue = (maxValue) => {
    const precision = getCurrencyPrecision();
    const currency = localStorage.getItem('currency') || 'CNY';
    if (currency !== 'CNY') {
        return Number(`99999999.${'9'.repeat(precision)}`);
    }
    return maxValue;
};
/**
 * 根据货币小数精度获取输入框最小值：精度0→1，精度1→0.1，精度2→0.01，以此类推
 * @returns
 */
const getMoneyMinValue = () => {
    const precision = getCurrencyPrecision();
    return Math.pow(10, -precision);
};
/**
 * 金额、比例formmater
 * @param number
 * @returns
 */
const moneyFormatter = (value = 0, styleFormatter = false, precision = 2) => {
    const num = new BigNumber(value || 0);
    if (num.isNaN())
        return value.toString();
    const decimalLength = String(+value)?.split('.')?.[1]?.length || 0; //小数的位数
    if (precision > 0) {
        return styleFormatter
            ? numberFormat(numberFixed(value, precision), precision)
            : numberFixed(value, precision) + '';
    }
    if (decimalLength > 2) {
        return styleFormatter ? numberFormat(numberFixed(value, 2), 2) : numberFixed(value, 2) + '';
    }
    return styleFormatter ? Number(value).toLocaleString() : Number(value) + '';
};
/**
 * 比例formmater
 * @param number
 * @returns
 */
const rateFormatter = (value = 0, styleFormatter = false) => {
    const num = new BigNumber(value || 0);
    if (num.isNaN())
        return value?.toString();
    const decimalLength = String(+value)?.split('.')?.[1]?.length || 0;
    if (decimalLength < 6) {
        return styleFormatter ? numberFormat(String(+value), decimalLength) : String(+value);
    }
    return styleFormatter ? numberFormat(numberFixed(value, 6), 6) : numberFixed(value, 6) + '';
};
/**
 * 单价、数量Operator,Formater
 * @param firstNum
 * @param secondNum
 * @param type
 * @param precision
 * @returns
 */
const priceQuantityFormatterOperator = (firstNum, secondNum, type = 'plus', precision = 8, styleFormatter = false) => {
    return priceQuantityFormatter(numberOperator(firstNum, secondNum, type, precision), styleFormatter);
};
/**
 * 金额Operator,Formater
 * @param firstNum
 * @param secondNum
 * @param type
 * @param precision
 * @returns
 */
const moneyFormatterOperator = (firstNum, secondNum, type = 'plus', precision = 8, styleFormatter = false) => {
    return moneyFormatter(numberOperator(firstNum, secondNum, type, precision), styleFormatter);
};
/**
 * 比例Operator,Formater
 * @param firstNum
 * @param secondNum
 * @param type
 * @param precision
 * @returns
 */
const rateFormatterOperator = (firstNum, secondNum, type = 'plus', precision = 8, styleFormatter = false) => {
    return rateFormatter(numberOperator(firstNum, secondNum, type, precision), styleFormatter);
};
/**
 * InputNumber格式化方法
 * @param val
 * @param precision 最多保留位数
 * @returns 格式化数字
 */
const inputNumberParser = (val, precision = 2) => {
    const powKey = Math.pow(10, precision);
    return Math.round(+(val ?? 0) * powKey) / powKey;
};
/**
 * 数字格式化，每隔三位用逗号分割，展示2位小数
 * @param number
 * @returns
 */
const numberToLocaleString$1 = (number, precision = 2) => {
    let intNum = Number(number ?? '');
    let isFloat = intNum.toString().includes('.');
    if (isFloat) {
        intNum = new BigNumber(intNum).dp(Number(precision)).toNumber();
        isFloat = intNum.toString().includes('.');
    }
    if (!isFloat) {
        const strNum = intNum.toLocaleString();
        return precision ? `${strNum}.${''.padEnd(Number(precision), '0')}` : `${strNum}`;
    }
    const [intPart, floatPart] = intNum.toString().split('.');
    if (floatPart?.length > Number(precision)) {
        return `${intPart.toLocaleString()}.${floatPart.slice(0, Number(precision))}`;
    }
    else if (floatPart?.length < Number(precision)) {
        return `${intPart.toLocaleString()}.${floatPart.padEnd(Number(precision), '0')}`;
    }
    return intNum.toLocaleString();
};
const isZero = (value) => {
    return value === '0' || value === 0;
};

const DEFAULT_CURRENCY = {
    currency_name: $t({ defaultMessage: '人民币' }),
    currency_code: 'CNY',
    currency_symbol: '¥',
    currency_unit: $t({ defaultMessage: '元' }),
    precision: 2,
};
const CURRENCY_LIST = [
    DEFAULT_CURRENCY,
    {
        currency_name: $t({ defaultMessage: '印度尼西亚盾' }),
        currency_code: 'IDR',
        currency_symbol: 'Rp',
        currency_unit: $t({ defaultMessage: '盾' }),
        precision: 0,
    },
    {
        currency_name: $t({ defaultMessage: '马来西亚林吉特' }),
        currency_code: 'MYR',
        currency_symbol: 'RM',
        currency_unit: $t({ defaultMessage: '林吉特' }),
        precision: 2,
    },
    {
        currency_name: $t({ defaultMessage: '港币' }),
        currency_code: 'HKD',
        currency_symbol: 'HK',
        currency_unit: $t({ defaultMessage: '港元' }),
        precision: 2,
    },
    {
        currency_name: $t({ defaultMessage: '澳门币' }),
        currency_code: 'MOP',
        currency_symbol: 'MOP$',
        currency_unit: $t({ defaultMessage: '澳门元' }),
        precision: 2,
    },
    {
        currency_name: $t({ defaultMessage: '日元' }),
        currency_code: 'JPY',
        currency_symbol: '¥',
        currency_unit: $t({ defaultMessage: '日元' }),
        precision: 0,
    },
    {
        currency_name: $t({ defaultMessage: '美元' }),
        currency_code: 'USD',
        currency_symbol: '$',
        currency_unit: $t({ defaultMessage: '美元' }),
        precision: 2,
    },
    {
        currency_name: $t({ defaultMessage: '新加坡元' }),
        currency_code: 'SGD',
        currency_symbol: 'S$',
        currency_unit: $t({ defaultMessage: '新元' }),
        precision: 2,
    },
];
const CURRENCY_MAP = CURRENCY_LIST.reduce((currencyMap, currency) => {
    currencyMap[currency.currency_code] = currency;
    return currencyMap;
}, {});
const useCurrencyByCode = (currencyCode) => {
    const normalizedCurrencyCode = (currencyCode || DEFAULT_CURRENCY.currency_code)
        .trim()
        .toUpperCase();
    return CURRENCY_MAP[normalizedCurrencyCode] || DEFAULT_CURRENCY;
};
const useCurrency = (_locationId) => {
    const currency = useCurrencyByCode(localStorage.getItem('currency') || DEFAULT_CURRENCY.currency_code);
    return useMemo(() => ({
        ...currency,
        formatCurrency: (value = 0, styleFormatter = false) => moneyFormatterWithCurrencyPrecision(value, styleFormatter, currency.precision),
    }), [currency]);
};

class EventHub {
    constructor() {
        this.listeners = new Map();
        this.add = this.add.bind(this);
        this.on = this.on.bind(this);
        this.once = this.once.bind(this);
        this.emit = this.emit.bind(this);
        this.off = this.off.bind(this);
    }
    add(event, handler, once) {
        [event].flat().forEach((evt) => {
            this.listeners.set(evt, this.listeners.get(evt)?.add({ once, handler }) ?? new Set([{ once, handler }]));
        });
    }
    on(event, handler) {
        this.add(event, handler, false);
        return () => {
            this.off(event, handler);
        };
    }
    once(event, handler) {
        this.add(event, handler, true);
    }
    emit(event, ...args) {
        let done = false;
        this.listeners.get(event)?.forEach(({ once, handler }) => {
            handler(...args);
            done = true;
            if (once) {
                this.listeners.delete(event);
            }
        });
        return done;
    }
    off(event, handler) {
        if (event === undefined) {
            this.listeners.clear();
        }
        else {
            [event].flat().forEach((evt) => {
                if (handler === undefined) {
                    this.listeners.delete(evt);
                }
                else {
                    this.listeners.get(evt)?.forEach((payload, _, payloads) => {
                        if (handler === payload.handler) {
                            payloads.delete(payload);
                        }
                    });
                }
            });
        }
    }
}

const DEFAULT_GLOBAL_STATE = {
    stockSetting: {
        product_price_precision: 4,
        receive_auto_sync_cost_price_is: false,
        audited_is: false,
        auto_store_in_hours: '',
        center_out_order_audit_direct_create_store_in_order_is: false,
        location_request_no_display_central_inventory: false,
        location_request_no_exceed_distribution_center_inventory: false,
        not_show_item_type_without_items_for_app: false,
        convert_actual_item: false,
        purchase_order_first_add_audit_is: false,
        transfer_order_first_add_audit_is: false,
        inventory_order_first_add_audit_is: false,
        request_template_required: false,
        request_template_location_id_list: [],
        transfer_in_create_by_transfer_out: false,
        transfer_in_audit_create_again_by_remain: false,
        request_price_as_transfer_price_is: false,
        autoMaintainPriceInterface: false,
        auto_maintain_purchase_unit_price: false,
        produce_order_first_add_audit_is: false,
        request_center_inventory_sub_not_transfer_amount: false,
        auto_generate_receive_by_purchase: false,
        staff_required_purchase: false,
        staff_required_production: false,
        staff_required_inventory: false,
        staff_required_transfer: false,
        staff_required_finance: false,
        finance_order_first_add_audit_is: false,
    },
};
const createApp = () => {
    // 将状态都放在 app 对象内部，确保共享
    const eventHub = new EventHub();
    const subject = new Subject();
    let state = DEFAULT_GLOBAL_STATE;
    return {
        setState(value) {
            state = {
                ...state,
                ...value,
            };
            subject.next(state);
        },
        updateStockArchivesSettings(archivesSettings) {
            const stockSetting = {
                ...state.stockSetting,
                archivesSettings,
            };
            state = {
                ...state,
                stockSetting,
            };
            if (typeof window !== 'undefined') {
                window.stockSettingConfig = stockSetting;
            }
            subject.next(state);
        },
        getState() {
            return state;
        },
        subscribe(handler) {
            return subject.subscribe({
                next: handler,
            });
        },
        event: {
            listen(params) {
                return eventHub.on(params.namespace, params.handler);
            },
            broadcast(params) {
                if (params.continus) {
                    let flag = false;
                    let count = 0;
                    const maxCount = 10;
                    const action = () => {
                        flag = eventHub.emit(params.namespace, {
                            type: params.type,
                            data: params.data,
                        });
                        count += 1;
                        if (!flag && count <= maxCount) {
                            setTimeout(action, 500);
                        }
                    };
                    action();
                }
                else {
                    eventHub.emit(params.namespace, {
                        type: params.type,
                        data: params.data,
                    });
                }
            },
        },
    };
};
// 从 window 获取共享的 app 实例，如果没有则创建新的并挂载到 window
const getOrCreateApp = () => {
    if (typeof window !== 'undefined') {
        const windowAny = window;
        if (windowAny.__MONOREPO_APP__) {
            return windowAny.__MONOREPO_APP__;
        }
        const app = createApp();
        windowAny.__MONOREPO_APP__ = app;
        return app;
    }
    return createApp();
};
const app = getOrCreateApp();

function useBaseAppState(selector) {
    /** 兼容第一次参数迁移到预发环境下，base-app未发布情况下，没有设置全局状态的情况 */
    const defaultState = useMemo(() => selector({ ...DEFAULT_GLOBAL_STATE, ...app.getState() }), []);
    const [state, setState] = useState(defaultState);
    useEffect(() => {
        return app.subscribe((value) => {
            setState((prev) => {
                const next = selector(value);
                if (shallowEqual(next, prev)) {
                    return prev;
                }
                return next || selector(DEFAULT_GLOBAL_STATE);
            });
        }).unsubscribe;
    }, []);
    return [state, app];
}

var jsxRuntime = {exports: {}};

var reactJsxRuntime_production_min = {};

/*
object-assign
(c) Sindre Sorhus
@license MIT
*/

var objectAssign;
var hasRequiredObjectAssign;

function requireObjectAssign () {
	if (hasRequiredObjectAssign) return objectAssign;
	hasRequiredObjectAssign = 1;
	/* eslint-disable no-unused-vars */
	var getOwnPropertySymbols = Object.getOwnPropertySymbols;
	var hasOwnProperty = Object.prototype.hasOwnProperty;
	var propIsEnumerable = Object.prototype.propertyIsEnumerable;

	function toObject(val) {
		if (val === null || val === undefined) {
			throw new TypeError('Object.assign cannot be called with null or undefined');
		}

		return Object(val);
	}

	function shouldUseNative() {
		try {
			if (!Object.assign) {
				return false;
			}

			// Detect buggy property enumeration order in older V8 versions.

			// https://bugs.chromium.org/p/v8/issues/detail?id=4118
			var test1 = new String('abc');  // eslint-disable-line no-new-wrappers
			test1[5] = 'de';
			if (Object.getOwnPropertyNames(test1)[0] === '5') {
				return false;
			}

			// https://bugs.chromium.org/p/v8/issues/detail?id=3056
			var test2 = {};
			for (var i = 0; i < 10; i++) {
				test2['_' + String.fromCharCode(i)] = i;
			}
			var order2 = Object.getOwnPropertyNames(test2).map(function (n) {
				return test2[n];
			});
			if (order2.join('') !== '0123456789') {
				return false;
			}

			// https://bugs.chromium.org/p/v8/issues/detail?id=3056
			var test3 = {};
			'abcdefghijklmnopqrst'.split('').forEach(function (letter) {
				test3[letter] = letter;
			});
			if (Object.keys(Object.assign({}, test3)).join('') !==
					'abcdefghijklmnopqrst') {
				return false;
			}

			return true;
		} catch (err) {
			// We don't expect any of the above to throw, but better to be safe.
			return false;
		}
	}

	objectAssign = shouldUseNative() ? Object.assign : function (target, source) {
		var from;
		var to = toObject(target);
		var symbols;

		for (var s = 1; s < arguments.length; s++) {
			from = Object(arguments[s]);

			for (var key in from) {
				if (hasOwnProperty.call(from, key)) {
					to[key] = from[key];
				}
			}

			if (getOwnPropertySymbols) {
				symbols = getOwnPropertySymbols(from);
				for (var i = 0; i < symbols.length; i++) {
					if (propIsEnumerable.call(from, symbols[i])) {
						to[symbols[i]] = from[symbols[i]];
					}
				}
			}
		}

		return to;
	};
	return objectAssign;
}

/** @license React v17.0.2
 * react-jsx-runtime.production.min.js
 *
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var hasRequiredReactJsxRuntime_production_min;

function requireReactJsxRuntime_production_min () {
	if (hasRequiredReactJsxRuntime_production_min) return reactJsxRuntime_production_min;
	hasRequiredReactJsxRuntime_production_min = 1;
requireObjectAssign();var f=React__default,g=60103;reactJsxRuntime_production_min.Fragment=60107;if("function"===typeof Symbol&&Symbol.for){var h=Symbol.for;g=h("react.element");reactJsxRuntime_production_min.Fragment=h("react.fragment");}var m=f.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED.ReactCurrentOwner,n=Object.prototype.hasOwnProperty,p={key:true,ref:true,__self:true,__source:true};
	function q(c,a,k){var b,d={},e=null,l=null;void 0!==k&&(e=""+k);void 0!==a.key&&(e=""+a.key);void 0!==a.ref&&(l=a.ref);for(b in a)n.call(a,b)&&!p.hasOwnProperty(b)&&(d[b]=a[b]);if(c&&c.defaultProps)for(b in a=c.defaultProps,a) void 0===d[b]&&(d[b]=a[b]);return {$$typeof:g,type:c,key:e,ref:l,props:d,_owner:m.current}}reactJsxRuntime_production_min.jsx=q;reactJsxRuntime_production_min.jsxs=q;
	return reactJsxRuntime_production_min;
}

var reactJsxRuntime_development = {};

/** @license React v17.0.2
 * react-jsx-runtime.development.js
 *
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var hasRequiredReactJsxRuntime_development;

function requireReactJsxRuntime_development () {
	if (hasRequiredReactJsxRuntime_development) return reactJsxRuntime_development;
	hasRequiredReactJsxRuntime_development = 1;
	(function (exports) {

		if (process.env.NODE_ENV !== "production") {
		  (function() {

		var React = React__default;
		var _assign = requireObjectAssign();

		// ATTENTION
		// When adding new symbols to this file,
		// Please consider also adding to 'react-devtools-shared/src/backend/ReactSymbols'
		// The Symbol used to tag the ReactElement-like types. If there is no native Symbol
		// nor polyfill, then a plain number is used for performance.
		var REACT_ELEMENT_TYPE = 0xeac7;
		var REACT_PORTAL_TYPE = 0xeaca;
		exports.Fragment = 0xeacb;
		var REACT_STRICT_MODE_TYPE = 0xeacc;
		var REACT_PROFILER_TYPE = 0xead2;
		var REACT_PROVIDER_TYPE = 0xeacd;
		var REACT_CONTEXT_TYPE = 0xeace;
		var REACT_FORWARD_REF_TYPE = 0xead0;
		var REACT_SUSPENSE_TYPE = 0xead1;
		var REACT_SUSPENSE_LIST_TYPE = 0xead8;
		var REACT_MEMO_TYPE = 0xead3;
		var REACT_LAZY_TYPE = 0xead4;
		var REACT_BLOCK_TYPE = 0xead9;
		var REACT_SERVER_BLOCK_TYPE = 0xeada;
		var REACT_FUNDAMENTAL_TYPE = 0xead5;
		var REACT_DEBUG_TRACING_MODE_TYPE = 0xeae1;
		var REACT_LEGACY_HIDDEN_TYPE = 0xeae3;

		if (typeof Symbol === 'function' && Symbol.for) {
		  var symbolFor = Symbol.for;
		  REACT_ELEMENT_TYPE = symbolFor('react.element');
		  REACT_PORTAL_TYPE = symbolFor('react.portal');
		  exports.Fragment = symbolFor('react.fragment');
		  REACT_STRICT_MODE_TYPE = symbolFor('react.strict_mode');
		  REACT_PROFILER_TYPE = symbolFor('react.profiler');
		  REACT_PROVIDER_TYPE = symbolFor('react.provider');
		  REACT_CONTEXT_TYPE = symbolFor('react.context');
		  REACT_FORWARD_REF_TYPE = symbolFor('react.forward_ref');
		  REACT_SUSPENSE_TYPE = symbolFor('react.suspense');
		  REACT_SUSPENSE_LIST_TYPE = symbolFor('react.suspense_list');
		  REACT_MEMO_TYPE = symbolFor('react.memo');
		  REACT_LAZY_TYPE = symbolFor('react.lazy');
		  REACT_BLOCK_TYPE = symbolFor('react.block');
		  REACT_SERVER_BLOCK_TYPE = symbolFor('react.server.block');
		  REACT_FUNDAMENTAL_TYPE = symbolFor('react.fundamental');
		  symbolFor('react.scope');
		  symbolFor('react.opaque.id');
		  REACT_DEBUG_TRACING_MODE_TYPE = symbolFor('react.debug_trace_mode');
		  symbolFor('react.offscreen');
		  REACT_LEGACY_HIDDEN_TYPE = symbolFor('react.legacy_hidden');
		}

		var MAYBE_ITERATOR_SYMBOL = typeof Symbol === 'function' && Symbol.iterator;
		var FAUX_ITERATOR_SYMBOL = '@@iterator';
		function getIteratorFn(maybeIterable) {
		  if (maybeIterable === null || typeof maybeIterable !== 'object') {
		    return null;
		  }

		  var maybeIterator = MAYBE_ITERATOR_SYMBOL && maybeIterable[MAYBE_ITERATOR_SYMBOL] || maybeIterable[FAUX_ITERATOR_SYMBOL];

		  if (typeof maybeIterator === 'function') {
		    return maybeIterator;
		  }

		  return null;
		}

		var ReactSharedInternals = React.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED;

		function error(format) {
		  {
		    for (var _len2 = arguments.length, args = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
		      args[_key2 - 1] = arguments[_key2];
		    }

		    printWarning('error', format, args);
		  }
		}

		function printWarning(level, format, args) {
		  // When changing this logic, you might want to also
		  // update consoleWithStackDev.www.js as well.
		  {
		    var ReactDebugCurrentFrame = ReactSharedInternals.ReactDebugCurrentFrame;
		    var stack = ReactDebugCurrentFrame.getStackAddendum();

		    if (stack !== '') {
		      format += '%s';
		      args = args.concat([stack]);
		    }

		    var argsWithFormat = args.map(function (item) {
		      return '' + item;
		    }); // Careful: RN currently depends on this prefix

		    argsWithFormat.unshift('Warning: ' + format); // We intentionally don't use spread (or .apply) directly because it
		    // breaks IE9: https://github.com/facebook/react/issues/13610
		    // eslint-disable-next-line react-internal/no-production-logging

		    Function.prototype.apply.call(console[level], console, argsWithFormat);
		  }
		}

		// Filter certain DOM attributes (e.g. src, href) if their values are empty strings.

		var enableScopeAPI = false; // Experimental Create Event Handle API.

		function isValidElementType(type) {
		  if (typeof type === 'string' || typeof type === 'function') {
		    return true;
		  } // Note: typeof might be other than 'symbol' or 'number' (e.g. if it's a polyfill).


		  if (type === exports.Fragment || type === REACT_PROFILER_TYPE || type === REACT_DEBUG_TRACING_MODE_TYPE || type === REACT_STRICT_MODE_TYPE || type === REACT_SUSPENSE_TYPE || type === REACT_SUSPENSE_LIST_TYPE || type === REACT_LEGACY_HIDDEN_TYPE || enableScopeAPI ) {
		    return true;
		  }

		  if (typeof type === 'object' && type !== null) {
		    if (type.$$typeof === REACT_LAZY_TYPE || type.$$typeof === REACT_MEMO_TYPE || type.$$typeof === REACT_PROVIDER_TYPE || type.$$typeof === REACT_CONTEXT_TYPE || type.$$typeof === REACT_FORWARD_REF_TYPE || type.$$typeof === REACT_FUNDAMENTAL_TYPE || type.$$typeof === REACT_BLOCK_TYPE || type[0] === REACT_SERVER_BLOCK_TYPE) {
		      return true;
		    }
		  }

		  return false;
		}

		function getWrappedName(outerType, innerType, wrapperName) {
		  var functionName = innerType.displayName || innerType.name || '';
		  return outerType.displayName || (functionName !== '' ? wrapperName + "(" + functionName + ")" : wrapperName);
		}

		function getContextName(type) {
		  return type.displayName || 'Context';
		}

		function getComponentName(type) {
		  if (type == null) {
		    // Host root, text node or just invalid type.
		    return null;
		  }

		  {
		    if (typeof type.tag === 'number') {
		      error('Received an unexpected object in getComponentName(). ' + 'This is likely a bug in React. Please file an issue.');
		    }
		  }

		  if (typeof type === 'function') {
		    return type.displayName || type.name || null;
		  }

		  if (typeof type === 'string') {
		    return type;
		  }

		  switch (type) {
		    case exports.Fragment:
		      return 'Fragment';

		    case REACT_PORTAL_TYPE:
		      return 'Portal';

		    case REACT_PROFILER_TYPE:
		      return 'Profiler';

		    case REACT_STRICT_MODE_TYPE:
		      return 'StrictMode';

		    case REACT_SUSPENSE_TYPE:
		      return 'Suspense';

		    case REACT_SUSPENSE_LIST_TYPE:
		      return 'SuspenseList';
		  }

		  if (typeof type === 'object') {
		    switch (type.$$typeof) {
		      case REACT_CONTEXT_TYPE:
		        var context = type;
		        return getContextName(context) + '.Consumer';

		      case REACT_PROVIDER_TYPE:
		        var provider = type;
		        return getContextName(provider._context) + '.Provider';

		      case REACT_FORWARD_REF_TYPE:
		        return getWrappedName(type, type.render, 'ForwardRef');

		      case REACT_MEMO_TYPE:
		        return getComponentName(type.type);

		      case REACT_BLOCK_TYPE:
		        return getComponentName(type._render);

		      case REACT_LAZY_TYPE:
		        {
		          var lazyComponent = type;
		          var payload = lazyComponent._payload;
		          var init = lazyComponent._init;

		          try {
		            return getComponentName(init(payload));
		          } catch (x) {
		            return null;
		          }
		        }
		    }
		  }

		  return null;
		}

		// Helpers to patch console.logs to avoid logging during side-effect free
		// replaying on render function. This currently only patches the object
		// lazily which won't cover if the log function was extracted eagerly.
		// We could also eagerly patch the method.
		var disabledDepth = 0;
		var prevLog;
		var prevInfo;
		var prevWarn;
		var prevError;
		var prevGroup;
		var prevGroupCollapsed;
		var prevGroupEnd;

		function disabledLog() {}

		disabledLog.__reactDisabledLog = true;
		function disableLogs() {
		  {
		    if (disabledDepth === 0) {
		      /* eslint-disable react-internal/no-production-logging */
		      prevLog = console.log;
		      prevInfo = console.info;
		      prevWarn = console.warn;
		      prevError = console.error;
		      prevGroup = console.group;
		      prevGroupCollapsed = console.groupCollapsed;
		      prevGroupEnd = console.groupEnd; // https://github.com/facebook/react/issues/19099

		      var props = {
		        configurable: true,
		        enumerable: true,
		        value: disabledLog,
		        writable: true
		      }; // $FlowFixMe Flow thinks console is immutable.

		      Object.defineProperties(console, {
		        info: props,
		        log: props,
		        warn: props,
		        error: props,
		        group: props,
		        groupCollapsed: props,
		        groupEnd: props
		      });
		      /* eslint-enable react-internal/no-production-logging */
		    }

		    disabledDepth++;
		  }
		}
		function reenableLogs() {
		  {
		    disabledDepth--;

		    if (disabledDepth === 0) {
		      /* eslint-disable react-internal/no-production-logging */
		      var props = {
		        configurable: true,
		        enumerable: true,
		        writable: true
		      }; // $FlowFixMe Flow thinks console is immutable.

		      Object.defineProperties(console, {
		        log: _assign({}, props, {
		          value: prevLog
		        }),
		        info: _assign({}, props, {
		          value: prevInfo
		        }),
		        warn: _assign({}, props, {
		          value: prevWarn
		        }),
		        error: _assign({}, props, {
		          value: prevError
		        }),
		        group: _assign({}, props, {
		          value: prevGroup
		        }),
		        groupCollapsed: _assign({}, props, {
		          value: prevGroupCollapsed
		        }),
		        groupEnd: _assign({}, props, {
		          value: prevGroupEnd
		        })
		      });
		      /* eslint-enable react-internal/no-production-logging */
		    }

		    if (disabledDepth < 0) {
		      error('disabledDepth fell below zero. ' + 'This is a bug in React. Please file an issue.');
		    }
		  }
		}

		var ReactCurrentDispatcher = ReactSharedInternals.ReactCurrentDispatcher;
		var prefix;
		function describeBuiltInComponentFrame(name, source, ownerFn) {
		  {
		    if (prefix === undefined) {
		      // Extract the VM specific prefix used by each line.
		      try {
		        throw Error();
		      } catch (x) {
		        var match = x.stack.trim().match(/\n( *(at )?)/);
		        prefix = match && match[1] || '';
		      }
		    } // We use the prefix to ensure our stacks line up with native stack frames.


		    return '\n' + prefix + name;
		  }
		}
		var reentry = false;
		var componentFrameCache;

		{
		  var PossiblyWeakMap = typeof WeakMap === 'function' ? WeakMap : Map;
		  componentFrameCache = new PossiblyWeakMap();
		}

		function describeNativeComponentFrame(fn, construct) {
		  // If something asked for a stack inside a fake render, it should get ignored.
		  if (!fn || reentry) {
		    return '';
		  }

		  {
		    var frame = componentFrameCache.get(fn);

		    if (frame !== undefined) {
		      return frame;
		    }
		  }

		  var control;
		  reentry = true;
		  var previousPrepareStackTrace = Error.prepareStackTrace; // $FlowFixMe It does accept undefined.

		  Error.prepareStackTrace = undefined;
		  var previousDispatcher;

		  {
		    previousDispatcher = ReactCurrentDispatcher.current; // Set the dispatcher in DEV because this might be call in the render function
		    // for warnings.

		    ReactCurrentDispatcher.current = null;
		    disableLogs();
		  }

		  try {
		    // This should throw.
		    if (construct) {
		      // Something should be setting the props in the constructor.
		      var Fake = function () {
		        throw Error();
		      }; // $FlowFixMe


		      Object.defineProperty(Fake.prototype, 'props', {
		        set: function () {
		          // We use a throwing setter instead of frozen or non-writable props
		          // because that won't throw in a non-strict mode function.
		          throw Error();
		        }
		      });

		      if (typeof Reflect === 'object' && Reflect.construct) {
		        // We construct a different control for this case to include any extra
		        // frames added by the construct call.
		        try {
		          Reflect.construct(Fake, []);
		        } catch (x) {
		          control = x;
		        }

		        Reflect.construct(fn, [], Fake);
		      } else {
		        try {
		          Fake.call();
		        } catch (x) {
		          control = x;
		        }

		        fn.call(Fake.prototype);
		      }
		    } else {
		      try {
		        throw Error();
		      } catch (x) {
		        control = x;
		      }

		      fn();
		    }
		  } catch (sample) {
		    // This is inlined manually because closure doesn't do it for us.
		    if (sample && control && typeof sample.stack === 'string') {
		      // This extracts the first frame from the sample that isn't also in the control.
		      // Skipping one frame that we assume is the frame that calls the two.
		      var sampleLines = sample.stack.split('\n');
		      var controlLines = control.stack.split('\n');
		      var s = sampleLines.length - 1;
		      var c = controlLines.length - 1;

		      while (s >= 1 && c >= 0 && sampleLines[s] !== controlLines[c]) {
		        // We expect at least one stack frame to be shared.
		        // Typically this will be the root most one. However, stack frames may be
		        // cut off due to maximum stack limits. In this case, one maybe cut off
		        // earlier than the other. We assume that the sample is longer or the same
		        // and there for cut off earlier. So we should find the root most frame in
		        // the sample somewhere in the control.
		        c--;
		      }

		      for (; s >= 1 && c >= 0; s--, c--) {
		        // Next we find the first one that isn't the same which should be the
		        // frame that called our sample function and the control.
		        if (sampleLines[s] !== controlLines[c]) {
		          // In V8, the first line is describing the message but other VMs don't.
		          // If we're about to return the first line, and the control is also on the same
		          // line, that's a pretty good indicator that our sample threw at same line as
		          // the control. I.e. before we entered the sample frame. So we ignore this result.
		          // This can happen if you passed a class to function component, or non-function.
		          if (s !== 1 || c !== 1) {
		            do {
		              s--;
		              c--; // We may still have similar intermediate frames from the construct call.
		              // The next one that isn't the same should be our match though.

		              if (c < 0 || sampleLines[s] !== controlLines[c]) {
		                // V8 adds a "new" prefix for native classes. Let's remove it to make it prettier.
		                var _frame = '\n' + sampleLines[s].replace(' at new ', ' at ');

		                {
		                  if (typeof fn === 'function') {
		                    componentFrameCache.set(fn, _frame);
		                  }
		                } // Return the line we found.


		                return _frame;
		              }
		            } while (s >= 1 && c >= 0);
		          }

		          break;
		        }
		      }
		    }
		  } finally {
		    reentry = false;

		    {
		      ReactCurrentDispatcher.current = previousDispatcher;
		      reenableLogs();
		    }

		    Error.prepareStackTrace = previousPrepareStackTrace;
		  } // Fallback to just using the name if we couldn't make it throw.


		  var name = fn ? fn.displayName || fn.name : '';
		  var syntheticFrame = name ? describeBuiltInComponentFrame(name) : '';

		  {
		    if (typeof fn === 'function') {
		      componentFrameCache.set(fn, syntheticFrame);
		    }
		  }

		  return syntheticFrame;
		}
		function describeFunctionComponentFrame(fn, source, ownerFn) {
		  {
		    return describeNativeComponentFrame(fn, false);
		  }
		}

		function shouldConstruct(Component) {
		  var prototype = Component.prototype;
		  return !!(prototype && prototype.isReactComponent);
		}

		function describeUnknownElementTypeFrameInDEV(type, source, ownerFn) {

		  if (type == null) {
		    return '';
		  }

		  if (typeof type === 'function') {
		    {
		      return describeNativeComponentFrame(type, shouldConstruct(type));
		    }
		  }

		  if (typeof type === 'string') {
		    return describeBuiltInComponentFrame(type);
		  }

		  switch (type) {
		    case REACT_SUSPENSE_TYPE:
		      return describeBuiltInComponentFrame('Suspense');

		    case REACT_SUSPENSE_LIST_TYPE:
		      return describeBuiltInComponentFrame('SuspenseList');
		  }

		  if (typeof type === 'object') {
		    switch (type.$$typeof) {
		      case REACT_FORWARD_REF_TYPE:
		        return describeFunctionComponentFrame(type.render);

		      case REACT_MEMO_TYPE:
		        // Memo may contain any component type so we recursively resolve it.
		        return describeUnknownElementTypeFrameInDEV(type.type, source, ownerFn);

		      case REACT_BLOCK_TYPE:
		        return describeFunctionComponentFrame(type._render);

		      case REACT_LAZY_TYPE:
		        {
		          var lazyComponent = type;
		          var payload = lazyComponent._payload;
		          var init = lazyComponent._init;

		          try {
		            // Lazy may contain any component type so we recursively resolve it.
		            return describeUnknownElementTypeFrameInDEV(init(payload), source, ownerFn);
		          } catch (x) {}
		        }
		    }
		  }

		  return '';
		}

		var loggedTypeFailures = {};
		var ReactDebugCurrentFrame = ReactSharedInternals.ReactDebugCurrentFrame;

		function setCurrentlyValidatingElement(element) {
		  {
		    if (element) {
		      var owner = element._owner;
		      var stack = describeUnknownElementTypeFrameInDEV(element.type, element._source, owner ? owner.type : null);
		      ReactDebugCurrentFrame.setExtraStackFrame(stack);
		    } else {
		      ReactDebugCurrentFrame.setExtraStackFrame(null);
		    }
		  }
		}

		function checkPropTypes(typeSpecs, values, location, componentName, element) {
		  {
		    // $FlowFixMe This is okay but Flow doesn't know it.
		    var has = Function.call.bind(Object.prototype.hasOwnProperty);

		    for (var typeSpecName in typeSpecs) {
		      if (has(typeSpecs, typeSpecName)) {
		        var error$1 = void 0; // Prop type validation may throw. In case they do, we don't want to
		        // fail the render phase where it didn't fail before. So we log it.
		        // After these have been cleaned up, we'll let them throw.

		        try {
		          // This is intentionally an invariant that gets caught. It's the same
		          // behavior as without this statement except with a better message.
		          if (typeof typeSpecs[typeSpecName] !== 'function') {
		            var err = Error((componentName || 'React class') + ': ' + location + ' type `' + typeSpecName + '` is invalid; ' + 'it must be a function, usually from the `prop-types` package, but received `' + typeof typeSpecs[typeSpecName] + '`.' + 'This often happens because of typos such as `PropTypes.function` instead of `PropTypes.func`.');
		            err.name = 'Invariant Violation';
		            throw err;
		          }

		          error$1 = typeSpecs[typeSpecName](values, typeSpecName, componentName, location, null, 'SECRET_DO_NOT_PASS_THIS_OR_YOU_WILL_BE_FIRED');
		        } catch (ex) {
		          error$1 = ex;
		        }

		        if (error$1 && !(error$1 instanceof Error)) {
		          setCurrentlyValidatingElement(element);

		          error('%s: type specification of %s' + ' `%s` is invalid; the type checker ' + 'function must return `null` or an `Error` but returned a %s. ' + 'You may have forgotten to pass an argument to the type checker ' + 'creator (arrayOf, instanceOf, objectOf, oneOf, oneOfType, and ' + 'shape all require an argument).', componentName || 'React class', location, typeSpecName, typeof error$1);

		          setCurrentlyValidatingElement(null);
		        }

		        if (error$1 instanceof Error && !(error$1.message in loggedTypeFailures)) {
		          // Only monitor this failure once because there tends to be a lot of the
		          // same error.
		          loggedTypeFailures[error$1.message] = true;
		          setCurrentlyValidatingElement(element);

		          error('Failed %s type: %s', location, error$1.message);

		          setCurrentlyValidatingElement(null);
		        }
		      }
		    }
		  }
		}

		var ReactCurrentOwner = ReactSharedInternals.ReactCurrentOwner;
		var hasOwnProperty = Object.prototype.hasOwnProperty;
		var RESERVED_PROPS = {
		  key: true,
		  ref: true,
		  __self: true,
		  __source: true
		};
		var specialPropKeyWarningShown;
		var specialPropRefWarningShown;

		function hasValidRef(config) {
		  {
		    if (hasOwnProperty.call(config, 'ref')) {
		      var getter = Object.getOwnPropertyDescriptor(config, 'ref').get;

		      if (getter && getter.isReactWarning) {
		        return false;
		      }
		    }
		  }

		  return config.ref !== undefined;
		}

		function hasValidKey(config) {
		  {
		    if (hasOwnProperty.call(config, 'key')) {
		      var getter = Object.getOwnPropertyDescriptor(config, 'key').get;

		      if (getter && getter.isReactWarning) {
		        return false;
		      }
		    }
		  }

		  return config.key !== undefined;
		}

		function warnIfStringRefCannotBeAutoConverted(config, self) {
		  {
		    if (typeof config.ref === 'string' && ReactCurrentOwner.current && self) ;
		  }
		}

		function defineKeyPropWarningGetter(props, displayName) {
		  {
		    var warnAboutAccessingKey = function () {
		      if (!specialPropKeyWarningShown) {
		        specialPropKeyWarningShown = true;

		        error('%s: `key` is not a prop. Trying to access it will result ' + 'in `undefined` being returned. If you need to access the same ' + 'value within the child component, you should pass it as a different ' + 'prop. (https://reactjs.org/link/special-props)', displayName);
		      }
		    };

		    warnAboutAccessingKey.isReactWarning = true;
		    Object.defineProperty(props, 'key', {
		      get: warnAboutAccessingKey,
		      configurable: true
		    });
		  }
		}

		function defineRefPropWarningGetter(props, displayName) {
		  {
		    var warnAboutAccessingRef = function () {
		      if (!specialPropRefWarningShown) {
		        specialPropRefWarningShown = true;

		        error('%s: `ref` is not a prop. Trying to access it will result ' + 'in `undefined` being returned. If you need to access the same ' + 'value within the child component, you should pass it as a different ' + 'prop. (https://reactjs.org/link/special-props)', displayName);
		      }
		    };

		    warnAboutAccessingRef.isReactWarning = true;
		    Object.defineProperty(props, 'ref', {
		      get: warnAboutAccessingRef,
		      configurable: true
		    });
		  }
		}
		/**
		 * Factory method to create a new React element. This no longer adheres to
		 * the class pattern, so do not use new to call it. Also, instanceof check
		 * will not work. Instead test $$typeof field against Symbol.for('react.element') to check
		 * if something is a React Element.
		 *
		 * @param {*} type
		 * @param {*} props
		 * @param {*} key
		 * @param {string|object} ref
		 * @param {*} owner
		 * @param {*} self A *temporary* helper to detect places where `this` is
		 * different from the `owner` when React.createElement is called, so that we
		 * can warn. We want to get rid of owner and replace string `ref`s with arrow
		 * functions, and as long as `this` and owner are the same, there will be no
		 * change in behavior.
		 * @param {*} source An annotation object (added by a transpiler or otherwise)
		 * indicating filename, line number, and/or other information.
		 * @internal
		 */


		var ReactElement = function (type, key, ref, self, source, owner, props) {
		  var element = {
		    // This tag allows us to uniquely identify this as a React Element
		    $$typeof: REACT_ELEMENT_TYPE,
		    // Built-in properties that belong on the element
		    type: type,
		    key: key,
		    ref: ref,
		    props: props,
		    // Record the component responsible for creating this element.
		    _owner: owner
		  };

		  {
		    // The validation flag is currently mutative. We put it on
		    // an external backing store so that we can freeze the whole object.
		    // This can be replaced with a WeakMap once they are implemented in
		    // commonly used development environments.
		    element._store = {}; // To make comparing ReactElements easier for testing purposes, we make
		    // the validation flag non-enumerable (where possible, which should
		    // include every environment we run tests in), so the test framework
		    // ignores it.

		    Object.defineProperty(element._store, 'validated', {
		      configurable: false,
		      enumerable: false,
		      writable: true,
		      value: false
		    }); // self and source are DEV only properties.

		    Object.defineProperty(element, '_self', {
		      configurable: false,
		      enumerable: false,
		      writable: false,
		      value: self
		    }); // Two elements created in two different places should be considered
		    // equal for testing purposes and therefore we hide it from enumeration.

		    Object.defineProperty(element, '_source', {
		      configurable: false,
		      enumerable: false,
		      writable: false,
		      value: source
		    });

		    if (Object.freeze) {
		      Object.freeze(element.props);
		      Object.freeze(element);
		    }
		  }

		  return element;
		};
		/**
		 * https://github.com/reactjs/rfcs/pull/107
		 * @param {*} type
		 * @param {object} props
		 * @param {string} key
		 */

		function jsxDEV(type, config, maybeKey, source, self) {
		  {
		    var propName; // Reserved names are extracted

		    var props = {};
		    var key = null;
		    var ref = null; // Currently, key can be spread in as a prop. This causes a potential
		    // issue if key is also explicitly declared (ie. <div {...props} key="Hi" />
		    // or <div key="Hi" {...props} /> ). We want to deprecate key spread,
		    // but as an intermediary step, we will use jsxDEV for everything except
		    // <div {...props} key="Hi" />, because we aren't currently able to tell if
		    // key is explicitly declared to be undefined or not.

		    if (maybeKey !== undefined) {
		      key = '' + maybeKey;
		    }

		    if (hasValidKey(config)) {
		      key = '' + config.key;
		    }

		    if (hasValidRef(config)) {
		      ref = config.ref;
		      warnIfStringRefCannotBeAutoConverted(config, self);
		    } // Remaining properties are added to a new props object


		    for (propName in config) {
		      if (hasOwnProperty.call(config, propName) && !RESERVED_PROPS.hasOwnProperty(propName)) {
		        props[propName] = config[propName];
		      }
		    } // Resolve default props


		    if (type && type.defaultProps) {
		      var defaultProps = type.defaultProps;

		      for (propName in defaultProps) {
		        if (props[propName] === undefined) {
		          props[propName] = defaultProps[propName];
		        }
		      }
		    }

		    if (key || ref) {
		      var displayName = typeof type === 'function' ? type.displayName || type.name || 'Unknown' : type;

		      if (key) {
		        defineKeyPropWarningGetter(props, displayName);
		      }

		      if (ref) {
		        defineRefPropWarningGetter(props, displayName);
		      }
		    }

		    return ReactElement(type, key, ref, self, source, ReactCurrentOwner.current, props);
		  }
		}

		var ReactCurrentOwner$1 = ReactSharedInternals.ReactCurrentOwner;
		var ReactDebugCurrentFrame$1 = ReactSharedInternals.ReactDebugCurrentFrame;

		function setCurrentlyValidatingElement$1(element) {
		  {
		    if (element) {
		      var owner = element._owner;
		      var stack = describeUnknownElementTypeFrameInDEV(element.type, element._source, owner ? owner.type : null);
		      ReactDebugCurrentFrame$1.setExtraStackFrame(stack);
		    } else {
		      ReactDebugCurrentFrame$1.setExtraStackFrame(null);
		    }
		  }
		}

		var propTypesMisspellWarningShown;

		{
		  propTypesMisspellWarningShown = false;
		}
		/**
		 * Verifies the object is a ReactElement.
		 * See https://reactjs.org/docs/react-api.html#isvalidelement
		 * @param {?object} object
		 * @return {boolean} True if `object` is a ReactElement.
		 * @final
		 */

		function isValidElement(object) {
		  {
		    return typeof object === 'object' && object !== null && object.$$typeof === REACT_ELEMENT_TYPE;
		  }
		}

		function getDeclarationErrorAddendum() {
		  {
		    if (ReactCurrentOwner$1.current) {
		      var name = getComponentName(ReactCurrentOwner$1.current.type);

		      if (name) {
		        return '\n\nCheck the render method of `' + name + '`.';
		      }
		    }

		    return '';
		  }
		}

		function getSourceInfoErrorAddendum(source) {
		  {

		    return '';
		  }
		}
		/**
		 * Warn if there's no key explicitly set on dynamic arrays of children or
		 * object keys are not valid. This allows us to keep track of children between
		 * updates.
		 */


		var ownerHasKeyUseWarning = {};

		function getCurrentComponentErrorInfo(parentType) {
		  {
		    var info = getDeclarationErrorAddendum();

		    if (!info) {
		      var parentName = typeof parentType === 'string' ? parentType : parentType.displayName || parentType.name;

		      if (parentName) {
		        info = "\n\nCheck the top-level render call using <" + parentName + ">.";
		      }
		    }

		    return info;
		  }
		}
		/**
		 * Warn if the element doesn't have an explicit key assigned to it.
		 * This element is in an array. The array could grow and shrink or be
		 * reordered. All children that haven't already been validated are required to
		 * have a "key" property assigned to it. Error statuses are cached so a warning
		 * will only be shown once.
		 *
		 * @internal
		 * @param {ReactElement} element Element that requires a key.
		 * @param {*} parentType element's parent's type.
		 */


		function validateExplicitKey(element, parentType) {
		  {
		    if (!element._store || element._store.validated || element.key != null) {
		      return;
		    }

		    element._store.validated = true;
		    var currentComponentErrorInfo = getCurrentComponentErrorInfo(parentType);

		    if (ownerHasKeyUseWarning[currentComponentErrorInfo]) {
		      return;
		    }

		    ownerHasKeyUseWarning[currentComponentErrorInfo] = true; // Usually the current owner is the offender, but if it accepts children as a
		    // property, it may be the creator of the child that's responsible for
		    // assigning it a key.

		    var childOwner = '';

		    if (element && element._owner && element._owner !== ReactCurrentOwner$1.current) {
		      // Give the component that originally created this child.
		      childOwner = " It was passed a child from " + getComponentName(element._owner.type) + ".";
		    }

		    setCurrentlyValidatingElement$1(element);

		    error('Each child in a list should have a unique "key" prop.' + '%s%s See https://reactjs.org/link/warning-keys for more information.', currentComponentErrorInfo, childOwner);

		    setCurrentlyValidatingElement$1(null);
		  }
		}
		/**
		 * Ensure that every element either is passed in a static location, in an
		 * array with an explicit keys property defined, or in an object literal
		 * with valid key property.
		 *
		 * @internal
		 * @param {ReactNode} node Statically passed child of any type.
		 * @param {*} parentType node's parent's type.
		 */


		function validateChildKeys(node, parentType) {
		  {
		    if (typeof node !== 'object') {
		      return;
		    }

		    if (Array.isArray(node)) {
		      for (var i = 0; i < node.length; i++) {
		        var child = node[i];

		        if (isValidElement(child)) {
		          validateExplicitKey(child, parentType);
		        }
		      }
		    } else if (isValidElement(node)) {
		      // This element was passed in a valid location.
		      if (node._store) {
		        node._store.validated = true;
		      }
		    } else if (node) {
		      var iteratorFn = getIteratorFn(node);

		      if (typeof iteratorFn === 'function') {
		        // Entry iterators used to provide implicit keys,
		        // but now we print a separate warning for them later.
		        if (iteratorFn !== node.entries) {
		          var iterator = iteratorFn.call(node);
		          var step;

		          while (!(step = iterator.next()).done) {
		            if (isValidElement(step.value)) {
		              validateExplicitKey(step.value, parentType);
		            }
		          }
		        }
		      }
		    }
		  }
		}
		/**
		 * Given an element, validate that its props follow the propTypes definition,
		 * provided by the type.
		 *
		 * @param {ReactElement} element
		 */


		function validatePropTypes(element) {
		  {
		    var type = element.type;

		    if (type === null || type === undefined || typeof type === 'string') {
		      return;
		    }

		    var propTypes;

		    if (typeof type === 'function') {
		      propTypes = type.propTypes;
		    } else if (typeof type === 'object' && (type.$$typeof === REACT_FORWARD_REF_TYPE || // Note: Memo only checks outer props here.
		    // Inner props are checked in the reconciler.
		    type.$$typeof === REACT_MEMO_TYPE)) {
		      propTypes = type.propTypes;
		    } else {
		      return;
		    }

		    if (propTypes) {
		      // Intentionally inside to avoid triggering lazy initializers:
		      var name = getComponentName(type);
		      checkPropTypes(propTypes, element.props, 'prop', name, element);
		    } else if (type.PropTypes !== undefined && !propTypesMisspellWarningShown) {
		      propTypesMisspellWarningShown = true; // Intentionally inside to avoid triggering lazy initializers:

		      var _name = getComponentName(type);

		      error('Component %s declared `PropTypes` instead of `propTypes`. Did you misspell the property assignment?', _name || 'Unknown');
		    }

		    if (typeof type.getDefaultProps === 'function' && !type.getDefaultProps.isReactClassApproved) {
		      error('getDefaultProps is only used on classic React.createClass ' + 'definitions. Use a static property named `defaultProps` instead.');
		    }
		  }
		}
		/**
		 * Given a fragment, validate that it can only be provided with fragment props
		 * @param {ReactElement} fragment
		 */


		function validateFragmentProps(fragment) {
		  {
		    var keys = Object.keys(fragment.props);

		    for (var i = 0; i < keys.length; i++) {
		      var key = keys[i];

		      if (key !== 'children' && key !== 'key') {
		        setCurrentlyValidatingElement$1(fragment);

		        error('Invalid prop `%s` supplied to `React.Fragment`. ' + 'React.Fragment can only have `key` and `children` props.', key);

		        setCurrentlyValidatingElement$1(null);
		        break;
		      }
		    }

		    if (fragment.ref !== null) {
		      setCurrentlyValidatingElement$1(fragment);

		      error('Invalid attribute `ref` supplied to `React.Fragment`.');

		      setCurrentlyValidatingElement$1(null);
		    }
		  }
		}

		function jsxWithValidation(type, props, key, isStaticChildren, source, self) {
		  {
		    var validType = isValidElementType(type); // We warn in this case but don't throw. We expect the element creation to
		    // succeed and there will likely be errors in render.

		    if (!validType) {
		      var info = '';

		      if (type === undefined || typeof type === 'object' && type !== null && Object.keys(type).length === 0) {
		        info += ' You likely forgot to export your component from the file ' + "it's defined in, or you might have mixed up default and named imports.";
		      }

		      var sourceInfo = getSourceInfoErrorAddendum();

		      if (sourceInfo) {
		        info += sourceInfo;
		      } else {
		        info += getDeclarationErrorAddendum();
		      }

		      var typeString;

		      if (type === null) {
		        typeString = 'null';
		      } else if (Array.isArray(type)) {
		        typeString = 'array';
		      } else if (type !== undefined && type.$$typeof === REACT_ELEMENT_TYPE) {
		        typeString = "<" + (getComponentName(type.type) || 'Unknown') + " />";
		        info = ' Did you accidentally export a JSX literal instead of a component?';
		      } else {
		        typeString = typeof type;
		      }

		      error('React.jsx: type is invalid -- expected a string (for ' + 'built-in components) or a class/function (for composite ' + 'components) but got: %s.%s', typeString, info);
		    }

		    var element = jsxDEV(type, props, key, source, self); // The result can be nullish if a mock or a custom function is used.
		    // TODO: Drop this when these are no longer allowed as the type argument.

		    if (element == null) {
		      return element;
		    } // Skip key warning if the type isn't valid since our key validation logic
		    // doesn't expect a non-string/function type and can throw confusing errors.
		    // We don't want exception behavior to differ between dev and prod.
		    // (Rendering will throw with a helpful message and as soon as the type is
		    // fixed, the key warnings will appear.)


		    if (validType) {
		      var children = props.children;

		      if (children !== undefined) {
		        if (isStaticChildren) {
		          if (Array.isArray(children)) {
		            for (var i = 0; i < children.length; i++) {
		              validateChildKeys(children[i], type);
		            }

		            if (Object.freeze) {
		              Object.freeze(children);
		            }
		          } else {
		            error('React.jsx: Static children should always be an array. ' + 'You are likely explicitly calling React.jsxs or React.jsxDEV. ' + 'Use the Babel transform instead.');
		          }
		        } else {
		          validateChildKeys(children, type);
		        }
		      }
		    }

		    if (type === exports.Fragment) {
		      validateFragmentProps(element);
		    } else {
		      validatePropTypes(element);
		    }

		    return element;
		  }
		} // These two functions exist to still get child warnings in dev
		// even with the prod transform. This means that jsxDEV is purely
		// opt-in behavior for better messages but that we won't stop
		// giving you warnings if you use production apis.

		function jsxWithValidationStatic(type, props, key) {
		  {
		    return jsxWithValidation(type, props, key, true);
		  }
		}
		function jsxWithValidationDynamic(type, props, key) {
		  {
		    return jsxWithValidation(type, props, key, false);
		  }
		}

		var jsx =  jsxWithValidationDynamic ; // we may want to special case jsxs internally to take advantage of static children.
		// for now we can ship identical prod functions

		var jsxs =  jsxWithValidationStatic ;

		exports.jsx = jsx;
		exports.jsxs = jsxs;
		  })();
		} 
	} (reactJsxRuntime_development));
	return reactJsxRuntime_development;
}

if (process.env.NODE_ENV === 'production') {
  jsxRuntime.exports = requireReactJsxRuntime_production_min();
} else {
  jsxRuntime.exports = requireReactJsxRuntime_development();
}

var jsxRuntimeExports = jsxRuntime.exports;

var IconContext = /*#__PURE__*/createContext({});

function _typeof(o) {
  "@babel/helpers - typeof";

  return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) {
    return typeof o;
  } : function (o) {
    return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o;
  }, _typeof(o);
}

function toPrimitive(t, r) {
  if ("object" != _typeof(t) || !t) return t;
  var e = t[Symbol.toPrimitive];
  if (void 0 !== e) {
    var i = e.call(t, r);
    if ("object" != _typeof(i)) return i;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return ("string" === r ? String : Number)(t);
}

function toPropertyKey(t) {
  var i = toPrimitive(t, "string");
  return "symbol" == _typeof(i) ? i : i + "";
}

function _defineProperty(e, r, t) {
  return (r = toPropertyKey(r)) in e ? Object.defineProperty(e, r, {
    value: t,
    enumerable: true,
    configurable: true,
    writable: true
  }) : e[r] = t, e;
}

function ownKeys(e, r) {
  var t = Object.keys(e);
  if (Object.getOwnPropertySymbols) {
    var o = Object.getOwnPropertySymbols(e);
    r && (o = o.filter(function (r) {
      return Object.getOwnPropertyDescriptor(e, r).enumerable;
    })), t.push.apply(t, o);
  }
  return t;
}
function _objectSpread2(e) {
  for (var r = 1; r < arguments.length; r++) {
    var t = null != arguments[r] ? arguments[r] : {};
    r % 2 ? ownKeys(Object(t), true).forEach(function (r) {
      _defineProperty(e, r, t[r]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) {
      Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r));
    });
  }
  return e;
}

function _arrayWithHoles(r) {
  if (Array.isArray(r)) return r;
}

function _iterableToArrayLimit(r, l) {
  var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"];
  if (null != t) {
    var e,
      n,
      i,
      u,
      a = [],
      f = true,
      o = false;
    try {
      if (i = (t = t.call(r)).next, 0 === l) ; else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0);
    } catch (r) {
      o = true, n = r;
    } finally {
      try {
        if (!f && null != t["return"] && (u = t["return"](), Object(u) !== u)) return;
      } finally {
        if (o) throw n;
      }
    }
    return a;
  }
}

function _arrayLikeToArray(r, a) {
  (null == a || a > r.length) && (a = r.length);
  for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e];
  return n;
}

function _unsupportedIterableToArray(r, a) {
  if (r) {
    if ("string" == typeof r) return _arrayLikeToArray(r, a);
    var t = {}.toString.call(r).slice(8, -1);
    return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0;
  }
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _slicedToArray(r, e) {
  return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest();
}

function _objectWithoutPropertiesLoose(r, e) {
  if (null == r) return {};
  var t = {};
  for (var n in r) if ({}.hasOwnProperty.call(r, n)) {
    if (-1 !== e.indexOf(n)) continue;
    t[n] = r[n];
  }
  return t;
}

function _objectWithoutProperties(e, t) {
  if (null == e) return {};
  var o,
    r,
    i = _objectWithoutPropertiesLoose(e, t);
  if (Object.getOwnPropertySymbols) {
    var n = Object.getOwnPropertySymbols(e);
    for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]);
  }
  return i;
}

var classnames = {exports: {}};

/*!
	Copyright (c) 2018 Jed Watson.
	Licensed under the MIT License (MIT), see
	http://jedwatson.github.io/classnames
*/

(function (module) {
	/* global define */

	(function () {

		var hasOwn = {}.hasOwnProperty;

		function classNames () {
			var classes = '';

			for (var i = 0; i < arguments.length; i++) {
				var arg = arguments[i];
				if (arg) {
					classes = appendClass(classes, parseValue(arg));
				}
			}

			return classes;
		}

		function parseValue (arg) {
			if (typeof arg === 'string' || typeof arg === 'number') {
				return arg;
			}

			if (typeof arg !== 'object') {
				return '';
			}

			if (Array.isArray(arg)) {
				return classNames.apply(null, arg);
			}

			if (arg.toString !== Object.prototype.toString && !arg.toString.toString().includes('[native code]')) {
				return arg.toString();
			}

			var classes = '';

			for (var key in arg) {
				if (hasOwn.call(arg, key) && arg[key]) {
					classes = appendClass(classes, key);
				}
			}

			return classes;
		}

		function appendClass (value, newClass) {
			if (!newClass) {
				return value;
			}
		
			if (value) {
				return value + ' ' + newClass;
			}
		
			return value + newClass;
		}

		if (module.exports) {
			classNames.default = classNames;
			module.exports = classNames;
		} else {
			window.classNames = classNames;
		}
	}()); 
} (classnames));

var classnamesExports = classnames.exports;
var classNames = /*@__PURE__*/getDefaultExportFromCjs(classnamesExports);

/**
 * Take input from [0, n] and return it as [0, 1]
 * @hidden
 */
function bound01(n, max) {
    if (isOnePointZero(n)) {
        n = '100%';
    }
    var isPercent = isPercentage(n);
    n = max === 360 ? n : Math.min(max, Math.max(0, parseFloat(n)));
    // Automatically convert percentage into number
    if (isPercent) {
        n = parseInt(String(n * max), 10) / 100;
    }
    // Handle floating point rounding errors
    if (Math.abs(n - max) < 0.000001) {
        return 1;
    }
    // Convert into [0, 1] range if it isn't already
    if (max === 360) {
        // If n is a hue given in degrees,
        // wrap around out-of-range values into [0, 360] range
        // then convert into [0, 1].
        n = (n < 0 ? (n % max) + max : n % max) / parseFloat(String(max));
    }
    else {
        // If n not a hue given in degrees
        // Convert into [0, 1] range if it isn't already.
        n = (n % max) / parseFloat(String(max));
    }
    return n;
}
/**
 * Need to handle 1.0 as 100%, since once it is a number, there is no difference between it and 1
 * <http://stackoverflow.com/questions/7422072/javascript-how-to-detect-number-as-a-decimal-including-1-0>
 * @hidden
 */
function isOnePointZero(n) {
    return typeof n === 'string' && n.indexOf('.') !== -1 && parseFloat(n) === 1;
}
/**
 * Check to see if string passed in is a percentage
 * @hidden
 */
function isPercentage(n) {
    return typeof n === 'string' && n.indexOf('%') !== -1;
}
/**
 * Return a valid alpha value [0,1] with all invalid values being set to 1
 * @hidden
 */
function boundAlpha(a) {
    a = parseFloat(a);
    if (isNaN(a) || a < 0 || a > 1) {
        a = 1;
    }
    return a;
}
/**
 * Replace a decimal with it's percentage value
 * @hidden
 */
function convertToPercentage(n) {
    if (n <= 1) {
        return "".concat(Number(n) * 100, "%");
    }
    return n;
}
/**
 * Force a hex value to have 2 characters
 * @hidden
 */
function pad2(c) {
    return c.length === 1 ? '0' + c : String(c);
}

// `rgbToHsl`, `rgbToHsv`, `hslToRgb`, `hsvToRgb` modified from:
// <http://mjijackson.com/2008/02/rgb-to-hsl-and-rgb-to-hsv-color-model-conversion-algorithms-in-javascript>
/**
 * Handle bounds / percentage checking to conform to CSS color spec
 * <http://www.w3.org/TR/css3-color/>
 * *Assumes:* r, g, b in [0, 255] or [0, 1]
 * *Returns:* { r, g, b } in [0, 255]
 */
function rgbToRgb(r, g, b) {
    return {
        r: bound01(r, 255) * 255,
        g: bound01(g, 255) * 255,
        b: bound01(b, 255) * 255,
    };
}
function hue2rgb(p, q, t) {
    if (t < 0) {
        t += 1;
    }
    if (t > 1) {
        t -= 1;
    }
    if (t < 1 / 6) {
        return p + (q - p) * (6 * t);
    }
    if (t < 1 / 2) {
        return q;
    }
    if (t < 2 / 3) {
        return p + (q - p) * (2 / 3 - t) * 6;
    }
    return p;
}
/**
 * Converts an HSL color value to RGB.
 *
 * *Assumes:* h is contained in [0, 1] or [0, 360] and s and l are contained [0, 1] or [0, 100]
 * *Returns:* { r, g, b } in the set [0, 255]
 */
function hslToRgb(h, s, l) {
    var r;
    var g;
    var b;
    h = bound01(h, 360);
    s = bound01(s, 100);
    l = bound01(l, 100);
    if (s === 0) {
        // achromatic
        g = l;
        b = l;
        r = l;
    }
    else {
        var q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        var p = 2 * l - q;
        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
    }
    return { r: r * 255, g: g * 255, b: b * 255 };
}
/**
 * Converts an RGB color value to HSV
 *
 * *Assumes:* r, g, and b are contained in the set [0, 255] or [0, 1]
 * *Returns:* { h, s, v } in [0,1]
 */
function rgbToHsv(r, g, b) {
    r = bound01(r, 255);
    g = bound01(g, 255);
    b = bound01(b, 255);
    var max = Math.max(r, g, b);
    var min = Math.min(r, g, b);
    var h = 0;
    var v = max;
    var d = max - min;
    var s = max === 0 ? 0 : d / max;
    if (max === min) {
        h = 0; // achromatic
    }
    else {
        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            case b:
                h = (r - g) / d + 4;
                break;
        }
        h /= 6;
    }
    return { h: h, s: s, v: v };
}
/**
 * Converts an HSV color value to RGB.
 *
 * *Assumes:* h is contained in [0, 1] or [0, 360] and s and v are contained in [0, 1] or [0, 100]
 * *Returns:* { r, g, b } in the set [0, 255]
 */
function hsvToRgb(h, s, v) {
    h = bound01(h, 360) * 6;
    s = bound01(s, 100);
    v = bound01(v, 100);
    var i = Math.floor(h);
    var f = h - i;
    var p = v * (1 - s);
    var q = v * (1 - f * s);
    var t = v * (1 - (1 - f) * s);
    var mod = i % 6;
    var r = [v, q, p, p, t, v][mod];
    var g = [t, v, v, q, p, p][mod];
    var b = [p, p, t, v, v, q][mod];
    return { r: r * 255, g: g * 255, b: b * 255 };
}
/**
 * Converts an RGB color to hex
 *
 * Assumes r, g, and b are contained in the set [0, 255]
 * Returns a 3 or 6 character hex
 */
function rgbToHex$1(r, g, b, allow3Char) {
    var hex = [
        pad2(Math.round(r).toString(16)),
        pad2(Math.round(g).toString(16)),
        pad2(Math.round(b).toString(16)),
    ];
    return hex.join('');
}
/** Converts a hex value to a decimal */
function convertHexToDecimal(h) {
    return parseIntFromHex(h) / 255;
}
/** Parse a base-16 hex value into a base-10 integer */
function parseIntFromHex(val) {
    return parseInt(val, 16);
}

// https://github.com/bahamas10/css-color-names/blob/master/css-color-names.json
/**
 * @hidden
 */
var names = {
    aliceblue: '#f0f8ff',
    antiquewhite: '#faebd7',
    aqua: '#00ffff',
    aquamarine: '#7fffd4',
    azure: '#f0ffff',
    beige: '#f5f5dc',
    bisque: '#ffe4c4',
    black: '#000000',
    blanchedalmond: '#ffebcd',
    blue: '#0000ff',
    blueviolet: '#8a2be2',
    brown: '#a52a2a',
    burlywood: '#deb887',
    cadetblue: '#5f9ea0',
    chartreuse: '#7fff00',
    chocolate: '#d2691e',
    coral: '#ff7f50',
    cornflowerblue: '#6495ed',
    cornsilk: '#fff8dc',
    crimson: '#dc143c',
    cyan: '#00ffff',
    darkblue: '#00008b',
    darkcyan: '#008b8b',
    darkgoldenrod: '#b8860b',
    darkgray: '#a9a9a9',
    darkgreen: '#006400',
    darkgrey: '#a9a9a9',
    darkkhaki: '#bdb76b',
    darkmagenta: '#8b008b',
    darkolivegreen: '#556b2f',
    darkorange: '#ff8c00',
    darkorchid: '#9932cc',
    darkred: '#8b0000',
    darksalmon: '#e9967a',
    darkseagreen: '#8fbc8f',
    darkslateblue: '#483d8b',
    darkslategray: '#2f4f4f',
    darkslategrey: '#2f4f4f',
    darkturquoise: '#00ced1',
    darkviolet: '#9400d3',
    deeppink: '#ff1493',
    deepskyblue: '#00bfff',
    dimgray: '#696969',
    dimgrey: '#696969',
    dodgerblue: '#1e90ff',
    firebrick: '#b22222',
    floralwhite: '#fffaf0',
    forestgreen: '#228b22',
    fuchsia: '#ff00ff',
    gainsboro: '#dcdcdc',
    ghostwhite: '#f8f8ff',
    goldenrod: '#daa520',
    gold: '#ffd700',
    gray: '#808080',
    green: '#008000',
    greenyellow: '#adff2f',
    grey: '#808080',
    honeydew: '#f0fff0',
    hotpink: '#ff69b4',
    indianred: '#cd5c5c',
    indigo: '#4b0082',
    ivory: '#fffff0',
    khaki: '#f0e68c',
    lavenderblush: '#fff0f5',
    lavender: '#e6e6fa',
    lawngreen: '#7cfc00',
    lemonchiffon: '#fffacd',
    lightblue: '#add8e6',
    lightcoral: '#f08080',
    lightcyan: '#e0ffff',
    lightgoldenrodyellow: '#fafad2',
    lightgray: '#d3d3d3',
    lightgreen: '#90ee90',
    lightgrey: '#d3d3d3',
    lightpink: '#ffb6c1',
    lightsalmon: '#ffa07a',
    lightseagreen: '#20b2aa',
    lightskyblue: '#87cefa',
    lightslategray: '#778899',
    lightslategrey: '#778899',
    lightsteelblue: '#b0c4de',
    lightyellow: '#ffffe0',
    lime: '#00ff00',
    limegreen: '#32cd32',
    linen: '#faf0e6',
    magenta: '#ff00ff',
    maroon: '#800000',
    mediumaquamarine: '#66cdaa',
    mediumblue: '#0000cd',
    mediumorchid: '#ba55d3',
    mediumpurple: '#9370db',
    mediumseagreen: '#3cb371',
    mediumslateblue: '#7b68ee',
    mediumspringgreen: '#00fa9a',
    mediumturquoise: '#48d1cc',
    mediumvioletred: '#c71585',
    midnightblue: '#191970',
    mintcream: '#f5fffa',
    mistyrose: '#ffe4e1',
    moccasin: '#ffe4b5',
    navajowhite: '#ffdead',
    navy: '#000080',
    oldlace: '#fdf5e6',
    olive: '#808000',
    olivedrab: '#6b8e23',
    orange: '#ffa500',
    orangered: '#ff4500',
    orchid: '#da70d6',
    palegoldenrod: '#eee8aa',
    palegreen: '#98fb98',
    paleturquoise: '#afeeee',
    palevioletred: '#db7093',
    papayawhip: '#ffefd5',
    peachpuff: '#ffdab9',
    peru: '#cd853f',
    pink: '#ffc0cb',
    plum: '#dda0dd',
    powderblue: '#b0e0e6',
    purple: '#800080',
    rebeccapurple: '#663399',
    red: '#ff0000',
    rosybrown: '#bc8f8f',
    royalblue: '#4169e1',
    saddlebrown: '#8b4513',
    salmon: '#fa8072',
    sandybrown: '#f4a460',
    seagreen: '#2e8b57',
    seashell: '#fff5ee',
    sienna: '#a0522d',
    silver: '#c0c0c0',
    skyblue: '#87ceeb',
    slateblue: '#6a5acd',
    slategray: '#708090',
    slategrey: '#708090',
    snow: '#fffafa',
    springgreen: '#00ff7f',
    steelblue: '#4682b4',
    tan: '#d2b48c',
    teal: '#008080',
    thistle: '#d8bfd8',
    tomato: '#ff6347',
    turquoise: '#40e0d0',
    violet: '#ee82ee',
    wheat: '#f5deb3',
    white: '#ffffff',
    whitesmoke: '#f5f5f5',
    yellow: '#ffff00',
    yellowgreen: '#9acd32',
};

/* eslint-disable @typescript-eslint/no-redundant-type-constituents */
/**
 * Given a string or object, convert that input to RGB
 *
 * Possible string inputs:
 * ```
 * "red"
 * "#f00" or "f00"
 * "#ff0000" or "ff0000"
 * "#ff000000" or "ff000000"
 * "rgb 255 0 0" or "rgb (255, 0, 0)"
 * "rgb 1.0 0 0" or "rgb (1, 0, 0)"
 * "rgba (255, 0, 0, 1)" or "rgba 255, 0, 0, 1"
 * "rgba (1.0, 0, 0, 1)" or "rgba 1.0, 0, 0, 1"
 * "hsl(0, 100%, 50%)" or "hsl 0 100% 50%"
 * "hsla(0, 100%, 50%, 1)" or "hsla 0 100% 50%, 1"
 * "hsv(0, 100%, 100%)" or "hsv 0 100% 100%"
 * ```
 */
function inputToRGB(color) {
    var rgb = { r: 0, g: 0, b: 0 };
    var a = 1;
    var s = null;
    var v = null;
    var l = null;
    var ok = false;
    var format = false;
    if (typeof color === 'string') {
        color = stringInputToObject(color);
    }
    if (typeof color === 'object') {
        if (isValidCSSUnit(color.r) && isValidCSSUnit(color.g) && isValidCSSUnit(color.b)) {
            rgb = rgbToRgb(color.r, color.g, color.b);
            ok = true;
            format = String(color.r).substr(-1) === '%' ? 'prgb' : 'rgb';
        }
        else if (isValidCSSUnit(color.h) && isValidCSSUnit(color.s) && isValidCSSUnit(color.v)) {
            s = convertToPercentage(color.s);
            v = convertToPercentage(color.v);
            rgb = hsvToRgb(color.h, s, v);
            ok = true;
            format = 'hsv';
        }
        else if (isValidCSSUnit(color.h) && isValidCSSUnit(color.s) && isValidCSSUnit(color.l)) {
            s = convertToPercentage(color.s);
            l = convertToPercentage(color.l);
            rgb = hslToRgb(color.h, s, l);
            ok = true;
            format = 'hsl';
        }
        if (Object.prototype.hasOwnProperty.call(color, 'a')) {
            a = color.a;
        }
    }
    a = boundAlpha(a);
    return {
        ok: ok,
        format: color.format || format,
        r: Math.min(255, Math.max(rgb.r, 0)),
        g: Math.min(255, Math.max(rgb.g, 0)),
        b: Math.min(255, Math.max(rgb.b, 0)),
        a: a,
    };
}
// <http://www.w3.org/TR/css3-values/#integers>
var CSS_INTEGER = '[-\\+]?\\d+%?';
// <http://www.w3.org/TR/css3-values/#number-value>
var CSS_NUMBER = '[-\\+]?\\d*\\.\\d+%?';
// Allow positive/negative integer/number.  Don't capture the either/or, just the entire outcome.
var CSS_UNIT = "(?:".concat(CSS_NUMBER, ")|(?:").concat(CSS_INTEGER, ")");
// Actual matching.
// Parentheses and commas are optional, but not required.
// Whitespace can take the place of commas or opening paren
var PERMISSIVE_MATCH3 = "[\\s|\\(]+(".concat(CSS_UNIT, ")[,|\\s]+(").concat(CSS_UNIT, ")[,|\\s]+(").concat(CSS_UNIT, ")\\s*\\)?");
var PERMISSIVE_MATCH4 = "[\\s|\\(]+(".concat(CSS_UNIT, ")[,|\\s]+(").concat(CSS_UNIT, ")[,|\\s]+(").concat(CSS_UNIT, ")[,|\\s]+(").concat(CSS_UNIT, ")\\s*\\)?");
var matchers = {
    CSS_UNIT: new RegExp(CSS_UNIT),
    rgb: new RegExp('rgb' + PERMISSIVE_MATCH3),
    rgba: new RegExp('rgba' + PERMISSIVE_MATCH4),
    hsl: new RegExp('hsl' + PERMISSIVE_MATCH3),
    hsla: new RegExp('hsla' + PERMISSIVE_MATCH4),
    hsv: new RegExp('hsv' + PERMISSIVE_MATCH3),
    hsva: new RegExp('hsva' + PERMISSIVE_MATCH4),
    hex3: /^#?([0-9a-fA-F]{1})([0-9a-fA-F]{1})([0-9a-fA-F]{1})$/,
    hex6: /^#?([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/,
    hex4: /^#?([0-9a-fA-F]{1})([0-9a-fA-F]{1})([0-9a-fA-F]{1})([0-9a-fA-F]{1})$/,
    hex8: /^#?([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/,
};
/**
 * Permissive string parsing.  Take in a number of formats, and output an object
 * based on detected format.  Returns `{ r, g, b }` or `{ h, s, l }` or `{ h, s, v}`
 */
function stringInputToObject(color) {
    color = color.trim().toLowerCase();
    if (color.length === 0) {
        return false;
    }
    var named = false;
    if (names[color]) {
        color = names[color];
        named = true;
    }
    else if (color === 'transparent') {
        return { r: 0, g: 0, b: 0, a: 0, format: 'name' };
    }
    // Try to match string input using regular expressions.
    // Keep most of the number bounding out of this function - don't worry about [0,1] or [0,100] or [0,360]
    // Just return an object and let the conversion functions handle that.
    // This way the result will be the same whether the tinycolor is initialized with string or object.
    var match = matchers.rgb.exec(color);
    if (match) {
        return { r: match[1], g: match[2], b: match[3] };
    }
    match = matchers.rgba.exec(color);
    if (match) {
        return { r: match[1], g: match[2], b: match[3], a: match[4] };
    }
    match = matchers.hsl.exec(color);
    if (match) {
        return { h: match[1], s: match[2], l: match[3] };
    }
    match = matchers.hsla.exec(color);
    if (match) {
        return { h: match[1], s: match[2], l: match[3], a: match[4] };
    }
    match = matchers.hsv.exec(color);
    if (match) {
        return { h: match[1], s: match[2], v: match[3] };
    }
    match = matchers.hsva.exec(color);
    if (match) {
        return { h: match[1], s: match[2], v: match[3], a: match[4] };
    }
    match = matchers.hex8.exec(color);
    if (match) {
        return {
            r: parseIntFromHex(match[1]),
            g: parseIntFromHex(match[2]),
            b: parseIntFromHex(match[3]),
            a: convertHexToDecimal(match[4]),
            format: named ? 'name' : 'hex8',
        };
    }
    match = matchers.hex6.exec(color);
    if (match) {
        return {
            r: parseIntFromHex(match[1]),
            g: parseIntFromHex(match[2]),
            b: parseIntFromHex(match[3]),
            format: named ? 'name' : 'hex',
        };
    }
    match = matchers.hex4.exec(color);
    if (match) {
        return {
            r: parseIntFromHex(match[1] + match[1]),
            g: parseIntFromHex(match[2] + match[2]),
            b: parseIntFromHex(match[3] + match[3]),
            a: convertHexToDecimal(match[4] + match[4]),
            format: named ? 'name' : 'hex8',
        };
    }
    match = matchers.hex3.exec(color);
    if (match) {
        return {
            r: parseIntFromHex(match[1] + match[1]),
            g: parseIntFromHex(match[2] + match[2]),
            b: parseIntFromHex(match[3] + match[3]),
            format: named ? 'name' : 'hex',
        };
    }
    return false;
}
/**
 * Check to see if it looks like a CSS unit
 * (see `matchers` above for definition).
 */
function isValidCSSUnit(color) {
    return Boolean(matchers.CSS_UNIT.exec(String(color)));
}

var hueStep = 2; // 色相阶梯

var saturationStep = 0.16; // 饱和度阶梯，浅色部分

var saturationStep2 = 0.05; // 饱和度阶梯，深色部分

var brightnessStep1 = 0.05; // 亮度阶梯，浅色部分

var brightnessStep2 = 0.15; // 亮度阶梯，深色部分

var lightColorCount = 5; // 浅色数量，主色上

var darkColorCount = 4; // 深色数量，主色下
// 暗色主题颜色映射关系表

var darkColorMap = [{
  index: 7,
  opacity: 0.15
}, {
  index: 6,
  opacity: 0.25
}, {
  index: 5,
  opacity: 0.3
}, {
  index: 5,
  opacity: 0.45
}, {
  index: 5,
  opacity: 0.65
}, {
  index: 5,
  opacity: 0.85
}, {
  index: 4,
  opacity: 0.9
}, {
  index: 3,
  opacity: 0.95
}, {
  index: 2,
  opacity: 0.97
}, {
  index: 1,
  opacity: 0.98
}]; // Wrapper function ported from TinyColor.prototype.toHsv
// Keep it here because of `hsv.h * 360`

function toHsv(_ref) {
  var r = _ref.r,
      g = _ref.g,
      b = _ref.b;
  var hsv = rgbToHsv(r, g, b);
  return {
    h: hsv.h * 360,
    s: hsv.s,
    v: hsv.v
  };
} // Wrapper function ported from TinyColor.prototype.toHexString
// Keep it here because of the prefix `#`


function toHex(_ref2) {
  var r = _ref2.r,
      g = _ref2.g,
      b = _ref2.b;
  return "#".concat(rgbToHex$1(r, g, b));
} // Wrapper function ported from TinyColor.prototype.mix, not treeshakable.
// Amount in range [0, 1]
// Assume color1 & color2 has no alpha, since the following src code did so.


function mix(rgb1, rgb2, amount) {
  var p = amount / 100;
  var rgb = {
    r: (rgb2.r - rgb1.r) * p + rgb1.r,
    g: (rgb2.g - rgb1.g) * p + rgb1.g,
    b: (rgb2.b - rgb1.b) * p + rgb1.b
  };
  return rgb;
}

function getHue(hsv, i, light) {
  var hue; // 根据色相不同，色相转向不同

  if (Math.round(hsv.h) >= 60 && Math.round(hsv.h) <= 240) {
    hue = light ? Math.round(hsv.h) - hueStep * i : Math.round(hsv.h) + hueStep * i;
  } else {
    hue = light ? Math.round(hsv.h) + hueStep * i : Math.round(hsv.h) - hueStep * i;
  }

  if (hue < 0) {
    hue += 360;
  } else if (hue >= 360) {
    hue -= 360;
  }

  return hue;
}

function getSaturation(hsv, i, light) {
  // grey color don't change saturation
  if (hsv.h === 0 && hsv.s === 0) {
    return hsv.s;
  }

  var saturation;

  if (light) {
    saturation = hsv.s - saturationStep * i;
  } else if (i === darkColorCount) {
    saturation = hsv.s + saturationStep;
  } else {
    saturation = hsv.s + saturationStep2 * i;
  } // 边界值修正


  if (saturation > 1) {
    saturation = 1;
  } // 第一格的 s 限制在 0.06-0.1 之间


  if (light && i === lightColorCount && saturation > 0.1) {
    saturation = 0.1;
  }

  if (saturation < 0.06) {
    saturation = 0.06;
  }

  return Number(saturation.toFixed(2));
}

function getValue(hsv, i, light) {
  var value;

  if (light) {
    value = hsv.v + brightnessStep1 * i;
  } else {
    value = hsv.v - brightnessStep2 * i;
  }

  if (value > 1) {
    value = 1;
  }

  return Number(value.toFixed(2));
}

function generate$1(color) {
  var opts = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var patterns = [];
  var pColor = inputToRGB(color);

  for (var i = lightColorCount; i > 0; i -= 1) {
    var hsv = toHsv(pColor);
    var colorString = toHex(inputToRGB({
      h: getHue(hsv, i, true),
      s: getSaturation(hsv, i, true),
      v: getValue(hsv, i, true)
    }));
    patterns.push(colorString);
  }

  patterns.push(toHex(pColor));

  for (var _i = 1; _i <= darkColorCount; _i += 1) {
    var _hsv = toHsv(pColor);

    var _colorString = toHex(inputToRGB({
      h: getHue(_hsv, _i),
      s: getSaturation(_hsv, _i),
      v: getValue(_hsv, _i)
    }));

    patterns.push(_colorString);
  } // dark theme patterns


  if (opts.theme === 'dark') {
    return darkColorMap.map(function (_ref3) {
      var index = _ref3.index,
          opacity = _ref3.opacity;
      var darkColorString = toHex(mix(inputToRGB(opts.backgroundColor || '#141414'), inputToRGB(patterns[index]), opacity * 100));
      return darkColorString;
    });
  }

  return patterns;
}

var presetPrimaryColors = {
  red: '#F5222D',
  volcano: '#FA541C',
  orange: '#FA8C16',
  gold: '#FAAD14',
  yellow: '#FADB14',
  lime: '#A0D911',
  green: '#52C41A',
  cyan: '#13C2C2',
  blue: '#1890FF',
  geekblue: '#2F54EB',
  purple: '#722ED1',
  magenta: '#EB2F96',
  grey: '#666666'
};
var presetPalettes = {};
var presetDarkPalettes = {};
Object.keys(presetPrimaryColors).forEach(function (key) {
  presetPalettes[key] = generate$1(presetPrimaryColors[key]);
  presetPalettes[key].primary = presetPalettes[key][5]; // dark presetPalettes

  presetDarkPalettes[key] = generate$1(presetPrimaryColors[key], {
    theme: 'dark',
    backgroundColor: '#141414'
  });
  presetDarkPalettes[key].primary = presetDarkPalettes[key][5];
});

/* eslint-disable no-console */
var warned = {};
var preWarningFns = [];

/**
 * Pre warning enable you to parse content before console.error.
 * Modify to null will prevent warning.
 */
var preMessage = function preMessage(fn) {
  preWarningFns.push(fn);
};

/**
 * Warning if condition not match.
 * @param valid Condition
 * @param message Warning message
 * @example
 * ```js
 * warning(false, 'some error'); // print some error
 * warning(true, 'some error'); // print nothing
 * warning(1 === 2, 'some error'); // print some error
 * ```
 */
function warning$1(valid, message) {
  if (process.env.NODE_ENV !== 'production' && !valid && console !== undefined) {
    var finalMessage = preWarningFns.reduce(function (msg, preMessageFn) {
      return preMessageFn(msg !== null && msg !== void 0 ? msg : '', 'warning');
    }, message);
    if (finalMessage) {
      console.error("Warning: ".concat(finalMessage));
    }
  }
}

/** @see Similar to {@link warning} */
function note(valid, message) {
  if (process.env.NODE_ENV !== 'production' && !valid && console !== undefined) {
    var finalMessage = preWarningFns.reduce(function (msg, preMessageFn) {
      return preMessageFn(msg !== null && msg !== void 0 ? msg : '', 'note');
    }, message);
    if (finalMessage) {
      console.warn("Note: ".concat(finalMessage));
    }
  }
}
function resetWarned() {
  warned = {};
}
function call(method, valid, message) {
  if (!valid && !warned[message]) {
    method(false, message);
    warned[message] = true;
  }
}

/** @see Same as {@link warning}, but only warn once for the same message */
function warningOnce(valid, message) {
  call(warning$1, valid, message);
}

/** @see Same as {@link warning}, but only warn once for the same message */
function noteOnce(valid, message) {
  call(note, valid, message);
}
warningOnce.preMessage = preMessage;
warningOnce.resetWarned = resetWarned;
warningOnce.noteOnce = noteOnce;

function canUseDom() {
  return !!(typeof window !== 'undefined' && window.document && window.document.createElement);
}

function contains(root, n) {
  if (!root) {
    return false;
  }

  // Use native if support
  if (root.contains) {
    return root.contains(n);
  }

  // `document.contains` not support with IE11
  var node = n;
  while (node) {
    if (node === root) {
      return true;
    }
    node = node.parentNode;
  }
  return false;
}

var APPEND_ORDER = 'data-rc-order';
var APPEND_PRIORITY = 'data-rc-priority';
var MARK_KEY = "rc-util-key";
var containerCache = new Map();
function getMark() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
    mark = _ref.mark;
  if (mark) {
    return mark.startsWith('data-') ? mark : "data-".concat(mark);
  }
  return MARK_KEY;
}
function getContainer(option) {
  if (option.attachTo) {
    return option.attachTo;
  }
  var head = document.querySelector('head');
  return head || document.body;
}
function getOrder(prepend) {
  if (prepend === 'queue') {
    return 'prependQueue';
  }
  return prepend ? 'prepend' : 'append';
}

/**
 * Find style which inject by rc-util
 */
function findStyles(container) {
  return Array.from((containerCache.get(container) || container).children).filter(function (node) {
    return node.tagName === 'STYLE';
  });
}
function injectCSS(css) {
  var option = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  if (!canUseDom()) {
    return null;
  }
  var csp = option.csp,
    prepend = option.prepend,
    _option$priority = option.priority,
    priority = _option$priority === void 0 ? 0 : _option$priority;
  var mergedOrder = getOrder(prepend);
  var isPrependQueue = mergedOrder === 'prependQueue';
  var styleNode = document.createElement('style');
  styleNode.setAttribute(APPEND_ORDER, mergedOrder);
  if (isPrependQueue && priority) {
    styleNode.setAttribute(APPEND_PRIORITY, "".concat(priority));
  }
  if (csp !== null && csp !== void 0 && csp.nonce) {
    styleNode.nonce = csp === null || csp === void 0 ? void 0 : csp.nonce;
  }
  styleNode.innerHTML = css;
  var container = getContainer(option);
  var firstChild = container.firstChild;
  if (prepend) {
    // If is queue `prepend`, it will prepend first style and then append rest style
    if (isPrependQueue) {
      var existStyle = (option.styles || findStyles(container)).filter(function (node) {
        // Ignore style which not injected by rc-util with prepend
        if (!['prepend', 'prependQueue'].includes(node.getAttribute(APPEND_ORDER))) {
          return false;
        }

        // Ignore style which priority less then new style
        var nodePriority = Number(node.getAttribute(APPEND_PRIORITY) || 0);
        return priority >= nodePriority;
      });
      if (existStyle.length) {
        container.insertBefore(styleNode, existStyle[existStyle.length - 1].nextSibling);
        return styleNode;
      }
    }

    // Use `insertBefore` as `prepend`
    container.insertBefore(styleNode, firstChild);
  } else {
    container.appendChild(styleNode);
  }
  return styleNode;
}
function findExistNode(key) {
  var option = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var container = getContainer(option);
  return (option.styles || findStyles(container)).find(function (node) {
    return node.getAttribute(getMark(option)) === key;
  });
}

/**
 * qiankun will inject `appendChild` to insert into other
 */
function syncRealContainer(container, option) {
  var cachedRealContainer = containerCache.get(container);

  // Find real container when not cached or cached container removed
  if (!cachedRealContainer || !contains(document, cachedRealContainer)) {
    var placeholderStyle = injectCSS('', option);
    var parentNode = placeholderStyle.parentNode;
    containerCache.set(container, parentNode);
    container.removeChild(placeholderStyle);
  }
}
function updateCSS(css, key) {
  var originOption = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var container = getContainer(originOption);
  var styles = findStyles(container);
  var option = _objectSpread2(_objectSpread2({}, originOption), {}, {
    styles: styles
  });

  // Sync real parent
  syncRealContainer(container, option);
  var existNode = findExistNode(key, option);
  if (existNode) {
    var _option$csp, _option$csp2;
    if ((_option$csp = option.csp) !== null && _option$csp !== void 0 && _option$csp.nonce && existNode.nonce !== ((_option$csp2 = option.csp) === null || _option$csp2 === void 0 ? void 0 : _option$csp2.nonce)) {
      var _option$csp3;
      existNode.nonce = (_option$csp3 = option.csp) === null || _option$csp3 === void 0 ? void 0 : _option$csp3.nonce;
    }
    if (existNode.innerHTML !== css) {
      existNode.innerHTML = css;
    }
    return existNode;
  }
  var newNode = injectCSS(css, option);
  newNode.setAttribute(getMark(option), key);
  return newNode;
}

function warning(valid, message) {
  warningOnce(valid, "[@ant-design/icons] ".concat(message));
}
function isIconDefinition(target) {
  return _typeof(target) === 'object' && typeof target.name === 'string' && typeof target.theme === 'string' && (_typeof(target.icon) === 'object' || typeof target.icon === 'function');
}
function normalizeAttrs() {
  var attrs = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return Object.keys(attrs).reduce(function (acc, key) {
    var val = attrs[key];
    switch (key) {
      case 'class':
        acc.className = val;
        delete acc.class;
        break;
      default:
        delete acc[key];
        acc[camelCase(key)] = val;
    }
    return acc;
  }, {});
}
function generate(node, key, rootProps) {
  if (!rootProps) {
    return /*#__PURE__*/React__default.createElement(node.tag, _objectSpread2({
      key: key
    }, normalizeAttrs(node.attrs)), (node.children || []).map(function (child, index) {
      return generate(child, "".concat(key, "-").concat(node.tag, "-").concat(index));
    }));
  }
  return /*#__PURE__*/React__default.createElement(node.tag, _objectSpread2(_objectSpread2({
    key: key
  }, normalizeAttrs(node.attrs)), rootProps), (node.children || []).map(function (child, index) {
    return generate(child, "".concat(key, "-").concat(node.tag, "-").concat(index));
  }));
}
function getSecondaryColor(primaryColor) {
  // choose the second color
  return generate$1(primaryColor)[0];
}
function normalizeTwoToneColors(twoToneColor) {
  if (!twoToneColor) {
    return [];
  }
  return Array.isArray(twoToneColor) ? twoToneColor : [twoToneColor];
}
var iconStyles = "\n.anticon {\n  display: inline-flex;\n  alignItems: center;\n  color: inherit;\n  font-style: normal;\n  line-height: 0;\n  text-align: center;\n  text-transform: none;\n  vertical-align: -0.125em;\n  text-rendering: optimizeLegibility;\n  -webkit-font-smoothing: antialiased;\n  -moz-osx-font-smoothing: grayscale;\n}\n\n.anticon > * {\n  line-height: 1;\n}\n\n.anticon svg {\n  display: inline-block;\n}\n\n.anticon::before {\n  display: none;\n}\n\n.anticon .anticon-icon {\n  display: block;\n}\n\n.anticon[tabindex] {\n  cursor: pointer;\n}\n\n.anticon-spin::before,\n.anticon-spin {\n  display: inline-block;\n  -webkit-animation: loadingCircle 1s infinite linear;\n  animation: loadingCircle 1s infinite linear;\n}\n\n@-webkit-keyframes loadingCircle {\n  100% {\n    -webkit-transform: rotate(360deg);\n    transform: rotate(360deg);\n  }\n}\n\n@keyframes loadingCircle {\n  100% {\n    -webkit-transform: rotate(360deg);\n    transform: rotate(360deg);\n  }\n}\n";
var useInsertStyles = function useInsertStyles() {
  var styleStr = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : iconStyles;
  var _useContext = useContext(IconContext),
    csp = _useContext.csp;
  useEffect(function () {
    updateCSS(styleStr, '@ant-design-icons', {
      prepend: true,
      csp: csp
    });
  }, []);
};

var _excluded$1 = ["icon", "className", "onClick", "style", "primaryColor", "secondaryColor"];
var twoToneColorPalette = {
  primaryColor: '#333',
  secondaryColor: '#E6E6E6',
  calculated: false
};
function setTwoToneColors(_ref) {
  var primaryColor = _ref.primaryColor,
    secondaryColor = _ref.secondaryColor;
  twoToneColorPalette.primaryColor = primaryColor;
  twoToneColorPalette.secondaryColor = secondaryColor || getSecondaryColor(primaryColor);
  twoToneColorPalette.calculated = !!secondaryColor;
}
function getTwoToneColors() {
  return _objectSpread2({}, twoToneColorPalette);
}
var IconBase = function IconBase(props) {
  var icon = props.icon,
    className = props.className,
    onClick = props.onClick,
    style = props.style,
    primaryColor = props.primaryColor,
    secondaryColor = props.secondaryColor,
    restProps = _objectWithoutProperties(props, _excluded$1);
  var colors = twoToneColorPalette;
  if (primaryColor) {
    colors = {
      primaryColor: primaryColor,
      secondaryColor: secondaryColor || getSecondaryColor(primaryColor)
    };
  }
  useInsertStyles();
  warning(isIconDefinition(icon), "icon should be icon definiton, but got ".concat(icon));
  if (!isIconDefinition(icon)) {
    return null;
  }
  var target = icon;
  if (target && typeof target.icon === 'function') {
    target = _objectSpread2(_objectSpread2({}, target), {}, {
      icon: target.icon(colors.primaryColor, colors.secondaryColor)
    });
  }
  return generate(target.icon, "svg-".concat(target.name), _objectSpread2({
    className: className,
    onClick: onClick,
    style: style,
    'data-icon': target.name,
    width: '1em',
    height: '1em',
    fill: 'currentColor',
    'aria-hidden': 'true'
  }, restProps));
};
IconBase.displayName = 'IconReact';
IconBase.getTwoToneColors = getTwoToneColors;
IconBase.setTwoToneColors = setTwoToneColors;

function setTwoToneColor(twoToneColor) {
  var _normalizeTwoToneColo = normalizeTwoToneColors(twoToneColor),
    _normalizeTwoToneColo2 = _slicedToArray(_normalizeTwoToneColo, 2),
    primaryColor = _normalizeTwoToneColo2[0],
    secondaryColor = _normalizeTwoToneColo2[1];
  return IconBase.setTwoToneColors({
    primaryColor: primaryColor,
    secondaryColor: secondaryColor
  });
}
function getTwoToneColor() {
  var colors = IconBase.getTwoToneColors();
  if (!colors.calculated) {
    return colors.primaryColor;
  }
  return [colors.primaryColor, colors.secondaryColor];
}

var _excluded = ["className", "icon", "spin", "rotate", "tabIndex", "onClick", "twoToneColor"];
// Initial setting
// should move it to antd main repo?
setTwoToneColor('#1890ff');
var Icon = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var className = props.className,
    icon = props.icon,
    spin = props.spin,
    rotate = props.rotate,
    tabIndex = props.tabIndex,
    onClick = props.onClick,
    twoToneColor = props.twoToneColor,
    restProps = _objectWithoutProperties(props, _excluded);
  var _React$useContext = React.useContext(IconContext),
    _React$useContext$pre = _React$useContext.prefixCls,
    prefixCls = _React$useContext$pre === void 0 ? 'anticon' : _React$useContext$pre,
    rootClassName = _React$useContext.rootClassName;
  var classString = classNames(rootClassName, prefixCls, _defineProperty(_defineProperty({}, "".concat(prefixCls, "-").concat(icon.name), !!icon.name), "".concat(prefixCls, "-spin"), !!spin || icon.name === 'loading'), className);
  var iconTabIndex = tabIndex;
  if (iconTabIndex === undefined && onClick) {
    iconTabIndex = -1;
  }
  var svgStyle = rotate ? {
    msTransform: "rotate(".concat(rotate, "deg)"),
    transform: "rotate(".concat(rotate, "deg)")
  } : undefined;
  var _normalizeTwoToneColo = normalizeTwoToneColors(twoToneColor),
    _normalizeTwoToneColo2 = _slicedToArray(_normalizeTwoToneColo, 2),
    primaryColor = _normalizeTwoToneColo2[0],
    secondaryColor = _normalizeTwoToneColo2[1];
  return /*#__PURE__*/React.createElement("span", _objectSpread2(_objectSpread2({
    role: "img",
    "aria-label": icon.name
  }, restProps), {}, {
    ref: ref,
    tabIndex: iconTabIndex,
    onClick: onClick,
    className: classString
  }), /*#__PURE__*/React.createElement(IconBase, {
    icon: icon,
    primaryColor: primaryColor,
    secondaryColor: secondaryColor,
    style: svgStyle
  }));
});
Icon.displayName = 'AntdIcon';
Icon.getTwoToneColor = getTwoToneColor;
Icon.setTwoToneColor = setTwoToneColor;

// This icon file is generated automatically.
var QuestionCircleOutlined$1 = { "icon": { "tag": "svg", "attrs": { "viewBox": "64 64 896 896", "focusable": "false" }, "children": [{ "tag": "path", "attrs": { "d": "M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372 372 166.6 372 372-166.6 372-372 372z" } }, { "tag": "path", "attrs": { "d": "M623.6 316.7C593.6 290.4 554 276 512 276s-81.6 14.5-111.6 40.7C369.2 344 352 380.7 352 420v7.6c0 4.4 3.6 8 8 8h48c4.4 0 8-3.6 8-8V420c0-44.1 43.1-80 96-80s96 35.9 96 80c0 31.1-22 59.6-56.1 72.7-21.2 8.1-39.2 22.3-52.1 40.9-13.1 19-19.9 41.8-19.9 64.9V620c0 4.4 3.6 8 8 8h48c4.4 0 8-3.6 8-8v-22.7a48.3 48.3 0 0130.9-44.8c59-22.7 97.1-74.7 97.1-132.5.1-39.3-17.1-76-48.3-103.3zM472 732a40 40 0 1080 0 40 40 0 10-80 0z" } }] }, "name": "question-circle", "theme": "outlined" };

var QuestionCircleOutlined = function QuestionCircleOutlined(props, ref) {
  return /*#__PURE__*/React.createElement(Icon, _objectSpread2(_objectSpread2({}, props), {}, {
    ref: ref,
    icon: QuestionCircleOutlined$1
  }));
};
var RefIcon = /*#__PURE__*/React.forwardRef(QuestionCircleOutlined);
if (process.env.NODE_ENV !== 'production') {
  RefIcon.displayName = 'QuestionCircleOutlined';
}

const useDescriptionModal = (props) => {
    const { textButtonText = $t({ defaultMessage: '报表说明' }), title = $t({ defaultMessage: '报表说明' }), content, modalProps, } = props;
    const [modalVisible, setModalVisible] = useState(false); // 控制弹窗显隐
    const handleOpen = useCallback(() => {
        setModalVisible(true);
    }, []);
    const handleClose = useCallback(() => {
        setModalVisible(false);
    }, []);
    const modalNode = useMemo(() => {
        // SSR 场景下没有 document，直接返回空节点
        if (typeof document === 'undefined') {
            return null;
        }
        return ReactDOM.createPortal(jsxRuntimeExports.jsx(Modal, { open: modalVisible, onCancel: handleClose, title: title, footer: null, width: 600, maskClosable: true, destroyOnClose: true, ...modalProps, children: jsxRuntimeExports.jsx("div", { className: "common-modal-tabs", style: {
                    overflow: 'hidden auto',
                }, children: content }) }), document.body);
    }, [content, handleClose, modalProps, modalVisible, title]);
    const renderTriggerContent = useCallback(() => (jsxRuntimeExports.jsxs(jsxRuntimeExports.Fragment, { children: [modalNode, jsxRuntimeExports.jsx(Space, { size: 0, children: jsxRuntimeExports.jsxs("div", { onClick: handleOpen, style: { cursor: 'pointer', userSelect: 'none' }, children: [jsxRuntimeExports.jsx("span", { style: { color: '#666' }, children: textButtonText }), jsxRuntimeExports.jsx(RefIcon, { style: { marginLeft: 4, color: '#666' } })] }) })] })), [handleOpen, textButtonText, modalNode]);
    /**
     * 渲染说明按钮
     * @param actions 操作按钮
     * @description  如果想要使用公共两端布局行式，就传入actions，此时会一起包裹到一个公共的row中处理样式
     *               如果不想使用公共两端布局行式，则不传入actions，此时会单独渲染说明按钮
     */
    const renderTextButton = useCallback((actions) => {
        if (!actions) {
            return renderTriggerContent();
        }
        return (jsxRuntimeExports.jsxs(Row, { justify: 'space-between', align: 'middle', style: { marginTop: '10px' }, children: [jsxRuntimeExports.jsx(Col, { children: actions }), jsxRuntimeExports.jsx(Col, { children: renderTriggerContent() })] }));
    }, [renderTriggerContent]);
    return {
        renderTextButton,
    };
};

/*
 * @Author: nhsoft.zhangxh
 * @Date: 2024-12-10 21:19:09
 * @LastEditors: nhsoft.zhangxh
 * @LastEditTime: 2024-12-10 21:19:10
 * @Description: file content
 */
function useSafeEffect(cb, depth) {
    useEffect(() => {
        let isNextRender = false;
        const returnValue = cb(!isNextRender);
        return () => {
            // 递归判断返回值
            // 如果是promise一直往下递归
            // 如果是函数则执行函数
            // 否则无事发生
            const recursion = (result) => {
                if (result instanceof Promise) {
                    result.then((subResult) => {
                        recursion(subResult);
                    });
                }
                else if (result instanceof Function) {
                    result();
                }
            };
            recursion(returnValue);
            isNextRender = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, depth);
}

/*
 * @Author: 张帮辉
 * @LastEditors: 赵泽文
 * @Date: 2022-03-09 14:18:26
 * @LastEditTime: 2025-02-08 17:28:59
 * @Description: file content
 */
/**
 * @param device_id 设备id
 * @param access_token 请求携带token
 * @param refresh_token 刷新token
 * @param merchant_id 商户id
 * @param operator 操作人
 * @param distribution_center_is 是否为配送中心
 * @param headquarters_is 是否为总部
 * @param direct_is 是否为直营店
 * @param location_name 门店名称
 * @param location_id 门店id
 * @param staff_id 员工id
 * @param role_id 角色id
 * @param old_mars_token 原mars_token用来对比登陆是否过期
 * @param location_zone_id mars location zone id
 * @param zone_is 是否是区域
 * @param account_id 账户id
 * @param scopeType  'HEADQUARTERS' | 'ZONE' | 'BRANCH'
 * @param convert_actual_item 虚拟物品转换方式 fasle:虚拟物品不转实际物品 true:虚拟物品转实际物品
 */
class StorageHanlder {
    constructor(isLocal = true) {
        this.storage = isLocal ? localStorage : sessionStorage;
        this.name = '';
    }
    /** 遍历 */
    key(index) {
        return this.storage.key(index);
    }
    /** 获取长度 */
    length() {
        return this.storage.length;
    }
    /** 存值 */
    setItem(key, value) {
        this.storage.setItem(this.name + key, JSON.stringify(value));
    }
    /**
     * 判断字符串是否可以被JSON.parse 避免解析错误
     * @param str
     * @returns {boolean}
     */
    /**
     * 获取localStorage数据
     * @description 注意：因为主项目进销存缓存存储方式不同，项目合并前，主项目使用cookie，进销存使用localstorage，注意区分location_id的不同
     * @description localstorage中的location_id在总部进入时显示为总部门店id，区域进入显示区域下第一家门店id，门店进入显示门店id；mars_location_id的数据同cookie中的location_id
     * @description cookie中的location_id在总部或区域进入显示总部或区域下第一家门店id，门店进入显示门店id
     */
    getItem(key) {
        const value = this.storage.getItem(this.name + key);
        // 避免一些特殊字符的处理
        try {
            return value ? JSON.parse(value) : '';
        }
        catch (error) {
            return value ?? '';
        }
    }
    /** 删值 */
    removeItem(key) {
        this.storage.removeItem(this.name + key);
    }
    /** 遍历删除 */
    clearAll(args) {
        // 获取所有需要删除的keys
        const keys = [];
        for (let i = 0; i < this.storage.length; i++) {
            const key = this.storage.key(i);
            if (key) {
                keys.push(key);
            }
        }
        keys.forEach((key) => {
            if (key) {
                if (args?.excludeKeys && args.excludeKeys?.length > 0) {
                    if ((this.name &&
                        key.indexOf(this.name) > -1 &&
                        args?.excludeKeys?.includes(key.split(this.name)?.[1])) ||
                        args?.excludeKeys?.includes(this.name)) {
                        return;
                    }
                }
                this.storage.removeItem(key);
            }
        });
    }
    /**
     * 清空所有
     * @param all 是否需要删除deviceId
     */
    clear(all) {
        if (!all) {
            const deviceId = this.getItem('device_id');
            if (deviceId) {
                this.clearAll();
                this.setItem('device_id', deviceId);
            }
        }
        else {
            this.clearAll();
        }
    }
}
const localCache = new StorageHanlder();
const sessionCache = new StorageHanlder(false);

/**
 * 动态计算表格scroll高度
 * @param params
 * @returns
 */
function getTableScroll(params) {
    let extraHeight = params?.extraHeight;
    const id = params?.id;
    if (typeof extraHeight === 'undefined') {
        //  默认列表页面，表格底部分页高度 + 空白 + 间距
        extraHeight = 70;
    }
    let tHeader = null;
    if (id) {
        tHeader = document.getElementById(id)
            ? window.document.getElementById(id)?.getElementsByClassName('ant-table-thead')[0]
            : null;
    }
    else {
        tHeader = document.getElementsByClassName('ant-table-thead')[0];
    }
    //表格内容距离顶部的距离
    let tHeaderBottom = 0;
    if (tHeader) {
        tHeaderBottom = tHeader.getBoundingClientRect().bottom;
    }
    // 表格底部距离浏览器底部的距离
    if (params?.pageType) {
        if (params?.pageType === 'DETAIL_WITH_SUMMARY') {
            // 总结栏高度 + 分页高度 + 底部按钮栏高度
            const element = id && document.getElementById(id);
            if (element) {
                extraHeight = element.getElementsByClassName('ant-table-summary')[0]?.clientHeight + 55;
            }
            else {
                extraHeight = document.getElementsByClassName('ant-table-summary')[0]?.clientHeight + 55;
            }
        }
        else if (params?.pageType === 'DETAIL') {
            // 分页高度 + 底部按钮栏高度
            extraHeight = 55;
        }
    }
    //窗体高度-表格内容顶部的高度-表格内容底部的高度
    const height = `calc(100vh - ${tHeaderBottom + extraHeight}px)`;
    return height;
}
/**
 * 动态计算表格最大高度
 * @param params
 * @returns
 */
function getTableMaxHeight(params) {
    const id = params?.id;
    let tHeader = null;
    if (id) {
        tHeader = document.getElementById(id)
            ? window.document.getElementById(id)?.getElementsByClassName('ant-table-thead')[0]
            : null;
    }
    else {
        tHeader = document.getElementsByClassName('ant-table-thead')[0];
    }
    //表格内容距离顶部的距离
    let tHeaderTop = 0;
    if (tHeader) {
        tHeaderTop = tHeader.getBoundingClientRect().top;
    }
    //窗体高度-表格内容顶部的高度-表格内容底部的高度
    const height = `calc(100vh - ${tHeaderTop + (params?.extraHeight || 20)}px)`;
    return height;
}
/**
 * 二维数组生成笛卡尔积组合
 */
const multiCartesian = function (data) {
    const len = data.length;
    const ft = (a, b) => {
        if (Array.isArray(a)) {
            return [...a, b];
        }
        return [a, b];
    };
    const Cartesian = (a, b) => {
        const ret = [];
        for (let i = 0; i < a.length; i++) {
            for (let j = 0; j < b.length; j++) {
                ret.push(ft(a[i], b[j]));
            }
        }
        return ret;
    };
    if (len === 0)
        return [];
    else if (len === 1) {
        return data[0].reduce((pre, cur) => {
            pre.push([cur]);
            return pre;
        }, []);
    }
    let r = data[0];
    for (let i = 1; i < len; i++) {
        r = Cartesian(r, data[i]);
    }
    return r;
};
/**
 *  对树结构数据进行遍历，返回匹配条件的数据
 * @param dataSource 需要遍历的数据源
 * @param childrenNodeName 数据源的children名称， 默认名称为children
 * @param checkedNodeKey 条件匹配属性名
 * @param checkedNodeValue 条件匹配属性值
 */
const treeTraverse = (dataSource, childrenNodeName = 'children', checkedNodeKey, checkedNodeValue) => {
    const checked = [];
    function traverse(data) {
        for (const item of data) {
            // 选择只满足该条件的记录
            if (item[checkedNodeKey] === checkedNodeValue) {
                checked.push(item);
            }
            if (item[childrenNodeName]) {
                traverse(item[childrenNodeName]);
            }
            else {
                continue;
            }
        }
        return;
    }
    traverse(dataSource);
    return checked;
};
/** 获取门店类型 */
const getShopType = () => {
    const isHead = Number(localCache.getItem('headquarters_is'));
    const isCenter = Number(localCache.getItem('distribution_center_is'));
    const isZone = Number(localCache.getItem('zone_is'));
    if (isHead) {
        return HEAD_QUARTERS;
    }
    else if (isZone) {
        return ZONE;
    }
    else if (isCenter) {
        return DISTRIBUTION_CENTER;
    }
    return NORMAL_SHOP;
};
const checkIsLocationView = () => {
    const type = getShopType();
    return ![HEAD_QUARTERS, ZONE].includes(type);
};
/** 匹配当前列表中的id的name */
const getNameById = ({ list, id, idText = 'id', nameText = 'name' }) => {
    const inLocationItem = list.find((item) => item[idText] === id);
    const name = inLocationItem ? inLocationItem[nameText] : '';
    return name;
};
/**
 * 获取当前门店类型(可能为多个角色，例如：既是总部同时也是配送中心)
 * @param location 门店
 * @returns
 */
const getLocationTypes$1 = (location) => {
    let isHead = Number(localCache.getItem('headquarters_is'));
    let isCenter = Number(localCache.getItem('distribution_center_is'));
    const isZone = Number(localCache.getItem('zone_is'));
    if (location) {
        isHead = Number(location.headquarters);
        isCenter = Number(location.distribution_center_is);
    }
    const typeArr = [];
    if (isHead) {
        typeArr.push(HEAD_QUARTERS);
        if (isCenter) {
            typeArr.push(DISTRIBUTION_CENTER);
        }
        if (isZone) {
            typeArr.push(ZONE);
        }
    }
    else {
        if (isCenter) {
            typeArr.push(DISTRIBUTION_CENTER);
        }
        else if (isZone) {
            typeArr.push(ZONE);
        }
        else {
            typeArr.push(NORMAL_SHOP);
        }
    }
    return typeArr;
};
const ArrayUtil = {
    isEmpty: (arr) => !arr || arr.length === 0,
    isNotEmpty: (arr) => !ArrayUtil.isEmpty(arr),
};
/**
 *  把参数转成数组格式
 *  例如
 *  a => [a]
 *  undefind => undefind
 */
const transformToArray = (arr, filterEmpty) => {
    if (arr == null)
        return arr;
    if (Array.isArray(arr)) {
        if (filterEmpty) {
            return arr.filter(Boolean);
        }
        return arr;
    }
    if (filterEmpty) {
        return [arr].filter(Boolean);
    }
    return [arr];
};
/**
 * select筛选（不区分大小写）
 * @param type 筛选字段默认children
 * @returns
 */
const filterSelect = (type = 'children') => (inputValue, option) => option?.[type]?.toLowerCase().indexOf(inputValue?.trim()?.toLowerCase()) >= 0;
/**
 * select筛选（区分大小写）
 * @param type 筛选字段默认children
 * @returns
 */
const filterSelectCaseSensitive = (type = 'children') => (inputValue, option) => {
    const keyword = inputValue?.trim() ?? '';
    if (!keyword) {
        return true;
    }
    return String(option?.[type] ?? '').indexOf(keyword) >= 0;
};
/**
 * 判断排序顺序
 * @param order 当前页的 sort_order
 * @param dataIndex 当前列的key
 * @returns
 */
const judgeSortOrder = ({ order, dataIndex }) => {
    let res = null;
    if (!order)
        return res;
    const sortOrderArray = order.split('_');
    const orderType = sortOrderArray.pop();
    const sortColumn = sortOrderArray.join('_').toLowerCase();
    if (sortColumn !== dataIndex)
        return res;
    if (orderType === 'ASC') {
        res = 'ascend';
    }
    else if (orderType === 'DESC') {
        res = 'descend';
    }
    return res;
};
/**
 * 排序方法
 * @param sorter
 * @param defaultOrder
 * @returns
 */
const tableSortHandler = (sorter, defaultOrder = DEFAULT_SORTER) => {
    const { columnKey, order, column } = sorter;
    /** 默认创建时间倒序排序 */
    let sortOrder = defaultOrder;
    if (column && order && columnKey) {
        const upperColumnKey = column.sortIndex
            ? column.sortIndex.toUpperCase()
            : columnKey.toUpperCase();
        const orderType = ESORT_TYPE[order];
        if (upperColumnKey && orderType) {
            sortOrder = `${upperColumnKey}_${orderType}`;
        }
    }
    return sortOrder;
};
/** 验证输入值(字符串)是否小于最小值 */
const verifyMinValue = (val, minLimit = '0') => {
    const reg = /^\d+[.]{0,1}[0-9]*$/;
    if (!reg.test(val)) {
        return minLimit;
    }
    let minVal = val;
    if (Number(val) < Number(minLimit)) {
        minVal = minLimit;
    }
    return minVal;
};
/**
 * 如果value存在数组中则就删除该value
 * 如果value不存在数组中则添加该value
 */
const dynamicAddOrDelete = (list, value) => {
    let index = -1;
    if (typeof value === 'string') {
        index = list.indexOf(value);
    }
    else {
        index = findIndex(list, value);
    }
    if (index > -1) {
        const nextList = list.slice();
        nextList.splice(index, 1);
        return nextList;
    }
    return [...list, value];
};
// 记录日志
const systemLog = (data, startOperateConsumeTime = Date.now()) => {
    const location_zone_id = localCache.getItem('location_zone_id');
    const location_id = localCache.getItem('location_id');
    const operator = window.getCookie('user_operator');
    const staff_id = window.getCookie('operator_id');
    const endOperateConsumeTime = Date.now();
    return saveSystemLog$1({
        ...data,
        operate_consume_time: data.operate_consume_time || `${(endOperateConsumeTime - startOperateConsumeTime) / 1000}`,
        operator,
        operator_id: staff_id,
        location_id: location_zone_id ? undefined : location_id,
        location_zone_id: location_zone_id,
    });
};
// 记录日志
const batchSystemLog = (data, startOperateConsumeTime = Date.now()) => {
    const location_zone_id = localCache.getItem('location_zone_id');
    const location_id = localCache.getItem('location_id');
    const operator = window.getCookie('user_operator');
    const staff_id = window.getCookie('operator_id');
    const endOperateConsumeTime = Date.now();
    const params = data.map((item) => ({
        ...item,
        operate_consume_time: item.operate_consume_time || `${(endOperateConsumeTime - startOperateConsumeTime) / 1000}`,
        operator,
        operator_id: staff_id,
        location_id: location_zone_id ? undefined : location_id,
        location_zone_id: location_zone_id,
    }));
    batchSaveSystemLog({
        system_log_save_list: params,
    });
};
const OSS_DOMAIN = 'mars-prod.shopmars.cn';
const OSS_ORIGIN = `https://${OSS_DOMAIN}/mars-file`;
// oss路径
const oss_image_path = (path) => `${OSS_ORIGIN}${path}`;
/** 获取门店列表 */
const useGetLocationList = () => {
    const LOCATION_ID = localCache.getItem('location_id');
    const LOCATION_ZONE_ID = localCache.getItem('location_zone_id');
    const [locationList, setLocationList] = useState([]);
    const getLocationList = async () => {
        const tmpPramas = {
            limit: 1000,
            offset: 0,
            active: true,
            chain_professional_is: true,
            current_location_zone_id: LOCATION_ZONE_ID,
        };
        if (LOCATION_ZONE_ID) {
            tmpPramas.location_zone_id = LOCATION_ZONE_ID;
        }
        else {
            tmpPramas.location_id_list = LOCATION_ID;
        }
        const result = await getLocationShopsList(tmpPramas);
        if (result.isSuccess()) {
            const resData = result.data;
            setLocationList(resData);
        }
    };
    useEffect(() => {
        getLocationList();
    }, []);
    return { locationList };
};
// 下载图片
const downloadImageByURL = (url, imageName = '图片', cb) => {
    fetch(url, {
        mode: 'cors', // 允许跨域
        headers: {
            origin: '*',
        },
    })
        .then((res) => {
        if (res.status === 200) {
            return res.blob();
        }
        throw new Error(`download error ${res.status}`);
    })
        .then((blob) => {
        const a = document.createElement('a');
        a.download = `${imageName}.jpg`;
        a.style.display = 'none';
        const blobUrl = window.URL.createObjectURL(blob);
        a.href = blobUrl;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
    })
        .catch((e) => window.console.log(e))
        .finally(() => {
        cb?.();
    });
};
// 转成canvas下载
const downloadToCanvas = (element, canvasName = '海报', options) => {
    const width = element.getBoundingClientRect().width;
    const height = element.getBoundingClientRect().height - 1;
    const ops = {
        width,
        height,
        useCORS: true,
        allowTaint: false,
        ...options,
    };
    html2canvas(element, ops).then((canvas) => {
        const img = canvas.toDataURL('image/jpeg');
        const aElem = document.createElement('a');
        document.body.appendChild(aElem);
        aElem.href = img;
        // 设置下载标题
        aElem.download = `${canvasName}.jpg`;
        aElem.click();
        document.body.removeChild(aElem);
    });
};
// 记录日志
const systemFormulaLog = (data, startOperateConsumeTime = Date.now()) => {
    const operator = window.getCookie('user_operator');
    const staff_id = window.getCookie('operator_id');
    const endOperateConsumeTime = Date.now();
    saveSystemLog$1({
        ...data,
        operate_consume_time: data.operate_consume_time || `${(endOperateConsumeTime - startOperateConsumeTime) / 1000}`,
        operator,
        operator_id: staff_id,
    });
};
// 临近天数处理
const preDaysHandler = (dayLimit, format) => {
    const dateFormat = format || 'YYYY-MM-DD';
    let startTime = moment().format(dateFormat);
    let endTime = moment().format(dateFormat);
    switch (dayLimit) {
        case '昨天':
        case 'YESTERDAY':
            startTime = moment().subtract(1, 'd').format(dateFormat);
            endTime = moment().subtract(1, 'd').format(dateFormat);
            break;
        case '近7天':
        case 'PAST_WEEK':
            startTime = moment().subtract(6, 'd').format(dateFormat);
            break;
        case '近30天':
        case 'PAST_MONTH':
            startTime = moment().subtract(29, 'd').format(dateFormat);
            break;
        case '近90天':
        case 'PAST_QUARTER':
            startTime = moment().subtract(89, 'd').format(dateFormat);
            break;
        case '近365天':
        case '近一年':
        case 'PAST_YEAR':
            startTime = moment().subtract(1, 'year').format(dateFormat);
            break;
        case '本周':
        case 'THIS_WEEK':
            startTime = moment().startOf('week').format(dateFormat);
            break;
        case '上周':
        case 'LAST_WEEK':
            startTime = moment().subtract(1, 'week').startOf('week').format(dateFormat);
            endTime = moment().subtract(1, 'week').endOf('week').format(dateFormat);
            break;
        case '本月':
        case 'THIS_MONTH':
            startTime = moment().startOf('month').format(dateFormat);
            endTime = moment().endOf('month').format(dateFormat);
            break;
        case '上月':
        case 'LAST_MONTH':
            startTime = moment().subtract(1, 'month').startOf('month').format(dateFormat);
            endTime = moment().subtract(1, 'month').endOf('month').format(dateFormat);
            break;
        case '本季度':
        case 'THIS_QUARTER':
            startTime = moment().startOf('quarter').format(dateFormat);
            endTime = moment().endOf('quarter').format(dateFormat);
            break;
        case '本年度':
        case 'THIS_YEAR':
            startTime = moment().startOf('year').format(dateFormat);
            endTime = moment().endOf('year').format(dateFormat);
            break;
    }
    return format ? [startTime, endTime] : [`${startTime} 00:00:00`, `${endTime} 23:59:59`];
};
const checkSelectTimeOverLimit = (requestTimeconfig) => {
    try {
        const config = JSON.parse(localStorage.getItem('staff_additional_permission'));
        if (config && config.staff_permission_date !== 'unlimited') {
            const currentBegin = dayjs(requestTimeconfig.beginTime).format('YYYY-MM-DD');
            const currentEnd = dayjs(requestTimeconfig.endTime).format('YYYY-MM-DD');
            switch (config.staff_permission_date) {
                case 'this_month':
                case 'this_quarter':
                // case 'this_year':
                //   const limitTimeList = preDaysHandler(config.staff_permission_date.toUpperCase());
                //   if (
                //     dayjs(currentBegin).isBefore(dayjs(limitTimeList[0])) ||
                //     dayjs(currentEnd).isAfter(dayjs(limitTimeList[1]))
                //   ) {
                //     return {
                //       result: false,
                //       msg: `系统允许查询时间范围为${limitTimeList[0]}至${limitTimeList[1]}，请修改查询时间范围`,
                //     };
                //   }
                //   break;
                // case 'custom':
                //   if (dayjs(currentBegin).isBefore(dayjs(config.start_datetime))) {
                //     return {
                //       result: false,
                //       msg: `系统允许最早查询时间为${config.start_datetime}，请修改查询时间范围`,
                //     };
                //   }
                //   break;
                // case 'day':
                //   if (
                //     dayjs(currentEnd).diff(dayjs(currentBegin), 'day') -
                //       Number(config.date_setting_count || 0) >
                //     0
                //   ) {
                //     return {
                //       result: false,
                //       msg: `系统允许查询时间范围为${config.date_setting_count}天，请修改查询时间范围`,
                //     };
                //   }
                //   break;
                // case 'month':
                //   if (
                //     dayjs(currentEnd).diff(dayjs(currentBegin), 'month', true) -
                //       Number(config.date_setting_count || 0) >
                //     0
                //   ) {
                //     return {
                //       result: false,
                //       msg: `系统允许查询时间范围为${config.date_setting_count}个月，请修改查询时间范围`,
                //     };
                //   }
                //   break;
                // case 'year':
                //   if (
                //     dayjs(currentEnd).diff(dayjs(currentBegin), 'year', true) -
                //       Number(config.date_setting_count || 0) >
                //     0
                //   ) {
                //     return {
                //       result: false,
                //       msg: `系统允许查询时间范围为${config.date_setting_count}年，请修改查询时间范围`,
                //     };
                //   }
                //   break;
            }
        }
    }
    catch (error) { }
    return { result: true, msg: '' };
};
const encodeDots = (val) => val?.replaceAll('.', 'fakerNo1');
const decodeDots$1 = (val) => val?.replaceAll('fakerNo1', '.');
const parseMapKey = (key, custom = '@##@') => key.replaceAll(custom, '.');
/** aggrid分组表格使用.取嵌套map数据
 * 主要解决map中key存在.时，取值为空问题
 * 格式化与解析格式化
 **/
const formatMapKey = (data, mapKey, custom = '@##@') => data?.map((item) => {
    if (item[mapKey]) {
        let target = {};
        Object.keys(item[mapKey]).forEach((key) => {
            if (key.indexOf('.') > -1) {
                // 使用 @##@ 替代 .
                const replaceKey = key.replace(/\./g, custom);
                target = { ...target, [replaceKey]: item[mapKey][key] };
            }
            else {
                target = { ...target, [key]: item[mapKey][key] };
            }
        });
        return { ...item, [mapKey]: target };
    }
    return item;
});
const goStockPage = async (orderNo, path, type = '') => {
    const res = await window.marsClient.stock.common.getIdByOrderNo(orderNo);
    if (res?.isSuccess()) {
        const { data } = res?.success();
        if (data) {
            window.marsHistory?.push(`${path}/${data}` + (type ? `/${type}` : ''));
        }
    }
};
const getEditPageIdParam = () => window.location.pathname.slice(window.location.pathname.lastIndexOf('/') + 1);
const isNewPage = () => getEditPageIdParam() === 'new';
const managementStyleProductEditState = () => !isNewPage() && localStorage.getItem('management_style') === 'false';
function getBrowserType() {
    return /macintosh|mac os x/i.test(navigator.userAgent);
}
const getUuid = () => {
    const uuid = [];
    const hexDigits = '0123456789abcdef';
    for (let i = 0; i < 32; i += 1) {
        uuid[i] = hexDigits.substr(Math.floor(Math.random() * 0x10), 1);
    }
    uuid[12] = '4';
    // eslint-disable-next-line no-bitwise
    uuid[16] = hexDigits.substr(uuid[16] | 0x8, 1);
    // uuid[8] = uuid[13] = uuid[18] = uuid[23] = "-";
    return uuid.join('');
};
// 拼接树形结构
const getTreeData = (treeList, sortField) => {
    // 如果需要排序，则按照sortField升序排序
    const sortedList = sortField
        ? [...treeList].sort((a, b) => (a[sortField] ?? 0) - (b[sortField] ?? 0))
        : treeList;
    const data = sortedList.map((ele) => ({
        ...ele,
        key: ele.id,
        title: ele.name,
    }));
    const cloneData = JSON.parse(JSON.stringify(data)); // 对源数据深度克隆
    return cloneData.filter((father) => {
        const branchArr = cloneData.filter((child) => father.id === child.parent_id); // 返回每一项的子级数组
        if (branchArr.length > 0) {
            // eslint-disable-next-line
            father.children = branchArr; // 如果存在子级，则给父级添加一个children属性，并赋值
        }
        return (father.parent_id === null ||
            cloneData.findIndex((item) => item.id === father.parent_id) === -1); // 返回第一层
    });
};
const makeTreeZIndex = (tree, index) => {
    if (tree && tree.length > 0) {
        return tree.reduce((sum, ele) => {
            const newIndex = index + 1;
            // eslint-disable-next-line
            ele.zIndex = index;
            makeTreeZIndex(ele.children, newIndex);
            return newIndex;
        }, index);
    }
    return index;
};
/**
 * @description: 返回一个所需位数的随机字符串
 * @param {number} length
 * @return {string}
 */
const randomString = (length) => {
    const strLength = length || 32;
    const strList = 'ABCDEFGHJKMNPQRSTWXYZabcdefhijkmnprstwxyz123456789';
    let strRandom = '';
    for (let i = 0; i < strLength; i += 1) {
        strRandom += strList.charAt(Math.floor(Math.random() * strList.length));
    }
    return strRandom;
};
const convertToTree = (flatData, parentId = null) => {
    const clonelFatData = cloneDeep(flatData);
    const children = clonelFatData.filter((node) => node.parent_id === parentId);
    if (!children.length) {
        return null;
    }
    return children.map((node) => ({
        ...node,
        children: convertToTree(flatData, node.id),
    }));
};
// 判断是否新商户
const isNewMerchant = () => {
    const targetDate = '2024-08-27 00:00:00';
    const currentLocationInfo = JSON.parse(localStorage.getItem('current_location'));
    const currentLocationCreatedAt = currentLocationInfo?.created_at;
    return dayjs(currentLocationCreatedAt).isAfter(targetDate);
};
/**
 * 数据扁平化
 * @param source
 * @returns
 */
const delayeringFn = (source) => {
    const sourceData = cloneDeep(source);
    const res = [];
    sourceData.forEach((el) => {
        res.push(el);
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        el.children && res.push(...delayeringFn(el.children));
    });
    return res;
};
function delay(duration, val) {
    return new Promise((res) => {
        setTimeout(() => {
            res(val);
        }, duration);
    });
}
/**
 * 数字格式化，每隔三位用逗号分割，展示2位小数
 * @param number
 * @returns
 */
const numberToLocaleString = (number, precision = 2) => {
    let intNum = Number(number ?? '');
    let isFloat = intNum.toString().includes('.');
    if (isFloat) {
        intNum = new BigNumber(intNum).dp(Number(precision)).toNumber();
        isFloat = intNum.toString().includes('.');
    }
    if (!isFloat) {
        const strNum = intNum.toLocaleString();
        return precision ? `${strNum}.${''.padEnd(Number(precision), '0')}` : `${strNum}`;
    }
    const [intPart, floatPart] = intNum.toString().split('.');
    if (floatPart?.length > precision) {
        return `${intPart.toLocaleString()}.${floatPart.slice(0, Number(precision))}`;
    }
    else if (floatPart?.length < precision) {
        return `${intPart.toLocaleString()}.${floatPart.padEnd(Number(precision), '0')}`;
    }
    return intNum.toLocaleString();
};
function moneyFormat(origin, fractionDigits) {
    return numberToLocaleString(origin, fractionDigits);
}
// 一围对象数组去重
const formatArray = (value, type) => {
    const targetFilter = type || 'id';
    const map = new Map();
    for (const item of value) {
        if (!map.has(item[targetFilter])) {
            map.set(item[targetFilter], item);
        }
    }
    return [...map.values()];
};
//数字转中文
const toChinesNum = (num) => {
    const changeNum = ['零', '一', '二', '三', '四', '五', '六', '七', '八', '九'];
    const unit = ['', '十', '百', '千', '万'];
    const getWan = (temp) => {
        const strArr = temp.toString().split('').reverse();
        let newNum = '';
        for (let i = 0; i < strArr.length; i++) {
            newNum =
                (i == 0 && strArr[i] == 0
                    ? ''
                    : i > 0 && strArr[i] == 0 && strArr[i - 1] == 0
                        ? ''
                        : changeNum[strArr[i]] + (strArr[i] == 0 ? unit[0] : unit[i])) + newNum;
        }
        return temp == 10 ? '十' : temp > 10 && temp < 20 ? '十' + changeNum[strArr[0]] : newNum;
    };
    const overWan = Math.floor(num / 10000);
    let noWan = (num % 10000) + '';
    if (noWan.toString().length < 4) {
        noWan = '0' + noWan;
    }
    return overWan ? getWan(overWan) + '万' + getWan(noWan) : getWan(num);
};
const findPathbyId = (tree, id, path) => {
    if (typeof path === 'undefined') {
        // eslint-disable-next-line no-param-reassign
        path = [];
    }
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < tree.length; i++) {
        const tempPath = [...path];
        tempPath.push(tree[i]);
        if (tree[i].id === id) {
            return tempPath;
        }
        if (tree[i].children) {
            const reuslt = findPathbyId(tree[i].children, id, tempPath);
            if (reuslt) {
                return reuslt;
            }
        }
    }
};
/** 校验是否应用新的营销计算规则*/
const checkUseNewPromotionCalRule = async () => {
    const reset = await window.marsClient.pos.posNative.checkUseNewPromotionCalRule();
    if (reset.isSuccess() && reset.success().data) {
        window.setCookie('new_promotion_cal_rule', '1');
    }
    else {
        window.setCookie('new_promotion_cal_rule', '0');
    }
};
const transUnitPriceDecimal = ({ value, isExport, }) => {
    const unitPriceDecimal = +window?.stockSettingConfig?.[isExport ? 'unit_price_export_decimal' : 'unit_price_show_decimal'];
    if (!unitPriceDecimal || (!value && !isExport && +value !== 0))
        return value;
    if (isExport && isUndefined(value)) {
        return `#,##0.${'#'.repeat(unitPriceDecimal)}`;
    }
    const isSplitPrice = String(value).includes(',');
    const valueToFormat = new BigNumber(String(value || 0).replaceAll(',', '')).toFixed(unitPriceDecimal);
    if (!isSplitPrice) {
        return valueToFormat;
    }
    // 金额按三位分割法
    const [intPart, floatPart] = valueToFormat.split('.');
    const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return floatPart !== undefined ? `${formattedInt}.${floatPart}` : formattedInt;
};
/**
 * 复制文本到剪切板
 * @param value - 要复制的文本
 */
const copyToClipboard = (value) => {
    const textareaEl = document.createElement('textarea');
    textareaEl.setAttribute('readonly', 'readonly'); // 防止手机上弹出软键盘
    textareaEl.value = value;
    document.body.appendChild(textareaEl);
    // 方法用于选择该元素中的文本。
    textareaEl.select();
    // 拷贝当前选中内容到剪贴板。启用这个功能的条件因浏览器不同而不同，而且不同时期，其启用条件也不尽相同。使用之前请检查浏览器兼容表，以确定是否可用。
    document.execCommand('copy');
    document.body.removeChild(textareaEl);
};
const getCurrentColumnItem = (i, columns) => {
    let target = {};
    for (const item of columns) {
        if (item?.dataIndex === i?.colId || item.colId === i?.colId || item?.field === i?.colId) {
            target = item;
            break;
        }
        if (item.children) {
            target = item.children.find((j) => j?.dataIndex === i?.colId || j.colId === i?.colId || j?.field === i?.colId);
            if (target) {
                break;
            }
        }
    }
    return target;
};
const getTitle$1 = (i) => decodeDots$1(i?.headerName) || decodeDots$1(i?.title);
/** 对字符串做简单哈希，得到短 key */
const simpleHash = (str, radix = 36) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash;
    }
    return Math.abs(hash).toString(radix);
};

/*
 * @Author: 赵泽文
 * @Date: 2022-11-03 16:10:59
 * @LastEditors: 赵泽文
 * @LastEditTime: 2024-06-04 16:27:44
 * @Description: 计算表格滚动条内容高度
 */
function useTableHeight(props) {
    const [tableHeight, setTableHeight] = useState('');
    useEffect(() => {
        setTableHeight(getTableMaxHeight(props));
    }, [props]);
    return {
        tableHeight,
    };
}

const useTimeCountDown = () => {
    const [remainSecond, setRemainSecond] = useState(0);
    const countDown = useCallback((time) => {
        setRemainSecond(time || 60);
        let clark = time;
        const timmer = setInterval(() => {
            clark -= 1;
            setRemainSecond(clark);
            if (clark <= 0) {
                clearInterval(timmer);
            }
        }, 1000);
    }, [setRemainSecond]);
    return [remainSecond, countDown];
};

/*
 * @Author: 吴灏
 * @Date: 2021-06-17 20:23:31
 * @LastEditors: 彭鑫诚
 * @LastEditTime: 2024-05-28 18:41:30
 * @Description: file content
 */
const useAntPagination = (props) => {
    const { defaultPage = 1, defaultPageSize = 20 } = props || {};
    const pageSizeOptions = useMemo(() => ['20', '50', '100', '200', '400'], []);
    const [page, setPage] = useState(defaultPage);
    const [pageSize, setPageSize] = useState(defaultPageSize);
    const offset = useMemo(() => (page - 1) * pageSize, [page, pageSize]);
    const limit = useMemo(() => pageSize, [pageSize]);
    const handlePaginationChange = useCallback((pg, size) => {
        setPage(pg);
        setPageSize(size || defaultPageSize);
    }, [defaultPageSize]);
    return {
        pageSizeOptions,
        page,
        pageSize,
        handlePaginationChange,
        offset,
        limit,
    };
};

/*
 * @Author: wangrt
 * @Date: 2025-12-02 10:18:11
 * @LastEditors: wangrt
 * @LastEditTime: 2025-12-02 10:54:35
 * @Description: 监听浏览器关闭弹窗
 */
const usePageUnload = (onBeforeUnload, onUnload) => {
    const callbackRef = useRef(onUnload);
    const beforeUnloadRef = useRef(onBeforeUnload);
    // 更新 ref 的值
    useEffect(() => {
        callbackRef.current = onUnload;
        beforeUnloadRef.current = onBeforeUnload;
    });
    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (beforeUnloadRef.current) {
                beforeUnloadRef.current(event);
            }
        };
        const handleUnload = () => {
            if (callbackRef.current) {
                callbackRef.current();
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        window.addEventListener('unload', handleUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('unload', handleUnload);
        };
    }, []);
};

const useCopyText = (string, copyTimeout = 3000) => {
    const [isCopied, setIsCopied] = useState(false);
    const timeout = useRef(null);
    useEffect(() => {
        return () => {
            if (timeout.current) {
                clearTimeout(timeout.current);
            }
        };
    }, []);
    const copy = useCallback(() => {
        if (timeout.current) {
            clearTimeout(timeout.current);
        }
        // navigator.clipboard有安全域名限制
        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(string);
            setIsCopied(true);
        }
        else {
            const textArea = document.createElement('textarea');
            document.body.appendChild(textArea);
            textArea.textContent = string;
            textArea.select();
            document?.execCommand('copy');
            setIsCopied(true);
        }
        timeout.current = setTimeout(() => {
            setIsCopied(false);
        }, copyTimeout);
    }, [string, copyTimeout]);
    return { isCopied, copy };
};

/*
 * @Author: rxw
 * @Date: 2021-11-09 16:45:49
 * @LastEditors: 彭鑫诚
 * @LastEditTime: 2024-03-20 10:32:42
 * @Description: file content
 */
function useDebounce(cb, duration, options) {
    const ref = useRef(cb);
    ref.current = cb;
    return useCallback(debounce$1(((...args) => ref.current(...args)), duration, options), []);
}

/*
 * @Author: nhsoft.zhangxh
 * @Date: 2025-01-14 11:30:42
 * @LastEditors: nhsoft.zhangxh
 * @LastEditTime: 2025-03-31 11:49:43
 * @Description: file content
 */
function useFetchOptions(props) {
    const { fetchApi, fieldNames = { label: 'label', value: 'value' }, isHeadquarters = false, } = props;
    const [list, setList] = useState([]);
    const [allData, setAllData] = useState([]);
    const getList = async () => {
        const res = await fetchApi();
        if (res?.isSuccess()) {
            const data = res.success().data || [];
            setAllData(data);
            if (isHeadquarters) {
                setList(data.map((item) => ({
                    label: item[fieldNames.label],
                    value: item[fieldNames.value],
                    headquarters: item.headquarters,
                })));
            }
            else {
                setList(data.map((item) => ({
                    label: item[fieldNames.label],
                    value: item[fieldNames.value],
                })));
            }
        }
    };
    useMount(getList);
    return {
        list,
        allData,
        refresh: getList,
    };
}

const decodeDots = (val) => val?.replace(/fakerNo1/g, '.');
function toHump(name) {
    return name?.replace(/\_(\w)/g, function (_, letter) {
        return letter?.toUpperCase();
    });
}
const getTitle = (i) => decodeDots(i?.headerName) || decodeDots(i?.title);
const getDataIndex = (i) => i?.dataIndex || i?.field;
const getPath = (path, upperCase) => upperCase ? path.replace(/([A-Z])/g, '_$1').toUpperCase() : path;
// 最多支持二级表头
const formatCustomColumnsExport = (grid, columns = [], options) => {
    if (!options?.enabled) {
        return [];
    }
    const rightAlignTypeList = [
        'number',
        'rightAligned',
        'numericColumn',
        'money',
        'currencyMoney',
        'amount',
    ];
    const getCurrentColumnItem = (i) => {
        let target = {};
        for (const item of columns) {
            if (item?.dataIndex === i?.colId || item.colId === i?.colId || item?.field === i?.colId) {
                target = item;
                break;
            }
            if (item.children) {
                target = item.children.find((j) => j?.dataIndex === i?.colId || j.colId === i?.colId || j?.field === i?.colId);
                if (target) {
                    break;
                }
            }
        }
        return target;
    };
    // 导出表头数据
    const grid_header_group_list = [];
    // 用于保存一级表头信息
    const grid_header_group_data = [];
    // 一级表头id
    const grid_header_group_id_list = grid.ref?.current?.columnApi?.getColumnGroupState();
    // 用于计算多级表头的列信息
    const grid_header_all_columns_info = grid.ref?.current?.columnApi?.getAllColumns();
    const customColumn = grid.ref?.current?.columnApi?.getColumnState();
    customColumn?.sort((a, b) => b?.pinned?.length || 0 - a?.pinned?.length || 0);
    const grid_header_list = customColumn
        ?.map((i) => {
        if (i?.colId === '$$index') {
            return {
                header: $t({ defaultMessage: '序号' }),
                path: getPath('rowNum', options?.upperCase),
                width: TABLE_COL_WIDTH.INDEX,
                right: false,
            };
        }
        if (i?.colId?.startsWith('$$')) {
            return;
        }
        if (options?.excludeFieldList?.includes(i?.colId)) {
            return;
        }
        const currentItem = getCurrentColumnItem(i);
        if (i?.hide || !currentItem) {
            return;
        }
        const field = getDataIndex(currentItem);
        if (!field) {
            return;
        }
        const defaultFormat = currentItem?.type === 'currencyMoney'
            ? getCurrencyPrecision() === 0
                ? '###0'
                : '#,##0.00'
            : currentItem?.type === 'money'
                ? '#,##0.##'
                : rightAlignTypeList?.includes(currentItem?.type) && !currentItem?.excludeNumberFormat
                    ? '#,##0.########'
                    : undefined;
        return {
            header: getTitle(currentItem),
            path: getPath(toHump(options?.replaceField?.[field] ?? field), options?.upperCase),
            width: i?.width,
            right: !!(currentItem?.type && rightAlignTypeList?.includes(currentItem?.type)),
            date_format: currentItem?.type === 'date' ? 'yyyy-MM-dd' : undefined,
            number_format: currentItem?.type === 'currencyMoney'
                ? defaultFormat
                : currentItem.number_format
                    ? currentItem.number_format
                    : defaultFormat,
        };
    })
        ?.filter(Boolean)
        ?.flat();
    if (grid_header_group_id_list?.length) {
        columns.forEach((item, index) => {
            if (item.children?.length) {
                grid_header_group_data.push({
                    group_name: item.headerName ?? item.title,
                    groupId: grid_header_group_id_list[index].groupId,
                });
            }
        });
        customColumn
            .filter((i) => !i.hide)
            .map((i) => grid_header_all_columns_info.find((j) => j?.colId === i?.colId)?.parent?.groupId)
            .forEach((item, index, arr) => {
            if (item !== arr[index - 1]) {
                const groupName = grid_header_group_data.find((j) => j?.groupId === item)?.group_name;
                if (groupName) {
                    grid_header_group_list.push({
                        group_name: groupName,
                        group_column: index,
                        group_colspan: 1,
                    });
                }
            }
            else if (grid_header_group_list.length > 0) {
                grid_header_group_list[grid_header_group_list.length - 1].group_colspan += 1;
            }
        });
    }
    return {
        grid_header_list: grid_header_list?.length ? grid_header_list : undefined,
        grid_header_group_list: grid_header_group_list?.length ? grid_header_group_list : undefined,
    };
};
// 最多支持三级表头
const formatCustomColumnsExportV2 = (grid, columns = [], options) => {
    if (!options?.enabled) {
        return [];
    }
    const rightAlignTypeList = [
        'number',
        'rightAligned',
        'numericColumn',
        'money',
        'currencyMoney',
        'amount',
    ];
    const getCurrentColumnItem = (i) => {
        let target = {};
        for (const item of columns) {
            if (item?.dataIndex === i?.colId || item.colId === i?.colId || item?.field === i?.colId) {
                target = item;
                break;
            }
            if (item.children) {
                if (item.children.some((child) => child.children?.length)) {
                    for (const child of item.children) {
                        target = child.children?.find((k) => k?.dataIndex === i?.colId || k.colId === i?.colId || k?.field === i?.colId);
                        if (target) {
                            break;
                        }
                    }
                }
                else {
                    target = item.children.find((j) => j?.dataIndex === i?.colId || j.colId === i?.colId || j?.field === i?.colId);
                }
                if (target) {
                    break;
                }
            }
        }
        return target;
    };
    // 导出表头数据
    const grid_header_group_list = [];
    // 用于保存一级表头信息
    const grid_header_group_data = [];
    // 用于保存顶层表头信息
    const grid_header_top_group_list = [];
    // 一级表头id
    const grid_header_group_id_list = grid.ref?.current?.columnApi?.getColumnGroupState();
    // 用于计算多级表头的列信息
    const grid_header_all_columns_info = grid.ref?.current?.columnApi?.getAllColumns();
    const customColumn = grid.ref?.current?.columnApi?.getColumnState();
    customColumn?.sort((a, b) => b?.pinned?.length || 0 - a?.pinned?.length || 0);
    const grid_header_list = customColumn
        ?.map((i) => {
        if (i?.colId === '$$index') {
            return {
                header: $t({ defaultMessage: '序号' }),
                path: options?.upperCase ? getPath('rowNum', options?.upperCase) : 'rowNum',
                width: TABLE_COL_WIDTH.INDEX,
                right: false,
            };
        }
        if (i?.colId?.startsWith('$$')) {
            return;
        }
        if (options?.excludeFieldList?.includes(i?.colId)) {
            return;
        }
        const currentItem = getCurrentColumnItem(i);
        if (i?.hide || !currentItem) {
            return;
        }
        const field = getDataIndex(currentItem);
        if (!field) {
            return;
        }
        if (options?.hideColumns && options?.hideColumns?.includes(field)) {
            return;
        }
        const path = options?.convertLineToCamel
            ? toHump((options?.replaceField?.[field] ?? field).replace(/\./g, ''))
            : getPath(toHump(options?.replaceField?.[field] ?? field), options?.upperCase);
        return {
            header: getTitle(currentItem),
            path,
            width: i?.width,
            right: !!(currentItem?.type && rightAlignTypeList?.includes(currentItem?.type)),
            date_format: currentItem?.type === 'date' ? 'yyyy-MM-dd' : undefined,
            number_format: currentItem?.type === 'currencyMoney'
                ? getCurrencyPrecision() === 0
                    ? '###0'
                    : '#,##0.00'
                : currentItem?.number_format ?? currentItem?.type === 'money'
                    ? '#,##0.00'
                    : rightAlignTypeList?.includes(currentItem?.type) && !currentItem?.excludeNumberFormat
                        ? '#,##0.########'
                        : undefined,
        };
    })
        ?.filter(Boolean)
        ?.flat();
    if (grid_header_group_id_list?.length) {
        // 处理顶层表头
        columns
            .filter((i) => options?.hideColumns && options?.hideColumns?.length > 0
            ? !options?.hideColumns?.includes(i.dataIndex)
            : true)
            .forEach((item) => {
            if (item.children?.length) {
                const hasNestedChildren = item.children.some((child) => child.children?.length);
                if (hasNestedChildren) {
                    // 寻找顶层表头索引位置
                    const firstIndex = grid_header_all_columns_info
                        .filter((col) => col.visible)
                        .filter((i) => options?.hideColumns && options?.hideColumns?.length > 0
                        ? !options?.hideColumns?.includes(i.colId)
                        : true)
                        .findIndex((col) => col.colId.indexOf(item.dataIndex) === 0 && col.colId.split('.').length > 0);
                    grid_header_top_group_list.push({
                        group_name: item.headerName || item.title,
                        group_column: firstIndex,
                        group_colspan: item.children.reduce((acc, child) => acc + (child.children?.length || 0), 0),
                    });
                }
            }
        });
        // 处理二级表头
        columns
            .filter((i) => options?.hideColumns && options?.hideColumns?.length > 0
            ? !options?.hideColumns?.includes(i.dataIndex)
            : true)
            .forEach((item) => {
            if (item.children?.length) {
                const hasNestedChildren = item.children.some((child) => child.children?.length);
                if (hasNestedChildren) {
                    // 如果是顶层表头，遍历其子项（二级表头）
                    item.children.forEach((child) => {
                        if (child.children?.length) {
                            const target = grid_header_all_columns_info.find((col) => col.colId.indexOf(child.dataIndex) === 0 && col.colId.split('.').length > 0);
                            grid_header_group_data.push({
                                group_name: child.headerName || child.title,
                                groupId: target?.parent?.groupId,
                            });
                        }
                    });
                }
                else {
                    // 如果是二级表头，直接添加
                    const target = grid_header_all_columns_info.find((col) => col.colId.indexOf(item.dataIndex) === 0 && col.colId.split('.').length > 0);
                    grid_header_group_data.push({
                        group_name: item.headerName || item.title,
                        groupId: target?.parent?.groupId,
                    });
                }
            }
        });
        customColumn
            .filter((i) => !i.hide)
            .filter((i) => options?.hideColumns && options?.hideColumns?.length > 0
            ? !options?.hideColumns?.includes(i.colId)
            : true)
            .map((i) => grid_header_all_columns_info.find((j) => j?.colId === i?.colId)?.parent?.groupId)
            .forEach((item, index, arr) => {
            if (item !== arr[index - 1]) {
                const groupName = grid_header_group_data.find((j) => j?.groupId === item)?.group_name;
                if (groupName) {
                    grid_header_group_list.push({
                        group_name: groupName,
                        group_column: index,
                        group_colspan: 1,
                    });
                }
            }
            else if (grid_header_group_list.length > 0) {
                grid_header_group_list[grid_header_group_list.length - 1].group_colspan += 1;
            }
        });
    }
    return {
        grid_header_list: grid_header_list?.length
            ? grid_header_list.map((header) => {
                // 去除path中带有excludeTopLevelFieldList字符串的前缀
                if (options?.excludeTopLevelFieldList?.length) {
                    options?.excludeTopLevelFieldList?.forEach((excludeField) => {
                        if (header.path.indexOf(excludeField) === 0) {
                            header.path = header.path.replace(new RegExp(`^${excludeField}`), '');
                        }
                    });
                }
                return header;
            })
            : undefined,
        grid_header_group_list: grid_header_group_list?.length ? grid_header_group_list : undefined,
        grid_header_top_group_list: grid_header_top_group_list?.length
            ? grid_header_top_group_list
            : undefined,
    };
};
// 格式化导出列
const formatExportColumns = (grid, columns = [], options) => {
    const customeColumn = grid.ref?.current?.columnApi?.getColumnState();
    const rightAlignTypeList = ['number', 'rightAligned', 'numericColumn', 'money', 'currencyMoney'];
    const grid_header_group_list = [];
    let index = 0;
    customeColumn?.sort((a, b) => b?.pinned?.length || 0 - a?.pinned?.length || 0);
    let tmpGroupItem = {};
    const getCurrentColumnItem = (i) => {
        if (i?.colId?.includes('.')) {
            const subList = columns?.find((j) => getTitle(j) === decodeDots(i?.colId?.split('.')?.[1]))?.children;
            return subList?.find((j) => getTitle(j) === decodeDots(i?.colId?.split('.')?.[2]));
        }
        return columns?.find((j) => getDataIndex(j) === i?.colId ||
            (i?.colId?.includes('.') && decodeDots(i?.colId?.split('.')?.[1]) === getTitle(j)));
    };
    const spetialItem = options?.spetialItem || '';
    const grid_header_list = customeColumn
        ?.map((i) => {
        const currentItem = getCurrentColumnItem(i);
        if (i?.colId === '$$index') {
            index += 1;
            return {
                header: $t({ defaultMessage: '序号' }),
                path: getPath('rowNum', options?.upperCase),
                width: TABLE_COL_WIDTH.INDEX,
                right: false,
            };
        }
        if (spetialItem === i?.colId) {
            if (!currentItem) {
                return;
            }
        }
        else {
            if (i?.hide || !currentItem) {
                return;
            }
        }
        if (i?.colId?.includes('.') && !options?.excludeGroupColumns) {
            const groupTitle = decodeDots(i?.colId?.split('.')?.[1]);
            if (!tmpGroupItem?.group_name) {
                tmpGroupItem = {
                    group_name: groupTitle,
                    group_column: index,
                    group_colspan: 1,
                };
            }
            else if (groupTitle === tmpGroupItem.group_name) {
                tmpGroupItem.group_colspan += 1;
            }
            else {
                grid_header_group_list.push(tmpGroupItem);
                tmpGroupItem = {
                    group_name: groupTitle,
                    group_column: index,
                    group_colspan: 1,
                };
            }
            const columnLength = customeColumn?.filter((k) => !k?.hide || !getCurrentColumnItem(k))?.length;
            if (index === columnLength - 1 && tmpGroupItem?.group_name) {
                grid_header_group_list.push(tmpGroupItem);
            }
            index += 1;
            return {
                header: decodeDots(i?.colId?.split('.')?.[2]),
                path: decodeDots(i?.colId?.split('.')?.[1]) + decodeDots(i?.colId?.split('.')?.[2]),
                width: i?.width,
                right: !!(currentItem?.type && rightAlignTypeList?.includes(currentItem?.type)),
                number_format: currentItem?.type === 'currencyMoney'
                    ? getCurrencyPrecision() === 0
                        ? '###0'
                        : '#,##0.00'
                    : currentItem?.number_format ?? undefined,
            };
        }
        else if (!options?.excludeGroupColumns && tmpGroupItem?.group_name) {
            grid_header_group_list.push(tmpGroupItem);
            tmpGroupItem = {};
        }
        index += 1;
        const field = getDataIndex(currentItem);
        return {
            header: getTitle(currentItem),
            path: getPath(toHump(options?.replaceField?.[field] ?? field), options?.upperCase),
            width: i?.width,
            right: !!(currentItem?.type && rightAlignTypeList?.includes(currentItem?.type)),
            number_format: currentItem?.type === 'currencyMoney'
                ? getCurrencyPrecision() === 0
                    ? '###0'
                    : '#,##0.00'
                : currentItem?.number_format ?? undefined,
        };
    })
        ?.filter(Boolean)
        ?.flat();
    return {
        grid_header_list: options?.excludeFieldList?.length
            ? grid_header_list.filter((item) => !options.excludeFieldList?.includes(item.path))
            : grid_header_list,
        grid_header_group_list: grid_header_group_list?.length ? grid_header_group_list : null,
    };
};
/**
 * 处理带点的字段导出（如 pay_log.sub_app_name）
 * formatExportColumns 无法正确处理两段式的 dataIndex，需要手动插入这些列
 *
 * @param grid - DataGrid 实例
 * @param columns - 列定义数组
 * @param nestedFieldsConfig - 需要手动插入的嵌套字段配置
 * @param options - formatExportColumns 的选项
 * @returns 包含手动插入列的导出参数
 *
 * @example
 * const exportParams = formatExportColumnsWithNestedFields(
 *   grid,
 *   columns,
 *   [
 *     {
 *       header: '子商户名称',
 *       path: 'SUB_APP_NAME',
 *       dataIndex: 'pay_log.sub_app_name',
 *       width: 150,
 *       right: false,
 *     },
 *   ],
 *   { upperCase: true }
 * );
 */
const formatExportColumnsWithNestedFields = (grid, columns, nestedFieldsConfig, options) => {
    // 获取实时列状态并排序
    const realTimeColumns = grid.ref?.current?.columnApi?.getColumnState();
    realTimeColumns?.sort((a, b) => {
        if (a.pinned === 'left' && b.pinned !== 'left')
            return -1;
        if (a.pinned !== 'left' && b.pinned === 'left')
            return 1;
        return 0;
    });
    // 建立colId到column的映射
    const colMap = columns.reduce((res, col) => {
        if (col.type === 'index') {
            res['$$index'] = col;
        }
        else {
            res[col.dataIndex] = col;
        }
        return res;
    }, {});
    // 获取按实时列排序的column
    const sortedColumns = realTimeColumns?.map((item) => ({
        ...(colMap[item.colId] ?? {}),
        hide: item.hide,
    }));
    // 获取导出列参数(会过滤掉带点的列)
    const columnParams = formatExportColumns(grid, sortedColumns, options);
    const gridHeaderList = columnParams.grid_header_list || [];
    // 计算每个嵌套字段在可见列中的索引位置，并按从小到大排序
    const manualColumns = nestedFieldsConfig
        .map((config) => ({
        ...config,
        index: sortedColumns
            ?.filter((item) => !item.hide)
            ?.findIndex((sortedColumn) => sortedColumn.dataIndex === config.dataIndex),
    }))
        .filter((item) => item.index !== -1) // 只保留可见的列
        .sort((a, b) => a.index - b.index); // 从小到大排序
    // 从左往右依次插入
    manualColumns.forEach((manualColumn) => {
        gridHeaderList.splice(manualColumn.index, 0, manualColumn);
    });
    return {
        ...columnParams,
        grid_header_list: gridHeaderList,
    };
};
const changeDateFilter = (e, searchForm, type, name = 'data_filter') => {
    const option = e === 'DATE_DAY' ? $t({ defaultMessage: '今天' }) : $t({ defaultMessage: '本月' });
    searchForm.setFields([
        {
            name,
            value: {
                dates: [moment().format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')],
                option: option,
                type,
            },
        },
    ]);
};

/**
 * 提取react dom节点中的文本
 * @param domNode react dom节点
 * @returns
 */
function extractTextFromReactDOM(domNode) {
    const texts = [];
    function traverse(node) {
        if (typeof node === 'string' || typeof node === 'number') {
            // 如果是字符串 或 数字，直接收集
            texts.push(node);
        }
        else if (Array.isArray(node)) {
            // 如果是数组，遍历每个子节点
            node.forEach((child) => traverse(child));
        }
        else if (typeof node === 'object' && node !== null) {
            // 如果是对象，检查 props.children
            if (node.props && node.props.children !== undefined && node.props.children !== null) {
                traverse(node.props.children);
            }
        }
    }
    traverse(domNode);
    return texts.join('');
}
/**
 * 自定义dataGrid的右键菜单弹窗
 * @param args dataGrid的参数
 * valueFormatter，render都做了处理
 * 后续如果有其他格式化的情况，继续追加处理逻辑即可
 * valueGetter就不做处理了，因为返回的value已经是处理过的结果了
 * @returns
 */
const getContextMenuItems = (args) => {
    // @ts-ignore
    const colDef = args?.column?.colDef;
    const record = args?.node?.data;
    let formattedText = args?.value ?? '';
    // 如果自定义了valueFormatter，则使用valueFormatter出来的结果
    if (colDef?.valueFormatter) {
        formattedText =
            colDef?.valueFormatter?.({
                ...args,
                data: record,
            }) || formattedText;
    }
    // 如果自定义了cellRenderer，则使用cellRenderer出来的结果
    if (colDef?.cellRenderer) {
        // 这里用trycatch处理是因为如果使用了render的情况下调用cellRenderer会抛异常
        // 这个时候要把异常捕获到并正常走下去
        try {
            const ele = colDef?.cellRenderer({ ...args, data: args?.node?.data });
            formattedText = extractTextFromReactDOM(ele) || formattedText;
        }
        catch (error) { }
    }
    // 如果自定义了render，则使用render出来的结果
    if (colDef?.cellRendererParams?.render) {
        formattedText =
            extractTextFromReactDOM(colDef?.cellRendererParams?.render(args?.value, record, args?.node?.rowIndex)) || formattedText;
    }
    // 如果自定义了innerRenderer，则使用innerRenderer出来的结果
    if (colDef?.cellRendererParams?.innerRenderer) {
        formattedText =
            extractTextFromReactDOM(colDef?.cellRendererParams?.innerRenderer({ ...args, data: args?.node?.data })) || formattedText;
    }
    // 如果自定义了processCellForClipboard，则使用processCellForClipboard出来的结果
    if (args.customCopyResult) {
        formattedText = args.customCopyResult(args) || formattedText;
    }
    return formattedText;
};

// This is a magic string replaced by rollup

const SDK_VERSION = "8.55.0" ;

/** Get's the global object for the current JavaScript runtime */
const GLOBAL_OBJ = globalThis ;

/**
 * Returns a global singleton contained in the global `__SENTRY__[]` object.
 *
 * If the singleton doesn't already exist in `__SENTRY__`, it will be created using the given factory
 * function and added to the `__SENTRY__` object.
 *
 * @param name name of the global singleton on __SENTRY__
 * @param creator creator Factory function to create the singleton if it doesn't already exist on `__SENTRY__`
 * @param obj (Optional) The global object on which to look for `__SENTRY__`, if not `GLOBAL_OBJ`'s return value
 * @returns the singleton
 */
function getGlobalSingleton(name, creator, obj) {
  const gbl = (obj || GLOBAL_OBJ) ;
  const __SENTRY__ = (gbl.__SENTRY__ = gbl.__SENTRY__ || {});
  const versionedCarrier = (__SENTRY__[SDK_VERSION] = __SENTRY__[SDK_VERSION] || {});
  return versionedCarrier[name] || (versionedCarrier[name] = creator());
}

/**
 * This serves as a build time flag that will be true by default, but false in non-debug builds or if users replace `__SENTRY_DEBUG__` in their generated code.
 *
 * ATTENTION: This constant must never cross package boundaries (i.e. be exported) to guarantee that it can be used for tree shaking.
 */
const DEBUG_BUILD = (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__);

/** Prefix for logging strings */
const PREFIX = 'Sentry Logger ';

const CONSOLE_LEVELS = [
  'debug',
  'info',
  'warn',
  'error',
  'log',
  'assert',
  'trace',
] ;

/** This may be mutated by the console instrumentation. */
const originalConsoleMethods

 = {};

/** JSDoc */

/**
 * Temporarily disable sentry console instrumentations.
 *
 * @param callback The function to run against the original `console` messages
 * @returns The results of the callback
 */
function consoleSandbox(callback) {
  if (!('console' in GLOBAL_OBJ)) {
    return callback();
  }

  const console = GLOBAL_OBJ.console ;
  const wrappedFuncs = {};

  const wrappedLevels = Object.keys(originalConsoleMethods) ;

  // Restore all wrapped console methods
  wrappedLevels.forEach(level => {
    const originalConsoleMethod = originalConsoleMethods[level] ;
    wrappedFuncs[level] = console[level] ;
    console[level] = originalConsoleMethod;
  });

  try {
    return callback();
  } finally {
    // Revert restoration to wrapped state
    wrappedLevels.forEach(level => {
      console[level] = wrappedFuncs[level] ;
    });
  }
}

function makeLogger() {
  let enabled = false;
  const logger = {
    enable: () => {
      enabled = true;
    },
    disable: () => {
      enabled = false;
    },
    isEnabled: () => enabled,
  };

  if (DEBUG_BUILD) {
    CONSOLE_LEVELS.forEach(name => {
      logger[name] = (...args) => {
        if (enabled) {
          consoleSandbox(() => {
            GLOBAL_OBJ.console[name](`${PREFIX}[${name}]:`, ...args);
          });
        }
      };
    });
  } else {
    CONSOLE_LEVELS.forEach(name => {
      logger[name] = () => undefined;
    });
  }

  return logger ;
}

/**
 * This is a logger singleton which either logs things or no-ops if logging is not enabled.
 * The logger is a singleton on the carrier, to ensure that a consistent logger is used throughout the SDK.
 */
const logger = getGlobalSingleton('logger', makeLogger);

/**
 * An object that contains globally accessible properties and maintains a scope stack.
 * @hidden
 */

/**
 * Returns the global shim registry.
 *
 * FIXME: This function is problematic, because despite always returning a valid Carrier,
 * it has an optional `__SENTRY__` property, which then in turn requires us to always perform an unnecessary check
 * at the call-site. We always access the carrier through this function, so we can guarantee that `__SENTRY__` is there.
 **/
function getMainCarrier() {
  // This ensures a Sentry carrier exists
  getSentryCarrier(GLOBAL_OBJ);
  return GLOBAL_OBJ;
}

/** Will either get the existing sentry carrier, or create a new one. */
function getSentryCarrier(carrier) {
  const __SENTRY__ = (carrier.__SENTRY__ = carrier.__SENTRY__ || {});

  // For now: First SDK that sets the .version property wins
  __SENTRY__.version = __SENTRY__.version || SDK_VERSION;

  // Intentionally populating and returning the version of "this" SDK instance
  // rather than what's set in .version so that "this" SDK always gets its carrier
  return (__SENTRY__[SDK_VERSION] = __SENTRY__[SDK_VERSION] || {});
}

// eslint-disable-next-line @typescript-eslint/unbound-method
const objectToString = Object.prototype.toString;
/**
 * Checks whether given value is an instance of the given built-in class.
 *
 * @param wat The value to be checked
 * @param className
 * @returns A boolean representing the result.
 */
function isBuiltin(wat, className) {
  return objectToString.call(wat) === `[object ${className}]`;
}

/**
 * Checks whether given value's type is an object literal, or a class instance.
 * {@link isPlainObject}.
 *
 * @param wat A value to be checked.
 * @returns A boolean representing the result.
 */
function isPlainObject(wat) {
  return isBuiltin(wat, 'Object');
}

/**
 * Checks whether given value has a then function.
 * @param wat A value to be checked.
 */
function isThenable(wat) {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  return Boolean(wat && wat.then && typeof wat.then === 'function');
}

/**
 * Defines a non-enumerable property on the given object.
 *
 * @param obj The object on which to set the property
 * @param name The name of the property to be set
 * @param value The value to which to set the property
 */
function addNonEnumerableProperty(obj, name, value) {
  try {
    Object.defineProperty(obj, name, {
      // enumerable: false, // the default, so we can save on bundle size by not explicitly setting it
      value: value,
      writable: true,
      configurable: true,
    });
  } catch (o_O) {
    DEBUG_BUILD && logger.log(`Failed to add non-enumerable property "${name}" to object`, obj);
  }
}

const ONE_SECOND_IN_MS = 1000;

/**
 * A partial definition of the [Performance Web API]{@link https://developer.mozilla.org/en-US/docs/Web/API/Performance}
 * for accessing a high-resolution monotonic clock.
 */

/**
 * Returns a timestamp in seconds since the UNIX epoch using the Date API.
 *
 * TODO(v8): Return type should be rounded.
 */
function dateTimestampInSeconds() {
  return Date.now() / ONE_SECOND_IN_MS;
}

/**
 * Returns a wrapper around the native Performance API browser implementation, or undefined for browsers that do not
 * support the API.
 *
 * Wrapping the native API works around differences in behavior from different browsers.
 */
function createUnixTimestampInSecondsFunc() {
  const { performance } = GLOBAL_OBJ ;
  if (!performance || !performance.now) {
    return dateTimestampInSeconds;
  }

  // Some browser and environments don't have a timeOrigin, so we fallback to
  // using Date.now() to compute the starting time.
  const approxStartingTimeOrigin = Date.now() - performance.now();
  const timeOrigin = performance.timeOrigin == undefined ? approxStartingTimeOrigin : performance.timeOrigin;

  // performance.now() is a monotonic clock, which means it starts at 0 when the process begins. To get the current
  // wall clock time (actual UNIX timestamp), we need to add the starting time origin and the current time elapsed.
  //
  // TODO: This does not account for the case where the monotonic clock that powers performance.now() drifts from the
  // wall clock time, which causes the returned timestamp to be inaccurate. We should investigate how to detect and
  // correct for this.
  // See: https://github.com/getsentry/sentry-javascript/issues/2590
  // See: https://github.com/mdn/content/issues/4713
  // See: https://dev.to/noamr/when-a-millisecond-is-not-a-millisecond-3h6
  return () => {
    return (timeOrigin + performance.now()) / ONE_SECOND_IN_MS;
  };
}

/**
 * Returns a timestamp in seconds since the UNIX epoch using either the Performance or Date APIs, depending on the
 * availability of the Performance API.
 *
 * BUG: Note that because of how browsers implement the Performance API, the clock might stop when the computer is
 * asleep. This creates a skew between `dateTimestampInSeconds` and `timestampInSeconds`. The
 * skew can grow to arbitrary amounts like days, weeks or months.
 * See https://github.com/getsentry/sentry-javascript/issues/2590.
 */
const timestampInSeconds = createUnixTimestampInSecondsFunc();

/**
 * The number of milliseconds since the UNIX epoch. This value is only usable in a browser, and only when the
 * performance API is available.
 */
(() => {
  // Unfortunately browsers may report an inaccurate time origin data, through either performance.timeOrigin or
  // performance.timing.navigationStart, which results in poor results in performance data. We only treat time origin
  // data as reliable if they are within a reasonable threshold of the current time.

  const { performance } = GLOBAL_OBJ ;
  if (!performance || !performance.now) {
    return undefined;
  }

  const threshold = 3600 * 1000;
  const performanceNow = performance.now();
  const dateNow = Date.now();

  // if timeOrigin isn't available set delta to threshold so it isn't used
  const timeOriginDelta = performance.timeOrigin
    ? Math.abs(performance.timeOrigin + performanceNow - dateNow)
    : threshold;
  const timeOriginIsReliable = timeOriginDelta < threshold;

  // While performance.timing.navigationStart is deprecated in favor of performance.timeOrigin, performance.timeOrigin
  // is not as widely supported. Namely, performance.timeOrigin is undefined in Safari as of writing.
  // Also as of writing, performance.timing is not available in Web Workers in mainstream browsers, so it is not always
  // a valid fallback. In the absence of an initial time provided by the browser, fallback to the current time from the
  // Date API.
  // eslint-disable-next-line deprecation/deprecation
  const navigationStart = performance.timing && performance.timing.navigationStart;
  const hasNavigationStart = typeof navigationStart === 'number';
  // if navigationStart isn't available set delta to threshold so it isn't used
  const navigationStartDelta = hasNavigationStart ? Math.abs(navigationStart + performanceNow - dateNow) : threshold;
  const navigationStartIsReliable = navigationStartDelta < threshold;

  if (timeOriginIsReliable || navigationStartIsReliable) {
    // Use the more reliable time origin
    if (timeOriginDelta <= navigationStartDelta) {
      return performance.timeOrigin;
    } else {
      return navigationStart;
    }
  }
  return dateNow;
})();

/**
 * UUID4 generator
 *
 * @returns string Generated UUID4.
 */
function uuid4() {
  const gbl = GLOBAL_OBJ ;
  const crypto = gbl.crypto || gbl.msCrypto;

  let getRandomByte = () => Math.random() * 16;
  try {
    if (crypto && crypto.randomUUID) {
      return crypto.randomUUID().replace(/-/g, '');
    }
    if (crypto && crypto.getRandomValues) {
      getRandomByte = () => {
        // crypto.getRandomValues might return undefined instead of the typed array
        // in old Chromium versions (e.g. 23.0.1235.0 (151422))
        // However, `typedArray` is still filled in-place.
        // @see https://developer.mozilla.org/en-US/docs/Web/API/Crypto/getRandomValues#typedarray
        const typedArray = new Uint8Array(1);
        crypto.getRandomValues(typedArray);
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return typedArray[0];
      };
    }
  } catch (_) {
    // some runtimes can crash invoking crypto
    // https://github.com/getsentry/sentry-javascript/issues/8935
  }

  // http://stackoverflow.com/questions/105034/how-to-create-a-guid-uuid-in-javascript/2117523#2117523
  // Concatenating the following numbers as strings results in '10000000100040008000100000000000'
  return (([1e7] ) + 1e3 + 4e3 + 8e3 + 1e11).replace(/[018]/g, c =>
    // eslint-disable-next-line no-bitwise
    ((c ) ^ ((getRandomByte() & 15) >> ((c ) / 4))).toString(16),
  );
}

/**
 * Updates a session object with the properties passed in the context.
 *
 * Note that this function mutates the passed object and returns void.
 * (Had to do this instead of returning a new and updated session because closing and sending a session
 * makes an update to the session after it was passed to the sending logic.
 * @see BaseClient.captureSession )
 *
 * @param session the `Session` to update
 * @param context the `SessionContext` holding the properties that should be updated in @param session
 */
// eslint-disable-next-line complexity
function updateSession(session, context = {}) {
  if (context.user) {
    if (!session.ipAddress && context.user.ip_address) {
      session.ipAddress = context.user.ip_address;
    }

    if (!session.did && !context.did) {
      session.did = context.user.id || context.user.email || context.user.username;
    }
  }

  session.timestamp = context.timestamp || timestampInSeconds();

  if (context.abnormal_mechanism) {
    session.abnormal_mechanism = context.abnormal_mechanism;
  }

  if (context.ignoreDuration) {
    session.ignoreDuration = context.ignoreDuration;
  }
  if (context.sid) {
    // Good enough uuid validation. — Kamil
    session.sid = context.sid.length === 32 ? context.sid : uuid4();
  }
  if (context.init !== undefined) {
    session.init = context.init;
  }
  if (!session.did && context.did) {
    session.did = `${context.did}`;
  }
  if (typeof context.started === 'number') {
    session.started = context.started;
  }
  if (session.ignoreDuration) {
    session.duration = undefined;
  } else if (typeof context.duration === 'number') {
    session.duration = context.duration;
  } else {
    const duration = session.timestamp - session.started;
    session.duration = duration >= 0 ? duration : 0;
  }
  if (context.release) {
    session.release = context.release;
  }
  if (context.environment) {
    session.environment = context.environment;
  }
  if (!session.ipAddress && context.ipAddress) {
    session.ipAddress = context.ipAddress;
  }
  if (!session.userAgent && context.userAgent) {
    session.userAgent = context.userAgent;
  }
  if (typeof context.errors === 'number') {
    session.errors = context.errors;
  }
  if (context.status) {
    session.status = context.status;
  }
}

/**
 * Generate a random, valid trace ID.
 */
function generateTraceId() {
  return uuid4();
}

/**
 * Generate a random, valid span ID.
 */
function generateSpanId() {
  return uuid4().substring(16);
}

/**
 * Shallow merge two objects.
 * Does not mutate the passed in objects.
 * Undefined/empty values in the merge object will overwrite existing values.
 *
 * By default, this merges 2 levels deep.
 */
function merge(initialObj, mergeObj, levels = 2) {
  // If the merge value is not an object, or we have no merge levels left,
  // we just set the value to the merge value
  if (!mergeObj || typeof mergeObj !== 'object' || levels <= 0) {
    return mergeObj;
  }

  // If the merge object is an empty object, and the initial object is not undefined, we return the initial object
  if (initialObj && mergeObj && Object.keys(mergeObj).length === 0) {
    return initialObj;
  }

  // Clone object
  const output = { ...initialObj };

  // Merge values into output, resursively
  for (const key in mergeObj) {
    if (Object.prototype.hasOwnProperty.call(mergeObj, key)) {
      output[key] = merge(output[key], mergeObj[key], levels - 1);
    }
  }

  return output;
}

const SCOPE_SPAN_FIELD = '_sentrySpan';

/**
 * Set the active span for a given scope.
 * NOTE: This should NOT be used directly, but is only used internally by the trace methods.
 */
function _setSpanForScope(scope, span) {
  if (span) {
    addNonEnumerableProperty(scope , SCOPE_SPAN_FIELD, span);
  } else {
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete (scope )[SCOPE_SPAN_FIELD];
  }
}

/**
 * Get the active span for a given scope.
 * NOTE: This should NOT be used directly, but is only used internally by the trace methods.
 */
function _getSpanForScope(scope) {
  return scope[SCOPE_SPAN_FIELD];
}

/**
 * Default value for maximum number of breadcrumbs added to an event.
 */
const DEFAULT_MAX_BREADCRUMBS = 100;

/**
 * Holds additional event information.
 */
class ScopeClass  {
  /** Flag if notifying is happening. */

  /** Callback for client to receive scope changes. */

  /** Callback list that will be called during event processing. */

  /** Array of breadcrumbs. */

  /** User */

  /** Tags */

  /** Extra */

  /** Contexts */

  /** Attachments */

  /** Propagation Context for distributed tracing */

  /**
   * A place to stash data which is needed at some point in the SDK's event processing pipeline but which shouldn't get
   * sent to Sentry
   */

  /** Fingerprint */

  /** Severity */

  /**
   * Transaction Name
   *
   * IMPORTANT: The transaction name on the scope has nothing to do with root spans/transaction objects.
   * It's purpose is to assign a transaction to the scope that's added to non-transaction events.
   */

  /** Session */

  /** Request Mode Session Status */
  // eslint-disable-next-line deprecation/deprecation

  /** The client on this scope */

  /** Contains the last event id of a captured event.  */

  // NOTE: Any field which gets added here should get added not only to the constructor but also to the `clone` method.

   constructor() {
    this._notifyingListeners = false;
    this._scopeListeners = [];
    this._eventProcessors = [];
    this._breadcrumbs = [];
    this._attachments = [];
    this._user = {};
    this._tags = {};
    this._extra = {};
    this._contexts = {};
    this._sdkProcessingMetadata = {};
    this._propagationContext = {
      traceId: generateTraceId(),
      spanId: generateSpanId(),
    };
  }

  /**
   * @inheritDoc
   */
   clone() {
    const newScope = new ScopeClass();
    newScope._breadcrumbs = [...this._breadcrumbs];
    newScope._tags = { ...this._tags };
    newScope._extra = { ...this._extra };
    newScope._contexts = { ...this._contexts };
    if (this._contexts.flags) {
      // We need to copy the `values` array so insertions on a cloned scope
      // won't affect the original array.
      newScope._contexts.flags = {
        values: [...this._contexts.flags.values],
      };
    }

    newScope._user = this._user;
    newScope._level = this._level;
    newScope._session = this._session;
    newScope._transactionName = this._transactionName;
    newScope._fingerprint = this._fingerprint;
    newScope._eventProcessors = [...this._eventProcessors];
    newScope._requestSession = this._requestSession;
    newScope._attachments = [...this._attachments];
    newScope._sdkProcessingMetadata = { ...this._sdkProcessingMetadata };
    newScope._propagationContext = { ...this._propagationContext };
    newScope._client = this._client;
    newScope._lastEventId = this._lastEventId;

    _setSpanForScope(newScope, _getSpanForScope(this));

    return newScope;
  }

  /**
   * @inheritDoc
   */
   setClient(client) {
    this._client = client;
  }

  /**
   * @inheritDoc
   */
   setLastEventId(lastEventId) {
    this._lastEventId = lastEventId;
  }

  /**
   * @inheritDoc
   */
   getClient() {
    return this._client ;
  }

  /**
   * @inheritDoc
   */
   lastEventId() {
    return this._lastEventId;
  }

  /**
   * @inheritDoc
   */
   addScopeListener(callback) {
    this._scopeListeners.push(callback);
  }

  /**
   * @inheritDoc
   */
   addEventProcessor(callback) {
    this._eventProcessors.push(callback);
    return this;
  }

  /**
   * @inheritDoc
   */
   setUser(user) {
    // If null is passed we want to unset everything, but still define keys,
    // so that later down in the pipeline any existing values are cleared.
    this._user = user || {
      email: undefined,
      id: undefined,
      ip_address: undefined,
      username: undefined,
    };

    if (this._session) {
      updateSession(this._session, { user });
    }

    this._notifyScopeListeners();
    return this;
  }

  /**
   * @inheritDoc
   */
   getUser() {
    return this._user;
  }

  /**
   * @inheritDoc
   */
  // eslint-disable-next-line deprecation/deprecation
   getRequestSession() {
    return this._requestSession;
  }

  /**
   * @inheritDoc
   */
  // eslint-disable-next-line deprecation/deprecation
   setRequestSession(requestSession) {
    this._requestSession = requestSession;
    return this;
  }

  /**
   * @inheritDoc
   */
   setTags(tags) {
    this._tags = {
      ...this._tags,
      ...tags,
    };
    this._notifyScopeListeners();
    return this;
  }

  /**
   * @inheritDoc
   */
   setTag(key, value) {
    this._tags = { ...this._tags, [key]: value };
    this._notifyScopeListeners();
    return this;
  }

  /**
   * @inheritDoc
   */
   setExtras(extras) {
    this._extra = {
      ...this._extra,
      ...extras,
    };
    this._notifyScopeListeners();
    return this;
  }

  /**
   * @inheritDoc
   */
   setExtra(key, extra) {
    this._extra = { ...this._extra, [key]: extra };
    this._notifyScopeListeners();
    return this;
  }

  /**
   * @inheritDoc
   */
   setFingerprint(fingerprint) {
    this._fingerprint = fingerprint;
    this._notifyScopeListeners();
    return this;
  }

  /**
   * @inheritDoc
   */
   setLevel(level) {
    this._level = level;
    this._notifyScopeListeners();
    return this;
  }

  /**
   * Sets the transaction name on the scope so that the name of e.g. taken server route or
   * the page location is attached to future events.
   *
   * IMPORTANT: Calling this function does NOT change the name of the currently active
   * root span. If you want to change the name of the active root span, use
   * `Sentry.updateSpanName(rootSpan, 'new name')` instead.
   *
   * By default, the SDK updates the scope's transaction name automatically on sensible
   * occasions, such as a page navigation or when handling a new request on the server.
   */
   setTransactionName(name) {
    this._transactionName = name;
    this._notifyScopeListeners();
    return this;
  }

  /**
   * @inheritDoc
   */
   setContext(key, context) {
    if (context === null) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete this._contexts[key];
    } else {
      this._contexts[key] = context;
    }

    this._notifyScopeListeners();
    return this;
  }

  /**
   * @inheritDoc
   */
   setSession(session) {
    if (!session) {
      delete this._session;
    } else {
      this._session = session;
    }
    this._notifyScopeListeners();
    return this;
  }

  /**
   * @inheritDoc
   */
   getSession() {
    return this._session;
  }

  /**
   * @inheritDoc
   */
   update(captureContext) {
    if (!captureContext) {
      return this;
    }

    const scopeToMerge = typeof captureContext === 'function' ? captureContext(this) : captureContext;

    const [scopeInstance, requestSession] =
      scopeToMerge instanceof Scope
        ? // eslint-disable-next-line deprecation/deprecation
          [scopeToMerge.getScopeData(), scopeToMerge.getRequestSession()]
        : isPlainObject(scopeToMerge)
          ? [captureContext , (captureContext ).requestSession]
          : [];

    const { tags, extra, user, contexts, level, fingerprint = [], propagationContext } = scopeInstance || {};

    this._tags = { ...this._tags, ...tags };
    this._extra = { ...this._extra, ...extra };
    this._contexts = { ...this._contexts, ...contexts };

    if (user && Object.keys(user).length) {
      this._user = user;
    }

    if (level) {
      this._level = level;
    }

    if (fingerprint.length) {
      this._fingerprint = fingerprint;
    }

    if (propagationContext) {
      this._propagationContext = propagationContext;
    }

    if (requestSession) {
      this._requestSession = requestSession;
    }

    return this;
  }

  /**
   * @inheritDoc
   */
   clear() {
    // client is not cleared here on purpose!
    this._breadcrumbs = [];
    this._tags = {};
    this._extra = {};
    this._user = {};
    this._contexts = {};
    this._level = undefined;
    this._transactionName = undefined;
    this._fingerprint = undefined;
    this._requestSession = undefined;
    this._session = undefined;
    _setSpanForScope(this, undefined);
    this._attachments = [];
    this.setPropagationContext({ traceId: generateTraceId() });

    this._notifyScopeListeners();
    return this;
  }

  /**
   * @inheritDoc
   */
   addBreadcrumb(breadcrumb, maxBreadcrumbs) {
    const maxCrumbs = typeof maxBreadcrumbs === 'number' ? maxBreadcrumbs : DEFAULT_MAX_BREADCRUMBS;

    // No data has been changed, so don't notify scope listeners
    if (maxCrumbs <= 0) {
      return this;
    }

    const mergedBreadcrumb = {
      timestamp: dateTimestampInSeconds(),
      ...breadcrumb,
    };

    this._breadcrumbs.push(mergedBreadcrumb);
    if (this._breadcrumbs.length > maxCrumbs) {
      this._breadcrumbs = this._breadcrumbs.slice(-maxCrumbs);
      if (this._client) {
        this._client.recordDroppedEvent('buffer_overflow', 'log_item');
      }
    }

    this._notifyScopeListeners();

    return this;
  }

  /**
   * @inheritDoc
   */
   getLastBreadcrumb() {
    return this._breadcrumbs[this._breadcrumbs.length - 1];
  }

  /**
   * @inheritDoc
   */
   clearBreadcrumbs() {
    this._breadcrumbs = [];
    this._notifyScopeListeners();
    return this;
  }

  /**
   * @inheritDoc
   */
   addAttachment(attachment) {
    this._attachments.push(attachment);
    return this;
  }

  /**
   * @inheritDoc
   */
   clearAttachments() {
    this._attachments = [];
    return this;
  }

  /** @inheritDoc */
   getScopeData() {
    return {
      breadcrumbs: this._breadcrumbs,
      attachments: this._attachments,
      contexts: this._contexts,
      tags: this._tags,
      extra: this._extra,
      user: this._user,
      level: this._level,
      fingerprint: this._fingerprint || [],
      eventProcessors: this._eventProcessors,
      propagationContext: this._propagationContext,
      sdkProcessingMetadata: this._sdkProcessingMetadata,
      transactionName: this._transactionName,
      span: _getSpanForScope(this),
    };
  }

  /**
   * @inheritDoc
   */
   setSDKProcessingMetadata(newData) {
    this._sdkProcessingMetadata = merge(this._sdkProcessingMetadata, newData, 2);
    return this;
  }

  /**
   * @inheritDoc
   */
   setPropagationContext(
    context,
  ) {
    this._propagationContext = {
      // eslint-disable-next-line deprecation/deprecation
      spanId: generateSpanId(),
      ...context,
    };
    return this;
  }

  /**
   * @inheritDoc
   */
   getPropagationContext() {
    return this._propagationContext;
  }

  /**
   * @inheritDoc
   */
   captureException(exception, hint) {
    const eventId = hint && hint.event_id ? hint.event_id : uuid4();

    if (!this._client) {
      logger.warn('No client configured on scope - will not capture exception!');
      return eventId;
    }

    const syntheticException = new Error('Sentry syntheticException');

    this._client.captureException(
      exception,
      {
        originalException: exception,
        syntheticException,
        ...hint,
        event_id: eventId,
      },
      this,
    );

    return eventId;
  }

  /**
   * @inheritDoc
   */
   captureMessage(message, level, hint) {
    const eventId = hint && hint.event_id ? hint.event_id : uuid4();

    if (!this._client) {
      logger.warn('No client configured on scope - will not capture message!');
      return eventId;
    }

    const syntheticException = new Error(message);

    this._client.captureMessage(
      message,
      level,
      {
        originalException: message,
        syntheticException,
        ...hint,
        event_id: eventId,
      },
      this,
    );

    return eventId;
  }

  /**
   * @inheritDoc
   */
   captureEvent(event, hint) {
    const eventId = hint && hint.event_id ? hint.event_id : uuid4();

    if (!this._client) {
      logger.warn('No client configured on scope - will not capture event!');
      return eventId;
    }

    this._client.captureEvent(event, { ...hint, event_id: eventId }, this);

    return eventId;
  }

  /**
   * This will be called on every set call.
   */
   _notifyScopeListeners() {
    // We need this check for this._notifyingListeners to be able to work on scope during updates
    // If this check is not here we'll produce endless recursion when something is done with the scope
    // during the callback.
    if (!this._notifyingListeners) {
      this._notifyingListeners = true;
      this._scopeListeners.forEach(callback => {
        callback(this);
      });
      this._notifyingListeners = false;
    }
  }
}

/**
 * Holds additional event information.
 */
const Scope = ScopeClass;

/** Get the default current scope. */
function getDefaultCurrentScope() {
  return getGlobalSingleton('defaultCurrentScope', () => new Scope());
}

/** Get the default isolation scope. */
function getDefaultIsolationScope() {
  return getGlobalSingleton('defaultIsolationScope', () => new Scope());
}

/**
 * This is an object that holds a stack of scopes.
 */
class AsyncContextStack {

   constructor(scope, isolationScope) {
    let assignedScope;
    if (!scope) {
      assignedScope = new Scope();
    } else {
      assignedScope = scope;
    }

    let assignedIsolationScope;
    if (!isolationScope) {
      assignedIsolationScope = new Scope();
    } else {
      assignedIsolationScope = isolationScope;
    }

    // scope stack for domains or the process
    this._stack = [{ scope: assignedScope }];
    this._isolationScope = assignedIsolationScope;
  }

  /**
   * Fork a scope for the stack.
   */
   withScope(callback) {
    const scope = this._pushScope();

    let maybePromiseResult;
    try {
      maybePromiseResult = callback(scope);
    } catch (e) {
      this._popScope();
      throw e;
    }

    if (isThenable(maybePromiseResult)) {
      // @ts-expect-error - isThenable returns the wrong type
      return maybePromiseResult.then(
        res => {
          this._popScope();
          return res;
        },
        e => {
          this._popScope();
          throw e;
        },
      );
    }

    this._popScope();
    return maybePromiseResult;
  }

  /**
   * Get the client of the stack.
   */
   getClient() {
    return this.getStackTop().client ;
  }

  /**
   * Returns the scope of the top stack.
   */
   getScope() {
    return this.getStackTop().scope;
  }

  /**
   * Get the isolation scope for the stack.
   */
   getIsolationScope() {
    return this._isolationScope;
  }

  /**
   * Returns the topmost scope layer in the order domain > local > process.
   */
   getStackTop() {
    return this._stack[this._stack.length - 1] ;
  }

  /**
   * Push a scope to the stack.
   */
   _pushScope() {
    // We want to clone the content of prev scope
    const scope = this.getScope().clone();
    this._stack.push({
      client: this.getClient(),
      scope,
    });
    return scope;
  }

  /**
   * Pop a scope from the stack.
   */
   _popScope() {
    if (this._stack.length <= 1) return false;
    return !!this._stack.pop();
  }
}

/**
 * Get the global async context stack.
 * This will be removed during the v8 cycle and is only here to make migration easier.
 */
function getAsyncContextStack() {
  const registry = getMainCarrier();
  const sentry = getSentryCarrier(registry);

  return (sentry.stack = sentry.stack || new AsyncContextStack(getDefaultCurrentScope(), getDefaultIsolationScope()));
}

function withScope$1(callback) {
  return getAsyncContextStack().withScope(callback);
}

function withSetScope(scope, callback) {
  const stack = getAsyncContextStack() ;
  return stack.withScope(() => {
    stack.getStackTop().scope = scope;
    return callback(scope);
  });
}

function withIsolationScope(callback) {
  return getAsyncContextStack().withScope(() => {
    return callback(getAsyncContextStack().getIsolationScope());
  });
}

/**
 * Get the stack-based async context strategy.
 */
function getStackAsyncContextStrategy() {
  return {
    withIsolationScope,
    withScope: withScope$1,
    withSetScope,
    withSetIsolationScope: (_isolationScope, callback) => {
      return withIsolationScope(callback);
    },
    getCurrentScope: () => getAsyncContextStack().getScope(),
    getIsolationScope: () => getAsyncContextStack().getIsolationScope(),
  };
}

/**
 * Get the current async context strategy.
 * If none has been setup, the default will be used.
 */
function getAsyncContextStrategy(carrier) {
  const sentry = getSentryCarrier(carrier);

  if (sentry.acs) {
    return sentry.acs;
  }

  // Otherwise, use the default one (stack)
  return getStackAsyncContextStrategy();
}

/**
 * Get the currently active scope.
 */
function getCurrentScope() {
  const carrier = getMainCarrier();
  const acs = getAsyncContextStrategy(carrier);
  return acs.getCurrentScope();
}

/**
 * Creates a new scope with and executes the given operation within.
 * The scope is automatically removed once the operation
 * finishes or throws.
 */

/**
 * Either creates a new active scope, or sets the given scope as active scope in the given callback.
 */
function withScope(
  ...rest
) {
  const carrier = getMainCarrier();
  const acs = getAsyncContextStrategy(carrier);

  // If a scope is defined, we want to make this the active scope instead of the default one
  if (rest.length === 2) {
    const [scope, callback] = rest;

    if (!scope) {
      return acs.withScope(callback);
    }

    return acs.withSetScope(scope, callback);
  }

  return acs.withScope(rest[0]);
}

/**
 * Parse either an `EventHint` directly, or convert a `CaptureContext` to an `EventHint`.
 * This is used to allow to update method signatures that used to accept a `CaptureContext` but should now accept an `EventHint`.
 */
function parseEventHintOrCaptureContext(
  hint,
) {
  if (!hint) {
    return undefined;
  }

  // If you pass a Scope or `() => Scope` as CaptureContext, we just return this as captureContext
  if (hintIsScopeOrFunction(hint)) {
    return { captureContext: hint };
  }

  if (hintIsScopeContext(hint)) {
    return {
      captureContext: hint,
    };
  }

  return hint;
}

function hintIsScopeOrFunction(
  hint,
) {
  return hint instanceof Scope || typeof hint === 'function';
}

const captureContextKeys = [
  'user',
  'level',
  'extra',
  'contexts',
  'tags',
  'fingerprint',
  'requestSession',
  'propagationContext',
] ;

function hintIsScopeContext(hint) {
  return Object.keys(hint).some(key => captureContextKeys.includes(key ));
}

/**
 * Captures an exception event and sends it to Sentry.
 *
 * @param exception The exception to capture.
 * @param hint Optional additional data to attach to the Sentry event.
 * @returns the id of the captured Sentry event.
 */
function captureException(exception, hint) {
  return getCurrentScope().captureException(exception, parseEventHintOrCaptureContext(hint));
}

/**
 * Captures a message event and sends it to Sentry.
 *
 * @param message The message to send to Sentry.
 * @param captureContext Define the level of the message or pass in additional data to attach to the message.
 * @returns the id of the captured message.
 */
function captureMessage(message, captureContext) {
  // This is necessary to provide explicit scopes upgrade, without changing the original
  // arity of the `captureMessage(message, level)` method.
  const level = undefined;
  const context = { captureContext } ;
  return getCurrentScope().captureMessage(message, level, context);
}

/*
 * @Author: sweet
 * @Date: 2021-12-08 10:07:53
 * @LastEditors: 赵泽文
 * @LastEditTime: 2025-01-14 17:48:17
 * @Description: file content
 */
function processError(err, name = '', metaData = {}, level = 'error') {
    const env = window.MARS_BASE_CONFIG.REACT_APP_ENV === 'prod'
        ? window.t('production_env')
        : window.t('development_env');
    const error = new Error(`${env}: ${err}`);
    captureException(error, {
        level,
        extra: {
            name,
            metaData,
        },
    });
}
/**
 * 处理info级别日志
 * @param text
 * @param tags
 */
function processInfo(text, tags) {
    if (tags) {
        withScope((scope) => {
            scope.setTags(tags);
            scope.setLevel('info');
            captureMessage(text);
        });
    }
    captureMessage(text);
}

/**
 * @description: rgb转Hex
 * @param {string} color
 * @return {*}
 */
const rgbToHex = (color) => {
    const rgb = color.split(',');
    const r = parseInt(rgb[0].split('(')[1], 10);
    const g = parseInt(rgb[1], 10);
    const b = parseInt(rgb[2].split(')')[0], 10);
    // eslint-disable-next-line no-bitwise
    const hex = `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
    return hex;
};
/**
 * @description: hex转rgb
 * @param {string} color
 * @return {*}
 */
const hexToRgb = (color) => {
    return `rgba(${parseInt(`0x${color.slice(1, 3)}`, 10)}, ${parseInt(`0x${color.slice(3, 5)}`, 10)}, ${parseInt(`0x${color.slice(5, 7)}`, 10)})`;
};
/**
 * @description: hex转rgba
 * @param {string} color
 * @param {string} opacity
 * @return {*}
 */
const hexToRgba = (color, opacity) => {
    return `rgba(${parseInt(`0x${color?.slice(1, 3)}`, 10)}, ${parseInt(`0x${color?.slice(3, 5)}`, 10)}, ${parseInt(`0x${color.slice(5, 7)}`, 10)}, ${opacity ?? 1})`;
};
/**
 * @description: rgb转Rgba
 * @param {string} color
 * @param {string} opacity
 * @return {*}
 */
const rgbToRgba = (color, opacity) => {
    const rgbArr = color.match(/[\d.]+/g) ?? [];
    if (rgbArr.length >= 3) {
        return `rgba(${rgbArr[0]}, ${rgbArr[1]}, ${rgbArr[2]}, ${opacity ?? 1})`;
    }
    return '';
};
/**
 * @description: rgba转rgb
 * @param {string} color
 * @return {*}
 */
const rgbaToRgb = (color) => {
    const rgbaArr = color.match(/[\d.]+/g) ?? [];
    if (rgbaArr.length >= 3) {
        return `rgba(${rgbaArr[0]}, ${rgbaArr[1]}, ${rgbaArr[2]})`;
    }
    return '';
};
/**
 * rgb或者ragba添加透明度
 * @param color
 * @param opacity
 * @returns {string}
 */
const rgbAddOpacity = (color, opacity) => {
    const rgbaArr = color.match(/[\d.]+/g) ?? [];
    if (rgbaArr.length >= 3) {
        return `rgba(${rgbaArr[0]}, ${rgbaArr[1]}, ${rgbaArr[2]}, ${opacity})`;
    }
    return color;
};

/*
 * @Author: rxw
 * @Date: 2024-12-23 19:16:28
 * @LastEditors: 赵泽文
 * @LastEditTime: 2025-08-18 17:42:35
 * @Description: file contents
 */
// 存储每个 tableType 的请求锁，避免重复请求
const requestLockMap = new Map();
const getColumns = async (tableType, isCoreServer, filterDeprecatedFields, hideAndPreserveFields) => {
    let result = {};
    const params = { staff_id: window.getCookie('operator_id'), item: tableType };
    if (isCoreServer) {
        result = await window.marsClient.analysis.table.read(params);
    }
    else {
        result = await getCustomColumn(params);
    }
    if (result.isSuccess()) {
        const data = result.data || result.success().data;
        if (data) {
            return {
                fields: [],
                columState: (data?.custom_column_filed_list || [])
                    ?.map((item) => ({
                    aggFunc: null,
                    pivot: false,
                    pivotIndex: null,
                    rowGroup: false,
                    rowGroupIndex: null,
                    flex: null,
                    sort: item.sort_filed,
                    sortIndex: item.sort,
                    hide: !item.display_is || hideAndPreserveFields?.includes(item.field),
                    pinned: ['$$selection', '$$index'].includes(item.field)
                        ? 'left'
                        : item.field === '$$actions'
                            ? 'right'
                            : item.fixed_is
                                ? 'left'
                                : undefined,
                    // pinned: item.fixed_is,
                    width: item.width,
                    colId: item.field,
                    /** 过滤弃用的字段 */
                    deprecated: filterDeprecatedFields && filterDeprecatedFields?.includes(item.field),
                }))
                    .filter((item) => !item.deprecated),
            };
        }
        return null;
    }
    return null;
};
const setColumns = async (rowState, tableType, isCoreServer, hideAndPreserveFields = []) => {
    const staff_id = window.getCookie('operator_id');
    const lockKey = `${tableType}_${staff_id}`;
    // 如果正在请求中，直接返回
    if (requestLockMap.get(lockKey)) {
        return;
    }
    const params = {
        item: tableType,
        staff_id,
        custom_column_filed_list: rowState?.columState?.map((item) => ({
            custom_column_filed_list: [],
            display_is: !item.hide || hideAndPreserveFields?.includes(item.colId),
            field: item.colId,
            fixed_is: !!item.pinned,
            sort: item.sortIndex || 0,
            sort_filed: item.sort || '',
            width: item.width,
        })),
    };
    if (isCoreServer) {
        await window.marsClient.analysis.table.update(params);
    }
    else {
        if (!staff_id)
            return;
        // 设置请求锁
        requestLockMap.set(lockKey, true);
        try {
            await updateCustomColumn(params);
        }
        finally {
            // 请求完成后删除锁，避免内存泄漏
            // 删除而不是设置为false，防止Map无限增长
            requestLockMap.delete(lockKey);
        }
    }
};
const transferCustomColumn = (tableType, isCoreServer = false, 
/** 过滤弃用的字段 */
filterDeprecatedFields = [], 
/** 在某些特定情况下显示时需要隐藏列字段，但是保存时需要将其改为显示保存，比如调出单的库存数量 */
hideAndPreserveFields = []) => ({
    set(state) {
        setColumns(state, tableType, isCoreServer, hideAndPreserveFields);
    },
    get() {
        return getColumns(tableType, isCoreServer, filterDeprecatedFields, hideAndPreserveFields);
    },
});
// 把索引列挪到数组最前面
const moveIndexColumnToFirst = (state) => {
    const newState = cloneDeep(state);
    const indexIndex = newState?.columState?.findIndex((item) => item.colId === '$$index');
    if (indexIndex > -1) {
        const indexItem = newState?.columState[indexIndex];
        newState?.columState?.splice(indexIndex, 1);
        newState?.columState?.unshift(indexItem);
        return newState;
    }
    return newState;
};

/** 检验数据消耗单位是否选择 */
const verifyCostCardUnit = (list, prefix = '') => {
    let text = '';
    if (list.some((item) => {
        if (!item.item_cost_unit_name) {
            if (item.item_name === item.spec) {
                text = `${item.item_name}`;
            }
            else {
                text = `${item.item_name} ${item.spec || ''}`;
            }
            text += ` 消耗单位尚未选择`;
            return true;
        }
        return false;
    })) {
        message.error(`${prefix}${text}`);
        throw new Error(`${prefix}${text}`);
    }
};
// 检验单价乘以数量是否等于金额
// export const verifyMoney = (list: ICostCardItemAttr[], prefix: string = '') => {
//   let text = '';
//   const isFalse = list.some((item) => {
//     if (
//       Number(item.item_cost_unit_price) * Number(item.item_cost_qty) !==
//       Number(item.item_cost_money)
//     ) {
//       text = `${item.item_name} 消耗金额不等于消耗单价*消耗量`;
//       return true;
//     } else {
//       return false;
//     }
//   });
//   if (isFalse) {
//     message.error(`${prefix}${text}`);
//     throw new Error(`${prefix}${text}`);
//   }
// };
// 检验数量
const verifyCostCardQty = (list, name = '') => {
    let productName = '';
    if (list.some((item) => {
        if ((Number(item.item_cost_qty) === 0 || Number(item.item_cost_base_unit_qty) === 0) &&
            !productName) {
            if (item.item_name === item.spec) {
                productName = `${name}${item.item_name}`;
            }
            else {
                productName = `${name}${item.item_name} ${item.spec || ''}`;
            }
            return true;
        }
        return false;
    })) {
        message.error(`${productName}${window.t('quantity_must_be_greater_than_0')}`);
        throw new Error(`${productName}${window.t('quantity_must_be_greater_than_0')}`);
    }
};
// 检验单价
const verifyCostCardMoney = (list, name = '') => {
    let productName = '';
    if (list.some((item) => {
        if (Number(item.item_cost_unit_price) === 0) {
            if (item.item_name === item.spec) {
                productName = `${name}${item.item_name}`;
            }
            else {
                productName = `${name}${item.item_name} ${item.spec || ''}`;
            }
            return true;
        }
        return false;
    })) {
        message.error(`${productName} 消耗金额单价必须大于0`);
        throw new Error(`${productName}消耗金额单价必须大于0`);
    }
};
/**
 * 删除商品成本卡
 * @param table 原表格
 * @param id 更改id
 * @param page 当前页
 * @returns
 */
const tableCostCardDelete = (table, id, page = 1) => {
    const tmpTable = [...table];
    const findIndex = tmpTable.findIndex((item) => item.cost_card_id === id);
    if (findIndex > -1) {
        tmpTable.splice(findIndex, 1);
    }
    let newPage = page;
    if (tmpTable.length === 0 && page > 1) {
        newPage = page - 1;
    }
    return {
        dataSource: tmpTable,
        resultPage: newPage,
    };
};
/**
 * 使用成本卡权限
 * @returns {boolean} 包含成本卡编辑权限设置的对象。
 */
const useCostCardPermission = () => {
    const [setting, setSetting] = useState(false);
    // 获取成本卡设置
    const getCostCardSettings = async () => {
        setSetting(false);
        const result = await getSettings();
        if (result.isSuccess()) {
            const { cost_card_setting } = result.data;
            setSetting(cost_card_setting?.allow_edit_cost_card_for_branch || false);
        }
    };
    useEffect(() => {
        getCostCardSettings();
    }, []);
    return { setting };
};

/**
 * 兼容 safari, 将 string 转为 Date 类型
 * @param time
 * @returns
 */
const getDateHandler = (time) => {
    return new Date(time?.replace(/-/g, '/'));
};
/**
 * 比较两个日期的大小(第一个大于第二个日期时返回 1, 相等时返回 0, 小于时返回 -1)
 * @param firstDate 需要比较的日期
 * @param secondDate 默认当前日期
 * @returns
 */
const compareDate = (firstDate, secondDate = new Date()) => {
    if (!firstDate || !secondDate)
        return 0;
    const tmpFirstDate = typeof firstDate === 'string' ? getDateHandler(firstDate) : firstDate;
    const tmpSecondDate = typeof secondDate === 'string' ? getDateHandler(secondDate) : secondDate;
    if (tmpFirstDate < tmpSecondDate) {
        return -1;
    }
    if (tmpFirstDate > tmpSecondDate) {
        return 1;
    }
    return 0;
};
/**
 * 将 时分秒 转成 number， 用来计算
 * @param time
 * @returns
 */
const hoursToSec = (time) => {
    if (!time)
        return 0;
    const hour = time.split(':')[0];
    const minute = time.split(':')[1];
    const second = time.split(':')[2];
    return Number(hour) * 3600 + Number(minute) * 60 + Number(second);
};
/**
 * 比较两个时分秒的大小
 * @param firstTime
 * @param secondTime
 * @returns
 */
const compareHours = (firstTime, secondTime) => {
    return hoursToSec(firstTime) - hoursToSec(secondTime) > 0;
};
/** 获取昨日时间 */
const getYesterday = () => {
    const start = moment().subtract(1, 'day');
    const end = moment(start);
    return [start, end];
};
/** 获取今日时间 */
const getToday = () => {
    const start = moment();
    const end = moment(start);
    return [start, end];
};
/** 获取今日时间 */
const getTodayTime = () => {
    return [moment('00:00', 'HH:mm'), moment('23:59', 'HH:mm')];
};
/** 获取今日时间New */
const getTodayTimeNew = () => {
    return [moment('00:00:00', 'HH:mm:ss'), moment('23:59:59', 'HH:mm:ss')];
};
/** 获取最近一周时间 */
const getCurrentWeek = (limitToday) => {
    const start = moment().week(moment().week()).startOf('week');
    const end = limitToday ? moment().endOf('day') : moment().week(moment().week()).endOf('week');
    return [start, end];
};
/** 获取最近上周时间 */
const getLastWeek = () => {
    const start = moment().week(moment().week()).subtract(7, 'd').startOf('week');
    const end = moment().week(moment().week()).subtract(7, 'd').endOf('week');
    return [start, end];
};
/** 获取最近7天时间 */
const getSevenDays = () => {
    const start = moment().startOf('day').subtract(6, 'days');
    const end = moment().endOf('day');
    return [start, end];
};
/** 获取最近30天时间 */
const getThirtyDays = () => {
    return [moment().add(-29, 'days').startOf('day'), moment().endOf('day')];
};
/** 获取本月时间 */
const getCurrentMonth = (limitToday) => {
    const start = moment().month(moment().month()).startOf('month');
    const end = limitToday ? moment().endOf('day') : moment().month(moment().month()).endOf('month');
    return [start, end];
};
/** 获取上个月时间 */
const getLastMonth = () => {
    const start = moment()
        .month(moment().month() - 1)
        .startOf('month');
    const end = moment()
        .month(moment().month() - 1)
        .endOf('month');
    return [start, end];
};
/** 获取本季度时间 */
const getCurrentQuarter = (limitToday) => {
    const start = moment().startOf('quarter');
    const end = limitToday ? moment().endOf('day') : moment().endOf('quarter');
    return [start, end];
};
/** 获取本年时间 */
const getCurrentYear = (limitToday) => {
    const start = moment().year(moment().year()).startOf('year');
    const end = limitToday ? moment().endOf('day') : moment().year(moment().year()).endOf('year');
    return [start, end];
};
const getRangeOptions = (limitCount, limitToday) => {
    const origin = [
        [window.t('today'), getToday()],
        [window.t('yesterday'), getYesterday()],
        [window.t('last_seven_days'), [moment().subtract(6, 'days'), moment()]],
        [window.t('last_thirty_days'), [moment().subtract(29, 'days'), moment()]],
        [window.t('week'), getCurrentWeek(limitToday)],
        [window.t('last_week'), getLastWeek()],
        [window.t('current_month'), getCurrentMonth(limitToday)],
        [window.t('last_month'), getLastMonth()],
        [window.t('current_quarter'), getCurrentQuarter(limitToday)],
        [window.t('current_year'), getCurrentYear(limitToday)],
    ];
    return Object.fromEntries((limitCount || 0) > 0 ? origin.slice(0, limitCount) : origin);
};
/** 默认可查询年数 */
const DEFAULT_AVAILABLE_QUERY_YEAR = 2;
/**
 * 读取商户可查询年数（base-app 登录后写入 localStorage 的 `available_query_year`，值为总年数）。
 * 缺失、非法（NaN / <= 0）时回退默认 2 年。
 */
function getAvailableQueryYear() {
    try {
        const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('available_query_year') : null;
        const year = Number(raw);
        if (raw !== null && Number.isFinite(year) && year > 0)
            return year;
    }
    catch (e) {
        // localStorage 不可用时走默认值
    }
    return DEFAULT_AVAILABLE_QUERY_YEAR;
}
/**
 * 报表 range-picker disabledDate：仅以当前时间为锚，不读表单。
 * 可查询范围由 localStorage `available_query_year`（总年数，默认 2）控制。
 * 按日：不可晚于今天；不可早于「今天 0 点」按自然年往前推 N 年。
 * 按月：不可晚于当前月；不可早于当前月往前推 N*12 - 1 个月（与当前月共 N*12 个月）。
 * @param cycleType 非必传，默认按日 `DATE_DAY`。
 */
function getReportRangePickerDisabledDate(cycleType, disableStartDateOnly) {
    const mode = cycleType ?? 'DATE_DAY';
    const onlyDisableStartDate = disableStartDateOnly ?? false;
    const availableYears = getAvailableQueryYear();
    return (current) => {
        if (!current)
            return false;
        if (mode === 'DATE_MONTH') {
            const curM = current.clone().startOf('month');
            const nowMonthStart = moment().startOf('month');
            if (curM.isAfter(nowMonthStart, 'month'))
                return true;
            const minMonth = moment()
                .startOf('month')
                .subtract(availableYears * 12 - 1, 'months');
            if (curM.isBefore(minMonth, 'month'))
                return true;
            return false;
        }
        if (current > moment().endOf('day') && !onlyDisableStartDate)
            return true;
        const minDay = moment().startOf('day').subtract(availableYears, 'years');
        if (current.clone().startOf('day').isBefore(minDay, 'day'))
            return true;
        return false;
    };
}

var ObjectStatus;
(function (ObjectStatus) {
    ObjectStatus["ADDED"] = "added";
    ObjectStatus["EQUAL"] = "equal";
    ObjectStatus["DELETED"] = "deleted";
    ObjectStatus["UPDATED"] = "updated";
})(ObjectStatus || (ObjectStatus = {}));
const diffLabel = (text, diff) => {
    if (diff) {
        return jsxRuntimeExports.jsx("span", { style: { color: 'red' }, children: text });
    }
    return text;
};
const getDiffKey = (diffR, ignoreKey = ['created_at', 'updated_at']) => {
    const key = [];
    for (const item of diffR) {
        if (item.status === 'equal' || ignoreKey.includes(item.property))
            continue;
        let subDiff = [];
        if (item.diff) {
            subDiff = getDiffKey(item.diff, ignoreKey);
        }
        if (item.diff) {
            if (subDiff.length > 0) {
                key.push(item.property, ...subDiff);
            }
        }
        else {
            key.push(item.property);
        }
    }
    return key;
};
/**
 * 移除时间字符串中的年月日部分
 */
const removeDate = (date) => {
    return moment(date).format(`HH:mm:ss`);
};

//base64转文件
function dataURLtoBlob(dataurl) {
    const arr = dataurl.split(',');
    const mime = arr[0]?.match(/:(.*?);/)?.[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
}
// 下载图片
const downloadImageByURLForWx = (url, imageName = '小程序码', cb) => {
    fetch(url)
        .then((res) => {
        if (res.status === 200) {
            return res.blob();
        }
        throw new Error(`download error ${res.status}`);
    })
        .then((blob) => {
        const a = document.createElement('a');
        a.download = `${imageName}.jpg`;
        a.style.display = 'none';
        a.href = URL.createObjectURL(blob);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    })
        .catch((e) => window.console.log(e))
        .finally(() => {
        cb?.();
    });
};

/*
 * @Author: 彭鑫诚
 * @Date: 2024-11-07 15:02:15
 * @LastEditors: 彭鑫诚
 * @LastEditTime: 2024-11-12 10:13:00
 * @Description: 处理筛选项增删改查接口
 */
function filterService(key, service) {
    let url = key;
    // 物品筛选器保存统一接口
    const PRODUCT_FILTER_KEYS = [
        'ADJUSTMENT_ORDER_PRODUCT',
        'CHECK_ORDER_PRODUCT',
        'FINISHED_PRODUCT',
        'INNER_ORDER_ORDER_PRODUCT',
        'PRODUCE_FAST_ORDER_PRODUCT',
        'PRODUCE_MATERIAL_ORDER_PRODUCT',
        'PRODUCE_PLAN_ORDER_PRODUCT',
        'PRODUCE_STORAGE_ORDER_PRODUCT',
        'PURCHASE_ORDER_PRODUCT',
        'RECEIVE_ORDER_PRODUCT',
        'REQUEST_ORDER_PRODUCT',
        'RETURN_ORDER_PRODUCT',
        'TRANSFER_IN_ORDER_PRODUCT',
        'TRANSFER_OUT_ORDER_PRODUCT',
        'LOCATION_DISTRIBUTION_PRICE_PRODUCT',
        'DISTRIBUTION_GROSS_PRODUCT',
        'INVENTOTY_CHANGE_STATISTICS_PRODUCT',
        'REQUEST_ORDER_TEMPLATE_PRODUCT',
        'PRODUCE_REPORT_PRODUCE_ITEM',
    ];
    // 库存进出明细、汇总
    const STOCK_INVENTORY_KEYS = [
        'STOCK_INVENTORY_LEVEL_LOG',
        'STOCK_INVENTORY_LEVEL_LOG_IN_SUM',
        'STOCK_INVENTORY_LEVEL_LOG_OUT_SUM',
        'STOCK_INVENTORY_LEVEL_LOG_SUM',
    ];
    if (STOCK_INVENTORY_KEYS.includes(key)) {
        url = 'STOCK_INVENTORY_LEVEL/LOG';
    }
    if (PRODUCT_FILTER_KEYS.includes(key)) {
        return { list: filterList, add: filterCreate, delete: filterDelete, update: filterUpdate };
    }
    return {
        list: (data) => generalFilterList(url, data, service),
        add: (data) => generalFilterCreate(url, data, service),
        delete: (id) => generalFilterDelete(url, id, service),
        update: (data) => generalFilterUpdate(url, data, service),
    };
}
const getFilter = async (storageKey, fields, service, initQuery) => {
    const staff_id = window.getCookie('operator_id');
    const res = await filterService(storageKey, service).list({
        resource_type: storageKey,
        staff_id,
    });
    if (res.isSuccess()) {
        const data = res.data || [];
        const transferData = taransferReturn(data, fields, service, initQuery);
        return transferData;
    }
    return [];
};
const setFilter = async (state, storageKey, fields, service, initQuery) => {
    const staff_id = window.getCookie('operator_id');
    const { action, target } = state;
    if (!target.key && action === 'set') {
        const params = {
            name: target.title,
            resource_type: storageKey,
            query_fields: target.fields,
            staff_id: staff_id,
            query: target.title === '$$default' ? undefined : target.value,
        };
        const tranferedParams = taransferParams(params, service);
        const res = await filterService(storageKey, service).add(tranferedParams);
        if (res.isSuccess()) {
            message.success('保存成功');
            return getFilter(storageKey, fields, service, initQuery);
        }
    }
    if (target.key) {
        if (action === 'set') {
            const params = {
                name: target.title,
                resource_type: storageKey,
                query_fields: target.fields,
                staff_id: staff_id,
                query: target.title === '$$default' ? undefined : target.value,
                id: target.key,
            };
            const tranferedParams = taransferParams(params, service);
            await filterService(storageKey, service).update(tranferedParams);
            message.success('保存成功');
        }
        if (action === 'delete') {
            await filterService(storageKey, service).delete(target.key);
            message.success('删除成功');
        }
    }
    return;
};
const transferCustomFilter = (storageKey, fields, service = 'chain', initQuery) => {
    if (!storageKey) {
        return undefined;
    }
    return {
        get() {
            return getFilter(storageKey, fields, service, initQuery);
        },
        set(state) {
            return setFilter(state, storageKey, fields, service, initQuery);
        },
    };
};
// 处理筛选器保存参数
const taransferParams = (params, service) => {
    // 通用筛选器
    if (service !== 'report') {
        return params;
    }
    // 报表筛选器
    const result = {
        staff_id: params.staff_id,
        title: params.name,
        content: JSON.stringify({
            ...params.query,
            special_filter_id: [ZONE, HEAD_QUARTERS].includes(getShopType())
                ? window.getCookie('location_zone_id')
                : window.getCookie('location_id'),
        }),
    };
    if (params.id) {
        result.id = params.id;
    }
    return result;
};
// 根基门店、区域唯一id过滤筛选器
const filterReturn = (data) => data?.filter((item) => {
    const content = JSON.parse(item.content ?? '{}');
    const special_filter_id = content.special_filter_id;
    const special_system_id = [ZONE, HEAD_QUARTERS].includes(getShopType())
        ? window.getCookie('location_zone_id')
        : window.getCookie('location_id');
    if (special_filter_id === special_system_id) {
        return true;
    }
    return false;
}) || [];
// 处理筛选器返回值
const taransferReturn = (data, fields, service, initQuery) => {
    // 通用筛选器
    if (service !== 'report') {
        let isHaveDefault = false; // 为兼容历史数据，判断是否有默认的数据
        let defaultIndex = -1; // 为兼容历史数据，判断是否有默认的数据
        const result = data.map((item, index) => {
            const query = item.query || {};
            Object.keys(query).forEach((key) => {
                if (query[key] === null) {
                    query[key] = undefined;
                }
            });
            if (item.name === '$$default') {
                isHaveDefault = true;
                defaultIndex = index;
            }
            if (!item.query_fields) {
                item.query_fields = [];
            }
            // 后续新增的筛选字段默认显示
            fields.forEach((single) => {
                if (!item.query_fields?.find((field) => field.key === single.key)) {
                    item.query_fields.push({ key: single.key, hidden: false });
                }
            });
            return {
                title: item.name,
                value: (item.query ?? {}),
                fields: item.query_fields ?? fields.map((single) => ({ key: single.key, hidden: false })),
                key: item.id,
            };
        });
        if (!isHaveDefault) {
            result.unshift({
                title: '$$default',
                value: initQuery ?? {},
                fields: fields.map((single) => ({ key: single.key, hidden: false })),
                key: '',
            });
        }
        else {
            if (defaultIndex > 0) {
                result.unshift(result.splice(defaultIndex, 1)[0]);
            }
        }
        return result.length ? result : [];
    }
    // 报表筛选器
    let isHaveDefault = false; // 为兼容历史数据，判断是否有默认的数据
    let defaultIndex = -1; // 为兼容历史数据，判断是否有默认的数据
    const result = filterReturn(data).map((item, index) => {
        if (item.name === '$$default') {
            isHaveDefault = true;
            defaultIndex = index;
        }
        return {
            title: item.title,
            value: JSON.parse(item.content ?? '{}'),
            fields: [],
            key: item.id,
        };
    });
    if (!isHaveDefault) {
        result.unshift({
            title: '$$default',
            value: initQuery ?? {},
            fields: [],
            key: '',
        });
    }
    else {
        if (defaultIndex > 0) {
            result.unshift(result.splice(defaultIndex, 1)[0]);
        }
    }
    return result.length ? result : [];
};

/*
 * @Author: 张帮辉
 * @LastEditors: 赵泽文
 * @Date: 2022-04-20 14:49:34
 * @LastEditTime: 2026-03-30 15:50:17
 * @Description: 单据常用方法
 */
const emptyProductData = {
    id: '',
    assist_is: false,
    assist_unit: '',
    assist_unit_rate: '',
    base_unit_price: '',
    base_unit_qty: '',
    showStock: '',
    showMoney: '',
    detail_money: '',
    showPrice: '',
    use_unit_price: '',
    use_unit_qty: '',
    use_unit_rate: '',
    detail_cost_money: '',
    gift_is: false,
    product_psi: {
        product_id: '',
        merchant_id: '',
        base_unit: '',
        base_unit_chain_is: false,
        use_unit: '',
        assist_unit: '',
        assist_unit_chain_is: false,
        assist_unit_rate: '',
        sub_unit1: '',
        sub_unit1_chain_is: false,
        sub_unit2: '',
        sub_unit2_chain_is: false,
        sub_unit_rate1: '',
        sub_unit_rate2: '',
        created_at: '',
    },
    product_variant_psi: {
        product_variant_id: '',
        product_id: '',
        merchant_id: '',
        chain_price: '0',
        created_at: '',
        item_type_id: '',
        spec: '',
        base_unit: '',
        sub_unit1: '',
        sub_unit_rate1: '',
        sub_unit2: '',
        sub_unit_rate2: '',
        assist_unit: '',
        assist_unit_rate: '',
        base_unit_chain_is: false,
        sub_unit1_chain_is: false,
        sub_unit2_chain_is: false,
        assist_unit_chain_is: false,
        use_unit: '',
        stop_purchase_is: false,
        stop_sell_is: false,
        produce_place: '',
        cost_calculate_type: '',
        item_type_name: '',
    },
    product_id: '',
    product_title: '',
    product_variant_title: '',
    product_variant_barcode: '',
    use_unit: '',
    product_variant_id: '',
    base_unit: '',
    gift_use_unit: '',
    gift_use_unit_qty: '',
    gift_use_unit_rate: '',
    gift_base_unit_qty: '',
};
/** 采购单单价取价规则 */
const getPurchaseUseUnitPrice = (item, use_unit = '', assist_unit_as_use_unit) => {
    const currentUseUnit = use_unit || item?.use_unit;
    const currentUnit = item?.product_variant_psi?.all_unit_list?.find((i) => i?.sub_unit === currentUseUnit);
    const itemBaseUnit = item?.base_unit || item?.product_variant_psi?.base_unit;
    const baseUnit = item?.product_variant_psi?.all_unit_list?.find((i) => i?.sub_unit === itemBaseUnit);
    const isBaseUnit = currentUnit?.sub_unit === itemBaseUnit;
    let resUseUnitPrice = 0;
    // 1.取供应商约定价格
    // 2.取最近进价
    if (+(item?.agreed_price || 0) || +(item?.last_purchasing_price || 0)) {
        resUseUnitPrice = priceQuantityFormatterOperator(+(item?.agreed_price || 0) || +(item?.last_purchasing_price || 0), assist_unit_as_use_unit && item?.product_variant_psi?.use_assist_unit_is
            ? item?.product_variant_psi?.assist_unit_rate
            : currentUnit?.sub_unit_rate, 'mult');
    }
    // 3.副单位有采购单价取采购单价
    else if (+(currentUnit?.sub_unit_purchase_price || 0)) {
        if (isBaseUnit) {
            resUseUnitPrice = priceQuantityFormatterOperator(currentUnit?.sub_unit_purchase_price, currentUnit?.sub_unit_rate, 'mult');
        }
        else {
            resUseUnitPrice = priceQuantityFormatter(currentUnit?.sub_unit_purchase_price || '0');
        }
    }
    // 当前是副单位，且不是基本单位，无采购单价或采购单价为0则取基本单位采购单价*当前单位的换算率
    else if (currentUnit && !isBaseUnit && +(baseUnit?.sub_unit_purchase_price || 0)) {
        resUseUnitPrice = priceQuantityFormatterOperator(baseUnit?.sub_unit_purchase_price, currentUnit?.sub_unit_rate, 'mult');
    }
    // 4.若是辅助单位，则取基本单位采购单价*辅助单位换算率，如果采购单价为0则取档案成本价
    else if (assist_unit_as_use_unit && item?.product_variant_psi?.use_assist_unit_is) {
        resUseUnitPrice = priceQuantityFormatterOperator(Number(baseUnit?.sub_unit_purchase_price) > 0
            ? baseUnit?.sub_unit_purchase_price
            : item.cost_price, item?.product_variant_psi?.assist_unit_rate || '0', 'mult');
    }
    // 5.若基本单位无采购价或未0，则取参考成本*当前单位的换算率
    else {
        resUseUnitPrice = priceQuantityFormatterOperator(item.cost_price, currentUnit?.sub_unit_rate || '', 'mult');
    }
    return resUseUnitPrice;
};
/** 采购单基本单价取价规则 */
const getPurchaseBaseUnitPrice = (item, use_unit = '') => {
    const currentUseUnit = use_unit || item?.use_unit;
    const product_variant_psi = item?.product_variant_psi || {};
    const currentUnit = product_variant_psi?.all_unit_list?.find((i) => i?.sub_unit === currentUseUnit);
    const itemBaseUnit = item?.base_unit || item?.product_variant_psi?.base_unit;
    const isBaseUnit = currentUnit?.sub_unit === itemBaseUnit;
    const baseUnit = product_variant_psi?.all_unit_list?.find((i) => i?.sub_unit === itemBaseUnit);
    let resBaseUnitPrice = 0;
    // 1.取供应商约定价格
    // 2.取最近进价
    if (+(item?.agreed_price || 0) || +(item?.last_purchasing_price || 0)) {
        resBaseUnitPrice = priceQuantityFormatter(+(item?.agreed_price || 0) || +(item?.last_purchasing_price || 0));
    }
    // 3.副单位有采购单价取采购单价/当前单位的换算率，无采购单价或采购单价为0则取基本单位采购单价
    else if (+(currentUnit?.sub_unit_purchase_price || 0)) {
        if (isBaseUnit) {
            resBaseUnitPrice = priceQuantityFormatter(currentUnit?.sub_unit_purchase_price || '0');
        }
        else {
            resBaseUnitPrice = priceQuantityFormatterOperator(currentUnit?.sub_unit_purchase_price, currentUnit?.sub_unit_rate, 'divide');
        }
    }
    else if (!isBaseUnit && +(baseUnit?.sub_unit_purchase_price || 0)) {
        resBaseUnitPrice = priceQuantityFormatterOperator(baseUnit?.sub_unit_purchase_price || '0');
    }
    // 4.若基本单位无采购价或未0 或为辅助单位，则取参考成本
    else {
        resBaseUnitPrice = priceQuantityFormatterOperator(item.cost_price || '0');
    }
    return resBaseUnitPrice;
};
class OrderHandler {
    constructor() {
        /**
         * 提取商品核心字段(包含数组)
         * @param item
         */
        this.getProductKeyAttr = (item) => {
            if (Array.isArray(item)) {
                return item.map((product) => this.getKeyAttr(product));
            }
            return this.getKeyAttr(item);
        };
    }
    /**
     * 更新单据属性
     * @param item 需要更改的item
     * @param key 商品规格的索引值
     * @param value 最新的更改值
     * @param variantsPriceData 更改赠送标记需要重新查询商品价格
     * @param priceType 查询价格类型，配送价或参考成本价
     * @param isPurchaseOrder 是否是采购订单
     * @returns
     */
    updateOrderAttr({ item, key, value, isPurchaseOrder = false, assist_unit_as_use_unit, }) {
        const tmpItem = item;
        let customRate = '0';
        /** 当数量都为0 优先使用上次倍率 */
        if (Number(tmpItem.use_unit_qty) === 0 && Number(tmpItem.base_unit_qty) === 0) {
            if (tmpItem.prevRate) {
                customRate = tmpItem.prevRate;
            }
            else {
                customRate = tmpItem.use_unit_rate ?? '0';
            }
        }
        else {
            // 保存非标品基本数量/常用数量自定义比例
            customRate = priceQuantityFormatterOperator(tmpItem.base_unit_qty, tmpItem.use_unit_qty, 'divide', 8);
        }
        /** 保存这次倍率 */
        tmpItem.prevRate = customRate;
        tmpItem[key] = value;
        // 常用数量
        const tmpItemUseAmount = tmpItem.use_unit_qty || '0';
        // 要货单位单价(保存用)
        const tmpItemUsePrice = tmpItem.use_unit_price || '0';
        // 要货单位和基本单位换算比例
        const tmpItemRate = tmpItem.use_unit_rate || 1;
        /** 是否是非标品 */
        const assistIs = (assist_unit_as_use_unit && tmpItem.product_variant_psi.use_assist_unit_is) ?? false;
        // 更改要货单位
        if (key === 'use_unit') {
            /** 切换单位 清空上次倍率  */
            tmpItem.prevRate = undefined;
            // 更改基本数量
            tmpItem.base_unit_qty = priceQuantityFormatterOperator(tmpItemUseAmount, tmpItemRate, 'mult', 8);
            // 更改要货单位基本单价
            if (isPurchaseOrder) {
                tmpItem.base_unit_price = getPurchaseBaseUnitPrice(tmpItem);
            }
            // 更改要货单位单价(保存用)
            tmpItem.use_unit_price = isPurchaseOrder
                ? getPurchaseUseUnitPrice(tmpItem, '', assist_unit_as_use_unit)
                : priceQuantityFormatterOperator(tmpItem.base_unit_price, tmpItemRate, 'mult', 8);
            // 更改要货单位单价(展示用)
            tmpItem.showPrice = isPurchaseOrder
                ? getPurchaseUseUnitPrice(tmpItem, '', assist_unit_as_use_unit)
                : priceQuantityFormatterOperator(tmpItem.base_unit_price, tmpItemRate, 'mult', 8);
            // 更改金额(保存用)
            tmpItem.detail_money = priceQuantityFormatterOperator(tmpItemUseAmount, tmpItem.use_unit_price, 'mult', 8);
            // 更改金额(展示用)
            tmpItem.showMoney = moneyFormatterOperator(tmpItemUseAmount, tmpItem.use_unit_price, 'mult');
            // 更改对应单位库存数量
            tmpItem.inventory_quantity = priceQuantityFormatterOperator(tmpItem.base_unit_amount, tmpItemRate, 'divide');
            // 更改对应单位可用库存数量
            tmpItem.available_amount = priceQuantityFormatterOperator(tmpItem.available_base_amount, tmpItemRate, 'divide');
            // 更改对应门店库存数量
            tmpItem.current_location_stock = priceQuantityFormatterOperator(tmpItem.current_location_base_stock, tmpItemRate, 'divide', 8);
            // 更改对应配送中心库存数量
            tmpItem.distribution_center_stock = priceQuantityFormatterOperator(tmpItem.distribution_center_base_stock, tmpItemRate, 'divide', 8);
            // 差异数量 = 基本单位库存 - 基本数量
            const profitLossAmount = priceQuantityFormatterOperator(tmpItem.base_unit_qty, tmpItem?.base_unit_amount || 0, 'minus', 8);
            // 更改差异金额 = 差异数量 * 基本单价
            tmpItem.diff_money = moneyFormatterOperator(profitLossAmount, tmpItem.base_unit_price, 'mult');
            // 更改在订量
            tmpItem.pending_order_qty = priceQuantityFormatterOperator(tmpItem.pending_order_base_qty, tmpItemRate, 'divide', 8);
            // 采购金额-不含税
            tmpItem.detail_no_rate_money = moneyFormatterOperator(tmpItem.detail_money, priceQuantityFormatterOperator(1, item.tax_rate, 'plus'), 'divide');
            // 采购单价-不含税
            tmpItem.use_unit_no_rate_price = priceQuantityFormatterOperator(tmpItem.detail_no_rate_money, tmpItem.use_unit_qty, 'divide', 8);
            tmpItem.base_unit_no_rate_price = priceQuantityFormatterOperator(tmpItem.detail_no_rate_money, tmpItem.base_unit_qty, 'divide', 8);
            // 税额
            tmpItem.rate_money = moneyFormatterOperator(tmpItem.detail_money, tmpItem.detail_no_rate_money, 'minus');
            tmpItem.rate_money = moneyFormatterOperator(tmpItem.detail_money, tmpItem.detail_no_rate_money, 'minus');
            tmpItem.out_stock_qty = priceQuantityFormatterOperator(tmpItem.out_stock_base_qty, tmpItemRate, 'divide');
            // 配送单价
            tmpItem.transfer_use_unit_price = priceQuantityFormatterOperator(tmpItem.transfer_base_unit_price, tmpItemRate, 'mult', 8);
            // 配送金额
            tmpItem.transfer_money = moneyFormatterOperator(tmpItemUseAmount, tmpItem.transfer_use_unit_price, 'mult', 8);
            tmpItem.receive_transfer_money = tmpItem.transfer_money;
            tmpItem.receive_purchase_money = tmpItem.detail_money;
            tmpItem.transfer_receive_base_unit_qty = tmpItem.base_unit_qty;
        }
        // 更改赠品单位
        if (key === 'gift_use_unit') {
            tmpItem.gift_use_unit_rate = tmpItem.gift_use_unit_rate ?? '1';
            tmpItem.gift_base_unit_qty = priceQuantityFormatterOperator(tmpItem.gift_use_unit_qty, tmpItem.gift_use_unit_rate, 'mult', 8);
        }
        // 更改赠品数量
        if (key === 'gift_use_unit_qty') {
            tmpItem.gift_base_unit_qty = priceQuantityFormatterOperator(tmpItem.gift_use_unit_qty, tmpItem.gift_use_unit_rate, 'mult', 8);
        }
        // 更改常用数量
        if (key === 'use_unit_qty') {
            if (Number(value) > Number(MAX_DATA_LIMIT)) {
                message.error({
                    content: `${window.t('the_maximum_quantity_cannot_exceed')} ${MAX_DATA_LIMIT}`,
                    key: 'handleUpdateProductAttr',
                });
            }
            // 更改金额(保存用)
            tmpItem.detail_money = priceQuantityFormatterOperator(tmpItemUseAmount, tmpItemUsePrice, 'mult');
            // 更改金额(展示用)
            tmpItem.showMoney = moneyFormatterOperator(tmpItemUseAmount, tmpItemUsePrice, 'mult');
            if (!assistIs) {
                // 更改基本单位数量
                tmpItem.base_unit_qty = priceQuantityFormatterOperator(tmpItemUseAmount, tmpItemRate, 'mult', 8);
            }
            else {
                // 更改基本单位数量  总价/基本单价
                tmpItem.base_unit_qty = priceQuantityFormatterOperator(value, customRate, 'mult', 8);
            }
            // 差异数量 = 基本单位库存 - 基本数量
            const profitLossAmount = priceQuantityFormatterOperator(tmpItem.base_unit_qty, tmpItem?.base_unit_amount || 0, 'minus', 8);
            // 更改差异金额 = 差异数量 * 基本单价
            tmpItem.diff_money = moneyFormatterOperator(profitLossAmount, tmpItem.base_unit_price, 'mult');
            // 采购金额-不含税
            tmpItem.detail_no_rate_money = moneyFormatterOperator(tmpItem.detail_money, priceQuantityFormatterOperator(1, item.tax_rate, 'plus'), 'divide');
            // 采购单价-不含税
            tmpItem.use_unit_no_rate_price = priceQuantityFormatterOperator(tmpItem.detail_no_rate_money, value, 'divide', 8);
            // 税额
            tmpItem.rate_money = moneyFormatterOperator(tmpItem.detail_money, tmpItem.detail_no_rate_money, 'minus');
            // 配送金额
            tmpItem.transfer_money = moneyFormatterOperator(tmpItemUseAmount, tmpItem.transfer_use_unit_price, 'mult', 8);
            tmpItem.receive_transfer_money = tmpItem.transfer_money;
            tmpItem.receive_purchase_money = tmpItem.detail_money;
            tmpItem.transfer_receive_base_unit_qty = tmpItem.base_unit_qty;
            tmpItem.transfer_receive_use_unit_qty = value;
        }
        // 更改常用要货单位单价(展示用)
        if (key === 'showPrice') {
            if (Number(value) > Number(MAX_PRICE_LIMIT)) {
                message.error({
                    content: `${window.t('max_price_cannot_exeed')} ${MAX_PRICE_LIMIT}`,
                    key: 'handleUpdateProductAttr',
                });
            }
            // 保存单价(保存用)
            tmpItem.use_unit_price = priceQuantityFormatter(value);
            // 更改金额(保存用)
            tmpItem.detail_money = priceQuantityFormatterOperator(value, tmpItemUseAmount, 'mult');
            // 更改金额(展示用)
            tmpItem.showMoney = moneyFormatterOperator(value, tmpItemUseAmount, 'mult');
            tmpItem.base_unit_price = priceQuantityFormatterOperator(value, tmpItemRate, 'divide', 8);
            // 采购金额-不含税
            tmpItem.detail_no_rate_money = moneyFormatterOperator(tmpItem.detail_money, priceQuantityFormatterOperator(1, item.tax_rate, 'plus'), 'divide');
            tmpItem.base_unit_no_rate_price = priceQuantityFormatterOperator(tmpItem.detail_no_rate_money, tmpItem.base_unit_qty, 'divide');
            // 采购单价-不含税
            tmpItem.use_unit_no_rate_price = priceQuantityFormatterOperator(tmpItem.detail_no_rate_money, tmpItem.use_unit_qty, 'divide', 8);
            // 税额
            tmpItem.rate_money = moneyFormatterOperator(tmpItem.detail_money, tmpItem.detail_no_rate_money, 'minus');
            tmpItem.receive_purchase_money = tmpItem.detail_money;
        }
        // 更改金额(展示用)
        if (key === 'showMoney') {
            if (Number(value) > Number(MAX_DATA_LIMIT)) {
                message.error({
                    content: `${window.t('max_amounnt_cannot_exeed')} ${MAX_DATA_LIMIT}`,
                    key: 'handleUpdateProductAttr',
                });
            }
            // 更改金额(保存用)
            tmpItem.detail_money = moneyFormatter(value);
            tmpItem.receive_purchase_money = tmpItem.detail_money;
            // 特别注意数量作为除数为0的情况
            if (Number(tmpItem.use_unit_qty)) {
                // 更改单价(展示用)
                tmpItem.showPrice = priceQuantityFormatterOperator(value, tmpItem.use_unit_qty || 1, 'divide', 8);
                // 更改单价(保存用)
                tmpItem.use_unit_price = tmpItem.showPrice;
                if (assistIs) {
                    tmpItem.base_unit_price = priceQuantityFormatterOperator(tmpItem.detail_money, tmpItem.base_unit_qty, 'divide', 8);
                }
                else {
                    // 更改基本单价
                    tmpItem.base_unit_price = priceQuantityFormatterOperator(priceQuantityFormatterOperator(value, tmpItem.use_unit_qty || 1, 'divide', 8), tmpItem.use_unit_rate, 'divide', 8);
                }
                // 采购金额-不含税
                tmpItem.detail_no_rate_money = moneyFormatterOperator(tmpItem.detail_money, priceQuantityFormatterOperator(1, item.tax_rate, 'plus'), 'divide');
                tmpItem.base_unit_no_rate_price = priceQuantityFormatterOperator(tmpItem.detail_no_rate_money, tmpItem.base_unit_qty, 'divide');
                // 采购单价-不含税
                tmpItem.use_unit_no_rate_price = priceQuantityFormatterOperator(tmpItem.detail_no_rate_money, tmpItem.use_unit_qty, 'divide', 8);
                // 税额
                tmpItem.rate_money = moneyFormatterOperator(tmpItem.detail_money, tmpItem.detail_no_rate_money, 'minus');
            }
        }
        // 修改基本数量
        if (key === 'base_unit_qty') {
            if (Number(value) === 0) {
                tmpItem.use_unit_qty = '0';
                tmpItem.showMoney = '0';
                tmpItem.detail_money = '0';
            }
            else {
                if (Number(value) > Number(MAX_DATA_LIMIT)) {
                    message.error({
                        content: `${window.t('the_basic_maximum_quantity_cannot_exceed')} ${MAX_DATA_LIMIT}`,
                        key: 'handleUpdateProductAttr',
                    });
                }
                // 更改金额(保存用)
                tmpItem.detail_money = priceQuantityFormatterOperator(value, tmpItem.base_unit_price, 'mult');
                // 更改金额(展示用)
                tmpItem.showMoney = moneyFormatterOperator(value, tmpItem.base_unit_price, 'mult');
                if (!assistIs) {
                    // 更改要货单位数量
                    tmpItem.use_unit_qty = priceQuantityFormatterOperator(value, tmpItemRate, 'divide', 8);
                }
                else {
                    // 更改单价(展示用)
                    tmpItem.showPrice = priceQuantityFormatterOperator(priceQuantityFormatterOperator(value, tmpItem.base_unit_price, 'mult', 8), tmpItem.use_unit_qty || 1, 'divide', 8);
                    // 更改单价(保存用)
                    tmpItem.use_unit_price = priceQuantityFormatterOperator(priceQuantityFormatterOperator(value, tmpItem.base_unit_price, 'mult', 8), tmpItem.use_unit_qty || 1, 'divide', 8);
                    // 保存非标品基本数量/常用数量自定义比例
                    tmpItem.prevRate = priceQuantityFormatterOperator(value, tmpItem.use_unit_qty, 'divide', 8);
                    tmpItem.use_unit_rate = priceQuantityFormatterOperator(value, tmpItem.use_unit_qty, 'divide', 8);
                }
            }
            // 差异数量 = 基本单位库存 - 基本数量
            const profitLossAmount = priceQuantityFormatterOperator(value, tmpItem?.base_unit_amount || 0, 'minus', 8);
            // 更改差异金额 = 差异数量 * 基本单价
            tmpItem.diff_money = moneyFormatterOperator(profitLossAmount, tmpItem.base_unit_price, 'mult');
            tmpItem.transfer_receive_base_unit_qty = value;
            tmpItem.transfer_receive_use_unit_qty = tmpItem.use_unit_qty;
            tmpItem.receive_purchase_money = tmpItem.detail_money;
        }
        // 更改基本单价
        if (key === 'base_unit_price') {
            if (Number(value) > Number(MAX_PRICE_LIMIT)) {
                message.error({
                    content: `${window.t('max_price_cannot_exeed')} ${MAX_PRICE_LIMIT}`,
                    key: 'handleUpdateProductAttr',
                });
            }
            // 更改金额(展示用)
            tmpItem.showMoney = moneyFormatterOperator(value, tmpItem.base_unit_qty, 'mult');
            // 更改金额(保存用)
            tmpItem.detail_money = priceQuantityFormatterOperator(value, tmpItem.base_unit_qty, 'mult');
            // 保存单价(展示用)
            tmpItem.showPrice = priceQuantityFormatterOperator(priceQuantityFormatterOperator(value, tmpItem.base_unit_qty, 'mult', 8), tmpItemUseAmount, 'divide', 8);
            // 保存单价(保存用)
            tmpItem.use_unit_price = priceQuantityFormatterOperator(priceQuantityFormatterOperator(value, tmpItem.base_unit_qty, 'mult', 8), tmpItemUseAmount, 'divide', 8);
        }
        // 修改现成本价(库存成本调整)
        if (key === 'current_cost_price') {
            // 更改现基本单价
            tmpItem.current_base_price = priceQuantityFormatterOperator(value, tmpItemRate, 'divide', 8);
            // 更改金额差
            tmpItem.stock_cost_diff_money = moneyFormatterOperator(+priceQuantityFormatterOperator(value, tmpItemRate, 'divide', 8) -
                +tmpItem.origin_base_price, tmpItem.base_unit_qty, 'mult');
        }
        // 修改现基本单价(库存成本调整)
        if (key === 'current_base_price') {
            // 更改现成本价
            tmpItem.current_cost_price = priceQuantityFormatterOperator(value, tmpItemRate, 'mult', 8);
            // 更改金额差
            tmpItem.stock_cost_diff_money = moneyFormatterOperator(value - +tmpItem.origin_base_price, tmpItem.base_unit_qty, 'mult', 8);
        }
        // 修改税率
        if (key === 'show_tax_rate') {
            tmpItem.tax_rate = priceQuantityFormatterOperator(value, '100', 'divide');
            const rate = priceQuantityFormatterOperator(1, tmpItem.tax_rate, 'plus');
            // 采购金额-不含税
            tmpItem.detail_no_rate_money = moneyFormatterOperator(tmpItem.detail_money, rate, 'divide');
            tmpItem.rate_money = moneyFormatterOperator(tmpItem.detail_money, tmpItem.detail_no_rate_money, 'minus');
            // 采购单价-不含税
            tmpItem.use_unit_no_rate_price = priceQuantityFormatterOperator(tmpItem.detail_no_rate_money, tmpItem.use_unit_qty, 'divide', 8);
            tmpItem.base_unit_no_rate_price = priceQuantityFormatterOperator(tmpItem.detail_no_rate_money, tmpItem.base_unit_qty, 'divide');
        }
        // 修改配送单价
        if (key === 'transfer_use_unit_price') {
            tmpItem.transfer_base_unit_price = priceQuantityFormatterOperator(value, tmpItem.use_unit_rate, 'divide');
            tmpItem.transfer_money = moneyFormatterOperator(value, tmpItem.use_unit_qty, 'mult', 8);
            tmpItem.receive_transfer_money = tmpItem.transfer_money;
        }
        // 修改配送基本单价
        if (key === 'transfer_base_unit_price') {
            tmpItem.transfer_use_unit_price = priceQuantityFormatterOperator(value, tmpItem.use_unit_rate, 'mult');
            tmpItem.transfer_money = moneyFormatterOperator(value, tmpItem.base_unit_qty, 'mult', 8);
            tmpItem.receive_transfer_money = tmpItem.transfer_money;
        }
        // 修改配送金额
        if (key === 'transfer_money') {
            tmpItem.transfer_use_unit_price = priceQuantityFormatterOperator(value, tmpItem.use_unit_qty, 'divide');
            tmpItem.transfer_base_unit_price = priceQuantityFormatterOperator(value, tmpItem.base_unit_qty, 'divide');
            tmpItem.receive_transfer_money = value;
        }
        // 修改收货数量
        if (key === 'transfer_receive_use_unit_qty') {
            tmpItem.transfer_receive_base_unit_qty = priceQuantityFormatterOperator(value, tmpItem.use_unit_rate, 'mult', 8);
            tmpItem.receive_purchase_money = priceQuantityFormatterOperator(tmpItem.transfer_receive_base_unit_qty, tmpItem.base_unit_price, 'mult', 8);
            tmpItem.receive_transfer_money = priceQuantityFormatterOperator(tmpItem.transfer_receive_base_unit_qty, tmpItem.transfer_base_unit_price, 'mult', 8);
        }
        // 修改收货基本数量
        if (key === 'transfer_receive_base_unit_qty') {
            tmpItem.transfer_receive_use_unit_qty = priceQuantityFormatterOperator(value, tmpItem.use_unit_rate, 'divide', 8);
            tmpItem.receive_purchase_money = priceQuantityFormatterOperator(value, tmpItem.base_unit_price, 'mult', 8);
            tmpItem.receive_transfer_money = priceQuantityFormatterOperator(value, tmpItem.transfer_base_unit_price, 'mult', 8);
        }
        // 修改配送特价
        if (key === 'use_chain_discount_val') {
            tmpItem.chain_discount_val = priceQuantityFormatterOperator(value, tmpItemRate, 'divide', 8);
        }
        // 修改基本单价特价
        if (key === 'chain_discount_val') {
            tmpItem.use_chain_discount_val = priceQuantityFormatterOperator(tmpItem.chain_discount_val, tmpItemRate, 'mult', 8);
        }
        // 修改配送折扣
        if (key === 'use_chain_discount_rate') {
            tmpItem.chain_discount_val = value;
        }
        // 修改起始数量
        if (key === 'use_threshold_qty') {
            tmpItem.threshold_qty = priceQuantityFormatterOperator(value, tmpItemRate, 'mult', 8);
        }
        return tmpItem;
    }
    /**
     * 计算单据总金额以及金额误差方法
     * 注意：此函数会直接修改传入的 orderList 中每个 item 的 detail_round_money 和 detail_money 属性
     * @param orderList - 会被直接修改的订单列表
     * @returns
     */
    calcMoney(orderList, orderType) {
        // 总金额
        let money = '0';
        // 总成本金额
        let cost_money = '0';
        // 金额差值
        let roundMoney = '0';
        // 配送金额
        let transferMoney = '0';
        orderList.forEach((item) => {
            const { base_unit_price, base_unit_qty, base_unit_amount, detail_cost_money, transfer_money, } = item;
            // 明细金额
            let detail_money = '0';
            cost_money = priceQuantityFormatterOperator(cost_money, priceQuantityFormatter(detail_cost_money || '0'), 'plus');
            transferMoney = priceQuantityFormatterOperator(transferMoney, transfer_money, 'plus');
            if (orderType === 'checkOrder') {
                // 盘点单计算规则： 盈亏数量 * 基本单位价格 = (基本数量 - 基本单位库存) * 基本单位价格
                const profitLossAmount = priceQuantityFormatterOperator(base_unit_qty, base_unit_amount || 0, 'minus', 8);
                detail_money = priceQuantityFormatterOperator(base_unit_price, profitLossAmount, 'mult', 8);
                const round = priceQuantityFormatterOperator(detail_money, moneyFormatter(detail_money), 'minus', 8);
                roundMoney = priceQuantityFormatterOperator(roundMoney, round, 'plus', 8);
            }
            else {
                detail_money = priceQuantityFormatterOperator(base_unit_price, base_unit_qty, 'mult', 8);
                // 计算规则： 基本单位数量 * 基本单位价格
                const totalMoney = priceQuantityFormatterOperator(base_unit_price, base_unit_qty, 'mult', 8);
                const round = priceQuantityFormatterOperator(totalMoney, moneyFormatter(totalMoney), 'minus', 8);
                roundMoney = priceQuantityFormatterOperator(roundMoney, round, 'plus', 8);
            }
            money = moneyFormatterOperator(money, priceQuantityFormatter(detail_money), 'plus');
            // 单个物品的金额差值
            item.detail_round_money = priceQuantityFormatterOperator(detail_money, moneyFormatter(detail_money), 'minus', 8);
            item.detail_money = moneyFormatter(detail_money);
        });
        return {
            money,
            roundMoney,
            cost_money,
            transferMoney,
            orderList,
        };
    }
    /**
     * 校验商品列表 数量 * 金额 = 金额
     * @param {IOrderPropsUpdateAttr[]} list
     */
    verifyMoney(list, type) {
        let productName = '';
        if (list.some((item) => {
            if (type === 'checkOrder') ;
            else {
                if (!((Number(moneyFormatterOperator(item.use_unit_price, item.use_unit_qty, 'mult')) ===
                    Number(moneyFormatter(item.detail_money)) &&
                    Number(moneyFormatterOperator(item.base_unit_price, item.base_unit_qty, 'mult')) ===
                        Number(moneyFormatter(item.detail_money))) ||
                    (item.assist_is &&
                        Number(item.use_unit_qty) === 0 &&
                        Number(moneyFormatterOperator(item.base_unit_price, item.base_unit_qty, 'mult')) ===
                            Number(moneyFormatter(item.detail_money))))) {
                    if (item.product_title === item.product_variant_title) {
                        productName = `${item.product_title}`;
                    }
                    else {
                        productName = `${item.product_title} ${item.product_variant_title}`;
                    }
                    return true;
                }
            }
            return false;
        })) {
            message.error(`${productName}${window.t('amount_calculation_error')}`);
            throw new Error(`${productName}金额计算错误`);
        }
    }
    /**
     * 校验单据金额 + 金额误差 是否等于明细金额 + 明细金额误差 之和
     * @param money 单据金额
     * @param roundMoney 单据金额误差
     * @param list 明细列表
     * @param prefix 报错前缀
     */
    verifyMoneyWithRound(money, roundMoney, list, prefix = '') {
        const totalMoneyWithRound = priceQuantityFormatterOperator(money, roundMoney, 'plus', 8);
        const detailTotalMoneyWithRound = list.reduce((pre, cur) => {
            const currentTotalMoney = priceQuantityFormatterOperator(cur.detail_money || '0', cur.detail_round_money || '0', 'plus', 8);
            return priceQuantityFormatterOperator(pre, currentTotalMoney, 'plus', 8);
        }, '0');
        if (Number(totalMoneyWithRound) !== Number(detailTotalMoneyWithRound)) {
            console.log('金额不一致', money, roundMoney, totalMoneyWithRound, detailTotalMoneyWithRound);
            processError(`${prefix}金额校验失败：单据金额${totalMoneyWithRound}与明细金额${detailTotalMoneyWithRound}不一致`, '单据明细金额不一致', {
                money,
                roundMoney,
                totalMoneyWithRound,
                detailTotalMoneyWithRound,
            });
        }
    }
    /**
     * 检验数据是否超出最大值
     * @param list
     */
    verifyDataValidity(list, name = '') {
        let productName = '';
        if (list.some((item) => {
            if (Number(item.use_unit_qty) > Number(MAX_DATA_LIMIT) && !productName) {
                if (item.product_title === item.product_variant_title) {
                    productName = `${name}${item.product_title}`;
                }
                else {
                    productName = `${name}${item.product_title} ${item.product_variant_title}`;
                }
                productName += `${window.t('the_maximum_quantity_cannot_exceed')} ${MAX_DATA_LIMIT}`;
                return true;
            }
            else if (Number(item.showPrice) > Number(MAX_PRICE_LIMIT) && !productName) {
                if (item.product_title === item.product_variant_title) {
                    productName = `${name}${item.product_title}`;
                }
                else {
                    productName = `${name}${item.product_title} ${item.product_variant_title}`;
                }
                productName += `单价最大不可超过 ${MAX_PRICE_LIMIT}`;
                return true;
            }
            else if (Number(item.showMoney) > Number(MAX_MONEY_LIMIT) && !productName) {
                if (item.product_title === item.product_variant_title) {
                    productName = `${name}${item.product_title}`;
                }
                else {
                    productName = `${name}${item.product_title} ${item.product_variant_title}`;
                }
                productName += `金额最大不可超过 ${MAX_MONEY_LIMIT}`;
                return true;
            }
            else if (Number(item.base_unit_qty) > Number(MAX_DATA_LIMIT) && !productName) {
                if (item.product_title === item.product_variant_title) {
                    productName = `${name}${item.product_title}`;
                }
                else {
                    productName = `${name}${item.product_title} ${item.product_variant_title}`;
                }
                productName += `${window.t('the_basic_maximum_quantity_cannot_exceed')} ${MAX_DATA_LIMIT}`;
                return true;
            }
            return false;
        })) {
            message.error(productName);
            throw new Error(`${productName}金额超出最大值`);
        }
    }
    /** 检验数据常用单位是否选择 */
    verifyProductUnit(list, prefix = '') {
        let text = '';
        if (list.some((item) => {
            if (!item.use_unit) {
                if (item.product_title === item.product_variant_title) {
                    text = `${item.product_title}`;
                }
                else {
                    text = `${item.product_title} ${item.product_variant_title || ''}`;
                }
                text += ` 常用单位尚未选择`;
                return true;
            }
            return false;
        })) {
            message.error(`${prefix}${text}`);
            throw new Error(`${prefix}${text}`);
        }
    }
    verifyQty(list, name = '') {
        let productName = '';
        if (list.some((item) => {
            if ((Number(item.use_unit_qty) === 0 || Number(item.base_unit_qty) === 0) && !productName) {
                if (item.product_title === item.product_variant_title) {
                    productName = `${name}${item.product_title}`;
                }
                else {
                    productName = `${name}${item.product_title} ${item.product_variant_title || ''}`;
                }
                return true;
            }
            return false;
        })) {
            message.error(`${productName}${window.t('quantity_must_be_greater_than_0')}`);
            throw new Error(`${productName}${window.t('quantity_must_be_greater_than_0')}`);
        }
    }
    verifyQtyAndGiftQty(list, type) {
        let productName = '';
        if (list.some((item) => {
            if (Number(item.base_unit_qty) !== 0 && Number(item.use_unit_qty) === 0 && !productName) {
                if (item.product_title === item.product_variant_title) {
                    productName = `${item.product_title}`;
                }
                else {
                    productName = `${item.product_title} ${item.product_variant_title}`;
                }
                return true;
            }
            return false;
        })) {
            message.error(`${productName}基本数量不为零时，数量不能为零`);
            throw new Error(`${productName}基本数量不为零时，数量不能为零`);
        }
        if ((type === 'audit' || type === 'saveAudit') &&
            list.some((item) => {
                if ((Number(item.use_unit_qty) === 0 || Number(item.base_unit_qty) === 0) &&
                    (Number(item.gift_use_unit_qty) === 0 || Number(item.gift_base_unit_qty) === 0) &&
                    !productName) {
                    if (item.product_title === item.product_variant_title) {
                        productName = `${item.product_title}`;
                    }
                    else {
                        productName = `${item.product_title} ${item.product_variant_title}`;
                    }
                    return true;
                }
                return false;
            })) {
            message.error(`${productName}${window.t('product_quantity_cannot_be_0')}`);
            throw new Error(`${productName}${window.t('product_quantity_cannot_be_0')}`);
        }
    }
    /** 校验成品原料中是否全部为0 */
    verifyMateriaQty(list, name = '') {
        if (list.every((item) => {
            if (Number(item.use_unit_qty) === 0 || Number(item.base_unit_qty) === 0) {
                return true;
            }
            return false;
        })) {
            message.error(`${name}${window.t('the_quantity_of_raw_materials_cannot_all_be_0')}`);
            throw new Error(`${name}${window.t('the_quantity_of_raw_materials_cannot_all_be_0')}`);
        }
    }
    /**
     * 获取当前商品正在使用的常用单位和基本单位比例值
     * @param item
     * @param compareKey
     * @returns
     */
    getProductUnitRate(item, compareKey = 'transfer_unit') {
        let rate = '1';
        if (item.use_assist_unit_is) {
            rate = item.assist_unit_rate || '1';
        }
        else if (item?.all_unit_list?.length) {
            rate =
                item.all_unit_list.find((each) => each.sub_unit === item[compareKey])?.sub_unit_rate || '1';
        }
        return rate;
    }
    /**
     * 获取当前商品正在使用的常用单位和基本单位比例值
     * @param item
     * @param compareKey
     * @returns
     */
    getProductUnitRateWithAssistIs(item, compareKey = 'transfer_unit', assist_is) {
        let rate = '1';
        if (assist_is) {
            rate = item.assist_unit_rate || '1';
        }
        else if (item?.all_unit_list?.length) {
            rate =
                item.all_unit_list.find((each) => each.sub_unit === item[compareKey])?.sub_unit_rate || '1';
        }
        return rate;
    }
    getProductTransferUnitKey(type) {
        if (type) {
            return type.toLowerCase();
        }
        return 'transfer_unit';
    }
    /** 获取配送单位类型 */
    getProductChainUnitType(type, item) {
        switch (type) {
            case 'TRANSFER_UNIT':
                return item.transfer_unit;
            case 'BASE_UNIT':
                return item.base_unit;
            case 'PURCHASE_UNIT':
                return item.purchase_unit;
            case 'INVENTORY_UNIT':
                return item.inventory_unit;
            case 'CONSUME_UNIT':
                return item.consume_unit;
            default:
                return item.transfer_unit;
        }
    }
    /** 获取配送单位换算率 */
    getProductChainUnitRate(type, item) {
        switch (type) {
            case 'TRANSFER_UNIT':
                return item.transfer_unit_rate || '1';
            case 'BASE_UNIT':
                return '1';
            case 'PURCHASE_UNIT':
                return item.purchase_unit_rate || '1';
            case 'INVENTORY_UNIT':
                return item.inventory_unit_rate || '1';
            case 'CONSUME_UNIT':
                return item.consume_unit_rate || '1';
            default:
                return item.transfer_unit_rate || '1';
        }
    }
    /**
     * 获取当前商品正在使用的常用单位和基本单位比例值
     * @param item
     * @param unit
     * @returns
     */
    getProductRateByUnit(item, unit, assist_unit_as_use_unit) {
        let rate = '1';
        if (item.use_assist_unit_is && assist_unit_as_use_unit) {
            rate = item.assist_unit_rate || '1';
        }
        else if (item?.all_unit_list?.length) {
            rate = item.all_unit_list.find((each) => each.sub_unit === unit)?.sub_unit_rate || '1';
        }
        return rate;
    }
    /** 对重复商品以及赠送商品进行检查 */
    checkRepeatProduct(list, data) {
        if (list.some((item) => item.id === data.id)) {
            message.error(`${window.t('duplicate_product_already_exists')} ${data.product_title}`);
            throw new Error(`已存在重复商品 ${data.product_title}`);
        }
    }
    /**
     * 提取商品核心字段(常用单位，常用数量、常用单价、金额、基本数量、基本单价、备注)
     * @param item
     */
    getKeyAttr(item) {
        const { use_unit, use_unit_price, showPrice, use_unit_qty, detail_money, showMoney, base_unit_price, base_unit_qty, memo, } = item || {};
        return {
            use_unit: use_unit,
            use_unit_price: priceQuantityFormatter(use_unit_price),
            showPrice: priceQuantityFormatter(showPrice),
            use_unit_qty: priceQuantityFormatter(use_unit_qty),
            detail_money: moneyFormatter(detail_money),
            showMoney: moneyFormatter(showMoney),
            base_unit_price: priceQuantityFormatter(base_unit_price),
            base_unit_qty: priceQuantityFormatter(base_unit_qty),
            memo,
        };
    }
    /**
     * 过滤商品被修改的单位字段
     */
    filterProductModifiedUnit(list) {
        return list;
        // const newData = [...list];
        // // 判断当前常用单位字段与现有单位是否可以匹配，主要考虑现有单位被修改或被删除情况，若不匹配常用单位置空
        // // 若单位名称相同，换算比例不同，则需要更新最新的换算比例
        // newData.forEach((item) => {
        //   let isSetBlank = false;
        //   let isMatched = false;
        //   if (item.assist_is) {
        //     isMatched = true;
        //     // 当辅助单位名称不匹配时，清空常用单位
        //     if (item.use_unit !== item?.product_variant_psi?.assist_unit) {
        //       isSetBlank = true;
        //     }
        //   } else {
        //     if (item?.product_variant_psi?.base_unit && item?.product_variant_psi?.base_unit_chain_is) {
        //       if (item.use_unit === item?.product_variant_psi?.base_unit) {
        //         isMatched = true;
        //         if (item.use_unit_rate !== '1') {
        //           isSetBlank = true;
        //         }
        //       }
        //     }
        //     for (let index = 0; index < item.product_variant_psi?.all_unit_list?.length; index++) {
        //       const element = item.product_variant_psi?.all_unit_list?.[index];
        //       if (element.sub_unit && item.use_unit === element.sub_unit) {
        //         isMatched = true;
        //         if (item.use_unit_rate !== element.sub_unit_rate) {
        //           isSetBlank = true;
        //         }
        //         break;
        //       }
        //     }
        //   }
        //   if (isSetBlank || !isMatched) {
        //     item.use_unit = '';
        //     item.use_unit_price = '0';
        //     item.use_unit_qty = '0';
        //     item.use_unit_rate = '';
        //     item.base_unit_qty = '0';
        //     item.detail_money = '0';
        //   }
        // });
        // return newData;
    }
    /**计算单价 */
    computeUseUnitPrice(item, baseUnitPrice, useUnitRate) {
        if (!item.product_variant_psi?.use_assist_unit_is) {
            /**多单位 */
            return priceQuantityFormatterOperator(baseUnitPrice, useUnitRate, 'mult', 8);
        }
        /**辅助单位 */
        if (Number(item.use_unit_qty) > 0) {
            return priceQuantityFormatterOperator(priceQuantityFormatterOperator(item.base_unit_qty, item.use_unit_qty, 'divide', 8), item.base_unit_price, 'mult', 8);
        }
        return '0';
    }
    /**计算实盘金额 */
    computeWinLossMoneyCount(item) {
        const dict = {
            check_win_count: '',
            check_win_money: '',
            check_loss_count: '',
            check_loss_money: '',
            assist_unit_diff_amount: '',
        };
        dict.assist_unit_diff_amount = priceQuantityFormatterOperator(item.assist_unit_qty, item.assist_unit_amount, 'minus', 8);
        if (Number(item.base_unit_qty || 0) - Number(item.base_unit_amount || 0) > 0) {
            dict.check_win_count = priceQuantityFormatterOperator(item.base_unit_qty, item.base_unit_amount, 'minus', 8);
            dict.check_win_money = priceQuantityFormatterOperator(dict.check_win_count, Number(item.use_unit_qty || 0) > 0
                ? item.profit_base_unit_price
                : item.loss_base_unit_price, 'mult', 8);
        }
        else if (Number(item.base_unit_qty || 0) - Number(item.base_unit_amount || 0) < 0) {
            dict.check_loss_count = priceQuantityFormatterOperator(item.base_unit_qty, item.base_unit_amount, 'minus', 8);
            dict.check_loss_money = priceQuantityFormatterOperator(dict.check_loss_count, item.loss_base_unit_price, 'mult', 8);
        }
        return dict;
    }
}
const { updateOrderAttr, calcMoney, verifyMoney, verifyMoneyWithRound, verifyDataValidity, verifyProductUnit, verifyQty, verifyQtyAndGiftQty, getProductUnitRate, getProductUnitRateWithAssistIs, getProductTransferUnitKey, getProductChainUnitType, getProductChainUnitRate, getProductRateByUnit, checkRepeatProduct, verifyMateriaQty, getProductKeyAttr, filterProductModifiedUnit, computeUseUnitPrice, computeWinLossMoneyCount, } = new OrderHandler();

/*
 * @Author: 王茂林
 * @Date: 2022-06-29 14:21:30
 * @LastEditors: nhsoft.zhangxh
 * @LastEditTime: 2024-02-22 20:47:34
 * @Description: file contents
 */
var UNITTYPE;
(function (UNITTYPE) {
    UNITTYPE["MULTIUNIT"] = "MULTIUNIT";
    UNITTYPE["ASSISTUNIT"] = "ASSISTUNIT";
})(UNITTYPE || (UNITTYPE = {}));
const calculateProductMetafieldApply_list = (metaFieldWithGroupList, productMetafieldApplyMap) => {
    /** 处理元字段:如果元字段类型是开关且元字段是必填，那创建商品时未进行勾选开关操作，则要直接传给后台为false */
    const product_metafield_apply_list = [];
    const filterProductMetafieldApplyList = Array.from(productMetafieldApplyMap)
        .filter((apply) => typeof apply[1] === 'boolean')
        .map((item) => item[0]);
    const metafield_list = [];
    metaFieldWithGroupList?.forEach((item) => item?.metafield_list?.forEach((info) => {
        if (info.value_type === 'BOOLEAN' && info.must_is) {
            metafield_list.push(info);
        }
    }));
    // value_type
    if (metafield_list?.length > 0) {
        metafield_list
            .filter((metafie) => metafie.active_is)
            .forEach((info) => {
            if (!filterProductMetafieldApplyList?.includes(info.id)) {
                product_metafield_apply_list.push({
                    metafield_id: info.id,
                    value: 'false',
                });
            }
        });
    }
    const new_product_metafield_apply_list = Array.from(productMetafieldApplyMap)
        .map((ele) => {
        if (typeof ele[1] === 'string' ? ele[1]?.trim() : typeof ele[1] === 'boolean' ? true : ele[1]) {
            return {
                metafield_id: ele[0],
                value: ele[1] || null,
            };
        }
        return null;
    })
        .filter((ele) => ele);
    /** 过滤去重 */
    const list = formatArray([...new_product_metafield_apply_list, ...product_metafield_apply_list], 'metafield_id');
    return list;
};
var WEIGHTUNITEN;
(function (WEIGHTUNITEN) {
    WEIGHTUNITEN["Kg"] = "\u5343\u514B";
    WEIGHTUNITEN["G"] = "\u514B";
})(WEIGHTUNITEN || (WEIGHTUNITEN = {}));
var WEIGHTUNITZN;
(function (WEIGHTUNITZN) {
    WEIGHTUNITZN["\u5343\u514B"] = "Kg";
    WEIGHTUNITZN["\u514B"] = "G";
})(WEIGHTUNITZN || (WEIGHTUNITZN = {}));
/** 组装多规格option */
const assembleProductOption = (data) => {
    const arr = ['大小', '颜色', '材料', '样式', '标题'];
    const newArr = [];
    data.forEach((_, index) => {
        if (index === 0) {
            newArr.push(['大小', '颜色', '材料', '样式', '标题']);
        }
        else {
            newArr.push(arr.slice(index));
        }
    });
    return newArr;
};

/**
 * @description: 正则检查手机号码或者电子邮件
 * @param {any} value
 * @return {boolean}
 */
const isPhoneOrEmail = (value) => /^1\d{10}$/.test(value) ||
    /^[A-Za-z0-9]+([_.][A-Za-z0-9]+)*@([A-Za-z0-9-]+\.)+[A-Za-z]{2,6}$/.test(value);
const NEW_EMAIL_REG = /^[A-Za-z0-9._+!#$%&'*/=?^_{|}-]+@[A-Za-z0-9_+!#$%&'*/=?^_{|}-]+(\.[A-Za-z0-9_+!#$%&'*/=?^_{|}-]+)+$/;
const isNewPhoneOrEmail = (value) => {
    const v = String(value ?? '').trim();
    return /^\d{10,15}$/.test(v) || (!!v && !v.includes('..') && NEW_EMAIL_REG.test(v));
};
const getPageQuery = () => parse(window.location.href.split('?')[1]);
const isFunction = (fc) => {
    return typeof fc === 'function';
};
// 记录日志
const saveSystemLog = (saveQuery) => {
    try {
        const location_zone_id = window.getCookie('location_zone_id');
        const location_id = window.getCookie('location_id');
        const operator = window.getCookie('use_staff_name') || window.getCookie('user_operator') || '';
        if (!operator) {
            processError('operator异常', 'saveSystemLog异常', { cookie: document.cookie }, 'log');
        }
        const operatorId = window.getCookie('operator_id');
        if (!operatorId) {
            console.log('logoutAndRedirect: saveSystemLog');
            // 如果没有operatorId，则退出登录
            window.logoutAndRedirect();
            return;
        }
        window.marsClient.systemLog.saveSystemLog({
            ...saveQuery,
            operator,
            operator_id: operatorId,
            location_id: location_zone_id ? undefined : location_id,
            location_zone_id: location_zone_id,
        });
    }
    catch (e) {
        processError(JSON.stringify(e), 'saveSystemLog异常', { cookie: document.cookie });
    }
};
/**
 * 匹配url中的payOrderNo信息
 * @param url url
 * @return {string}
 */
function parsePayOrderNoFromURL(url) {
    const regex = /payOrderNo=([^&]*)/;
    const match = url.match(regex);
    if (match && match[1]) {
        return match[1];
    }
    return null;
}
// 判断营销活动是否可编辑 具体逻辑看: Mars-8769
const canEditMarketing = (create_location_id, create_location_zone_id) => {
    const scopeType = getScopeType();
    const currentLocationId = window.getCookie('location_id');
    const currentLocationZoneId = window.getCookie('location_zone_id');
    let flag = false;
    if (scopeType === 'HEADQUARTERS') {
        // 总部
        if (!create_location_id && !create_location_zone_id) {
            flag = true;
        }
        else if (create_location_zone_id === currentLocationZoneId) {
            flag = true;
        }
    }
    else if (scopeType === 'ZONE') {
        // 区域
        if (create_location_zone_id === currentLocationZoneId) {
            flag = true;
        }
    }
    else {
        // 普通门店
        if (create_location_id === currentLocationId) {
            flag = true;
        }
    }
    return flag;
};
//返回查询字符串
const returnText = (record, text) => {
    const value = record.split(text) || [];
    const valueNew = JSON.parse(JSON.stringify(value));
    value.forEach((_, index) => {
        if (index !== value.length - 1) {
            /**插入 */
            valueNew.splice(index + 1, 0, text);
        }
    });
    return valueNew;
};
const isCloudUrl = (src) => {
    /** 华为云 */
    if (src.includes('myhuaweicloud.com')) {
        return 'Hw';
    }
    /** 腾讯云 */
    if (src.includes('myqcloud.com')) {
        return 'Tc';
    }
    /** 阿里云 */
    if (src.includes('aliyuncs.com')) {
        return 'Ali';
    }
    return 'Ali';
};
/**
 * 自动转换长数字为字符串
 * @param jsonString
 * @returns
 */
const autoConvertLongNumberToStringJsonParse = (jsonString) => {
    // 检查输入是否为空或无效
    if (!jsonString || typeof jsonString !== 'string' || jsonString.trim() === '') {
        return null;
    }
    try {
        // 使用正则匹配所有数值，并给大数值加上引号
        const fixedJsonString = jsonString.replace(/([,:{\[])\s*(-?\d+\.?\d*)(?=[,\]\}])/g, (match, prefix, number) => {
            // 判断是否为需要转换为字符串的数值
            if (number.length > 15 || !Number.isSafeInteger(Number(number))) {
                return `${prefix} "${number}"`;
            }
            return match;
        });
        return JSON.parse(fixedJsonString);
    }
    catch (error) {
        console.error('JSON 解析失败:', error, '原始字符串:', jsonString);
        return null;
    }
};
const getChinese = (str) => {
    const regex = /[^\u4e00-\u9fa5]+/g;
    const filteredStr = str.replace(regex, '');
    return filteredStr;
};
const makePosPin = () => {
    const arr = [1, 2, 3, 4, 5, 6];
    return arr.reduce((sum) => {
        return sum + Math.floor(Math.random() * 10).toString();
    }, '');
};
function bignumber(value, dp = BigNumber.ROUND_CEIL) {
    return new BigNumber(Number(value ?? 0)).dp(dp).toNumber();
}
function toFixedNumber(value, dp = BigNumber.ROUND_CEIL) {
    return new BigNumber(Number(value ?? 0)).toFixed(dp);
}
/**
 * 数组转树结构
 * @param {Array} arr 需要转树结构的数组
 * @param {Object} fieldsNames 构成树结构的相关字段配置
 *
 * @returns Array
 */
function arrayToTree({ arr = [], fieldsNames, }) {
    const fields = { id: 'id', pid: 'pid', children: 'children', ...fieldsNames };
    if (!arr || Object.prototype.toString.call(arr) !== '[object Array]' || !arr.length) {
        return [];
    }
    const result = [];
    const arrMap = {};
    for (const item of arr) {
        const id = item[fields.id];
        const pid = item[fields.pid];
        const childrenFields = fields.children;
        if (!arrMap[id]) {
            arrMap[id] = {};
        }
        arrMap[id] = { ...item, ...arrMap[id] };
        arrMap[id][childrenFields] = arrMap[id][childrenFields] || undefined;
        const treeItem = arrMap[id];
        if (!pid) {
            result.push(treeItem);
        }
        else {
            if (!arrMap[pid]) {
                arrMap[pid] = {};
            }
            arrMap[pid][childrenFields] = arrMap[pid][childrenFields] || [];
            arrMap[pid][childrenFields].push(treeItem);
        }
    }
    return result;
}
const getSize = (size) => {
    if (size < 1024) {
        return `${size}B`;
    }
    else if (size < 1024 * 1024) {
        return `${(size / 1024).toFixed(2)}KB`;
    }
    else {
        return `${(size / 1024 / 1024).toFixed(2)}MB`;
    }
};
const getRealWindow = () => {
    // 在 qiankun 沙箱环境中，window 可能是 Proxy
    // 需要获取真实的 window 对象
    if (window.rawWindow) {
        return window.rawWindow;
    }
    // 尝试通过 Function 获取真实 window
    return new Function('return this')();
};

/*
 * @Author: nhsoft.zhangxh
 * @Date: 2024-12-11 09:21:33
 * @LastEditors: nhsoft.zhangxh
 * @LastEditTime: 2024-12-11 09:21:35
 * @Description: file content
 */
/*
 * @Author: mikey.sweet
 * @Date: 2021-03-03 14:02:53
 * @Last Modified by: mikey.sweet
 * @Last Modified time: 2021-03-03 14:03:21
 */
// 一些正则表达式校验
const moneyReg = /^(([0-9]{0,8})?|(([0]\.\d{0,2}|[1-9][0-9]*\.\d{0,2})))$/; // 金额校验
const intReg = /^-?[1-9][0-9]*$/; // 整数校验
const numberReg = /^-?[1-9][0-9]?.?[0-9]*$/; // number校验
const numberStrReg = /^[0-9]+$/; // 只能输入0-9校验
const phoneReg = /^(13[0-9]|14[01456879]|15[0-35-9]|16[2567]|17[0-8]|18[0-9]|19[0-35-9])\d{8}$/; // 手机号码校验
const merchantPhoneReg = /^[^a-zA-Z\u4e00-\u9fa5]+$/;
const emailReg = /^\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/; // 电子邮件校验
var ValidateTypes;
(function (ValidateTypes) {
    ValidateTypes["equalsExp"] = "equalsExp";
    ValidateTypes["eqExp"] = "eqExp";
    ValidateTypes["numberExp"] = "numberExp";
    ValidateTypes["intExp"] = "intExp";
    ValidateTypes["phoneExp"] = "phoneExp";
    ValidateTypes["emailExp"] = "emailExp";
    ValidateTypes["regexp"] = "regexp";
    ValidateTypes["merchantPhoneExp"] = "merchantPhoneExp";
})(ValidateTypes || (ValidateTypes = {}));
const validateTypes = {
    equalsExp: ValidateTypes.equalsExp,
    eqExp: ValidateTypes.eqExp,
    numberExp: ValidateTypes.numberExp,
    intExp: ValidateTypes.intExp,
    phoneExp: ValidateTypes.phoneExp,
    emailExp: ValidateTypes.emailExp,
    regexp: ValidateTypes.regexp,
    merchantPhoneExp: ValidateTypes.merchantPhoneExp,
};
const { equalsExp, eqExp, numberExp, intExp, phoneExp, emailExp, regexp, merchantPhoneExp } = validateTypes;
const isMoney = (value) => moneyReg.test(value);
const isNumberStr = (value) => numberStrReg.test(value);
const isInt = (value) => intReg.test(value);
const validate = (data, rules) => {
    const errors = new Map();
    rules.forEach((rule) => {
        const res = { isOk: true, errorMsg: '' };
        const value = data[rule.fieldName];
        // 如果值不存在
        if (value === null || `${value}`?.trim() === '' || value === undefined) {
            // 如果是必填项就返回错误提示
            if (rule.isRquired) {
                res.isOk = false;
                res.errorMsg = rule.requiredMsg || rule.errorMsg || '';
            }
        }
        else {
            if (Array.isArray(rule.type)) {
                res.isOk = false;
                rule.type.forEach((item, index) => {
                    switch (item) {
                        case equalsExp:
                        case eqExp:
                            if (value === rule.eqValue) {
                                res.isOk = true;
                            }
                            break;
                        case numberExp:
                            if (numberReg.test(value)) {
                                res.isOk = true;
                            }
                            break;
                        case intExp:
                            if (intReg.test(value)) {
                                res.isOk = true;
                            }
                            break;
                        case phoneExp:
                            if (phoneReg.test(value)) {
                                res.isOk = true;
                            }
                            break;
                        case emailExp:
                            if (emailReg.test(value)) {
                                res.isOk = true;
                            }
                            break;
                        case regexp: // 自定义正则表达式
                            if (rule.regexp && Array.isArray(rule.regexp)
                                ? rule.regexp[index].test(value)
                                : rule.regexp?.test(value)) {
                                res.isOk = true;
                            }
                            break;
                        case merchantPhoneExp:
                            if (merchantPhoneReg.test(value)) {
                                res.isOk = true;
                            }
                            break;
                    }
                });
            }
            else {
                switch (rule.type) {
                    case equalsExp:
                    case eqExp:
                        if (value !== rule.eqValue) {
                            res.isOk = false;
                        }
                        break;
                    case numberExp:
                        if (!numberReg.test(value)) {
                            res.isOk = false;
                        }
                        break;
                    case intExp:
                        if (!intReg.test(value)) {
                            res.isOk = false;
                        }
                        break;
                    case phoneExp:
                        if (!phoneReg.test(value)) {
                            res.isOk = false;
                        }
                        break;
                    case emailExp:
                        if (!emailReg.test(value)) {
                            res.isOk = false;
                        }
                        break;
                    case regexp: // 自定义正则表达式
                        if (rule.regexp && Array.isArray(rule.regexp)
                            ? !rule.regexp[0].test(value)
                            : !rule.regexp?.test(value)) {
                            res.isOk = false;
                        }
                        break;
                    case merchantPhoneExp:
                        if (!merchantPhoneReg.test(value)) {
                            res.isOk = false;
                        }
                        break;
                }
            }
            res.errorMsg = rule.errorMsg || '';
        }
        if (!res.isOk) {
            errors.set(rule.fieldName, res);
        }
    });
    return errors;
};
const isPhone = (value) => phoneReg.test(value);
const isEmail = (value) => emailReg.test(value);
const isMerchantPhone = (value) => merchantPhoneReg.test(value);
// 校验表情包
const includeSpecial = (value) => {
    // eslint-disable-next-line
    const regStr = /[\uD83C|\uD83D|\uD83E][\uDC00-\uDFFF][\u200D|\uFE0F]|[\uD83C|\uD83D|\uD83E][\uDC00-\uDFFF]|[0-9|*|#]\uFE0F\u20E3|[0-9|#]\u20E3|[\u203C-\u3299]\uFE0F\u200D|[\u203C-\u3299]\uFE0F|[\u2122-\u2B55]|\u303D|[\A9|\AE]\u3030|\uA9|\uAE|\u3030/gi;
    return regStr.test(value);
};
// 特殊字符
const inSpecialCharacters = (value) => {
    // eslint-disable-next-line
    const regStr = /[`~!#$%^&*()_\+=<>?:"{}|~！#￥%……&*（）={}|《》？：“”【】、；‘’，。、\s+]/g;
    return regStr.test(value);
};
const maskSensitiveInformation = (input) => {
    // 判断输入是否为手机号
    const isPhoneNumber = /^\d{11}$/.test(input);
    // 判断输入是否为空
    if (input.length === 0) {
        return '';
    }
    // 脱敏逻辑
    let maskedString;
    if (isPhoneNumber) {
        // 脱敏手机号
        maskedString = input.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2');
    }
    else {
        // 脱敏名字（仅保留第一个字符）
        maskedString = input[0] + '*'.repeat(input.length - 1);
    }
    return maskedString;
};

/**
 * 通用中英文数字混排比较函数
 * 规则：数字 < 英文 < 中文（中文按拼音）
 * 空值排最后
 */
const mixedStringComparator = (valueA, valueB) => {
    const isEmpty = (val) => val === null || val === undefined || val === '';
    // 空值排最后
    if (isEmpty(valueA) && isEmpty(valueB))
        return 0;
    if (isEmpty(valueA))
        return 1; // A空 => 排后
    if (isEmpty(valueB))
        return -1; // B空 => 排后
    const getCategory = (str) => {
        if (!str)
            return 0;
        const ch = str[0];
        if (/\d/.test(ch))
            return 1; // 数字
        if (/[a-zA-Z]/.test(ch))
            return 2; // 英文
        if (/[\u4e00-\u9fa5]/.test(ch))
            return 3; // 中文
        return 0; // 其他
    };
    const typeA = getCategory(valueA);
    const typeB = getCategory(valueB);
    // 不同类型：数字 < 英文 < 中文
    if (typeA !== typeB)
        return typeA - typeB;
    // 同类型内部比较
    return valueA.localeCompare(valueB, 'zh', { sensitivity: 'base' });
};
/** 表格排序 */
const tableSort = (valueA, valueB, type) => {
    const isEmpty = (value) => value === null || value === undefined || value === '';
    if (type === 'money') {
        const moneyA = isEmpty(valueA) ? 0 : valueA;
        const moneyB = isEmpty(valueB) ? 0 : valueB;
        if (moneyA === moneyB)
            return 0;
        const numberMoneyA = +moneyA;
        const numberMoneyB = +moneyB;
        if (!isNaN(numberMoneyA) && !isNaN(numberMoneyB) && numberMoneyA === numberMoneyB) {
            return 0;
        }
        return isNaN(numberMoneyA) || isNaN(numberMoneyB)
            ? moneyA.toString().localeCompare(moneyB.toString())
            : numberMoneyA > numberMoneyB
                ? 1
                : -1;
    }
    if (valueA === valueB)
        return 0;
    if (isEmpty(valueA)) {
        return -1;
    }
    if (isEmpty(valueB)) {
        return 1;
    }
    if (type === 'date') {
        return moment(valueA).isAfter(moment(valueB)) ? 1 : -1;
    }
    else if (type === 'string') {
        return valueA?.toString()?.localeCompare(valueB?.toString(), 'zh-CN');
    }
    return isNaN(+valueA) || isNaN(+valueB)
        ? valueA.toString().localeCompare(valueB.toString())
        : +(valueA ?? 0) > +(valueB ?? 0)
            ? 1
            : -1;
};
/** 表格排序 */
const sort = (valueA, valueB, type) => {
    if (!valueA) {
        return -1;
    }
    if (!valueB) {
        return 1;
    }
    if (valueA === valueB)
        return 0;
    if (type === 'date') {
        // 日期比较
        return moment(valueA).isAfter(moment(valueB)) ? 1 : -1;
    }
    else {
        // 数字比较
        if (isNaN(+valueA) || isNaN(+valueB)) {
            // 字符串比较
            return valueA.localeCompare(valueB, 'zh-CN');
        }
        else {
            // 数字比较
            return +(valueA ?? 0) > +(valueB ?? 0) ? 1 : -1;
        }
    }
};
/**
 * 新增表格数据
 * @param table 原表格
 * @param newItem 新增项
 * @param pageSize 分页数量
 * @returns
 */
const tableAdd = (table, newItem, pageSize) => {
    const tmpTable = [...table];
    if (table.length >= pageSize) {
        tmpTable.pop();
    }
    tmpTable.unshift(newItem);
    return tmpTable;
};
/**
 * 更新表格数据
 * @param table 原表格
 * @param newItem 更改项目
 * @param id 更改id
 * @returns
 */
const tableUpdate = (table, newItem, id, customIdItem = 'id') => {
    const tmpTable = [...table];
    for (let i = 0; i < tmpTable.length; i++) {
        if (table[i][customIdItem] === id) {
            tmpTable[i] = newItem;
            break;
        }
    }
    return tmpTable;
};
/**
 * 删除表格数据
 * @param table 原表格
 * @param id 更改id
 * @param page 当前页
 * @returns
 */
const tableDelete = (table, id, page = 1) => {
    const tmpTable = [...table];
    const findIndex = tmpTable.findIndex((item) => item.id === id);
    if (findIndex > -1) {
        tmpTable.splice(findIndex, 1);
    }
    let newPage = page;
    if (tmpTable.length === 0 && page > 1) {
        newPage = page - 1;
    }
    return {
        dataSource: tmpTable,
        resultPage: newPage,
    };
};
/**
 * 动态计算表格最大高度
 * @param 与计算有关的自定义参数
 * @returns 默认获取高度为：页面标题 + 筛选器（无记录标签）+ 表格 + 分页条 = 屏幕高度 - 表格内容顶部的高度 - 表格内容底部的高度
 */
function getTableMaxHeightForHandle(params) {
    let defaultOffset = params?.isPageEmpty ? 74 : 60;
    const { id, extraHeight, isAntdTable, hasPageRow = true } = params || {};
    if (!hasPageRow) {
        defaultOffset = 20;
    }
    const tHeader = document.getElementById(id);
    // 表格内容距离顶部的距离
    let tHeaderTop = 0;
    if (tHeader) {
        if (isAntdTable) {
            if (id) {
                tHeaderTop = tHeader
                    ?.getElementsByClassName('ant-table-thead')[0]
                    ?.getBoundingClientRect().bottom;
            }
            else {
                tHeaderTop = document
                    .getElementsByClassName('ant-table-thead')[0]
                    ?.getBoundingClientRect().bottom;
            }
        }
        else {
            tHeaderTop = tHeader.getBoundingClientRect().top;
        }
    }
    if (params?.hasSummaryRow) {
        // 加上总结栏的34px
        defaultOffset = 124;
    }
    // 屏幕高度 - 表格内容顶部的高度 - 表格内容底部的高度
    const height = `calc(100vh - ${tHeaderTop + (extraHeight || defaultOffset)}px)`;
    return height;
}

/*
 * @Author: CLKimi
 * @Date: 2021-02-25 17:33:23
 * @LastEditors: rxw
 * @LastEditTime: 2025-10-13 16:33:47
 * @Description: file content
 */
const timeFormat = (time, type) => {
    const format = type || 'YYYY-MM-DD HH:mm:ss';
    if (format === 'YYYY-MM-DD HH:mm:ss') {
        return time ? time.substr(0, 19).replace('T', ' ') : '';
    }
    if (format === 'YYYY-MM-DD HH:mm') {
        return time ? time.substr(0, 16).replace('T', ' ') : '';
    }
    if (format === 'YYYY/MM/DD HH:mm:ss') {
        return time ? time.substr(0, 19).replace('T', ' ').replace('-', '/').replace('-', '/') : '';
    }
    if (format === 'MMDD HH:mm') {
        return time ? time.substr(5, 11).replace('T', '日 ').replace('-', '月') : '';
    }
    if (format === 'MM-DD HH:mm') {
        return time ? time.substr(5, 11).replace('T', ' ') : '';
    }
    if (format === 'YYYYMMDD') {
        return time ? time.substr(0, 11).replace('T', '日 ').replace('-', '年').replace('-', '月') : '';
    }
    if (format === 'YYYY-MM-DD') {
        return time ? time.substr(0, 10) : '';
    }
    if (format === 'YYYY/MM/DD') {
        return time ? time.substr(0, 10).replace('T', ' ').replace('-', '/').replace('-', '/') : '';
    }
    if (format === 'YYYY.MM.DD') {
        return time ? time.substr(0, 10).replace('-', '.').replace('-', '.') : '';
    }
    if (format === 'HH:mm:ss') {
        return time ? time.substr(12, 19) : '';
    }
    return '';
};
/**
 * 判断多个时间是否有交集
 * [[开始时间，结束时间], [开始时间，结束时间]]
 * [
 *  [2002-08-08 11:11:11, 2002-08-08 12:12:12],
 *  [2002-08-08 11:11:11, 2002-08-08 12:12:12]
 * ]
 * @param data
 * @returns
 *
 */
function isDateIntersection(data) {
    const tempList = [];
    let isOverLap = false;
    const isValid = data.every((item, index) => {
        // 数据无效直接中断退出
        if (!item[0] || !item[1]) {
            return false;
        }
        // 转换为时间戳
        const startTimeStamp = moment.isDate(item[0]) ? item[0].getTime() : new Date(item[0]).getTime();
        const endTimeStamp = moment.isDate(item[1]) ? item[1].getTime() : new Date(item[1]).getTime();
        if (startTimeStamp >= endTimeStamp) {
            return false;
        }
        // 将时段数据处理后存入数组
        tempList.push([
            {
                flag: index,
                value: startTimeStamp,
            },
            {
                flag: index,
                value: endTimeStamp,
            },
        ]);
        return true;
    });
    // 对数组进行扁平处理后 从小到大排序
    const timeFiledList = [].concat(...tempList);
    if (isValid) {
        timeFiledList.sort((a, b) => a.value - b.value);
        // 是否重叠标志
        isOverLap = false;
        for (let i = 0; i < timeFiledList.length; i += 2) {
            if (i > 0) {
                // 存在相同值时(排序中三个连续值) => 时间重叠
                if (timeFiledList[i].value == timeFiledList[i - 1].value ||
                    timeFiledList[i - 1].value == timeFiledList[i - 2].value) {
                    isOverLap = true;
                    break;
                }
            }
            // 相邻两个标记不同，则为重叠
            if (timeFiledList[i].flag != timeFiledList[i + 1].flag) {
                isOverLap = true;
                break;
            }
        }
    }
    return { isOverLap, isValid };
}

/*
 * @Author: 彭鑫诚
 * @Date: 2025-02-13 15:02:15
 * @LastEditors: 王仁涛 wangrt@cnlemon.net
 * @LastEditTime: 2026-04-09 20:08:35
 * @Description: 单据公用方法
 */
const getOrderPermission = (pathname = location.pathname) => {
    if (pathname.includes('pickUpDistributionOrder')) {
        return IPermissonCode.PERMISSION_ORDER_SELF_AND_DELIVER;
    }
    if (pathname.includes('sitDownOrder')) {
        return IPermissonCode.PERMISSION_DINE_IN_ORDER;
    }
    if (pathname.includes('thirdOrder')) {
        return IPermissonCode.PERMISSION_ORDER_THIRD_PLATFORM;
    }
    if (pathname.includes('productAppointmentOrder')) {
        return IPermissonCode.PERMISSION_ORDER_PRODUCT_RESERVE;
    }
    if (pathname.includes('productMallOrder')) {
        return IPermissonCode.PERMISSION_ORDER_SHOP;
    }
    if (pathname.includes('scanOrder')) {
        return IPermissonCode.PERMISSION_ORDER_PRODUCT_SCAN_CODE;
    }
    if (pathname.includes('cardorders')) {
        return IPermissonCode.PERMISSION_CARD_ORDER;
    }
    return IPermissonCode.PERMISSION_DINE_IN_ORDER;
};
/** 获取成套件物品明细 */
const getProductKitItem = async (productList) => {
    const kitProduct = productList.filter((item) => item.product_variant_psi.kit_is);
    if (kitProduct?.length) {
        const kitResult = await getProductByKitsId(kitProduct.map((item) => item.product_variant_psi?.product_variant_id) || []);
        if (kitResult?.isSuccess()) {
            const { data } = kitResult;
            const kitResultMap = data.reduce((pre, cur) => {
                if (!pre[cur.kit_item_id]) {
                    pre[cur.kit_item_id] = [cur];
                }
                else {
                    pre[cur.kit_item_id] = [...pre[cur.kit_item_id], cur];
                }
                return pre;
            }, {});
            kitProduct.forEach((item) => {
                item.kit_item_relation_list = kitResultMap[item.product_variant_psi?.product_variant_id];
            });
        }
    }
    return productList;
};
const scrollToFirstError = (formId) => {
    setTimeout(() => {
        if (formId) {
            const currentDomById = document.getElementById(formId);
            if (currentDomById?.querySelector('.ant-form-item-has-error')) {
                currentDomById
                    ?.querySelector('.ant-form-item-has-error')
                    ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            else {
                document
                    .querySelector('.ant-form-item-has-error')
                    ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
        else {
            document
                .querySelector('.ant-form-item-has-error')
                ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, 0);
};
const weedOutQuery = (FilterQuery, isSave) => {
    const query = {};
    Object.keys(FilterQuery)
        .filter((key) => FilterQuery[key])
        .forEach((item) => {
        if (item === 'active') {
            query[item] = FilterQuery[item] === window.t('enable');
        }
        else if (item === 'deliveryMethod') {
            query[FilterQuery[item]] = true;
        }
        else {
            let trueValue;
            if (!isSave) {
                if (typeof FilterQuery[item] === 'object') {
                    const str = FilterQuery[item]?.reduce((i, u) => `${i},${u}`, '');
                    trueValue = str.substr(1);
                }
                else {
                    trueValue = FilterQuery[item];
                }
            }
            else {
                trueValue = FilterQuery[item];
            }
            query[item] = trueValue;
        }
    });
    return query;
};
/**
 * 业务类型配合此方法使用，将参数正确分配到不同字段上
 * @param list
 */
const splitBusinessScenarioParam = (list, method = 'GET') => {
    // const fulfillment_type_list: string[] = [];
    const dine_type_list = [];
    // list.forEach((item) => {
    //   if (['DINING_IN', 'DINING_OUT'].includes(item)) {
    //     dine_type_list.push(item);
    //   } else {
    //     fulfillment_type_list.push(item);
    //   }
    // });
    list.forEach((item) => {
        dine_type_list.push(item);
    });
    return {
        // fulfillment_type_list:
        //   method === 'GET' ? fulfillment_type_list.join(',') : fulfillment_type_list,
        dine_type_list: method === 'GET' ? dine_type_list.join(',') : dine_type_list,
    };
};

const commonOrderJump = (orderId, jumpParams) => {
    const orderListTypeRouteTypeMap = {
        FULFILLMENT_DELIVERY: 'pickUpDistributionOrder',
        FULFILLMENT_DINNER: 'sitDownOrder',
        FUNCTION_TYPE_RESERVE: 'productAppointmentOrder',
        FUNCTION_TYPE_SCAN: 'scanOrder',
        FUNCTION_TYPE_SHOP: 'productMallOrder',
        SOURCE_THIRD: 'thirdOrder',
    };
    if (orderId) {
        getOrderJumpType(orderId).then((res) => {
            if (res.isSuccess() && res.data) {
                const orderListType = res.data.order_list_type;
                const routerType = orderListTypeRouteTypeMap[orderListType];
                if (routerType) {
                    // 跳转订单前校验是否有关联订单的权限
                    const orderPermission = getOrderPermission(routerType);
                    const check = hasOpteratePermission(orderPermission, IPermissonCodeKey.QUERY);
                    if (!check) {
                        message.error('账号无该订单查看权限', 2);
                        return;
                    }
                    window.marsHistory.push(`/main-operations/${routerType}/${orderId}`, {
                        order_id: orderId,
                        routerType,
                        ...(jumpParams ?? {}),
                    });
                }
            }
        });
    }
};

// 递归过滤树节点，找出匹配搜索条件的子节点
const getFilteredChildren = (nodes, searchText) => {
    const result = [];
    const traverse = (items) => {
        items?.forEach((item) => {
            if (item.name.toLowerCase().includes(searchText.toLowerCase())) {
                // 如果是叶子节点或没有子节点，添加到结果中
                if (!item.children || item.children.length === 0) {
                    result.push(item.id);
                }
            }
            // 继续遍历子节点
            if (item.children) {
                traverse(item.children);
            }
        });
    };
    traverse(nodes);
    return result;
};
// 找到对应的树节点
const findNodeInTree = (nodes, id) => {
    for (const node of nodes) {
        if (node.id === id)
            return node;
        if (node.children) {
            const found = findNodeInTree(node.children, id);
            if (found)
                return found;
        }
    }
    return null;
};
/**
 * 搜索模式下的树父节点全选
 * @param val 当前选中的值
 * @param tree 树数据
 * @param searchValue 搜索值
 * @param extra antd的treeSelect的onChange的extra参数
 * @returns 最终选中的值
 */
const treeParentSelectOnSearchMode = ({ val, tree, searchValue, extra, }) => {
    let finalVal = val;
    if (searchValue && extra?.triggerNode?.props?.children && extra.checked && extra.triggerValue) {
        const clickedValue = extra.triggerValue;
        const clickedTreeNode = findNodeInTree(tree, clickedValue);
        if (clickedTreeNode && clickedTreeNode.children) {
            // 获取该父节点下匹配搜索条件的子节点
            const filteredChildren = getFilteredChildren([clickedTreeNode], searchValue);
            // 从 val 中移除父节点，添加过滤后的子节点
            finalVal = [...val.filter((v) => v !== clickedValue), ...filteredChildren];
        }
    }
    return finalVal;
};
/**
 * 搜索模式下的树父节点全选
 * @param val 当前选中的值
 * @param tree 树数据
 * @param searchValue 搜索值
 * @param extra antd的treeSelect的onChange的extra参数
 * @returns 最终选中的值
 */
const treeParentSelectOnSearchModeNew = ({ val, tree, searchValue, }) => {
    const finalVal = [];
    const processedIds = new Set(); // 用于去重
    // 递归获取所有匹配搜索条件的叶子节点
    const getMatchingLeafNodes = (nodes) => {
        if (!nodes || nodes.length === 0)
            return [];
        const result = [];
        for (const node of nodes) {
            if (node.isLeaf || !node.children || node.children.length === 0) {
                // 叶子节点：如果有搜索条件，检查是否匹配；否则直接添加
                if (!searchValue || node.name.toLowerCase().includes(searchValue.toLowerCase())) {
                    result.push(node.id);
                }
            }
            else {
                // 非叶子节点：递归处理子节点
                result.push(...getMatchingLeafNodes(node.children));
            }
        }
        return result;
    };
    // 处理每个选中的值
    for (const v of val) {
        const node = findNodeInTree(tree, v);
        if (node) {
            if (node.children && node.children.length > 0) {
                // 如果是父节点，展开为匹配搜索条件的子节点
                const matchingChildren = getMatchingLeafNodes([node]);
                matchingChildren.forEach((id) => {
                    if (!processedIds.has(id)) {
                        finalVal.push(id);
                        processedIds.add(id);
                    }
                });
            }
            else {
                // 如果是叶子节点，直接添加
                if (!processedIds.has(v)) {
                    finalVal.push(v);
                    processedIds.add(v);
                }
            }
        }
        else {
            // 如果找不到节点，保留原值（可能是外部传入的值）
            if (!processedIds.has(v)) {
                finalVal.push(v);
                processedIds.add(v);
            }
        }
    }
    return finalVal;
};

/**
 * Cookie 和 LocalStorage 跨域传递工具（后端中转方案）
 *
 * 使用方式：
 * - openNewWindowWithStorageViaBackend (源页面使用)
 * - restoreStorageFromBackend (目标页面使用)
 * - analyzeStorageSize (分析存储数据大小)
 */
/**
 * 获取所有 Cookie 并转换为对象
 */
const getAllCookies = (includeKeys) => {
    const cookies = {};
    if (document.cookie) {
        document.cookie.split(';').forEach((cookie) => {
            const [key, value] = cookie.split('=').map((c) => c.trim());
            if (key) {
                // 如果指定了 includeKeys，只包含匹配的 key
                const shouldInclude = !includeKeys || includeKeys.some((includeKey) => key === includeKey);
                if (shouldInclude) {
                    try {
                        // 尝试解码，如果失败则使用原始值
                        cookies[key] = unescape(value || '');
                    }
                    catch (e) {
                        // 如果解码失败，直接使用原始值
                        cookies[key] = value || '';
                    }
                }
            }
        });
    }
    return cookies;
};
/**
 * 获取所有 localStorage 数据
 */
const getAllLocalStorage = (includeKeys) => {
    const storage = {};
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) {
            // 如果指定了 includeKeys，只包含匹配的 key
            const shouldInclude = !includeKeys || includeKeys.some((includeKey) => key === includeKey);
            if (shouldInclude) {
                storage[key] = localStorage.getItem(key) || '';
            }
        }
    }
    return storage;
};
/**
 * 分析存储数据大小
 */
const analyzeStorageSize = (includeKeys) => {
    const cookies = getAllCookies(includeKeys);
    const localStorage = getAllLocalStorage(includeKeys);
    // 计算每个 Cookie 的大小
    const cookieSizes = Object.entries(cookies).map(([key, value]) => {
        const size = new Blob([JSON.stringify({ [key]: value })]).size;
        return {
            key,
            size,
            type: 'Cookie',
            value: value.substring(0, 50) + (value.length > 50 ? '...' : ''),
        };
    });
    // 计算每个 localStorage 的大小
    const localStorageSizes = Object.entries(localStorage).map(([key, value]) => {
        const size = new Blob([JSON.stringify({ [key]: value })]).size;
        return {
            key,
            size,
            type: 'LocalStorage',
            value: value.substring(0, 50) + (value.length > 50 ? '...' : ''),
        };
    });
    // 合并并排序
    const allSizes = [...cookieSizes, ...localStorageSizes].sort((a, b) => b.size - a.size);
    // 计算总大小
    const totalCookieSize = cookieSizes.reduce((sum, item) => sum + item.size, 0);
    const totalLocalStorageSize = localStorageSizes.reduce((sum, item) => sum + item.size, 0);
    const totalSize = totalCookieSize + totalLocalStorageSize;
    // 计算编码后的大小
    const data = { cookies, localStorage, timestamp: Date.now() };
    const jsonString = JSON.stringify(data);
    const utf8Bytes = new TextEncoder().encode(jsonString);
    const binaryString = Array.from(utf8Bytes, (byte) => String.fromCharCode(byte)).join('');
    const encoded = btoa(binaryString);
    console.group('📊 存储数据分析报告');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    if (includeKeys && includeKeys.length > 0) {
        console.log('✅ 只包含以下关键字的数据:', includeKeys.join(', '));
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    }
    else {
        console.log('📦 包含所有数据');
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    }
    console.log('📈 总体统计:');
    console.log(`  Cookie 项数: ${cookieSizes.length}`);
    console.log(`  Cookie 总大小: ${totalCookieSize} 字节 (${(totalCookieSize / 1024).toFixed(2)} KB)`);
    console.log(`  LocalStorage 项数: ${localStorageSizes.length}`);
    console.log(`  LocalStorage 总大小: ${totalLocalStorageSize} 字节 (${(totalLocalStorageSize / 1024).toFixed(2)} KB)`);
    console.log(`  原始总大小: ${totalSize} 字节 (${(totalSize / 1024).toFixed(2)} KB)`);
    console.log(`  JSON 字符串长度: ${jsonString.length} 字符`);
    console.log(`  Base64 编码后: ${encoded.length} 字符`);
    console.log(`  压缩率: ${((1 - encoded.length / jsonString.length) * 100).toFixed(2)}%`);
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('\n🏆 占用空间最大的前 10 项:');
    console.table(allSizes.slice(0, 10).map((item, index) => ({
        排名: index + 1,
        类型: item.type,
        键名: item.key,
        大小: `${item.size} 字节`,
        KB: `${(item.size / 1024).toFixed(2)} KB`,
        预览: item.value,
    })));
    // 如果有特别大的项（超过 10KB），单独标注
    const largeItems = allSizes.filter((item) => item.size > 10240);
    if (largeItems.length > 0) {
        console.warn('\n⚠️ 发现超大数据项 (>10KB):');
        console.table(largeItems.map((item) => ({
            类型: item.type,
            键名: item.key,
            大小: `${(item.size / 1024).toFixed(2)} KB`,
            建议: '考虑排除或优化',
        })));
    }
    console.groupEnd();
    return {
        totalSize,
        totalCookieSize,
        totalLocalStorageSize,
        cookieCount: cookieSizes.length,
        localStorageCount: localStorageSizes.length,
        encodedSize: encoded.length,
        jsonSize: jsonString.length,
        topItems: allSizes.slice(0, 10),
        largeItems,
    };
};
/**
 * 通过后端中转打开新窗口（解决 URL 长度限制）
 * 需要后端提供两个接口：
 * 1. POST /api/storage/save - 保存数据，返回临时 token
 * 2. GET /api/storage/get?token=xxx - 根据 token 获取数据
 */
const openNewWindowWithStorageViaBackend = async (url, options) => {
    try {
        // 判断当前域名和目标URL的域名是否是同一个顶级域名
        const currentDomain = window.location.hostname;
        const targetUrl = new URL(url);
        const targetDomain = targetUrl.hostname;
        const isSameDomain = currentDomain === targetDomain;
        console.log(isSameDomain, currentDomain, targetDomain);
        if (options?.debug) {
            console.log('🔍 域名检查:', {
                currentDomain,
                targetDomain,
                isSameDomain,
            });
        }
        // 如果是同一个顶级域名，直接跳转，不需要数据中转
        if (isSameDomain) {
            console.log('✅ 同个顶级域名下，直接跳转');
            const newWindow = window.open(url);
            if (newWindow) {
                console.log('✅ 新窗口已打开');
            }
            else {
                console.error('❌ 打开新窗口失败，可能被浏览器拦截');
            }
            return;
        }
        // 不同顶级域名，需要通过后端中转数据
        console.log('🔄 不同顶级域名，使用后端中转方式传递数据');
        const defaultIncludeKeys = [
            'device_id',
            'access_token',
            'refresh_token',
            'merchant_id',
            'user_operator',
            'account_id',
            'operator_id',
            'location_zone_id',
            'location_id',
            'use_staff_name',
            'location_is',
            'base_url',
            'isInternal',
            'current_location_name',
            'merchant_domain',
            'headquarters_is',
            'location_name',
            'mars_location_name',
            'mars_location_id',
            'zone_is',
            'distribution_center_is',
        ];
        const includeKeys = options?.includeKeys || defaultIncludeKeys;
        console.log('🔄 使用后端中转方式传递数据');
        // 收集数据
        const cookieData = getAllCookies(includeKeys);
        const data = {
            ...cookieData,
            access_token: window.getCookie(`${cookieData.merchant_id}_access_token`) ||
                localStorage.getItem(`${cookieData.merchant_id}_access_token`),
            refresh_token: window.getCookie(`${cookieData.merchant_id}_refresh_token`) ||
                localStorage.getItem(`${cookieData.merchant_id}_refresh_token`),
        };
        if (options?.debug) {
            console.log('📦 准备传递的数据:', data);
        }
        // 发送数据到后端，获取临时 token
        const response = await saveDomainCookieData(cookieData);
        console.log(response, 'response');
        if (!response.isSuccess()) {
            throw new Error(`后端保存失败: ${response}`);
        }
        // 使用 token 打开新窗口
        const finalUrl = `${url}${url.includes('?') ? '&' : '?'}merchant_id=${cookieData.merchant_id}`;
        console.log('🔗 最终 URL 长度:', finalUrl.length, '字符');
        const newWindow = window.open(finalUrl);
        if (newWindow) {
            console.log('✅ 新窗口已打开');
        }
        else {
            console.error('❌ 打开新窗口失败，可能被浏览器拦截');
        }
    }
    catch (error) {
        console.error('❌ 通过后端中转失败', error);
        throw error;
    }
};
/**
 * 从后端获取并恢复存储数据
 */
const restoreStorageFromBackend = async (options) => {
    try {
        // 从 URL 获取 token
        const params = new URLSearchParams(window.location.search);
        const merchantId = params.get('merchant_id');
        console.log(merchantId, 'merchantId');
        if (!merchantId) {
            console.log('未找到存储 token');
            return false;
        }
        console.log('🔄 从后端获取存储数据，merchantId:', merchantId);
        // 从后端获取数据
        const response = await getDomainCookieData(merchantId);
        if (!response.isSuccess()) {
            throw new Error(`后端获取失败: ${response}`);
        }
        const data = response.success()?.data;
        // 恢复 Cookie
        Object.entries(data).forEach(([key, value]) => {
            window.setCookie(key, value);
        });
        return true;
    }
    catch (error) {
        console.error('❌ 从后端恢复数据失败', error);
        return false;
    }
};

const SHOP_DECORATION_MP_MANAGE_PATH = '/main-operations/weapp/decorateNavigation';
const SHOP_DECORATION_ENTRY_PATH = '/decorate/shoppage/merchantPage';
const buildShopDecorationEntryPath = (params) => {
    const searchParams = new URLSearchParams();
    if (params?.appid) {
        searchParams.set('appid', params.appid);
    }
    if (params?.location_id) {
        searchParams.set('location_id', params.location_id);
    }
    const queryString = searchParams.toString();
    return `${SHOP_DECORATION_ENTRY_PATH}${queryString ? `?${queryString}` : ''}`;
};
const buildShopDecorationEntryUrl = (params) => window.MARS_BASE_CONFIG.SALE_APP_URL + buildShopDecorationEntryPath(params);
/** 打开小程序装修页；appid 为空时由后端返回默认可用方案。 */
const openShopDecorationEntry = (params) => openNewWindowWithStorageViaBackend(buildShopDecorationEntryUrl(params));
/** 打开小程序管理页（与装修页顶部「跳转主页」一致）。 */
const openShopDecorationMpManage = () => {
    window.open(`${window.MARS_BASE_CONFIG.BASE_URL}${SHOP_DECORATION_MP_MANAGE_PATH}`);
};
/** 线上店铺-装修方案菜单直接进入小程序装修页。 */
const navigateToShopDecorationEntry = () => {
    openShopDecorationEntry();
};

/** 新版装修方案生效所需的微信小程序最低版本。 */
const DECORATION_SCHEME_REQUIRED_WECHAT_MP_VERSION = '26.0720.0';
/** 新版装修方案生效所需的支付宝小程序最低版本。 */
const DECORATION_SCHEME_REQUIRED_ALI_MP_VERSION = '26.0720.0';
const parseVersionSegments = (version) => version
    .trim()
    .split('.')
    .map((segment) => Number.parseInt(segment, 10))
    .map((segment) => (Number.isNaN(segment) ? 0 : segment));
/** 比较两个点分版本号，返回值与 Array.sort 比较函数一致。 */
const compareMiniProgramVersion = (left, right) => {
    const leftSegments = parseVersionSegments(left);
    const rightSegments = parseVersionSegments(right);
    const maxLength = Math.max(leftSegments.length, rightSegments.length);
    for (let index = 0; index < maxLength; index += 1) {
        const leftValue = leftSegments[index] ?? 0;
        const rightValue = rightSegments[index] ?? 0;
        if (leftValue > rightValue) {
            return 1;
        }
        if (leftValue < rightValue) {
            return -1;
        }
    }
    return 0;
};
const isMiniProgramVersionBelow = (currentVersion, requiredVersion) => {
    if (!currentVersion || currentVersion.trim() === '-' || currentVersion.trim() === '') {
        return true;
    }
    return compareMiniProgramVersion(currentVersion.trim(), requiredVersion) < 0;
};

var NavHomeActiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%203%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-80.000000%2C%20-32.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-3%22%20transform%3D%22translate%2880.000000%2C%2032.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22Active%3Dyes%22%20transform%3D%22translate%283.500000%2C%203.000000%29%22%20fill%3D%22%234A4A4A%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M4.84416%2C0.7266225%20C5.73548%2C-0.2422075%207.2645%2C-0.2422075%208.1558%2C0.7266225%20L12.0097%2C4.9156425%20C12.6466%2C5.6078325%2013%2C6.5140425%2013%2C7.4546325%20L13%2C10.7845325%20C13%2C12.3033325%2011.7688%2C13.5345325%2010.25%2C13.5345325%20L8.5%2C13.5345325%20C7.6716%2C13.5345325%207%2C12.8629325%207%2C12.0345325%20L7%2C10.0345325%20L6%2C10.0345325%20L6%2C12.0345325%20C6%2C12.8629325%205.32843%2C13.5345325%204.5%2C13.5345325%20L2.75%2C13.5345325%20C1.23122%2C13.5345325%200%2C12.3033325%200%2C10.7845325%20L0%2C7.4546325%20C0%2C6.5140425%200.35345%2C5.6078325%200.99026%2C4.9156425%20L4.84416%2C0.7266225%20Z%20M7.0519%2C1.7422125%20C6.7548%2C1.4192625%206.24516%2C1.4192625%205.94805%2C1.7422125%20L2.09416%2C5.9312325%20C1.71207%2C6.3465425%201.5%2C6.8902725%201.5%2C7.4546325%20L1.5%2C10.7845325%20C1.5%2C11.4749325%202.05964%2C12.0345325%202.75%2C12.0345325%20L4.5%2C12.0345325%20L4.5%2C10.0345325%20C4.5%2C9.2061325%205.17157%2C8.5345325%206%2C8.5345325%20L7%2C8.5345325%20C7.8284%2C8.5345325%208.5%2C9.2061325%208.5%2C10.0345325%20L8.5%2C12.0345325%20L10.25%2C12.0345325%20C10.9404%2C12.0345325%2011.5%2C11.4749325%2011.5%2C10.7845325%20L11.5%2C7.4546325%20C11.5%2C6.8902725%2011.2879%2C6.3465425%2010.9058%2C5.9312325%20L7.0519%2C1.7422125%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavHomeInactiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%202%E5%A4%87%E4%BB%BD%202%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-60.000000%2C%20-32.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-2%E5%A4%87%E4%BB%BD-2%22%20transform%3D%22translate%2860.000000%2C%2032.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M14%2C15.9999%20L12%2C15.9999%20C11.4477%2C15.9999%2011%2C15.5522%2011%2C14.9999%20L11%2C12.4999%20C11%2C12.2238%2010.7761%2C11.9999%2010.5%2C11.9999%20L9.5%2C11.9999%20C9.2239%2C11.9999%209%2C12.2238%209%2C12.4999%20L9%2C14.9999%20C9%2C15.5522%208.5523%2C15.9999%208%2C15.9999%20L6%2C15.9999%20C4.8954%2C15.9999%204%2C15.1045%204%2C13.9999%20L4%2C9.7426%20C4%2C8.9469%204.3161%2C8.1839%204.8787%2C7.6213%20L8.5858%2C3.9142%20C9.3668%2C3.1331%2010.6332%2C3.1331%2011.4142%2C3.9142%20L15.1213%2C7.6213%20C15.6839%2C8.1839%2016%2C8.9469%2016%2C9.7426%20L16%2C13.9999%20C16%2C15.1045%2015.1046%2C15.9999%2014%2C15.9999%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%20fill%3D%22%234A4A4A%22%20fill-rule%3D%22nonzero%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavOrderActiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%207%E5%A4%87%E4%BB%BD%205%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-144.000000%2C%20-32.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-7%E5%A4%87%E4%BB%BD-5%22%20transform%3D%22translate%28144.000000%2C%2032.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22Contract%22%20transform%3D%22translate%282.000000%2C%203.000000%29%22%20fill%3D%22%234A4A4A%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M6.25%2C13.25%20C6.25%2C13.6642%205.91421%2C14%205.5%2C14%20L5.25%2C14%20C3.73122%2C14%202.5%2C12.7688%202.5%2C11.25%20L2.5%2C2%20C2.5%2C1.72386%202.27614%2C1.5%202%2C1.5%20C1.58579%2C1.5%201.25%2C1.16421%201.25%2C0.75%20C1.25%2C0.33579%201.58579%2C0%202%2C0%20L10.5%2C0%20C11.6046%2C0%2012.5%2C0.89543%2012.5%2C2%20L12.5%2C10.25%20C12.5%2C10.6642%2012.1642%2C11%2011.75%2C11%20C11.3358%2C11%2011%2C10.6642%2011%2C10.25%20L11%2C2%20C11%2C1.72386%2010.7761%2C1.5%2010.5%2C1.5%20L3.93699%2C1.5%20C3.97812%2C1.65981%204%2C1.82735%204%2C2%20L4%2C11.25%20C4%2C11.9403%204.55964%2C12.5%205.25%2C12.5%20L5.5%2C12.5%20C5.91421%2C12.5%206.25%2C12.8358%206.25%2C13.25%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M5.5%2C3.74999%20C5.5%2C3.33578%205.83579%2C2.99999%206.25%2C2.99999%20L8.75%2C2.99999%20C9.1642%2C2.99999%209.5%2C3.33578%209.5%2C3.74999%20C9.5%2C4.16421%209.1642%2C4.49999%208.75%2C4.49999%20L6.25%2C4.49999%20C5.83579%2C4.49999%205.5%2C4.16421%205.5%2C3.74999%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M5.5%2C6.74999%20C5.5%2C6.33578%205.83579%2C5.99999%206.25%2C5.99999%20L8.75%2C5.99999%20C9.1642%2C5.99999%209.5%2C6.33578%209.5%2C6.74999%20C9.5%2C7.1642%209.1642%2C7.5%208.75%2C7.5%20L6.25%2C7.5%20C5.83579%2C7.5%205.5%2C7.1642%205.5%2C6.74999%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M0%2C2%20C0%2C0.89542%200.89543%2C0%202%2C0%20C3.10457%2C0%204%2C0.89543%204%2C2%20L4%2C5.74999%20C4%2C6.16421%203.66421%2C6.5%203.25%2C6.5%20L0.75%2C6.5%20C0.33579%2C6.5%200%2C6.16421%200%2C5.74999%20L0%2C2%20Z%20M2%2C1.5%20C1.72386%2C1.5%201.5%2C1.72385%201.5%2C2%20L1.5%2C4.99999%20L2.5%2C4.99999%20L2.5%2C2%20C2.5%2C1.72386%202.27614%2C1.5%202%2C1.5%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M6.5%2C10.25%20C6.5%2C9.8358%206.83579%2C9.5%207.25%2C9.5%20L14.5%2C9.5%20C14.9142%2C9.5%2015.25%2C9.8358%2015.25%2C10.25%20L15.25%2C11.75%20C15.25%2C12.9926%2014.2426%2C14%2013%2C14%20L5.25%2C14%20C4.83579%2C14%204.5%2C13.6642%204.5%2C13.25%20C4.5%2C12.8358%204.83579%2C12.5%205.25%2C12.5%20C5.94036%2C12.5%206.5%2C11.9403%206.5%2C11.25%20L6.5%2C10.25%20Z%20M7.7001%2C12.5%20L13%2C12.5%20C13.4142%2C12.5%2013.75%2C12.1642%2013.75%2C11.75%20L13.75%2C11%20L8%2C11%20L8%2C11.25%20C8%2C11.7001%207.8919%2C12.125%207.7001%2C12.5%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavOrderInactiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-124.000000%2C%20-32.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84%22%20transform%3D%22translate%28124.000000%2C%2032.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22Icon-%285%29%22%20transform%3D%22translate%282.000000%2C%203.000000%29%22%20fill%3D%22%234A4A4A%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M2%2C0%20C0.89543%2C0%200%2C0.895431%200%2C2%20L0%2C5.75%20C0%2C6.16421%200.335786%2C6.5%200.75%2C6.5%20L1.5%2C6.5%20L2.5%2C6.5%20L2.75%2C6.5%20L2.75%2C10.8594%20C2.75%2C11.8949%203.58947%2C12.7344%204.625%2C12.7344%20C5.66053%2C12.7344%206.5%2C11.8949%206.5%2C10.8594%20L6.5%2C10.2544%20C6.5%2C9.83774%206.83775%2C9.5%207.25438%2C9.5%20L12.5%2C9.5%20L12.5%2C2.00001%20C12.5%2C0.895436%2011.6046%2C0%2010.5%2C0%20L2.00558%2C0%20L2%2C0%20Z%20M15.25%2C10.75%20L12.309%2C10.75%20L11.191%2C10.75%20L7.75%2C10.75%20L7.75%2C10.8594%20C7.75%2C12.544%206.41698%2C13.9173%204.7482%2C13.982%20C4.66043%2C13.994%204.57723%2C14%204.5%2C14%20L5.25%2C14%20L5.5%2C14%20L13.05%2C14%20C14.265%2C14%2015.25%2C13.015%2015.25%2C11.8%20L15.25%2C10.75%20Z%20M1.99784%2C1.5%20C1.72269%2C1.50117%201.5%2C1.72458%201.5%2C2%20L1.5%2C5%20L2.5%2C5%20L2.5%2C2.00001%20L2.5%2C2%20C2.5%2C1.96548%202.4965%2C1.93178%202.48984%2C1.89923%20C2.44322%2C1.67139%202.24162%2C1.5%202%2C1.5%20L1.99784%2C1.5%20Z%20M6%2C3%20C5.58579%2C3%205.25%2C3.33579%205.25%2C3.75%20C5.25%2C4.16421%205.58579%2C4.5%206%2C4.5%20L9%2C4.5%20C9.41421%2C4.5%209.75%2C4.16421%209.75%2C3.75%20C9.75%2C3.33579%209.41421%2C3%209%2C3%20L6%2C3%20Z%20M5.25%2C6.75%20C5.25%2C6.33579%205.58579%2C6%206%2C6%20L9%2C6%20C9.41421%2C6%209.75%2C6.33579%209.75%2C6.75%20C9.75%2C7.16421%209.41421%2C7.5%209%2C7.5%20L6%2C7.5%20C5.58579%2C7.5%205.25%2C7.16421%205.25%2C6.75%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavProductActiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%209%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-208.000000%2C%20-32.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-9%22%20transform%3D%22translate%28208.000000%2C%2032.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22Active%3Dyes-%284%29%22%20transform%3D%22translate%283.500244%2C%203.000000%29%22%20fill%3D%22%234A4A4A%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M3.5%2C5.5%20C2.94772%2C5.5%202.5%2C5.94771%202.5%2C6.5%20L2.5%2C9.5%20C2.5%2C10.0523%202.94771%2C10.5%203.5%2C10.5%20L7.5%2C10.5%20C8.0523%2C10.5%208.5%2C10.0523%208.5%2C9.5%20L8.5%2C6.5%20C8.5%2C5.94772%208.0523%2C5.5%207.5%2C5.5%20L3.5%2C5.5%20Z%20M4%2C9%20L4%2C7%20L7%2C7%20L7%2C9%20L4%2C9%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M1.81463%2C0.94946%20C2.23645%2C0.35394%202.9209%2C0%203.65068%2C0%20L9.4472%2C0%20C10.1995%2C0%2010.902%2C0.37598%2011.3193%2C1.00192%20L12.5381%2C2.83013%20C12.8393%2C3.28186%2013%2C3.81263%2013%2C4.35555%20L13%2C11.25%20C13%2C12.2165%2012.2165%2C13%2011.25%2C13%20L1.75%2C13%20C0.7835%2C13%200%2C12.2165%200%2C11.25%20L0%2C4.38657%20C0%2C3.81712%200.17678%2C3.26171%200.50593%2C2.79703%20L1.81463%2C0.94946%20Z%20M3.65068%2C1.5%20C3.40742%2C1.5%203.17927%2C1.61798%203.03866%2C1.81649%20L2.20034%2C3%20L5.75%2C3%20L5.75%2C1.5%20L3.65068%2C1.5%20Z%20M7.25%2C3%20L10.8486%2C3%20L10.0713%2C1.83397%20C9.9322%2C1.62533%209.698%2C1.5%209.4472%2C1.5%20L7.25%2C1.5%20L7.25%2C3%20Z%20M11.5%2C4.5%20L1.5%2C4.5%20L1.5%2C11.25%20C1.5%2C11.3881%201.61193%2C11.5%201.75%2C11.5%20L11.25%2C11.5%20C11.3881%2C11.5%2011.5%2C11.3881%2011.5%2C11.25%20L11.5%2C4.5%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavProductInactiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%2014%E5%A4%87%E4%BB%BD%202%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-188.000000%2C%20-32.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-14%E5%A4%87%E4%BB%BD-2%22%20transform%3D%22translate%28188.000000%2C%2032.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22PackageFilled%22%20transform%3D%22translate%284.000000%2C%203.000000%29%22%20fill%3D%22%234A4A4A%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M12.5%2C12.4747%20C12.8093%2C12.1591%2013%2C11.7268%2013%2C11.25%20L13%2C4.35555%20C13%2C3.81263%2012.8393%2C3.28186%2012.5381%2C2.83013%20L11.3193%2C1.00192%20C10.902%2C0.37598%2010.1995%2C0%209.4472%2C0%20L3.65068%2C0%20C2.9209%2C0%202.23645%2C0.35394%201.81463%2C0.94946%20L0.50593%2C2.79703%20C0.17678%2C3.26171%200%2C3.81712%200%2C4.38657%20L0%2C11.25%20C0%2C11.7268%200.1907%2C12.1591%200.5%2C12.4747%20L0.5%2C12.5%20L0.52525%2C12.5%20C0.84089%2C12.8093%201.27317%2C13%201.75%2C13%20L11.25%2C13%20C11.7268%2C13%2012.1591%2C12.8093%2012.4747%2C12.5%20L12.5%2C12.5%20L12.5%2C12.4747%20Z%20M3.03866%2C1.81649%20C3.17927%2C1.61798%203.40742%2C1.5%203.65068%2C1.5%20L5.75%2C1.5%20L5.75%2C3%20L2.20034%2C3%20L3.03866%2C1.81649%20Z%20M7.25%2C3%20L10.8486%2C3%20L10.0713%2C1.83397%20C9.9322%2C1.62533%209.698%2C1.5%209.4472%2C1.5%20L7.25%2C1.5%20L7.25%2C3%20Z%20M3.25%2C6.75%20C3.25%2C6.47386%203.47386%2C6.25%203.75%2C6.25%20L7.25%2C6.25%20C7.5261%2C6.25%207.75%2C6.47386%207.75%2C6.75%20L7.75%2C9.25%20C7.75%2C9.5261%207.5261%2C9.75%207.25%2C9.75%20L3.75%2C9.75%20C3.47386%2C9.75%203.25%2C9.5261%203.25%2C9.25%20L3.25%2C6.75%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavCustomerActiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%2015%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-272.000000%2C%20-32.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-15%22%20transform%3D%22translate%28272.000000%2C%2032.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22Active%3Dyes-%281%29%22%20transform%3D%22translate%283.909362%2C%202.500000%29%22%20fill%3D%22%234A4A4A%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M6.09088152%2C0%20C4.15788152%2C0%202.59088152%2C1.567%202.59088152%2C3.5%20C2.59088152%2C5.433%204.15788152%2C7%206.09088152%2C7%20C8.02388152%2C7%209.59088152%2C5.433%209.59088152%2C3.5%20C9.59088152%2C1.567%208.02388152%2C0%206.09088152%2C0%20Z%20M4.09088152%2C3.5%20C4.09088152%2C2.39543%204.98631152%2C1.5%206.09088152%2C1.5%20C7.19548152%2C1.5%208.09088152%2C2.39543%208.09088152%2C3.5%20C8.09088152%2C4.60457%207.19548152%2C5.5%206.09088152%2C5.5%20C4.98631152%2C5.5%204.09088152%2C4.60457%204.09088152%2C3.5%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M11.5747815%2C11.2268%20C10.4683815%2C9.2352%208.36918152%2C8%206.09087152%2C8%20C3.81257152%2C8%201.71335152%2C9.2352%200.606911517%2C11.2268%20L0.170251517%2C12.0128%20C-0.325058483%2C12.9043%200.319631517%2C14%201.33955152%2C14%20L10.8421815%2C14%20C11.8620815%2C14%2012.5067815%2C12.9043%2012.0114815%2C12.0128%20L11.5747815%2C11.2268%20Z%20M1.91815152%2C11.9552%20C2.76003152%2C10.4398%204.35732152%2C9.5%206.09087152%2C9.5%20C7.82438152%2C9.5%209.42168152%2C10.4398%2010.2635815%2C11.9552%20L10.5662815%2C12.5%20L1.61551152%2C12.5%20L1.91815152%2C11.9552%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavCustomerInactiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%206%E5%A4%87%E4%BB%BD%202%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-252.000000%2C%20-32.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-6%E5%A4%87%E4%BB%BD-2%22%20transform%3D%22translate%28252.000000%2C%2032.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%E5%A4%87%E4%BB%BD-3%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M6.5001%2C6.5%20C6.5001%2C4.567%208.0671%2C3%2010.0001%2C3%20C11.9331%2C3%2013.5001%2C4.567%2013.5001%2C6.5%20C13.5001%2C8.433%2011.9331%2C10%2010.0001%2C10%20C8.0671%2C10%206.5001%2C8.433%206.5001%2C6.5%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%20fill%3D%22%234A4A4A%22%20fill-rule%3D%22nonzero%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M4.5161%2C14.227%20C5.6225%2C12.235%207.7218%2C11%2010.0001%2C11%20C12.2784%2C11%2014.3776%2C12.235%2015.484%2C14.227%20L15.9207%2C15.013%20C16.416%2C15.904%2015.7713%2C17%2014.7514%2C17%20L5.2487%2C17%20C4.2288%2C17%203.5841%2C15.904%204.0794%2C15.013%20L4.5161%2C14.227%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%20fill%3D%22%234A4A4A%22%20fill-rule%3D%22nonzero%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavMarketingActiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%2016%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-336.000000%2C%20-32.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-16%22%20transform%3D%22translate%28336.000000%2C%2032.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22Active%3Dyes-%283%29%22%20transform%3D%22translate%283.500000%2C%203.500000%29%22%20fill%3D%22%234A4A4A%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M9.4479956%2C4.5%20C10.0002956%2C4.5%2010.4479956%2C4.05228%2010.4479956%2C3.5%20C10.4479956%2C2.94772%2010.0002956%2C2.5%209.4479956%2C2.5%20C8.8956956%2C2.5%208.4479956%2C2.94772%208.4479956%2C3.5%20C8.4479956%2C4.05228%208.8956956%2C4.5%209.4479956%2C4.5%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%20fill-rule%3D%22nonzero%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M7.7238956%2C0%20C6.7047956%2C0%205.7296156%2C0.41477%205.0227256%2C1.14885%20L0.769115597%2C5.56605%20C-0.270344403%2C6.6455%20-0.254184403%2C8.3585%200.805455597%2C9.4181%20L3.7033656%2C12.316%20C4.6666756%2C13.2793%206.2239256%2C13.294%207.2051956%2C12.3491%20L11.9522956%2C7.7778%20C12.5884956%2C7.1651%2012.9479956%2C6.31995%2012.9479956%2C5.43673%20L12.9479956%2C3.25%20C12.9479956%2C1.45507%2011.4928956%2C0%209.6979956%2C0%20L7.7238956%2C0%20Z%20M6.1032056%2C2.18931%20C6.5272956%2C1.74886%207.1124956%2C1.5%207.7238956%2C1.5%20L9.6979956%2C1.5%20C10.6644956%2C1.5%2011.4479956%2C2.2835%2011.4479956%2C3.25%20L11.4479956%2C5.43673%20C11.4479956%2C5.91231%2011.2543956%2C6.36741%2010.9118956%2C6.6973%20L6.1647756%2C11.2686%20C5.7722456%2C11.6466%205.1493456%2C11.6407%204.7640256%2C11.2554%20L1.8661156%2C8.3575%20C1.3844556%2C7.8758%201.3771156%2C7.0972%201.8495956%2C6.6065%20L6.1032056%2C2.18931%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavMarketingInactiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%205%E5%A4%87%E4%BB%BD%202%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-316.000000%2C%20-32.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-5%E5%A4%87%E4%BB%BD-2%22%20transform%3D%22translate%28316.000000%2C%2032.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M8.5745%2C4.6488%20C9.2814%2C3.9148%2010.2566%2C3.5%2011.2757%2C3.5%20L13.2498%2C3.5%20C15.0447%2C3.5%2016.4998%2C4.9551%2016.4998%2C6.75%20L16.4998%2C8.9367%20C16.4998%2C9.8199%2016.1403%2C10.6651%2015.5041%2C11.2778%20L10.757%2C15.8491%20C9.7757%2C16.794%208.2184%2C16.7793%207.2551%2C15.816%20L4.3572%2C12.9181%20C3.2976%2C11.8585%203.2814%2C10.1455%204.3209%2C9.0661%20L8.5745%2C4.6488%20Z%20M12.9998%2C8%20C13.552%2C8%2013.9998%2C7.5523%2013.9998%2C7%20C13.9998%2C6.4477%2013.552%2C6%2012.9998%2C6%20C12.4475%2C6%2011.9998%2C6.4477%2011.9998%2C7%20C11.9998%2C7.5523%2012.4475%2C8%2012.9998%2C8%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%20fill%3D%22%234A4A4A%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavWeappActiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3EGlobe%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22Globe%22%20stroke%3D%22none%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M3%2C10%20C3%2C6.13401%206.13401%2C3%2010%2C3%20C13.866%2C3%2017%2C6.13401%2017%2C10%20C17%2C13.866%2013.866%2C17%2010%2C17%20C6.13401%2C17%203%2C13.866%203%2C10%20Z%20M10%2C4.5%20C7.98377%2C4.5%206.22093%2C5.58491%205.26342%2C7.2028%20L7.26256%2C9.20192%20C7.73473%2C9.67409%208%2C10.3145%208%2C10.9823%20L8%2C11.5%20C8%2C11.7762%208.22386%2C12%208.5%2C12%20C9.60457%2C12%2010.5%2C12.8954%2010.5%2C14%20L10.5%2C15.4776%20C12.52%2C15.2955%2014.2251%2C14.0213%2015.0202%2C12.25%20L14%2C12.25%20C13.8011%2C12.25%2013.6103%2C12.171%2013.4697%2C12.0303%20C13.329%2C11.8897%2013.25%2C11.6989%2013.25%2C11.5%20L13.25%2C11%20C13.25%2C10.5858%2012.9142%2C10.25%2012.5%2C10.25%20L9.99999%2C10.25%20C9.02786%2C10.25%208.24999%2C9.45506%208.24999%2C8.49422%20C8.24999%2C7.95425%208.49956%2C7.43886%208.93064%2C7.10609%20L9.39256%2C6.74951%20C9.48897%2C6.67509%209.54543%2C6.56019%209.54543%2C6.4384%20L9.54543%2C6.39764%20C9.54543%2C5.5134%2010.165%2C4.77388%2010.9937%2C4.58954%20C10.6714%2C4.53072%2010.3393%2C4.5%2010%2C4.5%20Z%20M12.8752%2C5.31043%20C12.537%2C5.75693%2012.001%2C6.04537%2011.3977%2C6.04537%20C11.2032%2C6.04537%2011.0454%2C6.20308%2011.0454%2C6.39764%20L11.0454%2C6.4384%20C11.0454%2C7.02499%2010.7735%2C7.57845%2010.3091%2C7.9369%20L9.84723%2C8.29347%20C9.78773%2C8.3394%209.74999%2C8.41444%209.74999%2C8.49422%20C9.74999%2C8.63794%209.86755%2C8.75002%209.99999%2C8.75002%20L12.5%2C8.75002%20C13.6582%2C8.75002%2014.6119%2C9.62503%2014.7363%2C10.75%20L15.4493%2C10.75%20C15.4827%2C10.5048%2015.5%2C10.2544%2015.5%2C10%20C15.5%2C8.01633%2014.4498%2C6.27793%2012.8752%2C5.31043%20Z%20M4.5%2C10%20C4.5%2C9.55654%204.55248%2C9.12534%204.65158%2C8.71227%20L6.2019%2C10.2626%20C6.39277%2C10.4534%206.5%2C10.7123%206.5%2C10.9822%20L6.5%2C11.5%20C6.5%2C12.6046%207.39543%2C13.5%208.50001%2C13.5%20C8.77615%2C13.5%209%2C13.7239%209%2C14%20L9%2C15.4093%20C6.43989%2C14.9391%204.5%2C12.6961%204.5%2C10%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%20fill%3D%22%234A4A4A%22%3E%3C%2Fpath%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavWeappInactiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3EGlobeFilled%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22GlobeFilled%22%20stroke%3D%22none%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M3%2C10%20C3%2C6.13401%206.13401%2C3%2010%2C3%20C13.866%2C3%2017%2C6.13401%2017%2C10%20C17%2C13.866%2013.866%2C17%2010%2C17%20C6.13401%2C17%203%2C13.866%203%2C10%20Z%20M5.01873%2C7.6652%20L6.90899%2C9.55545%20C7.2874%2C9.93385%207.49999%2C10.4471%207.49998%2C10.9822%20L7.49998%2C11.5%20C7.49998%2C12.0523%207.9477%2C12.5%208.49999%2C12.5%20C9.32842%2C12.5%209.99999%2C13.1716%209.99999%2C14%20L9.99999%2C15.5%20C12.4258%2C15.5%2014.4847%2C13.9296%2015.2157%2C11.75%20L14%2C11.75%20C13.8619%2C11.75%2013.75%2C11.638%2013.75%2C11.5%20L13.75%2C11%20C13.75%2C10.3096%2013.1904%2C9.74998%2012.5%2C9.74998%20L9.99997%2C9.74998%20C9.30774%2C9.74998%208.74997%2C9.18265%208.74997%2C8.49418%20C8.74997%2C8.10761%208.92893%2C7.739%209.23615%2C7.50184%20L9.69807%2C7.14527%20C9.91713%2C6.97618%2010.0454%2C6.71508%2010.0454%2C6.43836%20L10.0454%2C6.3976%20C10.0454%2C5.65076%2010.6508%2C5.04533%2011.3977%2C5.04533%20L11.4547%2C5.04533%20C11.6484%2C5.04533%2011.8259%2C4.97607%2011.9639%2C4.86097%20C11.3539%2C4.62776%2010.6919%2C4.5%2010%2C4.5%20C7.79719%2C4.5%205.89684%2C5.79499%205.01873%2C7.6652%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%20fill%3D%22%234A4A4A%22%3E%3C%2Fpath%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavChannelActiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%2018%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-464.000000%2C%20-32.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-18%22%20transform%3D%22translate%28464.000000%2C%2032.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22Active%3Dyes-%286%29%22%20transform%3D%22translate%283.500000%2C%203.000000%29%22%20fill%3D%22%234A4A4A%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M7.0393%2C0.1223325%20C6.6983%2C-0.0407775%206.30173%2C-0.0407775%205.96068%2C0.1223325%20L0.71068%2C2.6332025%20C0.27642%2C2.8408925%200%2C3.2794925%200%2C3.7608625%20L0%2C4.9457525%20C0%2C5.6361125%200.55964%2C6.1957525%201.25%2C6.1957525%20L1.25%2C8.9457525%20L1%2C8.9457525%20C0.44772%2C8.9457525%200%2C9.3934525%200%2C9.9457525%20L0%2C11.9457525%20C0%2C12.4980525%200.44771%2C12.9457525%201%2C12.9457525%20L12%2C12.9457525%20C12.5523%2C12.9457525%2013%2C12.4980525%2013%2C11.9457525%20L13%2C9.9457525%20C13%2C9.3934525%2012.5523%2C8.9457525%2012%2C8.9457525%20L11.75%2C8.9457525%20L11.75%2C6.1957525%20C12.4404%2C6.1957525%2013%2C5.6361125%2013%2C4.9457525%20L13%2C3.7608625%20C13%2C3.2794925%2012.7236%2C2.8408925%2012.2893%2C2.6332025%20L7.0393%2C0.1223325%20Z%20M10.25%2C8.9457525%20L10.25%2C6.1957525%20L8.75%2C6.1957525%20L8.75%2C8.9457525%20L10.25%2C8.9457525%20Z%20M7.25%2C8.9457525%20L7.25%2C6.1957525%20L5.75%2C6.1957525%20L5.75%2C8.9457525%20L7.25%2C8.9457525%20Z%20M4.25%2C8.9457525%20L4.25%2C6.1957525%20L2.75%2C6.1957525%20L2.75%2C8.9457525%20L4.25%2C8.9457525%20Z%20M1.5%2C3.9184225%20L6.5%2C1.5271225%20L11.5%2C3.9184225%20L11.5%2C4.6957525%20L1.5%2C4.6957525%20L1.5%2C3.9184225%20Z%20M1.5%2C11.4457525%20L1.5%2C10.4457525%20L11.5%2C10.4457525%20L11.5%2C11.4457525%20L1.5%2C11.4457525%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavChannelInactiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%208%E5%A4%87%E4%BB%BD%202%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-444.000000%2C%20-32.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-8%E5%A4%87%E4%BB%BD-2%22%20transform%3D%22translate%28444.000000%2C%2032.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%E5%A4%87%E4%BB%BD-27%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M10.5393%2C3.177%20C10.1983%2C3.013%209.8017%2C3.013%209.4607%2C3.177%20L4.2107%2C5.687%20C3.7764%2C5.895%203.5%2C6.334%203.5%2C6.815%20L3.5%2C8%20C3.5%2C8.69%204.0596%2C9.25%204.75%2C9.25%20L4.75%2C12.25%20L4.5%2C12.25%20C3.9477%2C12.25%203.5%2C12.698%203.5%2C13.25%20L3.5%2C14.75%20C3.5%2C15.302%203.9477%2C15.75%204.5%2C15.75%20L15.5%2C15.75%20C16.0523%2C15.75%2016.5%2C15.302%2016.5%2C14.75%20L16.5%2C13.25%20C16.5%2C12.698%2016.0523%2C12.25%2015.5%2C12.25%20L15.25%2C12.25%20L15.25%2C9.25%20C15.9404%2C9.25%2016.5%2C8.69%2016.5%2C8%20L16.5%2C6.815%20C16.5%2C6.334%2016.2236%2C5.895%2015.7893%2C5.687%20L10.5393%2C3.177%20Z%20M12.25%2C9.25%20L13.75%2C9.25%20L13.75%2C12.25%20L12.25%2C12.25%20L12.25%2C9.25%20Z%20M9.25%2C9.25%20L10.75%2C9.25%20L10.75%2C12.25%20L9.25%2C12.25%20L9.25%2C9.25%20Z%20M6.25%2C9.25%20L7.75%2C9.25%20L7.75%2C12.25%20L6.25%2C12.25%20L6.25%2C9.25%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%20fill%3D%22%234A4A4A%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavShippingActiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%2020%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-144.000000%2C%20-76.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-20%22%20transform%3D%22translate%28144.000000%2C%2076.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%E5%A4%87%E4%BB%BD-6%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22Active%3Dyes-%288%29%22%20transform%3D%22translate%282.000000%2C%204.445752%29%22%20fill%3D%22%234A4A4A%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M1.5%2C0.75%20C1.5%2C0.33579%201.83579%2C0%202.25%2C0%20L9.2414%2C0%20C10.4692%2C0%2011.5483%2C0.81394%2011.8856%2C1.99452%20L12.3125%2C3.48862%20C12.337%2C3.57452%2012.4055%2C3.6408%2012.4922%2C3.66247%20L14.1744%2C4.08303%20C14.9535%2C4.27779%2015.5%2C4.97776%2015.5%2C5.7808%20L15.5%2C7%20C15.5%2C7.7108%2015.0763%2C8.3226%2014.4676%2C8.5966%20C14.4889%2C8.7279%2014.5%2C8.8627%2014.5%2C9%20C14.5%2C10.3807%2013.3807%2C11.5%2012%2C11.5%20C10.6193%2C11.5%209.5%2C10.3807%209.5%2C9%20C9.5%2C8.9156%209.5042%2C8.8322%209.5123%2C8.75%20L6.48766%2C8.75%20C6.49582%2C8.8322%206.5%2C8.9156%206.5%2C9%20C6.5%2C10.3807%205.38071%2C11.5%204%2C11.5%20C2.61929%2C11.5%201.5%2C10.3807%201.5%2C9%20C1.5%2C8.6444%201.57422%2C8.3062%201.70802%2C8%20L1.25%2C8%20C0.83579%2C8%200.5%2C7.6642%200.5%2C7.25%20C0.5%2C6.8358%200.83579%2C6.5%201.25%2C6.5%20L3.75%2C6.5%20C3.77988%2C6.5%203.80935%2C6.5017%203.83831%2C6.5051%20C3.89177%2C6.5017%203.94568%2C6.5%204%2C6.5%20C4.69935%2C6.5%205.33163%2C6.7872%205.78536%2C7.25%20L10.2146%2C7.25%20C10.6684%2C6.7872%2011.3007%2C6.5%2012%2C6.5%20C12.6982%2C6.5%2013.3296%2C6.7863%2013.7832%2C7.2478%20C13.9056%2C7.2316%2014%2C7.1268%2014%2C7%20L14%2C5.7808%20C14%2C5.6661%2013.9219%2C5.5661%2013.8106%2C5.5382%20L12.1284%2C5.11769%20C11.5217%2C4.966%2011.042%2C4.50205%2010.8702%2C3.9007%20L10.4433%2C2.4066%20C10.29%2C1.86997%209.7995%2C1.5%209.2414%2C1.5%20L2.25%2C1.5%20C1.83579%2C1.5%201.5%2C1.16421%201.5%2C0.75%20Z%20M4%2C10%20C4.55228%2C10%205%2C9.5523%205%2C9%20C5%2C8.4477%204.55228%2C8%204%2C8%20C3.44772%2C8%203%2C8.4477%203%2C9%20C3%2C9.5523%203.44772%2C10%204%2C10%20Z%20M12%2C10%20C12.5523%2C10%2013%2C9.5523%2013%2C9%20C13%2C8.4477%2012.5523%2C8%2012%2C8%20C11.4477%2C8%2011%2C8.4477%2011%2C9%20C11%2C9.5523%2011.4477%2C10%2012%2C10%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M0.75%2C3.5%20C0.33579%2C3.5%200%2C3.83579%200%2C4.25%20C0%2C4.66421%200.33579%2C5%200.75%2C5%20L5.75%2C5%20C6.16421%2C5%206.5%2C4.66421%206.5%2C4.25%20C6.5%2C3.83579%206.16421%2C3.5%205.75%2C3.5%20L0.75%2C3.5%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%20fill-rule%3D%22nonzero%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavShippingInactiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%2013%E5%A4%87%E4%BB%BD%202%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-124.000000%2C%20-76.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-13%E5%A4%87%E4%BB%BD-2%22%20transform%3D%22translate%28124.000000%2C%2076.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22Group-58652%22%20transform%3D%22translate%282.000000%2C%204.500000%29%22%20fill%3D%22%234A4A4A%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M2.25%2C0%20C1.83579%2C0%201.5%2C0.335786%201.5%2C0.75%20C1.5%2C1.16421%201.83579%2C1.5%202.25%2C1.5%20L4.33333%2C1.5%20L5.5%2C1.5%20C6.05228%2C1.5%206.5%2C1.94772%206.5%2C2.5%20C6.5%2C3.05228%206.05228%2C3.5%205.5%2C3.5%20L3.66667%2C3.5%20L0.75%2C3.5%20C0.335786%2C3.5%200%2C3.83579%200%2C4.25%20C0%2C4.66421%200.335786%2C5%200.75%2C5%20L3.16667%2C5%20L3.75%2C5%20C4.16421%2C5%204.5%2C5.33579%204.5%2C5.75%20C4.5%2C6.16421%204.16421%2C6.5%203.75%2C6.5%20L2.66667%2C6.5%20L1.25%2C6.5%20C0.83579%2C6.5%200.5%2C6.83579%200.5%2C7.25%20C0.5%2C7.66421%200.83579%2C8%201.25%2C8%20L1.70802%2C8%20C1.57422%2C8.30623%201.5%2C8.64445%201.5%2C9%20C1.5%2C10.3807%202.61929%2C11.5%204%2C11.5%20C5.38071%2C11.5%206.5%2C10.3807%206.5%2C9%20C6.5%2C8.91563%206.49582%2C8.83223%206.48766%2C8.75%20L9.5123%2C8.75%20C9.5042%2C8.83223%209.5%2C8.91563%209.5%2C9%20C9.5%2C10.3807%2010.6193%2C11.5%2012%2C11.5%20C13.3807%2C11.5%2014.5%2C10.3807%2014.5%2C9%20C14.5%2C8.86265%2014.4889%2C8.7279%2014.4676%2C8.59657%20C15.0763%2C8.32258%2015.5%2C7.71077%2015.5%2C7%20L15.5%2C5.78078%20C15.5%2C4.97776%2014.9535%2C4.27779%2014.1744%2C4.08303%20L12.4922%2C3.66247%20C12.4055%2C3.6408%2012.337%2C3.57452%2012.3125%2C3.48862%20L11.8856%2C1.99452%20C11.5483%2C0.81394%2010.4692%2C0%209.2414%2C0%20L2.25%2C0%20Z%20M5%2C9%20C5%2C9.5523%204.55228%2C10%204%2C10%20C3.44772%2C10%203%2C9.5523%203%2C9%20C3%2C8.44772%203.44772%2C8%204%2C8%20C4.55228%2C8%205%2C8.44772%205%2C9%20Z%20M13%2C9%20C13%2C9.5523%2012.5523%2C10%2012%2C10%20C11.4477%2C10%2011%2C9.5523%2011%2C9%20C11%2C8.44772%2011.4477%2C8%2012%2C8%20C12.5523%2C8%2013%2C8.44772%2013%2C9%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavSettingActiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%2021%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-208.000000%2C%20-76.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-21%22%20transform%3D%22translate%28208.000000%2C%2076.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%E5%A4%87%E4%BB%BD-7%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22Active%3Dyes-%282%29%22%20transform%3D%22translate%283.000000%2C%203.000000%29%22%20fill%3D%22%234A4A4A%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M9.50024364%2C7.5%20C9.50024364%2C8.8807%208.38094364%2C10%207.00024364%2C10%20C5.61953364%2C10%204.50024364%2C8.8807%204.50024364%2C7.5%20C4.50024364%2C6.11929%205.61953364%2C5%207.00024364%2C5%20C8.38094364%2C5%209.50024364%2C6.11929%209.50024364%2C7.5%20Z%20M8.00024364%2C7.5%20C8.00024364%2C8.0523%207.55254364%2C8.5%207.00024364%2C8.5%20C6.44796364%2C8.5%206.00024364%2C8.0523%206.00024364%2C7.5%20C6.00024364%2C6.94772%206.44796364%2C6.5%207.00024364%2C6.5%20C7.55254364%2C6.5%208.00024364%2C6.94772%208.00024364%2C7.5%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M6.37686364%2C0%20C5.45122364%2C0%204.70084364%2C0.75038%204.70084364%2C1.67601%20L4.70084364%2C2.36371%20C4.70084364%2C2.42017%204.65861364%2C2.53405%204.50364364%2C2.61502%20C4.34979364%2C2.6954%204.20033364%2C2.78295%204.05572364%2C2.87722%20C3.90801364%2C2.97352%203.78677364%2C2.95337%203.73738364%2C2.92486%20L3.13757364%2C2.57856%20C2.33595364%2C2.11574%201.31091364%2C2.3904%200.848103644%2C3.19202%20L0.224823644%2C4.27157%20C-0.237996356%2C5.07319%200.0366636442%2C6.09823%200.838283644%2C6.56105%20L1.48577364%2C6.93487%20C1.53442364%2C6.96296%201.61065364%2C7.05468%201.60515364%2C7.2258%20C1.60327364%2C7.28431%201.60232364%2C7.34302%201.60232364%2C7.40193%20C1.60232364%2C7.49837%201.60486364%2C7.5943%201.60988364%2C7.6896%20C1.61913364%2C7.8654%201.54111364%2C7.9604%201.49164364%2C7.9889%20L0.838293644%2C8.3661%20C0.0366636442%2C8.829%20-0.237986356%2C9.854%200.224833644%2C10.6556%20L0.848103644%2C11.7352%20C1.31092364%2C12.5368%202.33596364%2C12.8114%203.13758364%2C12.3486%20L3.83803364%2C11.9442%20C3.88655364%2C11.9162%204.00425364%2C11.8959%204.15016364%2C11.9868%20C4.26514364%2C12.0584%204.38304364%2C12.1258%204.50364364%2C12.1888%20C4.65861364%2C12.2698%204.70084364%2C12.3837%204.70084364%2C12.4401%20L4.70084364%2C13.2512%20C4.70084364%2C14.1768%205.45122364%2C14.9272%206.37686364%2C14.9272%20L7.62344364%2C14.9272%20C8.54904364%2C14.9272%209.29944364%2C14.1768%209.29944364%2C13.2512%20L9.29944364%2C12.4402%20C9.29944364%2C12.3837%209.34164364%2C12.2699%209.49664364%2C12.1889%20C9.61724364%2C12.1259%209.73514364%2C12.0585%209.85014364%2C11.9868%20C9.99604364%2C11.896%2010.1137436%2C11.9163%2010.1622436%2C11.9443%20L10.8626436%2C12.3486%20C11.6642436%2C12.8114%2012.6893436%2C12.5368%2013.1521436%2C11.7352%20L13.7754436%2C10.6556%20C14.2382436%2C9.854%2013.9635436%2C8.829%2013.1619436%2C8.3661%20L12.5087436%2C7.989%20C12.4592436%2C7.9604%2012.3812436%2C7.8655%2012.3905436%2C7.6897%20C12.3955436%2C7.5944%2012.3980436%2C7.4984%2012.3980436%2C7.40193%20C12.3980436%2C7.34299%2012.3971436%2C7.28425%2012.3952436%2C7.22572%20C12.3897436%2C7.0546%2012.4659436%2C6.96288%2012.5146436%2C6.93479%20L13.1619436%2C6.56105%20C13.9635436%2C6.09823%2014.2382436%2C5.07319%2013.7754436%2C4.27157%20L13.1521436%2C3.19202%20C12.6893436%2C2.3904%2011.6643436%2C2.11574%2010.8626436%2C2.57856%20L10.2629436%2C2.92481%20C10.2135436%2C2.95332%2010.0923436%2C2.97347%209.94464364%2C2.87718%20C9.79994364%2C2.7829%209.65054364%2C2.69534%209.49664364%2C2.61496%20C9.34164364%2C2.53399%209.29944364%2C2.42011%209.29944364%2C2.36364%20L9.29944364%2C1.67601%20C9.29944364%2C0.75038%208.54904364%2C0%207.62344364%2C0%20L6.37686364%2C0%20Z%20M6.20084364%2C1.67601%20C6.20084364%2C1.5788%206.27965364%2C1.5%206.37686364%2C1.5%20L7.62344364%2C1.5%20C7.72064364%2C1.5%207.79944364%2C1.5788%207.79944364%2C1.67601%20L7.79944364%2C2.36364%20C7.79944364%2C3.0921%208.26134364%2C3.66196%208.80204364%2C3.94446%20C8.91304364%2C4.00245%209.02094364%2C4.06566%209.12544364%2C4.13377%20C9.64244364%2C4.47075%2010.3752436%2C4.59204%2011.0129436%2C4.22385%20L11.6126436%2C3.8776%20C11.6968436%2C3.82899%2011.8045436%2C3.85784%2011.8531436%2C3.94202%20L12.4763436%2C5.02157%20C12.5249436%2C5.10576%2012.4961436%2C5.2134%2012.4119436%2C5.26201%20L11.7646436%2C5.63575%20C11.1414436%2C5.99556%2010.8765436%2C6.66964%2010.8960436%2C7.27392%20C10.8973436%2C7.3164%2010.8980436%2C7.35907%2010.8980436%2C7.40193%20C10.8980436%2C7.47209%2010.8962436%2C7.5417%2010.8925436%2C7.6109%20C10.8601436%2C8.2264%2011.1219436%2C8.9204%2011.7587436%2C9.288%20L12.4119436%2C9.6652%20C12.4961436%2C9.7138%2012.5249436%2C9.8214%2012.4763436%2C9.9056%20L11.8531436%2C10.9852%20C11.8045436%2C11.0693%2011.6968436%2C11.0982%2011.6126436%2C11.0496%20L10.9122436%2C10.6452%20C10.2879436%2C10.2848%209.57084364%2C10.3937%209.05724364%2C10.7136%20C8.97424364%2C10.7653%208.88904364%2C10.8139%208.80204364%2C10.8594%20C8.26134364%2C11.1419%207.79944364%2C11.7117%207.79944364%2C12.4402%20L7.79944364%2C13.2512%20C7.79944364%2C13.3484%207.72064364%2C13.4272%207.62344364%2C13.4272%20L6.37686364%2C13.4272%20C6.27965364%2C13.4272%206.20084364%2C13.3484%206.20084364%2C13.2512%20L6.20084364%2C12.4401%20C6.20084364%2C11.7117%205.73896364%2C11.1419%205.19824364%2C10.8593%20C5.11124364%2C10.8139%205.02614364%2C10.7652%204.94310364%2C10.7135%20C4.42950364%2C10.3937%203.71236364%2C10.2847%203.08803364%2C10.6452%20L2.38758364%2C11.0496%20C2.30339364%2C11.0982%202.19575364%2C11.0693%202.14714364%2C10.9852%20L1.52386364%2C9.9056%20C1.47526364%2C9.8214%201.50410364%2C9.7138%201.58829364%2C9.6652%20L2.24164364%2C9.288%20C2.87843364%2C8.9203%203.14021364%2C8.2263%203.10781364%2C7.6108%20C3.10417364%2C7.5417%203.10232364%2C7.47207%203.10232364%2C7.40193%20C3.10232364%2C7.35909%203.10301364%2C7.31643%203.10438364%2C7.27398%20C3.12380364%2C6.66971%202.85898364%2C5.99564%202.23577364%2C5.63583%20L1.58828364%2C5.26201%20C1.50410364%2C5.2134%201.47525364%2C5.10576%201.52386364%2C5.02157%20L2.14713364%2C3.94202%20C2.19574364%2C3.85784%202.30339364%2C3.82899%202.38757364%2C3.8776%20L2.98738364%2C4.2239%20C3.62511364%2C4.59209%204.35794364%2C4.47079%204.87488364%2C4.1338%20C4.97936364%2C4.06569%205.08725364%2C4.00249%205.19824364%2C3.9445%20C5.73896364%2C3.662%206.20084364%2C3.09215%206.20084364%2C2.36371%20L6.20084364%2C1.67601%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavSettingInactiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%2012%E5%A4%87%E4%BB%BD%202%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-188.000000%2C%20-76.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-12%E5%A4%87%E4%BB%BD-2%22%20transform%3D%22translate%28188.000000%2C%2076.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22SettingsFilled%22%20transform%3D%22translate%283.300000%2C%203.000000%29%22%20fill%3D%22%234A4A4A%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M4.70079468%2C1.37601%20C4.70079468%2C0.61606%205.31685468%2C0%206.07680468%2C0%20L7.32339468%2C0%20C8.08329468%2C0%208.69939468%2C0.61606%208.69939468%2C1.37601%20L8.69939468%2C2.06364%20C8.69939468%2C2.25451%208.82559468%2C2.45959%209.05769468%2C2.58086%20C9.20299468%2C2.65676%209.34409468%2C2.73945%209.48069468%2C2.8285%20C9.70229468%2C2.97293%209.94579468%2C2.98107%2010.1128947%2C2.88462%20L10.7125947%2C2.53836%20C11.3707947%2C2.15839%2012.2122947%2C2.38388%2012.5922947%2C3.04202%20L13.2155947%2C4.12157%20C13.5954947%2C4.77971%2013.3700947%2C5.62126%2012.7118947%2C6.00124%20L12.0645947%2C6.37498%20C11.9009947%2C6.46941%2011.7870947%2C6.67761%2011.7953947%2C6.93536%20C11.7970947%2C6.99068%2011.7979947%2C7.04621%2011.7979947%2C7.10192%20C11.7979947%2C7.19313%2011.7955947%2C7.28385%2011.7908947%2C7.37395%20C11.7769947%2C7.63765%2011.8917947%2C7.85245%2012.0586947%2C7.94885%20L12.7118947%2C8.32595%20C13.3699947%2C8.70595%2013.5954947%2C9.54745%2013.2155947%2C10.20565%20L12.5922947%2C11.28515%20C12.2122947%2C11.94325%2011.3707947%2C12.16875%2010.7125947%2C11.78885%20L10.0121947%2C11.38445%20C9.84859468%2C11.28995%209.61099468%2C11.29555%209.39149468%2C11.43215%20C9.28289468%2C11.49985%209.17159468%2C11.56345%209.05769468%2C11.62295%20C8.82559468%2C11.74425%208.69939468%2C11.94935%208.69939468%2C12.14025%20L8.69939468%2C12.95115%20C8.69939468%2C13.71115%208.08329468%2C14.32715%207.32339468%2C14.32715%20L6.07681468%2C14.32715%20C5.31685468%2C14.32715%204.70079468%2C13.71115%204.70079468%2C12.95115%20L4.70079468%2C12.14015%20C4.70079468%2C11.94925%204.57463468%2C11.74425%204.34251468%2C11.62295%20C4.22863468%2C11.56345%204.11729468%2C11.49975%204.00869468%2C11.43215%20C3.78925468%2C11.29545%203.55166468%2C11.28995%203.38798468%2C11.38445%20L2.68753468%2C11.78885%20C2.02939468%2C12.16875%201.18784468%2C11.94325%200.807864681%2C11.28515%20L0.184584681%2C10.20565%20C-0.195385319%2C9.54745%200.0301046807%2C8.70595%200.688244681%2C8.32595%20L1.34158468%2C7.94875%20C1.50853468%2C7.85235%201.62330468%2C7.63765%201.60941468%2C7.37385%20C1.60467468%2C7.28375%201.60227468%2C7.19311%201.60227468%2C7.10192%20C1.60227468%2C7.04623%201.60317468%2C6.99073%201.60495468%2C6.93544%20C1.61323468%2C6.67769%201.49928468%2C6.4695%201.33572468%2C6.37506%20L0.688234681%2C6.00124%20C0.0300946807%2C5.62126%20-0.195395319%2C4.77971%200.184584681%2C4.12157%20L0.807854681%2C3.04202%20C1.18783468%2C2.38388%202.02938468%2C2.15839%202.68752468%2C2.53836%20L3.28733468%2C2.88467%20C3.45438468%2C2.98111%203.69795468%2C2.97297%203.91950468%2C2.82854%20C4.05609468%2C2.7395%204.19723468%2C2.65682%204.34251468%2C2.58091%20C4.57463468%2C2.45964%204.70079468%2C2.25457%204.70079468%2C2.0637%20L4.70079468%2C1.37601%20Z%20M6.70019468%2C8.69995%20C7.52859468%2C8.69995%208.20019468%2C8.02835%208.20019468%2C7.19995%20C8.20019468%2C6.37152%207.52859468%2C5.69995%206.70019468%2C5.69995%20C5.87176468%2C5.69995%205.20019468%2C6.37152%205.20019468%2C7.19995%20C5.20019468%2C8.02835%205.87176468%2C8.69995%206.70019468%2C8.69995%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavAccountManagementActiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%2024%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-1583.000000%2C%20-120.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-24%22%20transform%3D%22translate%281583.000000%2C%20120.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-12%E5%A4%87%E4%BB%BD-5%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20stroke%3D%22%23979797%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22ReceiptFolded%22%20transform%3D%22translate%284.000000%2C%203.000000%29%22%20fill%3D%22%234A4A4A%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M2%2C0%20C0.89543%2C0%200%2C0.89543%200%2C2%20L0%2C5.75%20C0%2C6.16421%200.33579%2C6.5%200.75%2C6.5%20L2.75%2C6.5%20L2.75%2C12.2686%20C2.75%2C13.0135%203.64012%2C13.3981%204.18256%2C12.8875%20L5.625%2C11.5299%20L7.236%2C13.0462%20C7.5248%2C13.318%207.9752%2C13.318%208.264%2C13.0462%20L9.875%2C11.5299%20L11.3174%2C12.8875%20C11.8599%2C13.3981%2012.75%2C13.0135%2012.75%2C12.2686%20L12.75%2C2%20C12.75%2C0.89543%2011.8546%2C0%2010.75%2C0%20L2.25571%2C0%20L2.26051%2C0.01681%20C2.17524%2C0.00572%202.08829%2C0%202%2C0%20Z%20M1.5%2C2%20C1.5%2C1.72386%201.72386%2C1.5%202%2C1.5%20C2.27614%2C1.5%202.5%2C1.72386%202.5%2C2%20L2.5%2C5%20L1.5%2C5%20L1.5%2C2%20Z%20M4.25%2C1.51996%20L4.2443%2C1.5%20L10.75%2C1.5%20C11.0261%2C1.5%2011.25%2C1.72386%2011.25%2C2%20L11.25%2C10.7642%20L10.5604%2C10.1151%20C10.1753%2C9.7527%209.5747%2C9.7527%209.1896%2C10.1151%20L7.75%2C11.4701%20L6.31037%2C10.1151%20C5.92532%2C9.7527%205.32469%2C9.7527%204.93964%2C10.1151%20L4.25%2C10.7642%20L4.25%2C1.51996%20Z%20M6.25%2C3%20C5.83579%2C3%205.5%2C3.33579%205.5%2C3.75%20C5.5%2C4.16421%205.83579%2C4.5%206.25%2C4.5%20L9.25%2C4.5%20C9.6642%2C4.5%2010%2C4.16421%2010%2C3.75%20C10%2C3.33579%209.6642%2C3%209.25%2C3%20L6.25%2C3%20Z%20M5.5%2C6.75%20C5.5%2C6.33579%205.83579%2C6%206.25%2C6%20L9.25%2C6%20C9.6642%2C6%2010%2C6.33579%2010%2C6.75%20C10%2C7.1642%209.6642%2C7.5%209.25%2C7.5%20L6.25%2C7.5%20C5.83579%2C7.5%205.5%2C7.1642%205.5%2C6.75%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavAccountManagementInactiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%2025%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-1563.000000%2C%20-120.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-25%22%20transform%3D%22translate%281563.000000%2C%20120.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-12%E5%A4%87%E4%BB%BD-4%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20stroke%3D%22%23979797%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22ReceiptFoldedFilled%E5%A4%87%E4%BB%BD%22%20transform%3D%22translate%284.000000%2C%203.000000%29%22%20fill%3D%22%234A4A4A%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M2%2C0%20C0.89543%2C0%200%2C0.89543%200%2C2%20L0%2C5.75%20C0%2C6.16421%200.33579%2C6.5%200.75%2C6.5%20L2.75%2C6.5%20L2.75%2C12.2686%20C2.75%2C13.0135%203.64012%2C13.3981%204.18256%2C12.8875%20L5.625%2C11.5299%20L7.236%2C13.0462%20C7.5248%2C13.318%207.9752%2C13.318%208.264%2C13.0462%20L9.875%2C11.5299%20L11.3174%2C12.8875%20C11.8599%2C13.3981%2012.75%2C13.0135%2012.75%2C12.2686%20L12.75%2C2%20C12.75%2C0.89543%2011.8546%2C0%2010.75%2C0%20L2.25571%2C0%20L2.26051%2C0.01681%20C2.17524%2C0.00572%202.08829%2C0%202%2C0%20Z%20M1.5%2C2%20C1.5%2C1.72386%201.72386%2C1.5%202%2C1.5%20C2.27614%2C1.5%202.5%2C1.72386%202.5%2C2%20L2.5%2C5%20L1.5%2C5%20L1.5%2C2%20Z%20M5.5%2C3.75%20C5.5%2C3.33579%205.83579%2C3%206.25%2C3%20L9.75%2C3%20C10.1642%2C3%2010.5%2C3.33579%2010.5%2C3.75%20C10.5%2C4.16421%2010.1642%2C4.5%209.75%2C4.5%20L6.25%2C4.5%20C5.83579%2C4.5%205.5%2C4.16421%205.5%2C3.75%20Z%20M6.25%2C6%20C5.83579%2C6%205.5%2C6.33579%205.5%2C6.75%20C5.5%2C7.1642%205.83579%2C7.5%206.25%2C7.5%20L9.75%2C7.5%20C10.1642%2C7.5%2010.5%2C7.1642%2010.5%2C6.75%20C10.5%2C6.33579%2010.1642%2C6%209.75%2C6%20L6.25%2C6%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavWechatActiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%2026%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-1648.000000%2C%20-120.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-26%22%20transform%3D%22translate%281648.000000%2C%20120.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84%22%20transform%3D%22translate%282.500000%2C%203.000000%29%22%20fill%3D%22%234A4A4A%22%20fill-rule%3D%22nonzero%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M4.97473991%2C6.15848713%20C4.43134625%2C6.15848713%204.00114949%2C5.73747126%204.00114949%2C5.20568123%20C4.00114949%2C4.67392084%204.43134625%2C4.25293461%204.97473991%2C4.25293461%20C5.51814115%2C4.25293461%205.94833033%2C4.67392084%205.94833033%2C5.20568123%20C5.94833033%2C5.73747126%205.51814115%2C6.15848713%204.97473991%2C6.15848713%20Z%20M10.0252298%2C6.15848713%20C9.48182097%2C6.15848713%209.05166967%2C5.73747126%209.05166967%2C5.20568123%20C9.05166967%2C4.67392084%209.48182097%2C4.25293461%2010.0252298%2C4.25293461%20C10.5686386%2C4.25293461%2010.9988657%2C4.67392084%2010.9988657%2C5.20568123%20C10.9988657%2C5.73747126%2010.5686386%2C6.15848713%2010.0252298%2C6.15848713%20Z%20M3.57749819%2C11.8668014%20L4.11955853%2C11.5434447%20C4.73297488%2C11.1775565%205.45937603%2C11.0366992%206.16909537%2C11.1460655%20C6.32932256%2C11.1707397%206.46106492%2C11.1897083%206.56273153%2C11.2028234%20C6.87045865%2C11.2425391%207.1834888%2C11.2626933%207.50000379%2C11.2626933%20C10.8490172%2C11.2626933%2013.4848493%2C9.02320303%2013.4848493%2C6.37232941%20C13.4848493%2C3.72141874%2010.8490172%2C1.48192846%207.50000379%2C1.48192846%20C4.15096002%2C1.48192846%201.51515075%2C3.72141874%201.51515075%2C6.37232941%20C1.51515075%2C7.38411606%201.89367329%2C8.3527786%202.59975626%2C9.17384106%20C2.63633958%2C9.2163724%202.6910441%2C9.27520496%202.76217285%2C9.34767127%20C3.36469527%2C9.96141194%203.66849815%2C10.7987756%203.59625576%2C11.646661%20L3.57749819%2C11.8668014%20Z%20M3.10020298%2C13.8874109%20C2.96158941%2C13.9721031%202.79867283%2C14.0107073%202.6357714%2C13.9974441%20C2.21882464%2C13.9635079%201.90898388%2C13.6053258%201.94372629%2C13.1974991%20L2.08633986%2C11.5235868%20C2.12246105%2C11.0996071%201.97055961%2C10.6809623%201.66930219%2C10.374129%20C1.5767492%2C10.2798043%201.50039318%2C10.1977054%201.44023412%2C10.1277584%20C0.534567912%2C9.07462595%200%2C7.77653072%200%2C6.37232941%20C0%2C2.85297162%203.35786194%2C0%207.5%2C0%20C11.6421229%2C0%2015%2C2.85297162%2015%2C6.37232941%20C15%2C9.89161311%2011.6421229%2C12.7446218%207.5%2C12.7446218%20C7.11401913%2C12.7446218%206.73485266%2C12.7197995%206.36454982%2C12.6720073%20C6.24886806%2C12.6571139%206.10508025%2C12.636441%205.93330761%2C12.6099145%20C5.578429%2C12.5552313%205.21523221%2C12.625697%204.90852782%2C12.8086411%20L3.10020298%2C13.8874109%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavWechatInactiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%2027%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-1628.000000%2C%20-120.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-27%22%20transform%3D%22translate%281628.000000%2C%20120.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-12%E5%A4%87%E4%BB%BD%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20stroke%3D%22%23979797%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84%22%20transform%3D%22translate%282.500000%2C%203.500000%29%22%20fill%3D%22%234A4A4A%22%20fill-rule%3D%22nonzero%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M2.59258991%2C10.7727159%20C1.01851398%2C9.65907386%200%2C7.97727273%200%2C6.13636932%20C0%2C2.74999489%203.35647164%2C0%207.5%2C0%20C11.6435284%2C0%2015%2C2.74999489%2015%2C6.13636932%20C15%2C9.52272159%2011.6435284%2C12.2727386%207.5%2C12.2727386%20C6.64350836%2C12.2727386%205.81017669%2C12.1590625%205.04628745%2C11.9317841%20C4.97684502%2C11.909108%204.88425012%2C11.909108%204.81480769%2C11.909108%20C4.67591532%2C11.909108%204.53703046%2C11.9545341%204.42129058%2C12.0227102%20L2.77777222%2C12.9545739%20C2.73147477%2C12.97725%202.68517731%2C13%202.63888736%2C13%20C2.5%2C13%202.38425512%2C12.8863977%202.38425512%2C12.7499716%20C2.38425512%2C12.6817955%202.40740009%2C12.6363693%202.43055257%2C12.5681932%20C2.45369755%2C12.5454432%202.66203234%2C11.7954318%202.77777222%2C11.340875%20C2.77777222%2C11.2954489%202.8009172%2C11.2272727%202.8009172%2C11.1818466%20C2.8009172%2C11%202.73147477%2C10.863642%202.59258991%2C10.7727159%20Z%20M4.99999%2C5.10501136%20C5.53795696%2C5.10501136%205.96384404%2C4.68535511%205.96384404%2C4.15525057%20C5.96384404%2C3.62514602%205.53795696%2C3.205475%204.99999%2C3.205475%20C4.46203054%2C3.205475%204.03613596%2C3.62514602%204.03613596%2C4.15525057%20C4.03613596%2C4.68535511%204.46203054%2C5.10501136%204.99999%2C5.10501136%20Z%20M9.99998%2C5.10501136%20C10.5379545%2C5.10501136%2010.963804%2C4.68535511%2010.963804%2C4.15525057%20C10.963804%2C3.62514602%2010.5379545%2C3.205475%209.99998%2C3.205475%20C9.46200554%2C3.205475%209.03615596%2C3.62514602%209.03615596%2C4.15525057%20C9.03615596%2C4.68535511%209.46200554%2C5.10501136%209.99998%2C5.10501136%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavMerchantPayIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%2023%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-1692.000000%2C%20-120.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-23%22%20transform%3D%22translate%281692.000000%2C%20120.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22CurrencyConvert%22%20transform%3D%22translate%283.000000%2C%204.000000%29%22%20fill%3D%22%234A4A4A%22%20fill-rule%3D%22nonzero%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M7.25%2C2.7499975%20C7.25%2C2.3357875%207.5858%2C2%208%2C2%20L11.6893%2C2%20L10.9697%2C1.2803275%20C10.6768%2C0.9874375%2010.6768%2C0.5125575%2010.9697%2C0.2196675%20C11.2626%2C-0.0732225%2011.7374%2C-0.0732225%2012.0303%2C0.2196675%20L14.0303%2C2.2196675%20C14.171%2C2.3603175%2014.25%2C2.5510875%2014.25%2C2.7499975%20C14.25%2C2.9489075%2014.171%2C3.1396775%2014.0303%2C3.2803275%20L12.0303%2C5.2803275%20C11.7374%2C5.5732175%2011.2626%2C5.5732175%2010.9697%2C5.2803275%20C10.6768%2C4.9874375%2010.6768%2C4.5125575%2010.9697%2C4.2196675%20L11.6893%2C3.5%20L8%2C3.5%20C7.5858%2C3.5%207.25%2C3.1642075%207.25%2C2.7499975%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M4.25%2C0.9999975%20C4.25%2C0.5857875%203.91421%2C0.2499975%203.5%2C0.2499975%20C3.08579%2C0.2499975%202.75%2C0.5857875%202.75%2C0.9999975%20L2.75%2C1.7499975%20L2.37509%2C1.7499975%20C1.06336%2C1.7499975%200%2C2.8133675%200%2C4.1250875%20C0%2C5.4368175%201.06337%2C6.5001975%202.37509%2C6.5001975%20L3.6545%2C6.5001975%20C4.1377%2C6.5001975%204.52941%2C6.8918975%204.52941%2C7.3750975%20C4.52941%2C7.8582975%204.1377%2C8.2499975%203.6545%2C8.2499975%20L0.75%2C8.2499975%20C0.33579%2C8.2499975%200%2C8.5857975%200%2C8.9999975%20C0%2C9.4141975%200.33579%2C9.7499975%200.75%2C9.7499975%20L1.75%2C9.7499975%20L1.75%2C10.4999975%20C1.75%2C10.9141975%202.08579%2C11.2499975%202.5%2C11.2499975%20C2.91421%2C11.2499975%203.25%2C10.9141975%203.25%2C10.4999975%20L3.25%2C9.7499975%20L3.6545%2C9.7499975%20C4.96613%2C9.7499975%206.02941%2C8.6866975%206.02941%2C7.3750975%20C6.02941%2C6.0634975%204.96613%2C5.0001875%203.6545%2C5.0001875%20L2.37509%2C5.0001875%20C1.89179%2C5.0001875%201.5%2C4.6083875%201.5%2C4.1250875%20C1.5%2C3.6417875%201.89179%2C3.2499975%202.37509%2C3.2499975%20L5.27941%2C3.2499975%20C5.69363%2C3.2499975%206.02941%2C2.9142075%206.02941%2C2.4999975%20C6.02941%2C2.0857875%205.69363%2C1.7499975%205.27941%2C1.7499975%20L4.25%2C1.7499975%20L4.25%2C0.9999975%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M9.8107%2C9.4999975%20L13.5%2C9.4999975%20C13.9142%2C9.4999975%2014.25%2C9.1641975%2014.25%2C8.7499975%20C14.25%2C8.3357975%2013.9142%2C7.9999975%2013.5%2C7.9999975%20L9.8107%2C7.9999975%20L10.5303%2C7.2802975%20C10.8232%2C6.9873975%2010.8232%2C6.5125975%2010.5303%2C6.2196975%20C10.2374%2C5.9267975%209.7626%2C5.9267975%209.4697%2C6.2196975%20L7.4697%2C8.2196975%20C7.1768%2C8.5125975%207.1768%2C8.9873975%207.4697%2C9.2802975%20L9.4697%2C11.2802975%20C9.7626%2C11.5731975%2010.2374%2C11.5731975%2010.5303%2C11.2802975%20C10.8232%2C10.9873975%2010.8232%2C10.5125975%2010.5303%2C10.2196975%20L9.8107%2C9.4999975%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavMiniProgramActiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%2028%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-1776.000000%2C%20-120.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-28%22%20transform%3D%22translate%281776.000000%2C%20120.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84%22%20transform%3D%22translate%283.000000%2C%203.000000%29%22%20fill%3D%22%234A4A4A%22%20fill-rule%3D%22nonzero%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M7%2C14%20C3.13400187%2C14%200%2C10.86596%200%2C7%20C0%2C3.134005%203.13400187%2C0%207%2C0%20C10.8659631%2C0%2014%2C3.134005%2014%2C7%20C14%2C10.86596%2010.8659631%2C14%207%2C14%20Z%20M7.000007%2C12.6%20C10.0928139%2C12.6%2012.6000014%2C10.09281%2012.6000014%2C7%20C12.6000014%2C3.907204%2010.0928139%2C1.4%207.000007%2C1.4%20C3.90720009%2C1.4%201.3999986%2C3.907204%201.3999986%2C7%20C1.3999986%2C10.09281%203.90720009%2C12.6%207.000007%2C12.6%20Z%20M7.7000063%2C8.4%20C7.7000063%2C9.75324%206.6031074%2C10.85014%205.24999475%2C10.85014%20C3.8968961%2C10.85014%202.7999972%2C9.75324%202.7999972%2C8.40014%20C2.7999972%2C7.43834%203.35964664%2C6.57755%204.21614478%2C6.17855%20C4.56657843%2C6.01524%204.98301502%2C6.167%205.14628285%2C6.51742%20C5.30955769%2C6.86784%205.15783284%2C7.28427%204.80740619%2C7.44751%20C4.43978056%2C7.6188%204.1999958%2C7.98763%204.1999958%2C8.40014%20C4.1999958%2C8.98009%204.67009433%2C9.45014%205.24999475%2C9.45014%20C5.82988817%2C9.45014%206.3000077%2C8.98009%206.3000077%2C8.40014%20L6.3000077%2C5.6%20C6.3000077%2C4.2469%207.3969066%2C3.15%208.75000525%2C3.15%20C10.1031039%2C3.15%2011.2000028%2C4.2469%2011.2000028%2C5.6%20C11.2000028%2C6.56187%2010.6403534%2C7.42259%209.78383422%2C7.82166%20C9.43341457%2C7.9849%209.01698498%2C7.83321%208.85367515%2C7.48279%20C8.69043531%2C7.1323%208.84212516%2C6.71587%209.19254481%2C6.55263%20C9.56018444%2C6.38134%209.7999972%2C6.01258%209.7999972%2C5.6%20C9.7999972%2C5.020099%209.32988467%2C4.55%208.75000525%2C4.55%20C8.17005583%2C4.55%207.7000063%2C5.020099%207.7000063%2C5.6%20L7.7000063%2C8.4%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavMiniProgramInactiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%2022%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-1756.000000%2C%20-120.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-22%22%20transform%3D%22translate%281756.000000%2C%20120.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%E5%A4%87%E4%BB%BD-9%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84%22%20transform%3D%22translate%283.000000%2C%203.000000%29%22%20fill%3D%22%234A4A4A%22%20fill-rule%3D%22nonzero%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M9.68779431%2C7.4837%20C9.67225433%2C7.48825%209.65692434%2C7.49301%209.64131436%2C7.49728%20C9.54604445%2C7.5243%209.44839455%2C7.54089%209.35158465%2C7.54089%20C8.92444508%2C7.54089%208.68322532%2C7.25235%208.81279519%2C6.8964%20C8.90757509%2C6.63607%209.15628484%2C6.41641%209.45994454%2C6.3147%20C9.93048407%2C6.14306%2010.2592737%2C5.75169%2010.2592737%2C5.296284%20C10.2592737%2C4.682636%209.66231434%2C4.185167%208.92591507%2C4.185167%20C8.18951581%2C4.185167%207.59255641%2C4.682636%207.59255641%2C5.296284%20L7.59255641%2C8.70373%20C7.59255641%2C9.52322%207.11781688%2C10.24177%206.40738759%2C10.64819%20C6.02014798%2C10.86974%205.56400044%2C11.00001%205.07410593%2C11.00001%20C3.68533531%2C11.00001%202.55557144%2C9.96989%202.55557144%2C8.70373%20C2.55557144%2C8.30067%202.67080533%2C7.92218%202.87161413%2C7.59262%20C3.18238582%2C7.08253%203.7002243%2C6.69193%204.32045868%2C6.51385%20C4.42655057%2C6.48088%204.53500146%2C6.46275%204.64193336%2C6.46275%20C5.07096993%2C6.46275%205.31330269%2C6.75262%205.18317982%2C7.11018%20C5.0950009%2C7.35245%204.85752614%2C7.56112%204.58065542%2C7.67207%20C4.54758745%2C7.68313%204.51540848%2C7.69552%204.48364952%2C7.70854%20C4.04385396%2C7.89012%203.74076126%2C8.26693%203.74076126%2C8.70373%20C3.74076126%2C9.31735%204.33769266%2C9.81477%205.07410593%2C9.81477%20C5.81042819%2C9.81477%206.40738759%2C9.31735%206.40738759%2C8.70373%20L6.40738759%2C5.296284%20C6.40738759%2C4.476759%206.88212712%2C3.758216%207.59255641%2C3.351747%20C7.97986602%2C3.130204%208.43598556%2C2.999969%208.92591507%2C2.999969%20C10.3146437%2C2.999969%2011.4444426%2C4.030068%2011.4444426%2C5.296284%20C11.4444426%2C5.69926%2011.3291527%2C6.07782%2011.1283929%2C6.40738%20C10.8189232%2C6.91523%2010.3042837%2C7.3045%209.68779431%2C7.4837%20Z%20M0%2C7%20C0%2C10.86596%203.13400887%2C14%207%2C14%20C10.8659631%2C14%2014%2C10.86596%2014%2C7%20C14%2C3.134012%2010.8659631%2C0%207%2C0%20C3.13400887%2C0%200%2C3.134012%200%2C7%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavInvoiceActiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%2034%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-1456.000000%2C%20-164.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-34%22%20transform%3D%22translate%281456.000000%2C%20164.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%E5%A4%87%E4%BB%BD-11%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22Receipt-%281%29%E5%A4%87%E4%BB%BD-2%22%20transform%3D%22translate%284.000000%2C%202.500000%29%22%20fill%3D%22%234A4A4A%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M3.75%2C2.5%20C3.33579%2C2.5%203%2C2.83579%203%2C3.25%20C3%2C3.66421%203.33579%2C4%203.75%2C4%20L8.25%2C4%20C8.6642%2C4%209%2C3.66421%209%2C3.25%20C9%2C2.83579%208.6634%2C2.5%208.2492%2C2.5%20L3.75%2C2.5%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%20fill-rule%3D%22nonzero%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M3%2C6.25%20C3%2C5.83579%203.33579%2C5.5%203.75%2C5.5%20L8.2492%2C5.5%20C8.6634%2C5.5%209%2C5.83579%209%2C6.25%20C9%2C6.66421%208.6642%2C7%208.25%2C7%20L3.75%2C7%20C3.33579%2C7%203%2C6.66421%203%2C6.25%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%20fill-rule%3D%22nonzero%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M3.75%2C8.5%20C3.33579%2C8.5%203%2C8.8358%203%2C9.25%20C3%2C9.6642%203.33579%2C10%203.75%2C10%20L6.2199%2C10%20C6.6341%2C10%206.9699%2C9.6642%206.9699%2C9.25%20C6.9699%2C8.8358%206.6332%2C8.5%206.219%2C8.5%20L3.75%2C8.5%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%20fill-rule%3D%22nonzero%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M0%2C13.5%20C0%2C14.121%200.38275%2C14.6779%200.9626%2C14.9004%20C1.54245%2C15.1229%202.19945%2C14.9651%202.61493%2C14.5034%20L3.74999%2C13.2423%20L4.88506%2C14.5034%20C5.16952%2C14.8195%205.57477%2C15.0000348%206%2C15.0000348%20C6.4252%2C15.0000348%206.8305%2C14.8195%207.1149%2C14.5034%20L8.25%2C13.2423%20L9.3851%2C14.5034%20C9.8005%2C14.9651%2010.4576%2C15.1229%2011.0374%2C14.9004%20C11.6172%2C14.6779%2012%2C14.121%2012%2C13.5%20L12%2C2.5%20C12%2C1.11929%2010.8807%2C0%209.5%2C0%20L2.5%2C0%20C1.11929%2C0%200%2C1.11929%200%2C2.5%20L0%2C13.5%20Z%20M2.5%2C1.5%20C1.94772%2C1.5%201.5%2C1.94772%201.5%2C2.5%20L1.5%2C13.5%20L3.00671%2C11.8259%20C3.40395%2C11.3845%204.09605%2C11.3845%204.49329%2C11.8259%20L6%2C13.5%20L7.5067%2C11.8259%20C7.904%2C11.3845%208.5961%2C11.3845%208.9933%2C11.8259%20L10.5%2C13.5%20L10.5%2C2.5%20C10.5%2C1.94772%2010.0523%2C1.5%209.5%2C1.5%20L2.5%2C1.5%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavInvoiceInactiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%2031%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-1436.000000%2C%20-164.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-31%22%20transform%3D%22translate%281436.000000%2C%20164.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%E5%A4%87%E4%BB%BD-10%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22Receipt-%281%29%E5%A4%87%E4%BB%BD%22%20transform%3D%22translate%284.000000%2C%202.500000%29%22%20fill%3D%22%234A4A4A%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M9.5%2C0%20C10.8807%2C0%2012%2C1.11929%2012%2C2.5%20L12%2C13.5%20C12%2C14.121%2011.6172%2C14.6779%2011.0374%2C14.9004%20C10.4576%2C15.1229%209.8005%2C14.9651%209.3851%2C14.5034%20L8.25%2C13.2423%20L7.1149%2C14.5034%20C6.8305%2C14.8195%206.4252%2C15.0000348%206%2C15.0000348%20C5.57477%2C15.0000348%205.16952%2C14.8195%204.88506%2C14.5034%20L3.74999%2C13.2423%20L2.61493%2C14.5034%20C2.19945%2C14.9651%201.54245%2C15.1229%200.9626%2C14.9004%20C0.38275%2C14.6779%200%2C14.121%200%2C13.5%20L0%2C2.5%20C0%2C1.11929%201.11929%2C0%202.5%2C0%20Z%20M6.219%2C8.5%20L3.75%2C8.5%20C3.33579%2C8.5%203%2C8.8358%203%2C9.25%20C3%2C9.6642%203.33579%2C10%203.75%2C10%20L6.2199%2C10%20C6.6341%2C10%206.9699%2C9.6642%206.9699%2C9.25%20C6.9699%2C8.8358%206.6332%2C8.5%206.219%2C8.5%20Z%20M8.2492%2C5.5%20L3.75%2C5.5%20C3.33579%2C5.5%203%2C5.83579%203%2C6.25%20C3%2C6.66421%203.33579%2C7%203.75%2C7%20L8.25%2C7%20C8.6642%2C7%209%2C6.66421%209%2C6.25%20C9%2C5.83579%208.6634%2C5.5%208.2492%2C5.5%20Z%20M8.2492%2C2.5%20L3.75%2C2.5%20C3.33579%2C2.5%203%2C2.83579%203%2C3.25%20C3%2C3.66421%203.33579%2C4%203.75%2C4%20L8.25%2C4%20C8.6642%2C4%209%2C3.66421%209%2C3.25%20C9%2C2.83579%208.6634%2C2.5%208.2492%2C2.5%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavAddMemberActiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%2038%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-1520.000000%2C%20-164.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-38%22%20transform%3D%22translate%281520.000000%2C%20164.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22PersonAdd%E5%A4%87%E4%BB%BD%22%20transform%3D%22translate%283.000000%2C%202.000000%29%22%20fill%3D%22%234A4A4A%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M13.3408815%2C0.75%20C13.3408815%2C0.33579%2013.0050815%2C0%2012.5908815%2C0%20C12.1766815%2C0%2011.8408815%2C0.33579%2011.8408815%2C0.75%20L11.8408815%2C1.75%20L10.8408815%2C1.75%20C10.4266815%2C1.75%2010.0908815%2C2.08579%2010.0908815%2C2.5%20C10.0908815%2C2.91421%2010.4266815%2C3.25%2010.8408815%2C3.25%20L11.8408815%2C3.25%20L11.8408815%2C4.25%20C11.8408815%2C4.66421%2012.1766815%2C5%2012.5908815%2C5%20C13.0050815%2C5%2013.3408815%2C4.66421%2013.3408815%2C4.25%20L13.3408815%2C3.25%20L14.3408815%2C3.25%20C14.7550815%2C3.25%2015.0908815%2C2.91421%2015.0908815%2C2.5%20C15.0908815%2C2.08579%2014.7550815%2C1.75%2014.3408815%2C1.75%20L13.3408815%2C1.75%20L13.3408815%2C0.75%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%20fill-rule%3D%22nonzero%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M6.09088152%2C2%20C4.15788152%2C2%202.59088152%2C3.567%202.59088152%2C5.5%20C2.59088152%2C7.433%204.15788152%2C9%206.09088152%2C9%20C8.02388152%2C9%209.59088152%2C7.433%209.59088152%2C5.5%20C9.59088152%2C3.567%208.02388152%2C2%206.09088152%2C2%20Z%20M4.09088152%2C5.5%20C4.09088152%2C4.39543%204.98631152%2C3.5%206.09088152%2C3.5%20C7.19548152%2C3.5%208.09088152%2C4.39543%208.09088152%2C5.5%20C8.09088152%2C6.60457%207.19548152%2C7.5%206.09088152%2C7.5%20C4.98631152%2C7.5%204.09088152%2C6.60457%204.09088152%2C5.5%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M11.5747815%2C13.2268%20C10.4683815%2C11.2352%208.36918152%2C10%206.09087152%2C10%20C3.81257152%2C10%201.71335152%2C11.2352%200.606911517%2C13.2268%20L0.170251517%2C14.0128%20C-0.325058483%2C14.9043%200.319631517%2C16%201.33955152%2C16%20L10.8421815%2C16%20C11.8620815%2C16%2012.5067815%2C14.9043%2012.0114815%2C14.0128%20L11.5747815%2C13.2268%20Z%20M1.91815152%2C13.9552%20C2.76003152%2C12.4398%204.35732152%2C11.5%206.09087152%2C11.5%20C7.82438152%2C11.5%209.42168152%2C12.4398%2010.2635815%2C13.9552%20L10.5662815%2C14.5%20L1.61551152%2C14.5%20L1.91815152%2C13.9552%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavAddMemberInactiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%2036%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-1500.000000%2C%20-164.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-36%22%20transform%3D%22translate%281500.000000%2C%20164.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%E5%A4%87%E4%BB%BD-12%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22PersonAdd%22%20transform%3D%22translate%282.500000%2C%202.000000%29%22%20fill%3D%22%234A4A4A%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M13.3408815%2C0.75%20C13.3408815%2C0.33579%2013.0050815%2C0%2012.5908815%2C0%20C12.1766815%2C0%2011.8408815%2C0.33579%2011.8408815%2C0.75%20L11.8408815%2C1.75%20L10.8408815%2C1.75%20C10.4266815%2C1.75%2010.0908815%2C2.08579%2010.0908815%2C2.5%20C10.0908815%2C2.91421%2010.4266815%2C3.25%2010.8408815%2C3.25%20L11.8408815%2C3.25%20L11.8408815%2C4.25%20C11.8408815%2C4.66421%2012.1766815%2C5%2012.5908815%2C5%20C13.0050815%2C5%2013.3408815%2C4.66421%2013.3408815%2C4.25%20L13.3408815%2C3.25%20L14.3408815%2C3.25%20C14.7550815%2C3.25%2015.0908815%2C2.91421%2015.0908815%2C2.5%20C15.0908815%2C2.08579%2014.7550815%2C1.75%2014.3408815%2C1.75%20L13.3408815%2C1.75%20L13.3408815%2C0.75%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%20fill-rule%3D%22nonzero%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M6.09088152%2C2%20C4.15788152%2C2%202.59088152%2C3.567%202.59088152%2C5.5%20C2.59088152%2C7.433%204.15788152%2C9%206.09088152%2C9%20C8.02388152%2C9%209.59088152%2C7.433%209.59088152%2C5.5%20C9.59088152%2C3.567%208.02388152%2C2%206.09088152%2C2%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M11.5747815%2C13.2268%20C10.4683815%2C11.2352%208.36918152%2C10%206.09087152%2C10%20C3.81257152%2C10%201.71335152%2C11.2352%200.606911517%2C13.2268%20L0.170251517%2C14.0128%20C-0.325058483%2C14.9043%200.319631517%2C16%201.33955152%2C16%20L10.8421815%2C16%20C11.8620815%2C16%2012.5067815%2C14.9043%2012.0114815%2C14.0128%20L11.5747815%2C13.2268%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavArchiveActiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%207%E5%A4%87%E4%BB%BD%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-1456.000000%2C%20-120.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-7%E5%A4%87%E4%BB%BD%22%20transform%3D%22translate%281456.000000%2C%20120.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M6.9755%2C3.5%20C5.6124%2C3.5%204.455%2C4.49858%204.2552%2C5.84699%20L3.5945%2C10.3071%20C3.5316%2C10.7316%203.5%2C11.1602%203.5%2C11.5894%20L3.5%2C13.25%20C3.5%2C15.0449%204.9551%2C16.5%206.75%2C16.5%20L13.25%2C16.5%20C15.0449%2C16.5%2016.5%2C15.0449%2016.5%2C13.25%20L16.5%2C11.5894%20C16.5%2C11.1602%2016.4684%2C10.7316%2016.4055%2C10.3071%20L15.7448%2C5.84699%20C15.545%2C4.49858%2014.3876%2C3.5%2013.0245%2C3.5%20L6.9755%2C3.5%20Z%20M5.739%2C6.06681%20C5.8298%2C5.4539%206.3559%2C5%206.9755%2C5%20L13.0245%2C5%20C13.6441%2C5%2014.1702%2C5.4539%2014.261%2C6.06681%20L14.8437%2C10%20L12.3604%2C10%20C11.8223%2C10%2011.3447%2C10.3443%2011.1745%2C10.8547%20L11.0164%2C11.3291%20C10.9824%2C11.4311%2010.8868%2C11.5%2010.7792%2C11.5%20L9.2208%2C11.5%20C9.1132%2C11.5%209.0176%2C11.4311%208.9836%2C11.3291%20L8.8255%2C10.8547%20C8.6553%2C10.3443%208.1777%2C10%207.6396%2C10%20L5.1563%2C10%20L5.739%2C6.06681%20Z%20M5.0006%2C11.5%20C5.0002%2C11.5298%205%2C11.5596%205%2C11.5894%20L5%2C13.25%20C5%2C14.2165%205.7835%2C15%206.75%2C15%20L13.25%2C15%20C14.2165%2C15%2015%2C14.2165%2015%2C13.25%20L15%2C11.5894%20C15%2C11.5596%2014.9998%2C11.5298%2014.9994%2C11.5%20L12.5406%2C11.5%20L12.4394%2C11.8034%20C12.2012%2C12.518%2011.5325%2C13%2010.7792%2C13%20L9.2208%2C13%20C8.4675%2C13%207.7988%2C12.518%207.5606%2C11.8034%20L7.4594%2C11.5%20L5.0006%2C11.5%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%20fill%3D%22%234A4A4A%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavArchiveInactiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%207%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-1436.000000%2C%20-120.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-7%22%20transform%3D%22translate%281436.000000%2C%20120.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220.0277622768%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M4.25522%2C5.84699%20C4.45499%2C4.49858%205.6124%2C3.5%206.97553%2C3.5%20L13.0245%2C3.5%20C14.3876%2C3.5%2015.545%2C4.49858%2015.7448%2C5.84699%20L16.4055%2C10.3071%20C16.4684%2C10.7316%2016.5%2C11.1602%2016.5%2C11.5894%20L16.5%2C13.25%20C16.5%2C15.0449%2015.0449%2C16.5%2013.25%2C16.5%20L6.75%2C16.5%20C4.95507%2C16.5%203.5%2C15.0449%203.5%2C13.25%20L3.5%2C11.5894%20C3.5%2C11.1602%203.53158%2C10.7316%203.59447%2C10.3071%20L4.25522%2C5.84699%20Z%20M6.97553%2C5%20C6.35593%2C5%205.82983%2C5.4539%205.73903%2C6.06681%20L5.15633%2C10%20L7.63962%2C10%20C8.17766%2C10%208.65533%2C10.3443%208.82547%2C10.8547%20L8.98359%2C11.3291%20C9.01762%2C11.4311%209.11315%2C11.5%209.22076%2C11.5%20L10.7792%2C11.5%20C10.8868%2C11.5%2010.9824%2C11.4311%2011.0164%2C11.3291%20L11.1745%2C10.8547%20C11.3447%2C10.3443%2011.8223%2C10%2012.3604%2C10%20L14.8437%2C10%20L14.261%2C6.06681%20C14.1702%2C5.4539%2013.6441%2C5%2013.0245%2C5%20L6.97553%2C5%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%20fill%3D%22%234A4A4A%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavPurchaseActiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%207%E5%A4%87%E4%BB%BD%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-1456.000000%2C%20-120.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-7%E5%A4%87%E4%BB%BD%22%20transform%3D%22translate%281456.000000%2C%20120.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M6.9755%2C3.5%20C5.6124%2C3.5%204.455%2C4.49858%204.2552%2C5.84699%20L3.5945%2C10.3071%20C3.5316%2C10.7316%203.5%2C11.1602%203.5%2C11.5894%20L3.5%2C13.25%20C3.5%2C15.0449%204.9551%2C16.5%206.75%2C16.5%20L13.25%2C16.5%20C15.0449%2C16.5%2016.5%2C15.0449%2016.5%2C13.25%20L16.5%2C11.5894%20C16.5%2C11.1602%2016.4684%2C10.7316%2016.4055%2C10.3071%20L15.7448%2C5.84699%20C15.545%2C4.49858%2014.3876%2C3.5%2013.0245%2C3.5%20L6.9755%2C3.5%20Z%20M5.739%2C6.06681%20C5.8298%2C5.4539%206.3559%2C5%206.9755%2C5%20L13.0245%2C5%20C13.6441%2C5%2014.1702%2C5.4539%2014.261%2C6.06681%20L14.8437%2C10%20L12.3604%2C10%20C11.8223%2C10%2011.3447%2C10.3443%2011.1745%2C10.8547%20L11.0164%2C11.3291%20C10.9824%2C11.4311%2010.8868%2C11.5%2010.7792%2C11.5%20L9.2208%2C11.5%20C9.1132%2C11.5%209.0176%2C11.4311%208.9836%2C11.3291%20L8.8255%2C10.8547%20C8.6553%2C10.3443%208.1777%2C10%207.6396%2C10%20L5.1563%2C10%20L5.739%2C6.06681%20Z%20M5.0006%2C11.5%20C5.0002%2C11.5298%205%2C11.5596%205%2C11.5894%20L5%2C13.25%20C5%2C14.2165%205.7835%2C15%206.75%2C15%20L13.25%2C15%20C14.2165%2C15%2015%2C14.2165%2015%2C13.25%20L15%2C11.5894%20C15%2C11.5596%2014.9998%2C11.5298%2014.9994%2C11.5%20L12.5406%2C11.5%20L12.4394%2C11.8034%20C12.2012%2C12.518%2011.5325%2C13%2010.7792%2C13%20L9.2208%2C13%20C8.4675%2C13%207.7988%2C12.518%207.5606%2C11.8034%20L7.4594%2C11.5%20L5.0006%2C11.5%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%20fill%3D%22%234A4A4A%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavPurchaseInactiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%206%E5%A4%87%E4%BB%BD%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-1628.000000%2C%20-76.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-6%E5%A4%87%E4%BB%BD%22%20transform%3D%22translate%281628.000000%2C%2076.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-33%22%20transform%3D%22translate%282.500000%2C%202.500000%29%22%20fill%3D%22%234A4A4A%22%20fill-rule%3D%22nonzero%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M0.75%2C0%20C0.33579%2C0%200%2C0.33579%200%2C0.75%20C0%2C1.16421%200.33579%2C1.5%200.75%2C1.5%20L2.36199%2C1.5%20C2.48863%2C1.5%202.59526%2C1.59469%202.61023%2C1.72045%20L3.65028%2C10.4569%20C3.75508%2C11.3371%204.50154%2C12%205.38801%2C12%20L11.75%2C12%20C12.1642%2C12%2012.5%2C11.6642%2012.5%2C11.25%20C12.5%2C10.8358%2012.1642%2C10.5%2011.75%2C10.5%20L5.38801%2C10.5%20C5.26137%2C10.5%205.15474%2C10.4053%205.13977%2C10.2796%20L5.04696%2C9.5%20L11.3979%2C9.5%20C12.8349%2C9.5%2014.0296%2C8.3937%2014.1398%2C6.96092%20L14.4978%2C2.30752%20C14.5138%2C2.09923%2014.4422%2C1.89369%2014.3003%2C1.74041%20C14.1583%2C1.58713%2013.9589%2C1.5%2013.75%2C1.5%20L4.09405%2C1.5%20C3.9704%2C0.6413%203.23393%2C0%202.36199%2C0%20L0.75%2C0%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M6.5%2C15%20C7.05228%2C15%207.5%2C14.5523%207.5%2C14%20C7.5%2C13.4477%207.05228%2C13%206.5%2C13%20C5.94772%2C13%205.5%2C13.4477%205.5%2C14%20C5.5%2C14.5523%205.94772%2C15%206.5%2C15%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M12.5%2C14%20C12.5%2C14.5523%2012.0523%2C15%2011.5%2C15%20C10.9477%2C15%2010.5%2C14.5523%2010.5%2C14%20C10.5%2C13.4477%2010.9477%2C13%2011.5%2C13%20C12.0523%2C13%2012.5%2C13.4477%2012.5%2C14%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavProduceActiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%2037%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-1712.000000%2C%20-76.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-37%22%20transform%3D%22translate%281712.000000%2C%2076.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M11.25%2C5.33333333%20C11.25%2C4.59694933%2011.8096%2C4%2012.5%2C4%20L15.75%2C4%20C16.4404%2C4%2017%2C4.59694933%2017%2C5.33333333%20L17%2C14.6666667%20C17%2C15.4030933%2016.4404%2C16%2015.75%2C16%20L4.25%2C16%20C3.5596%2C16%203%2C15.4030933%203%2C14.6666667%20L3%2C11.7333333%20C3%2C10.9969067%203.5596%2C10.4%204.25%2C10.4%20L7%2C10.4%20L7%2C8.53333333%20C7%2C7.79694933%207.5596%2C7.2%208.25%2C7.2%20L11.25%2C7.2%20L11.25%2C5.33333333%20Z%20M13.5%2C8%20L13.5%2C5.6%20L15.5%2C5.6%20L15.5%2C14.4%20L5.25%2C14.4%20L5.25%2C12%20L8.5%2C12%20C8.9142%2C12%209.25%2C11.6418133%209.25%2C11.2%20L9.25%2C8.8%20L12.75%2C8.8%20C13.1642%2C8.8%2013.5%2C8.441824%2013.5%2C8%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%20fill%3D%22%234A4A4A%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavProduceInactiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%209%E5%A4%87%E4%BB%BD%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-1692.000000%2C%20-76.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-9%E5%A4%87%E4%BB%BD%22%20transform%3D%22translate%281692.000000%2C%2076.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M11.25%2C5.33333333%20C11.25%2C4.59694933%2011.8096%2C4%2012.5%2C4%20L15.75%2C4%20C16.4404%2C4%2017%2C4.59694933%2017%2C5.33333333%20L17%2C14.6666667%20C17%2C15.4030933%2016.4404%2C16%2015.75%2C16%20L4.25%2C16%20C3.55964%2C16%203%2C15.4030933%203%2C14.6666667%20L3%2C11.7333333%20C3%2C10.9969067%203.55964%2C10.4%204.25%2C10.4%20L7%2C10.4%20L7%2C8.53333333%20C7%2C7.79694933%207.55964%2C7.2%208.25%2C7.2%20L11.25%2C7.2%20L11.25%2C5.33333333%20Z%20M12.75%2C5.6%20L12.75%2C8%20L14.25%2C8%20L14.25%2C5.6%20L12.75%2C5.6%20Z%20M8.5%2C8.8%20L10%2C8.8%20L10%2C11.2%20L8.5%2C11.2%20L8.5%2C8.8%20Z%20M4.5%2C12%20L6%2C12%20L6%2C14.4%20L4.5%2C14.4%20L4.5%2C12%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%20fill%3D%22%234A4A4A%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavStockActiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%2040%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-1776.000000%2C%20-76.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-40%22%20transform%3D%22translate%281776.000000%2C%2076.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-39%22%20transform%3D%22translate%283.000000%2C%203.000000%29%22%20fill%3D%22%234A4A4A%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M8.2652%2C0.389415%20C7.5016%2C-0.129805%206.4984%2C-0.129805%205.7348%2C0.389415%20L1.6413%2C3.172975%20C0.6146%2C3.871155%200%2C5.032305%200%2C6.273945%20L0%2C13.686035%20C0%2C14.100235%200.3358%2C14.436035%200.75%2C14.436035%20C1.1642%2C14.436035%201.5%2C14.100235%201.5%2C13.686035%20L1.5%2C6.273945%20C1.5%2C5.528965%201.8688%2C4.832275%202.4848%2C4.413365%20L6.5783%2C1.629805%20C6.8328%2C1.456735%207.1672%2C1.456735%207.4217%2C1.629805%20L11.5152%2C4.413365%20C12.1312%2C4.832275%2012.5%2C5.528955%2012.5%2C6.273945%20L12.5%2C13.686035%20C12.5%2C14.100235%2012.8358%2C14.436035%2013.25%2C14.436035%20C13.6642%2C14.436035%2014%2C14.100235%2014%2C13.686035%20L14%2C6.273945%20C14%2C5.032305%2013.3854%2C3.871155%2012.3587%2C3.172975%20L8.2652%2C0.389415%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%20fill-rule%3D%22nonzero%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M7%2C14.404535%20C6.9201%2C14.425135%206.8363%2C14.436035%206.75%2C14.436035%20L3.75%2C14.436035%20C3.1977%2C14.436035%202.75%2C13.988335%202.75%2C13.436035%20L2.75%2C9.936035%20C2.75%2C9.383735%203.1977%2C8.936035%203.75%2C8.936035%20L4.5%2C8.936035%20L4.5%2C5.936035%20C4.5%2C5.383745%204.9477%2C4.936035%205.5%2C4.936035%20L8.5%2C4.936035%20C9.0523%2C4.936035%209.5%2C5.383745%209.5%2C5.936035%20L9.5%2C8.936035%20L10.25%2C8.936035%20C10.8023%2C8.936035%2011.25%2C9.383735%2011.25%2C9.936035%20L11.25%2C13.436035%20C11.25%2C13.988335%2010.8023%2C14.436035%2010.25%2C14.436035%20L7.25%2C14.436035%20C7.1637%2C14.436035%207.0799%2C14.425135%207%2C14.404535%20Z%20M7.75%2C10.436035%20L7.75%2C12.936035%20L9.75%2C12.936035%20L9.75%2C10.436035%20L7.75%2C10.436035%20Z%20M6.25%2C12.936035%20L6.25%2C10.436035%20L4.25%2C10.436035%20L4.25%2C12.936035%20L6.25%2C12.936035%20Z%20M8%2C8.936035%20L8%2C6.436035%20L6%2C6.436035%20L6%2C8.936035%20L8%2C8.936035%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var WholesaleManagerActiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E4%BE%9B%E5%BA%94%E9%93%BE%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-2241%2C%20-208%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84%22%20transform%3D%22translate%282241%2C%20208%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%E5%A4%87%E4%BB%BD-22%22%20fill%3D%22%23FEE9E8%22%20fill-rule%3D%22nonzero%22%20opacity%3D%220%22%20x%3D%220%22%20y%3D%220%22%20width%3D%2220%22%20height%3D%2220%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20transform%3D%22translate%283%2C%203%29%22%20fill%3D%22%234A4A4A%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M7.9653%2C8.2379%20C7.9879%2C8.3215%208%2C8.4093%208%2C8.5%20L8%2C9%20C8%2C9.5523%207.5523%2C10%207%2C10%20C6.4477%2C10%206%2C9.5523%206%2C9%20L6%2C8.5%20C6%2C8.4093%206.0121%2C8.3215%206.0347%2C8.2379%20C4.9239%2C8.1536%203.8215%2C7.9235%202.7565%2C7.5477%20L2.5004%2C7.4572%20C2.1098%2C7.3194%201.9049%2C6.89098%202.0428%2C6.50039%20C2.1806%2C6.10979%202.609%2C5.9049%202.9996%2C6.04276%20L3.2558%2C6.13317%20C5.6787%2C6.98831%208.3213%2C6.98831%2010.7442%2C6.13317%20L11.0004%2C6.04276%20C11.391%2C5.9049%2011.8194%2C6.10979%2011.9572%2C6.50039%20C12.0951%2C6.89098%2011.8902%2C7.3194%2011.4996%2C7.4572%20L11.2435%2C7.5477%20C10.1785%2C7.9235%209.0761%2C8.1536%207.9653%2C8.2379%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%20fill-rule%3D%22nonzero%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M3.5%2C2.5%20L3.5%2C2.25%20C3.5%2C1.00736%204.5074%2C0%205.75%2C0%20L8.25%2C0%20C9.4926%2C0%2010.5%2C1.00736%2010.5%2C2.25%20L10.5%2C2.5%20L10.75%2C2.5%20C12.5449%2C2.5%2014%2C3.95507%2014%2C5.75%20L14%2C10.75%20C14%2C12.5449%2012.5449%2C14%2010.75%2C14%20L3.25%2C14%20C1.4551%2C14%200%2C12.5449%200%2C10.75%20L0%2C5.75%20C0%2C3.95507%201.4551%2C2.5%203.25%2C2.5%20L3.5%2C2.5%20Z%20M5%2C2.25%20C5%2C1.83579%205.3358%2C1.5%205.75%2C1.5%20L8.25%2C1.5%20C8.6642%2C1.5%209%2C1.83579%209%2C2.25%20L9%2C2.5%20L5%2C2.5%20L5%2C2.25%20Z%20M3.25%2C4%20C2.2835%2C4%201.5%2C4.7835%201.5%2C5.75%20L1.5%2C10.75%20C1.5%2C11.7165%202.2835%2C12.5%203.25%2C12.5%20L10.75%2C12.5%20C11.7165%2C12.5%2012.5%2C11.7165%2012.5%2C10.75%20L12.5%2C5.75%20C12.5%2C4.7835%2011.7165%2C4%2010.75%2C4%20L3.25%2C4%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var WholesaleManagerInactiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E4%BE%9B%E5%BA%94%E9%93%BE%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-2221%2C%20-208%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84%22%20transform%3D%22translate%282221%2C%20208%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20fill%3D%22%23FEE9E8%22%20fill-rule%3D%22nonzero%22%20opacity%3D%220%22%20x%3D%220%22%20y%3D%220%22%20width%3D%2220%22%20height%3D%2220%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M6.5%2C5.5%20L6.5%2C5.25%20C6.5%2C4.00736%207.50736%2C3%208.75%2C3%20L11.25%2C3%20C12.4926%2C3%2013.5%2C4.00736%2013.5%2C5.25%20L13.5%2C5.5%20L13.75%2C5.5%20C15.5449%2C5.5%2017%2C6.95507%2017%2C8.75%20L17%2C13.75%20C17%2C15.5449%2015.5449%2C17%2013.75%2C17%20L6.25%2C17%20C4.45507%2C17%203%2C15.5449%203%2C13.75%20L3%2C8.75%20C3%2C6.95507%204.45507%2C5.5%206.25%2C5.5%20L6.5%2C5.5%20Z%20M8%2C5.25%20C8%2C4.83579%208.33579%2C4.5%208.75%2C4.5%20L11.25%2C4.5%20C11.6642%2C4.5%2012%2C4.83579%2012%2C5.25%20L12%2C5.5%20L8%2C5.5%20L8%2C5.25%20Z%20M5.16062%2C9.54197%20C5.2755%2C9.21648%205.6325%2C9.04574%205.958%2C9.16062%20L6.21416%2C9.25103%20C8.66399%2C10.1157%2011.336%2C10.1157%2013.7858%2C9.25103%20L14.042%2C9.16062%20C14.3675%2C9.04574%2014.7245%2C9.21648%2014.8394%2C9.54197%20C14.9542%2C9.86747%2014.7835%2C10.2245%2014.458%2C10.3394%20L14.2018%2C10.4298%20C13.1584%2C10.798%2012.0786%2C11.025%2010.9904%2C11.1106%20C10.9967%2C11.1562%2011%2C11.2027%2011%2C11.25%20L11%2C12%20C11%2C12.5523%2010.5523%2C13%2010%2C13%20C9.44772%2C13%209%2C12.5523%209%2C12%20L9%2C11.25%20C9%2C11.2027%209.00329%2C11.1562%209.00964%2C11.1106%20C7.9214%2C11.025%206.84161%2C10.7981%205.79813%2C10.4298%20L5.54197%2C10.3394%20C5.21648%2C10.2245%205.04574%2C9.86747%205.16062%2C9.54197%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%20fill%3D%22%234A4A4A%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavStockInactiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%208%E5%A4%87%E4%BB%BD%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-1756.000000%2C%20-76.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-8%E5%A4%87%E4%BB%BD%22%20transform%3D%22translate%281756.000000%2C%2076.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M8.7709%2C3.3653775%20C9.51789%2C2.8782075%2010.4821%2C2.8782075%2011.2291%2C3.3653775%20L14.7985%2C5.6932475%20C15.86%2C6.3855075%2016.5%2C7.5670275%2016.5%2C8.8342875%20L16.5%2C15.7092275%20C16.5%2C16.3995275%2015.9404%2C16.9592275%2015.25%2C16.9592275%20L4.75%2C16.9592275%20C4.05964%2C16.9592275%203.5%2C16.3995275%203.5%2C15.7092275%20L3.5%2C8.8342875%20C3.5%2C7.5670275%204.14003%2C6.3855075%205.2015%2C5.6932475%20L8.7709%2C3.3653775%20Z%20M9.5%2C13.2092275%20C9.5%2C12.9331275%209.27614%2C12.7092275%209%2C12.7092275%20L7.5%2C12.7092275%20C7.22386%2C12.7092275%207%2C12.9331275%207%2C13.2092275%20L7%2C15.2092275%20C7%2C15.4853275%207.22386%2C15.7092275%207.5%2C15.7092275%20L9%2C15.7092275%20C9.27614%2C15.7092275%209.5%2C15.4853275%209.5%2C15.2092275%20L9.5%2C13.2092275%20Z%20M10.75%2C8.7092275%20C11.0261%2C8.7092275%2011.25%2C8.9330875%2011.25%2C9.2092275%20L11.25%2C11.2092275%20C11.25%2C11.4853275%2011.0261%2C11.7092275%2010.75%2C11.7092275%20L9.25%2C11.7092275%20C8.97386%2C11.7092275%208.75%2C11.4853275%208.75%2C11.2092275%20L8.75%2C9.2092275%20C8.75%2C8.9330875%208.97386%2C8.7092275%209.25%2C8.7092275%20L10.75%2C8.7092275%20Z%20M13%2C13.2092275%20C13%2C12.9331275%2012.7761%2C12.7092275%2012.5%2C12.7092275%20L11%2C12.7092275%20C10.7239%2C12.7092275%2010.5%2C12.9331275%2010.5%2C13.2092275%20L10.5%2C15.2092275%20C10.5%2C15.4853275%2010.7239%2C15.7092275%2011%2C15.7092275%20L12.5%2C15.7092275%20C12.7761%2C15.7092275%2013%2C15.4853275%2013%2C15.2092275%20L13%2C13.2092275%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%20fill%3D%22%234A4A4A%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavSettlementActiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%2043%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-1840.000000%2C%20-76.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-43%22%20transform%3D%22translate%281840.000000%2C%2076.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-42%22%20transform%3D%22translate%284.000000%2C%202.500000%29%22%20fill%3D%22%234A4A4A%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M0.9626%2C14.9004%20C0.3828%2C14.6779%200%2C14.1211%200%2C13.5%20L0%2C2.5%20C0%2C1.11929%201.1193%2C0%202.5%2C0%20L9.5%2C0%20C10.8807%2C0%2012%2C1.11929%2012%2C2.5%20L12%2C13.5%20C12%2C14.1211%2011.6172%2C14.6779%2011.0374%2C14.9004%20C10.4576%2C15.1229%209.8005%2C14.9651%209.3851%2C14.5035%20L8.25%2C13.2423%20L7.1149%2C14.5035%20C6.8305%2C14.8195%206.4252%2C15.0000375%206%2C15.0000375%20C5.5748%2C15.0000375%205.1695%2C14.8195%204.8851%2C14.5035%20L3.75%2C13.2423%20L2.6149%2C14.5035%20C2.1995%2C14.9651%201.5424%2C15.1229%200.9626%2C14.9004%20Z%20M8.3515%2C11.5%20L8.1485%2C11.5%20C7.911%2C11.524%207.6806%2C11.6327%207.5067%2C11.8259%20L6%2C13.5%20L4.4933%2C11.8259%20C4.3194%2C11.6327%204.089%2C11.524%203.8515%2C11.5%20L3.6485%2C11.5%20C3.411%2C11.524%203.1806%2C11.6327%203.0067%2C11.8259%20L1.5%2C13.5%20L1.5%2C2.5%20C1.5%2C1.94772%201.9477%2C1.5%202.5%2C1.5%20L9.5%2C1.5%20C10.0523%2C1.5%2010.5%2C1.94772%2010.5%2C2.5%20L10.5%2C13.5%20L8.9933%2C11.8259%20C8.8194%2C11.6327%208.589%2C11.524%208.3515%2C11.5%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M4.4939%2C2.93558%20C4.1822%2C2.66282%203.7083%2C2.6944%203.4356%2C3.00613%20C3.1628%2C3.31786%203.1944%2C3.79168%203.5061%2C4.06444%20L5.24999251%2C5.59034%20L5.24999251%2C6.00001%20L4%2C6.00001%20C3.5858%2C6.00001%203.24999251%2C6.3358%203.24999251%2C6.75000024%20C3.24999251%2C7.16422%203.5858%2C7.5%204%2C7.5%20L5.24999251%2C7.5%20L5.24999251%2C8%20L4%2C8%20C3.5858%2C8%203.24999251%2C8.3358%203.24999251%2C8.75000024%20C3.24999251%2C9.1642%203.5858%2C9.5%204%2C9.5%20L5.24999251%2C9.5%20L5.24999251%2C10%20C5.24999251%2C10.4142%205.5858%2C10.7500002%206%2C10.7500002%20C6.4142%2C10.7500002%206.74999251%2C10.4142%206.74999251%2C10%20L6.74999251%2C9.5%20L8%2C9.5%20C8.4142%2C9.5%208.75000749%2C9.1642%208.75000749%2C8.75000024%20C8.75000749%2C8.3358%208.4142%2C8%208%2C8%20L6.74999251%2C8%20L6.74999251%2C7.5%20L8%2C7.5%20C8.4142%2C7.5%208.75000749%2C7.16422%208.75000749%2C6.75000024%20C8.75000749%2C6.3358%208.4142%2C6.00001%208%2C6.00001%20L6.74999251%2C6.00001%20L6.74999251%2C5.59034%20L8.4939%2C4.06444%20C8.8056%2C3.79168%208.8372%2C3.31786%208.5644%2C3.00613%20C8.2917%2C2.6944%207.8179%2C2.66282%207.5061%2C2.93558%20L6%2C4.25343%20L4.4939%2C2.93558%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%20fill-rule%3D%22nonzero%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavSettlementInactiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%205%E5%A4%87%E4%BB%BD%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-1820.000000%2C%20-76.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-5%E5%A4%87%E4%BB%BD%22%20transform%3D%22translate%281820.000000%2C%2076.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E5%BD%A2%E7%8A%B6-2%22%20transform%3D%22translate%284.500000%2C%203.000000%29%22%20fill%3D%22%234A4A4A%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M0.64173%2C13.9336%20C0.25517%2C13.7853%200%2C13.414%200%2C13%20L0%2C11%20L0%2C9.5%20L0%2C2%20C0%2C0.89543%200.89543%2C-8.8817842e-16%202%2C-8.8817842e-16%20L9%2C-8.8817842e-16%20C10.1046%2C-8.8817842e-16%2011%2C0.89543%2011%2C2%20L11%2C9.5%20L11%2C11%20L11%2C13%20C11%2C13.414%2010.7448%2C13.7853%2010.3583%2C13.9336%20C9.9717%2C14.082%209.5337%2C13.9767%209.2567%2C13.669%20L7.7546%2C12%20L7.7454%2C12%20L6.2433%2C13.669%20C6.0537%2C13.8797%205.7835%2C14.0000481%205.5%2C14.0000481%20C5.21651%2C14.0000481%204.94635%2C13.8797%204.75671%2C13.669%20L3.25461%2C12%20L3.24538%2C12%20L1.74329%2C13.669%20C1.4663%2C13.9767%201.0283%2C14.082%200.64173%2C13.9336%20Z%20M2.93558%2C2.50613%20C3.20834%2C2.1944%203.68216%2C2.16282%203.99389%2C2.43558%20L5.5%2C3.75343%20L7.0061%2C2.43558%20C7.3179%2C2.16282%207.7917%2C2.1944%208.0644%2C2.50613%20C8.3372%2C2.81786%208.3056%2C3.29168%207.9939%2C3.56444%20L6.25000024%2C5.09034%20L6.25000024%2C5.50001%20L7.5%2C5.50001%20C7.9142%2C5.50001%208.25000024%2C5.8358%208.25000024%2C6.25000024%20C8.25000024%2C6.66422%207.9142%2C7%207.5%2C7%20L6.25000024%2C7%20L6.25000024%2C7.5%20L7.5%2C7.5%20C7.9142%2C7.5%208.25000024%2C7.8358%208.25000024%2C8.25000024%20C8.25000024%2C8.6642%207.9142%2C9%207.5%2C9%20L6.25000024%2C9%20L6.25000024%2C9.5%20C6.25000024%2C9.9142%205.9142%2C10.2500002%205.5%2C10.2500002%20C5.0858%2C10.2500002%204.75000024%2C9.9142%204.75000024%2C9.5%20L4.75000024%2C9%20L3.50001%2C9%20C3.0858%2C9%202.75000024%2C8.6642%202.75000024%2C8.25000024%20C2.75000024%2C7.8358%203.0858%2C7.5%203.50001%2C7.5%20L4.75000024%2C7.5%20L4.75000024%2C7%20L3.50001%2C7%20C3.0858%2C7%202.75000024%2C6.66422%202.75000024%2C6.25000024%20C2.75000024%2C5.8358%203.0858%2C5.50001%203.50001%2C5.50001%20L4.75000024%2C5.50001%20L4.75000024%2C5.09034%20L3.00613%2C3.56444%20C2.6944%2C3.29168%202.66282%2C2.81786%202.93558%2C2.50613%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavOperationActiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%2029%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-1840.000000%2C%20-120.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-29%22%20transform%3D%22translate%281840.000000%2C%20120.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%E5%A4%87%E4%BB%BD-17%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22OrdersStatus%E5%A4%87%E4%BB%BD-2%22%20transform%3D%22translate%283.500000%2C%203.000000%29%22%20fill%3D%22%234A4A4A%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M5.07891%2C2.41091986%20L4.21196%2C3.81970986%20C4.02996%2C4.11545986%203.70756%2C4.29560986%203.3603%2C4.29560986%20L1.5%2C4.29560986%20C1.08579%2C4.29560986%200.75%2C3.95982986%200.75%2C3.54560986%20C0.75%2C3.13139986%201.08579%2C2.79560986%201.5%2C2.79560986%20L3.0809%2C2.79560986%20L4.50798%2C0.476619856%20C4.99412%2C-0.313360144%206.20053%2C-0.073200144%206.34708%2C0.842719856%20L6.9381%2C4.53642986%20L7.8017%2C3.24090986%20C7.9872%2C2.96271986%208.2994%2C2.79560986%208.6338%2C2.79560986%20L11.5%2C2.79560986%20C11.9142%2C2.79560986%2012.25%2C3.13139986%2012.25%2C3.54560986%20C12.25%2C3.95982986%2011.9142%2C4.29560986%2011.5%2C4.29560986%20L8.9014%2C4.29560986%20L7.4781%2C6.43058986%20C6.9735%2C7.18749986%205.80231%2C6.93219986%205.65858%2C6.03387986%20L5.07891%2C2.41091986%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%20fill-rule%3D%22nonzero%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M1.65635%2C6.79589986%20L1.7882%2C5.90590986%20C1.8489%2C5.49616986%201.56595%2C5.11479986%201.15621%2C5.05409986%20C0.74647%2C4.99339986%200.3651%2C5.27634986%200.30439%2C5.68608986%20L0.09447%2C7.10309986%20C0.03158%2C7.52759986%200%2C7.95619986%200%2C8.38539986%20L0%2C10.0459999%20C0%2C11.8408999%201.45507%2C13.2959999%203.25%2C13.2959999%20L9.75%2C13.2959999%20C11.5449%2C13.2959999%2013%2C11.8408999%2013%2C10.0459999%20L13%2C8.38539986%20C13%2C7.95619986%2012.9684%2C7.52759986%2012.9055%2C7.10309986%20L12.6956%2C5.68608986%20C12.6349%2C5.27634986%2012.2535%2C4.99339986%2011.8438%2C5.05409986%20C11.4341%2C5.11479986%2011.1511%2C5.49616986%2011.2118%2C5.90590986%20L11.3437%2C6.79589986%20L8.8604%2C6.79589986%20C8.3223%2C6.79589986%207.8447%2C7.14019986%207.6745%2C7.65059986%20L7.5164%2C8.12499986%20C7.4824%2C8.22699986%207.3868%2C8.29589986%207.2792%2C8.29589986%20L5.72076%2C8.29589986%20C5.61315%2C8.29589986%205.51762%2C8.22699986%205.48359%2C8.12499986%20L5.32547%2C7.65059986%20C5.15533%2C7.14019986%204.67766%2C6.79589986%204.13962%2C6.79589986%20L1.65635%2C6.79589986%20Z%20M1.50055%2C8.29589986%20C1.50018%2C8.32569986%201.5%2C8.35549986%201.5%2C8.38539986%20L1.5%2C10.0459999%20C1.5%2C11.0124999%202.2835%2C11.7959999%203.25%2C11.7959999%20L9.75%2C11.7959999%20C10.7165%2C11.7959999%2011.5%2C11.0124999%2011.5%2C10.0459999%20L11.5%2C8.38539986%20C11.5%2C8.35549986%2011.4998%2C8.32569986%2011.4994%2C8.29589986%20L9.0406%2C8.29589986%20L8.9394%2C8.59929986%20C8.7012%2C9.31389986%208.0325%2C9.79589986%207.2792%2C9.79589986%20L5.72076%2C9.79589986%20C4.96751%2C9.79589986%204.29876%2C9.31389986%204.06056%2C8.59929986%20L3.95943%2C8.29589986%20L1.50055%2C8.29589986%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavOperationInactiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%2030%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-1820.000000%2C%20-120.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-30%22%20transform%3D%22translate%281820.000000%2C%20120.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%E5%A4%87%E4%BB%BD-18%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22OrdersStatus%E5%A4%87%E4%BB%BD-3%22%20transform%3D%22translate%283.500000%2C%203.000000%29%22%20fill%3D%22%234A4A4A%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M5.07891%2C2.41091986%20L4.21196%2C3.81970986%20C4.02996%2C4.11545986%203.70756%2C4.29560986%203.3603%2C4.29560986%20L1.5%2C4.29560986%20C1.08579%2C4.29560986%200.75%2C3.95982986%200.75%2C3.54560986%20C0.75%2C3.13139986%201.08579%2C2.79560986%201.5%2C2.79560986%20L3.0809%2C2.79560986%20L4.50798%2C0.476619856%20C4.99412%2C-0.313360144%206.20053%2C-0.073200144%206.34708%2C0.842719856%20L6.9381%2C4.53642986%20L7.8017%2C3.24090986%20C7.9872%2C2.96271986%208.2994%2C2.79560986%208.6338%2C2.79560986%20L11.5%2C2.79560986%20C11.9142%2C2.79560986%2012.25%2C3.13139986%2012.25%2C3.54560986%20C12.25%2C3.95982986%2011.9142%2C4.29560986%2011.5%2C4.29560986%20L8.9014%2C4.29560986%20L7.4781%2C6.43058986%20C6.9735%2C7.18749986%205.80231%2C6.93219986%205.65858%2C6.03387986%20L5.07891%2C2.41091986%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%20fill-rule%3D%22nonzero%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M1.65635%2C6.79589986%20L1.7882%2C5.90590986%20C1.8489%2C5.49616986%201.56595%2C5.11479986%201.15621%2C5.05409986%20C0.74647%2C4.99339986%200.3651%2C5.27634986%200.30439%2C5.68608986%20L0.09447%2C7.10309986%20C0.03158%2C7.52759986%200%2C7.95619986%200%2C8.38539986%20L0%2C10.0459999%20C0%2C11.8408999%201.45507%2C13.2959999%203.25%2C13.2959999%20L9.75%2C13.2959999%20C11.5449%2C13.2959999%2013%2C11.8408999%2013%2C10.0459999%20L13%2C8.38539986%20C13%2C7.95619986%2012.9684%2C7.52759986%2012.9055%2C7.10309986%20L12.6956%2C5.68608986%20C12.6349%2C5.27634986%2012.2535%2C4.99339986%2011.8438%2C5.05409986%20C11.4341%2C5.11479986%2011.1511%2C5.49616986%2011.2118%2C5.90590986%20L11.3437%2C6.79589986%20L8.8604%2C6.79589986%20C8.3223%2C6.79589986%207.8447%2C7.14019986%207.6745%2C7.65059986%20L7.5164%2C8.12499986%20C7.4824%2C8.22699986%207.3868%2C8.29589986%207.2792%2C8.29589986%20L5.72076%2C8.29589986%20C5.61315%2C8.29589986%205.51762%2C8.22699986%205.48359%2C8.12499986%20L5.32547%2C7.65059986%20C5.15533%2C7.14019986%204.67766%2C6.79589986%204.13962%2C6.79589986%20L1.65635%2C6.79589986%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavServiceActiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%2041%E5%A4%87%E4%BB%BD%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-1583.000000%2C%20-164.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-41%E5%A4%87%E4%BB%BD%22%20transform%3D%22translate%281583.000000%2C%20164.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84%22%20transform%3D%22translate%283.000000%2C%204.000000%29%22%20fill%3D%22%234A4A4A%22%20fill-rule%3D%22nonzero%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M1.24053825%2C1.19569651%20C2.82000734%2C-0.326677536%205.33657575%2C-0.395323953%207.0005521%2C0.989757258%20C8.6634598%2C-0.395323953%2011.1800412%2C-0.326677536%2012.7594778%2C1.19569651%20C14.3361124%2C2.7153448%2014.4098746%2C5.13501482%2012.9808295%2C6.73887901%20L7.92153711%2C11.6321187%20C7.43569699%2C12.1003338%206.66178027%2C12.1216248%206.1498106%2C11.6959291%20L6.07852451%2C11.6321187%20L1.01917343%2C6.73887901%20C-0.40988983%2C5.13501482%20-0.336101559%2C2.7153448%201.24053825%2C1.19569651%20Z%20M2.16205106%2C2.08389825%20C1.04965573%2C3.15608243%201.01787673%2C4.87537686%202.06670755%2C5.98458489%20L2.16205106%2C6.08080292%20L7.00003081%2C10.7438605%20L10.4552559%2C7.41265647%20L8.15188111%2C5.19260746%20L7.46078396%2C5.85878546%20C6.69735815%2C6.59455195%205.45962571%2C6.59455195%204.69621945%2C5.85878546%20C3.93281319%2C5.12295616%203.93281319%2C3.92996559%204.69621945%2C3.19415513%20L6.065362%2C1.87366938%20C4.94936326%2C1.0125431%203.33196397%2C1.05198495%202.26191675%2C1.99199495%20L2.16205106%2C2.08389825%20Z%20M7.69112796%2C3.860308%20C7.94558151%2C3.61503366%208.35818071%2C3.61503366%208.61263426%2C3.860308%20L11.3767622%2C6.52446101%20L11.8379715%2C6.08080292%20C12.983045%2C4.97712178%2012.983045%2C3.18761707%2011.8379715%2C2.08389825%20C10.7255436%2C1.01171406%208.94176227%2C0.981083818%207.79101971%2C1.99199495%20L7.69112796%2C2.08389825%20L5.61773227%2C4.08235686%20C5.38143908%2C4.31010847%205.36456239%2C4.66926175%205.5671022%2C4.9157608%20L5.61773227%2C4.97059%20C5.854045%2C5.19832277%206.22663537%2C5.21458939%206.48239214%2C5.01938988%20L6.53927766%2C4.97059%20L7.69112796%2C3.860308%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavServiceInactiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%2044%E5%A4%87%E4%BB%BD%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-1563.000000%2C%20-164.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-44%E5%A4%87%E4%BB%BD%22%20transform%3D%22translate%281563.000000%2C%20164.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-8%E5%A4%87%E4%BB%BD-3%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20stroke%3D%22%23979797%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%E5%A4%87%E4%BB%BD-27%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84%22%20transform%3D%22translate%283.000000%2C%204.000000%29%22%20fill%3D%22%234A4A4A%22%20fill-rule%3D%22nonzero%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M8.3822786%2C4.97058935%20C8.12782541%2C4.72527109%207.71522678%2C4.72527109%207.46077359%2C4.97058935%20L7.00002108%2C5.41468699%20C6.49104953%2C5.9051979%205.66591092%2C5.9051979%205.15697195%2C5.41468699%20C4.64803298%2C4.92411328%204.64803298%2C4.12879476%205.15697195%2C3.63825245%20L8.82595896%2C0.100952851%20C10.1968785%2C-0.197969895%2011.692125%2C0.166942583%2012.75946%2C1.19569657%20C14.3359621%2C2.71518755%2014.4098545%2C5.13451169%2012.9812676%2C6.73837557%20L11.6075462%2C8.07927286%20L8.3822786%2C4.97058935%20Z%20M1.24053654%2C1.19569657%20C2.65673636%2C-0.169311748%204.82632615%2C-0.365597757%206.45918586%2C0.60683854%20L4.23545391%2C2.75005088%20C3.21758248%2C3.7311355%203.21758248%2C5.32179767%204.23545391%2C6.30288228%20C5.22249121%2C7.25419709%206.80421267%2C7.28302481%207.82723832%2C6.38936545%20L7.9215261%2C6.30288228%20L10.6860411%2C8.96746815%20L7.9215261%2C11.6321168%20C7.41255454%2C12.1226277%206.58742246%2C12.1226277%206.07851607%2C11.6321168%20L1.24053654%2C6.96899735%20C-0.413512181%2C5.3747427%20-0.413512181%2C2.78995121%201.24053654%2C1.19569657%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavAddServiceActiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%2041%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-1648.000000%2C%20-164.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-41%22%20transform%3D%22translate%281648.000000%2C%20164.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%E5%A4%87%E4%BB%BD-14%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22Apps%22%20transform%3D%22translate%283.000000%2C%203.000000%29%22%20fill%3D%22%234A4A4A%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M0%2C2.42307692%20C0%2C1.08484923%201.08484923%2C0%202.42307692%2C0%20L5.38461538%2C0%20C5.97937846%2C0%206.46153846%2C0.482149231%206.46153846%2C1.07692308%20L6.46153846%2C5.38461538%20C6.46153846%2C5.97937846%205.97938923%2C6.46153846%205.38461538%2C6.46153846%20L1.07692308%2C6.46153846%20C0.48216%2C6.46153846%200%2C5.97938923%200%2C5.38461538%20L0%2C2.42307692%20Z%20M2.42307692%2C1.61538462%20C1.97700462%2C1.61538462%201.61538462%2C1.97700462%201.61538462%2C2.42307692%20L1.61538462%2C4.84615385%20L4.84615385%2C4.84615385%20L4.84615385%2C1.61538462%20L2.42307692%2C1.61538462%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M0%2C11.5769231%20C0%2C12.9151077%201.08484923%2C14%202.42307692%2C14%20L5.38461538%2C14%20C5.97937846%2C14%206.46153846%2C13.5178615%206.46153846%2C12.9230769%20L6.46153846%2C8.61538462%20C6.46153846%2C8.0206%205.97938923%2C7.53846154%205.38461538%2C7.53846154%20L1.07692308%2C7.53846154%20C0.48216%2C7.53846154%200%2C8.0206%200%2C8.61538462%20L0%2C11.5769231%20Z%20M2.42307692%2C12.3846154%20C1.97700462%2C12.3846154%201.61538462%2C12.0229846%201.61538462%2C11.5769231%20L1.61538462%2C9.15384615%20L4.84615385%2C9.15384615%20L4.84615385%2C12.3846154%20L2.42307692%2C12.3846154%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M11.5769231%2C14%20C12.9151077%2C14%2014%2C12.9151077%2014%2C11.5769231%20L14%2C8.61538462%20C14%2C8.0206%2013.5178615%2C7.53846154%2012.9230769%2C7.53846154%20L8.61538462%2C7.53846154%20C8.0206%2C7.53846154%207.53846154%2C8.0206%207.53846154%2C8.61538462%20L7.53846154%2C12.9230769%20C7.53846154%2C13.5178615%208.0206%2C14%208.61538462%2C14%20L11.5769231%2C14%20Z%20M12.3846154%2C11.5769231%20C12.3846154%2C12.0229846%2012.0229846%2C12.3846154%2011.5769231%2C12.3846154%20L9.15384615%2C12.3846154%20L9.15384615%2C9.15384615%20L12.3846154%2C9.15384615%20L12.3846154%2C11.5769231%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M10.7692308%2C0%20C11.2152923%2C0%2011.5769231%2C0.36162%2011.5769231%2C0.807692308%20L11.5769231%2C2.42307692%20L13.1923077%2C2.42307692%20C13.6383692%2C2.42307692%2014%2C2.78469692%2014%2C3.23076923%20C14%2C3.67684154%2013.6383692%2C4.03846154%2013.1923077%2C4.03846154%20L11.5769231%2C4.03846154%20L11.5769231%2C5.65384615%20C11.5769231%2C6.09991846%2011.2152923%2C6.46153846%2010.7692308%2C6.46153846%20C10.3231692%2C6.46153846%209.96153846%2C6.09991846%209.96153846%2C5.65384615%20L9.96153846%2C4.03846154%20L8.34615385%2C4.03846154%20C7.90009231%2C4.03846154%207.53846154%2C3.67684154%207.53846154%2C3.23076923%20C7.53846154%2C2.78469692%207.90009231%2C2.42307692%208.34615385%2C2.42307692%20L9.96153846%2C2.42307692%20L9.96153846%2C0.807692308%20C9.96153846%2C0.36162%2010.3231692%2C0%2010.7692308%2C0%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%20fill-rule%3D%22nonzero%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavAddServiceInactiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%2044%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E6%96%B0%E7%89%88%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-1628.000000%2C%20-164.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-44%22%20transform%3D%22translate%281628.000000%2C%20164.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22AppsFilled%22%20transform%3D%22translate%283.000000%2C%203.000000%29%22%20fill%3D%22%234A4A4A%22%20fill-rule%3D%22nonzero%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M10.8118812%2C0%20C11.3477069%2C0%2011.7821782%2C0.434415842%2011.7821782%2C0.97029703%20L11.7821782%2C2.21782178%20L13.029703%2C2.21782178%20C13.5655287%2C2.21782178%2014%2C2.65223762%2014%2C3.18811881%20C14%2C3.724%2013.5655287%2C4.15841584%2013.029703%2C4.15841584%20L11.7821782%2C4.15841584%20L11.7821782%2C5.40594059%20C11.7821782%2C5.94182178%2011.3477069%2C6.37623762%2010.8118812%2C6.37623762%20C10.2760554%2C6.37623762%209.84158416%2C5.94182178%209.84158416%2C5.40594059%20L9.84158416%2C4.15841584%20L8.59405941%2C4.15841584%20C8.05823366%2C4.15841584%207.62376238%2C3.724%207.62376238%2C3.18811881%20C7.62376238%2C2.65223762%208.05823366%2C2.21782178%208.59405941%2C2.21782178%20L9.84158416%2C2.21782178%20L9.84158416%2C0.97029703%20C9.84158416%2C0.434415842%2010.2760554%2C0%2010.8118812%2C0%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M2.21782178%2C0.138613861%20C0.992952079%2C0.138613861%200%2C1.13156594%200%2C2.35643564%20L0%2C5.40594059%20C0%2C5.86526257%200.372361188%2C6.23762376%200.831683168%2C6.23762376%20L5.26732673%2C6.23762376%20C5.72664871%2C6.23762376%206.0990099%2C5.86526257%206.0990099%2C5.40594059%20L6.0990099%2C0.97029703%20C6.0990099%2C0.51097505%205.72664871%2C0.138613861%205.26732673%2C0.138613861%20L2.21782178%2C0.138613861%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M0%2C11.7821782%20C0%2C13.0070812%200.992952079%2C14%202.21782178%2C14%20L5.26732673%2C14%20C5.72664871%2C14%206.0990099%2C13.6276277%206.0990099%2C13.1683168%20L6.0990099%2C8.73267327%20C6.0990099%2C8.27336238%205.72664871%2C7.9009901%205.26732673%2C7.9009901%20L0.831683168%2C7.9009901%20C0.372361188%2C7.9009901%200%2C8.27336238%200%2C8.73267327%20L0%2C11.7821782%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M11.6435644%2C14%20C12.8684673%2C14%2013.8613861%2C13.0070812%2013.8613861%2C11.7821782%20L13.8613861%2C8.73267327%20C13.8613861%2C8.27336238%2013.4890139%2C7.9009901%2013.029703%2C7.9009901%20L8.59405941%2C7.9009901%20C8.13474851%2C7.9009901%207.76237624%2C8.27336238%207.76237624%2C8.73267327%20L7.76237624%2C13.1683168%20C7.76237624%2C13.6276277%208.13474851%2C14%208.59405941%2C14%20L11.6435644%2C14%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavAccountInactiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%204%E5%A4%87%E4%BB%BD%203%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E4%BE%9B%E5%BA%94%E9%93%BE%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E4%B8%AA%E4%BA%BA%E4%BF%A1%E6%81%AF%22%20transform%3D%22translate%28-87.000000%2C%20-128.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-4%E5%A4%87%E4%BB%BD-3%22%20transform%3D%22translate%2887.000000%2C%20128.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-51%22%20transform%3D%22translate%284.000000%2C%203.000000%29%22%20fill%3D%22%234A4A4A%22%20fill-rule%3D%22nonzero%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M2.59088152%2C3.5%20C2.59088152%2C1.567%204.15788152%2C0%206.09088152%2C0%20C8.02388152%2C0%209.59088152%2C1.567%209.59088152%2C3.5%20C9.59088152%2C5.433%208.02388152%2C7%206.09088152%2C7%20C4.15788152%2C7%202.59088152%2C5.433%202.59088152%2C3.5%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M0.606911517%2C11.2268%20C1.71335152%2C9.2352%203.81257152%2C8%206.09087152%2C8%20C8.36918152%2C8%2010.4683815%2C9.2352%2011.5747815%2C11.2268%20L12.0114815%2C12.0128%20C12.5067815%2C12.9043%2011.8620815%2C14%2010.8421815%2C14%20L1.33955152%2C14%20C0.319631517%2C14%20-0.325058483%2C12.9043%200.170251517%2C12.0128%20L0.606911517%2C11.2268%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavAccountActiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%204%E5%A4%87%E4%BB%BD%202%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E4%BE%9B%E5%BA%94%E9%93%BE%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E4%B8%AA%E4%BA%BA%E4%BF%A1%E6%81%AF%22%20transform%3D%22translate%28-67.000000%2C%20-128.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-4%E5%A4%87%E4%BB%BD-2%22%20transform%3D%22translate%2867.000000%2C%20128.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-50%22%20transform%3D%22translate%284.000000%2C%203.000000%29%22%20fill%3D%22%234A4A4A%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M6.09085759%2C0%20C4.15785759%2C0%202.59085759%2C1.567%202.59085759%2C3.5%20C2.59085759%2C5.433%204.15785759%2C7%206.09085759%2C7%20C8.02385759%2C7%209.59085759%2C5.433%209.59085759%2C3.5%20C9.59085759%2C1.567%208.02385759%2C0%206.09085759%2C0%20Z%20M4.09085759%2C3.5%20C4.09085759%2C2.39543%204.98625759%2C1.5%206.09085759%2C1.5%20C7.19545759%2C1.5%208.09085759%2C2.39543%208.09085759%2C3.5%20C8.09085759%2C4.60457%207.19545759%2C5.5%206.09085759%2C5.5%20C4.98625759%2C5.5%204.09085759%2C4.60457%204.09085759%2C3.5%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M11.5747576%2C11.2268%20C10.4683576%2C9.2352%208.36915759%2C8%206.09085759%2C8%20C3.81255759%2C8%201.71335759%2C9.2352%200.606857591%2C11.2268%20L0.170257591%2C12.0128%20C-0.325042409%2C12.9043%200.319557591%2C14%201.33955759%2C14%20L10.8421576%2C14%20C11.8620576%2C14%2012.5067576%2C12.9043%2012.0114576%2C12.0128%20L11.5747576%2C11.2268%20Z%20M1.91815759%2C11.9552%20C2.76005759%2C10.4398%204.35725759%2C9.5%206.09085759%2C9.5%20C7.82435759%2C9.5%209.42165759%2C10.4398%2010.2635576%2C11.9552%20L10.5662576%2C12.5%20L1.61545759%2C12.5%20L1.91815759%2C11.9552%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavSecurityInactiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%206%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E4%BE%9B%E5%BA%94%E9%93%BE%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E4%B8%AA%E4%BA%BA%E4%BF%A1%E6%81%AF%22%20transform%3D%22translate%28-90.000000%2C%20-156.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-6%22%20transform%3D%22translate%2890.000000%2C%20156.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M4.50000013%2C6.5%20C4.50000013%2C4.567%206.06700013%2C3%208.00000013%2C3%20C9.93300013%2C3%2011.5000001%2C4.567%2011.5000001%2C6.5%20C11.5000001%2C8.433%209.93300013%2C10%208.00000013%2C10%20C6.06700013%2C10%204.50000013%2C8.433%204.50000013%2C6.5%20Z%20M12.7513001%2C17%20L12.0000001%2C17%20C11.4477001%2C17%2011.0000001%2C16.5523%2011.0000001%2C16%20L11.0000001%2C13%20C11.0000001%2C12.6077%2011.2259001%2C12.2681%2011.5548001%2C12.1043%20C11.6889001%2C12.0376%2011.8400001%2C12%2012.0000001%2C12%20L15.5000001%2C12%20L15.5000001%2C10.25%20C15.5000001%2C9.83579%2015.1642001%2C9.5%2014.7500001%2C9.5%20C14.3358001%2C9.5%2014.0000001%2C9.83579%2014.0000001%2C10.25%20C14.0000001%2C10.6642%2013.6642001%2C11%2013.2500001%2C11%20C12.8358001%2C11%2012.5000001%2C10.6642%2012.5000001%2C10.25%20C12.5000001%2C9.00736%2013.5074001%2C8%2014.7500001%2C8%20C15.9926001%2C8%2017.0000001%2C9.00736%2017.0000001%2C10.25%20L17.0000001%2C12%20C17.5523001%2C12%2018.0000001%2C12.4477%2018.0000001%2C13%20L18.0000001%2C16%20C18.0000001%2C16.5523%2017.5523001%2C17%2017.0000001%2C17%20L12.7513001%2C17%20Z%20M10.6053001%2C11.5665%20C10.2319001%2C11.9299%2010%2C12.4379%2010%2C13%20L10%2C16%20C10%2C16.3643%2010.0974001%2C16.7058%2010.2676001%2C17%20L9.41374013%2C17%20C9.38090013%2C17.0044%209.34738013%2C17.0067%209.31334013%2C17.0067%20L3.38385013%2C17.0067%20C2.97603013%2C17.0067%202.57874013%2C16.8398%202.30980013%2C16.5287%20C2.02854013%2C16.2033%201.91498013%2C15.7452%202.06926013%2C15.2863%20C2.39215013%2C14.3258%203.16392013%2C13.0395%204.43144013%2C12.129%20C5.18766013%2C11.5858%206.11020013%2C11.1848%207.19302013%2C11.0618%20C7.45854013%2C11.0212%207.72799013%2C11%208%2C11%20C8.91487013%2C11%209.80087013%2C11.1992%2010.6053001%2C11.5665%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%20fill%3D%22%234A4A4A%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavSecurityActiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%205%E5%A4%87%E4%BB%BD%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E4%BE%9B%E5%BA%94%E9%93%BE%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E4%B8%AA%E4%BA%BA%E4%BF%A1%E6%81%AF%22%20transform%3D%22translate%28-70.000000%2C%20-156.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-5%E5%A4%87%E4%BB%BD%22%20transform%3D%22translate%2870.000000%2C%20156.000000%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M7.99998567%2C4.5%20C6.89538567%2C4.5%205.99998567%2C5.39543%205.99998567%2C6.5%20C5.99998567%2C7.60457%206.89538567%2C8.5%207.99998567%2C8.5%20C9.10458567%2C8.5%209.99998567%2C7.60457%209.99998567%2C6.5%20C9.99998567%2C5.39543%209.10458567%2C4.5%207.99998567%2C4.5%20Z%20M4.49998567%2C6.5%20C4.49998567%2C4.567%206.06698567%2C3%207.99998567%2C3%20C9.93298567%2C3%2011.4999857%2C4.567%2011.4999857%2C6.5%20C11.4999857%2C8.433%209.93298567%2C10%207.99998567%2C10%20C6.06698567%2C10%204.49998567%2C8.433%204.49998567%2C6.5%20Z%20M14.7499857%2C9.5%20C15.1641857%2C9.5%2015.4999857%2C9.83579%2015.4999857%2C10.25%20L15.4999857%2C12%20L11.9999857%2C12%20C11.4476857%2C12%2010.9999857%2C12.4477%2010.9999857%2C13%20L10.9999857%2C16%20C10.9999857%2C16.5523%2011.4476857%2C17%2011.9999857%2C17%20L16.9999857%2C17%20C17.5522857%2C17%2017.9999857%2C16.5523%2017.9999857%2C16%20L17.9999857%2C13%20C17.9999857%2C12.4477%2017.5522857%2C12%2016.9999857%2C12%20L16.9999857%2C10.25%20C16.9999857%2C9.00736%2015.9925857%2C8%2014.7499857%2C8%20C13.5073857%2C8%2012.4999857%2C9.00736%2012.4999857%2C10.25%20C12.4999857%2C10.6642%2012.8357857%2C11%2013.2499857%2C11%20C13.6641857%2C11%2013.9999857%2C10.6642%2013.9999857%2C10.25%20C13.9999857%2C9.83579%2014.3357857%2C9.5%2014.7499857%2C9.5%20Z%20M12.4999857%2C13.5%20L12.4999857%2C15.5%20L16.4999857%2C15.5%20L16.4999857%2C13.5%20L12.4999857%2C13.5%20Z%20M5.30648567%2C13.3473%20C4.44348567%2C13.9672%203.87348567%2C14.8269%203.58858567%2C15.5067%20L9.31328567%2C15.5067%20C9.72758567%2C15.5067%2010.0632857%2C15.8424%2010.0632857%2C16.2567%20C10.0632857%2C16.6709%209.72758567%2C17.0067%209.31328567%2C17.0067%20L3.38388567%2C17.0067%20C2.97598567%2C17.0067%202.57868567%2C16.8398%202.30978567%2C16.5287%20C2.02848567%2C16.2033%201.91498567%2C15.7452%202.06928567%2C15.2863%20C2.39208567%2C14.3258%203.16388567%2C13.0395%204.43138567%2C12.129%20C5.72858567%2C11.1973%207.51528567%2C10.6837%209.76078567%2C11.2747%20C10.1613857%2C11.3801%2010.4006857%2C11.7903%2010.2952857%2C12.1909%20C10.1897857%2C12.5914%209.77968567%2C12.8307%209.37908567%2C12.7253%20C7.58108567%2C12.2521%206.25318567%2C12.6674%205.30648567%2C13.3473%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%20fill%3D%22%234A4A4A%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavSharedMutexesRulesActiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E4%BE%9B%E5%BA%94%E9%93%BE%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-2176%2C%20-208%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84%22%20transform%3D%22translate%282176%2C%20208%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20fill%3D%22%23FEE9E8%22%20fill-rule%3D%22nonzero%22%20opacity%3D%220%22%20x%3D%220%22%20y%3D%220%22%20width%3D%2220%22%20height%3D%2220%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M9.99998%2C14%20C9.58577%2C14%209.25%2C13.6642%209.25%2C13.25%20L9.25006%2C9.74999%20C9.25007%2C9.33577%209.58586%2C9%2010.0001%2C9%20C10.4143%2C9%2010.7501%2C9.3358%2010.7501%2C9.75001%20L10.75%2C13.25%20C10.75%2C13.6642%2010.4142%2C14%209.99998%2C14%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%20fill%3D%22%234A4A4A%22%20fill-rule%3D%22nonzero%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M9%2C7%20C9%2C6.44772%209.44772%2C6%2010%2C6%20C10.5523%2C6%2011%2C6.44772%2011%2C7%20C11%2C7.55228%2010.5523%2C8%2010%2C8%20C9.44772%2C8%209%2C7.55228%209%2C7%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%20fill%3D%22%234A4A4A%22%20fill-rule%3D%22nonzero%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M17%2C10%20C17%2C13.866%2013.866%2C17%2010%2C17%20C6.13401%2C17%203%2C13.866%203%2C10%20C3%2C6.13401%206.13401%2C3%2010%2C3%20C13.866%2C3%2017%2C6.13401%2017%2C10%20Z%20M15.5%2C10%20C15.5%2C13.0376%2013.0376%2C15.5%2010%2C15.5%20C6.96243%2C15.5%204.5%2C13.0376%204.5%2C10%20C4.5%2C6.96243%206.96243%2C4.5%2010%2C4.5%20C13.0376%2C4.5%2015.5%2C6.96243%2015.5%2C10%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%20fill%3D%22%234A4A4A%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavSharedMutexesRulesInactiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E4%BE%9B%E5%BA%94%E9%93%BE%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-2157%2C%20-208%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84%22%20transform%3D%22translate%282157%2C%20208%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20fill%3D%22%23FEE9E8%22%20fill-rule%3D%22nonzero%22%20opacity%3D%220%22%20x%3D%220%22%20y%3D%220%22%20width%3D%2220%22%20height%3D%2220%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M10%2C3%20C13.866%2C3%2017%2C6.13401%2017%2C10%20C17%2C13.866%2013.866%2C17%2010%2C17%20C6.13401%2C17%203%2C13.866%203%2C10%20C3%2C6.13401%206.13401%2C3%2010%2C3%20Z%20M10.0001%2C9%20C9.58586%2C9%209.25007%2C9.33577%209.25006%2C9.74999%20L9.25%2C13.25%20C9.25%2C13.6642%209.58577%2C14%209.99998%2C14%20C10.4142%2C14%2010.75%2C13.6642%2010.75%2C13.25%20L10.7501%2C9.75001%20C10.7501%2C9.3358%2010.4143%2C9%2010.0001%2C9%20Z%20M10%2C6%20C9.44772%2C6%209%2C6.44772%209%2C7%20C9%2C7.55228%209.44772%2C8%2010%2C8%20C10.5523%2C8%2011%2C7.55228%2011%2C7%20C11%2C6.44772%2010.5523%2C6%2010%2C6%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%E7%BB%93%E5%90%88%22%20fill%3D%22%234A4A4A%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavRestaurantActiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%2017%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E4%BE%9B%E5%BA%94%E9%93%BE%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-2305%2C%20-32%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-17%22%20transform%3D%22translate%282305%2C%2032%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22Active%3Dyes-%285%29%22%20transform%3D%22translate%283.0002%2C%203%29%22%20fill%3D%22%234A4A4A%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M10.257%2C0%20L3.74302%2C0%20C3.35944%2C0%202.9971%2C0.17611%202.76012%2C0.47772%20L0.37394%2C3.51468%20C0.13169%2C3.823%200%2C4.20375%200%2C4.59586%20L0%2C5.25%20C0%2C6.31867%200.60958%2C7.245%201.5%2C7.7001%20L1.5%2C12.25%20C1.5%2C13.2165%202.2835%2C14%203.25%2C14%20L10.75%2C14%20C11.7165%2C14%2012.5%2C13.2165%2012.5%2C12.25%20L12.5%2C7.7001%20C13.3904%2C7.245%2014%2C6.31867%2014%2C5.25%20L14%2C4.76879%20C14%2C4.26465%2013.8307%2C3.77511%2013.5192%2C3.3787%20L11.2399%2C0.47772%20C11.0029%2C0.17611%2010.6406%2C0%2010.257%2C0%20Z%20M10%2C12.5%20L10.75%2C12.5%20C10.8881%2C12.5%2011%2C12.3881%2011%2C12.25%20L11%2C8%20C10.212%2C8%209.5014%2C7.6686%209%2C7.1375%20C8.4986%2C7.6686%207.788%2C8%207%2C8%20C6.21199%2C8%205.50138%2C7.6686%205%2C7.1375%20C4.49862%2C7.6686%203.78801%2C8%203%2C8%20L3%2C12.25%20C3%2C12.3881%203.11193%2C12.5%203.25%2C12.5%20L7%2C12.5%20L7%2C10%20C7%2C9.4477%207.4477%2C9%208%2C9%20L9%2C9%20C9.5523%2C9%2010%2C9.4477%2010%2C10%20L10%2C12.5%20Z%20M3%2C6.5%20L2.75%2C6.5%20C2.05964%2C6.5%201.5%2C5.94036%201.5%2C5.25%20L1.5%2C4.59586%20C1.5%2C4.53985%201.51881%2C4.48545%201.55342%2C4.44141%20L3.86453%2C1.5%20L10.1355%2C1.5%20L12.3397%2C4.30543%20C12.4436%2C4.43757%2012.5%2C4.60075%2012.5%2C4.76879%20L12.5%2C5.25%20C12.5%2C5.94036%2011.9404%2C6.5%2011.25%2C6.5%20L11%2C6.5%20C10.3096%2C6.5%209.75%2C5.94036%209.75%2C5.25%20L9.75%2C4.75%20C9.75%2C4.33579%209.4142%2C4%209%2C4%20C8.5858%2C4%208.25%2C4.33579%208.25%2C4.75%20L8.25%2C5.25%20C8.25%2C5.94036%207.6904%2C6.5%207%2C6.5%20C6.30964%2C6.5%205.75%2C5.94036%205.75%2C5.25%20L5.75%2C4.75%20C5.75%2C4.33579%205.41421%2C4%205%2C4%20C4.58579%2C4%204.25%2C4.33579%204.25%2C4.75%20L4.25%2C5.25%20C4.25%2C5.94036%203.69036%2C6.5%203%2C6.5%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavRestaurantInactiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%2010%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E5%85%AC%E5%85%B1%E7%BB%84%E4%BB%B6%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E5%B8%AE%E5%8A%A9%E6%8F%90%E7%A4%BA%22%20transform%3D%22translate%28-16%2C%20-98%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-10%22%20transform%3D%22translate%2816%2C%2098%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22StoreFilled%22%20transform%3D%22translate%283%2C%203%29%22%20fill%3D%22%234A4A4A%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M9.23731046%2C0%20C10.0812105%2C0%2010.8784105%2C0.38744%2011.3997105%2C1.05099%20L13.5597105%2C3.80012%20L13.5697105%2C3.81407%20C14.3053105%2C4.84395%2013.8075105%2C6.20962%2012.7381105%2C6.62496%20L11.5679105%2C6.62496%20C11.0938105%2C6.62496%2010.6532105%2C6.38073%2010.4019105%2C5.97871%20L9.73941046%2C4.91871%20C9.62691046%2C4.73867%209.43771046%2C4.6357%209.24061046%2C4.62568%20C9.23051046%2C4.62517%209.22051046%2C4.62491%209.21051046%2C4.62488%20C9.09681046%2C4.62458%208.98171046%2C4.65526%208.87821046%2C4.71996%20C8.79081046%2C4.77458%208.72151046%2C4.84726%208.67231046%2C4.93014%20C8.65271046%2C4.96312%208.63591046%2C4.99828%208.62241046%2C5.03537%20C8.61431046%2C5.05745%208.60751046%2C5.07995%208.60201046%2C5.10276%20L8.52751046%2C5.40069%20C8.34761046%2C6.12024%207.70111046%2C6.62503%206.95941046%2C6.62503%20C6.21772046%2C6.62503%205.57121046%2C6.12024%205.39132046%2C5.40069%20L5.31678046%2C5.10255%20C5.31159046%2C5.08114%205.30527046%2C5.06001%205.29785046%2C5.03924%20C5.28363046%2C4.99938%205.26565046%2C4.96173%205.24442046%2C4.92659%20C5.19530046%2C4.84519%205.12681046%2C4.7738%205.04067046%2C4.71996%20C4.93715046%2C4.65526%204.82202046%2C4.62458%204.70834046%2C4.62488%20C4.69883046%2C4.6249%204.68929046%2C4.62515%204.67974046%2C4.62561%20C4.48210046%2C4.63518%204.29223046%2C4.73821%204.17942046%2C4.91871%20L3.51692046%2C5.97871%20C3.26565046%2C6.38073%202.82501046%2C6.62496%202.35092046%2C6.62496%20L1.18073046%2C6.62496%20C0.111310464%2C6.20962%20-0.386509536%2C4.84395%200.349120464%2C3.81407%20L0.359080464%2C3.80012%20L2.51911046%2C1.05099%20C3.04047046%2C0.38744%203.83762046%2C0%204.68149046%2C0%20L9.23731046%2C0%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%20fill-rule%3D%22nonzero%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M1.45942046%2C7.875%20L1.45942046%2C12.25%20C1.45942046%2C13.2165%202.24292046%2C14%203.20942046%2C14%20L10.7094105%2C14%20C11.6759105%2C14%2012.4594205%2C13.2165%2012.4594205%2C12.25%20L12.4594205%2C7.875%20L11.5679105%2C7.875%20C10.6628105%2C7.875%209.82161046%2C7.4087%209.34191046%2C6.64121%20L9.32941046%2C6.62126%20C8.80721046%2C7.3884%207.92921046%2C7.875%206.95941046%2C7.875%20C5.98964046%2C7.875%205.11162046%2C7.3884%204.58939046%2C6.62126%20L4.57692046%2C6.64121%20C4.09723046%2C7.4087%203.25600046%2C7.875%202.35092046%2C7.875%20L1.45942046%2C7.875%20Z%20M9.95941046%2C10.35%20C9.95941046%2C9.7977%209.51171046%2C9.35%208.95941046%2C9.35%20L7.95941046%2C9.35%20C7.40711046%2C9.35%206.95941046%2C9.7977%206.95941046%2C10.35%20L6.95941046%2C12.75%20C6.95941046%2C12.888%207.07131046%2C13%207.20941046%2C13%20L9.70941046%2C13%20C9.84751046%2C13%209.95941046%2C12.888%209.95941046%2C12.75%20L9.95941046%2C10.35%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavSMSActiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3EChat%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22Chat%22%20stroke%3D%22none%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%20stroke-width%3D%221%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84%22%20fill%3D%22%234A4A4A%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M6%2C8%20C6%2C7.58579%206.33579%2C7.25%206.75%2C7.25%20L12.25%2C7.25%20C12.6642%2C7.25%2013%2C7.58579%2013%2C8%20C13%2C8.41421%2012.6642%2C8.75%2012.25%2C8.75%20L6.75%2C8.75%20C6.33579%2C8.75%206%2C8.41421%206%2C8%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%20fill-rule%3D%22nonzero%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M6.75%2C9.5%20C6.33579%2C9.5%206%2C9.83579%206%2C10.25%20C6%2C10.6642%206.33579%2C11%206.75%2C11%20L10.25%2C11%20C10.6642%2C11%2011%2C10.6642%2011%2C10.25%20C11%2C9.83579%2010.6642%2C9.5%2010.25%2C9.5%20L6.75%2C9.5%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%20fill-rule%3D%22nonzero%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M3%2C8.75%20C3%2C6.12665%205.12665%2C4%207.75%2C4%20L12.25%2C4%20C14.8734%2C4%2017%2C6.12665%2017%2C8.75%20L17%2C9.5%20C17%2C12.1234%2014.8734%2C14.25%2012.25%2C14.25%20L10.6241%2C14.25%20L7.44628%2C16.6028%20C7.21864%2C16.7713%206.91547%2C16.7972%206.66252%2C16.6698%20C6.40957%2C16.5423%206.25%2C16.2832%206.25%2C16%20L6.25%2C14.0081%20C4.36171%2C13.38%203%2C11.5997%203%2C9.5%20L3%2C8.75%20Z%20M7.75%2C5.5%20C5.95507%2C5.5%204.5%2C6.95507%204.5%2C8.75%20L4.5%2C9.5%20C4.5%2C11.0858%205.63655%2C12.4078%207.13978%2C12.6929%20C7.49378%2C12.7601%207.75%2C13.0695%207.75%2C13.4298%20L7.75%2C14.5115%20L9.93034%2C12.8972%20C10.0595%2C12.8016%2010.2159%2C12.75%2010.3766%2C12.75%20L12.25%2C12.75%20C14.0449%2C12.75%2015.5%2C11.2949%2015.5%2C9.5%20L15.5%2C8.75%20C15.5%2C6.95507%2014.0449%2C5.5%2012.25%2C5.5%20L7.75%2C5.5%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavSMSInactiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3EChat%E5%A4%87%E4%BB%BD%202%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22Chat%E5%A4%87%E4%BB%BD-2%22%20stroke%3D%22none%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%20stroke-width%3D%221%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84%22%20fill%3D%22%234A4A4A%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M12.25%2C4%20C14.8734%2C4%2017%2C6.12665%2017%2C8.75%20L17%2C9.5%20C17%2C12.1234%2014.8734%2C14.25%2012.25%2C14.25%20L10.6241%2C14.25%20L7.44628%2C16.6028%20C7.21864%2C16.7713%206.91547%2C16.7972%206.66252%2C16.6698%20C6.40957%2C16.5423%206.25%2C16.2832%206.25%2C16%20L6.25%2C14.0081%20C4.36171%2C13.38%203%2C11.5997%203%2C9.5%20L3%2C8.75%20C3%2C6.12665%205.12665%2C4%207.75%2C4%20L12.25%2C4%20Z%20M10.25%2C9.5%20L6.75%2C9.5%20C6.33579%2C9.5%206%2C9.83579%206%2C10.25%20C6%2C10.6642%206.33579%2C11%206.75%2C11%20L10.25%2C11%20C10.6642%2C11%2011%2C10.6642%2011%2C10.25%20C11%2C9.83579%2010.6642%2C9.5%2010.25%2C9.5%20Z%20M12.25%2C7.25%20L6.75%2C7.25%20C6.33579%2C7.25%206%2C7.58579%206%2C8%20C6%2C8.41421%206.33579%2C8.75%206.75%2C8.75%20L12.25%2C8.75%20C12.6642%2C8.75%2013%2C8.41421%2013%2C8%20C13%2C7.58579%2012.6642%2C7.25%2012.25%2C7.25%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavInstitutionAccountActiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%2019%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E4%BE%9B%E5%BA%94%E9%93%BE%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-1985%2C%20-76%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-19%22%20transform%3D%22translate%281985%2C%2076%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%E5%A4%87%E4%BB%BD-4%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22Active%3Dyes-%287%29%22%20transform%3D%22translate%283%2C%204%29%22%20fill%3D%22%234A4A4A%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M8.7500003%2C7.0000003%20C8.7500003%2C6.5858003%209.0858003%2C6.2500003%209.5000003%2C6.2500003%20L10.7500003%2C6.2500003%20C11.1642003%2C6.2500003%2011.5000003%2C6.5858003%2011.5000003%2C7.0000003%20C11.5000003%2C7.4142003%2011.1642003%2C7.7500003%2010.7500003%2C7.7500003%20L9.5000003%2C7.7500003%20C9.0858003%2C7.7500003%208.7500003%2C7.4142003%208.7500003%2C7.0000003%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%20fill-rule%3D%22nonzero%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M9.5000003%2C4.0000003%20C9.0858003%2C4.0000003%208.7500003%2C4.3357903%208.7500003%2C4.7500003%20C8.7500003%2C5.1642103%209.0858003%2C5.5000003%209.5000003%2C5.5000003%20L10.7500003%2C5.5000003%20C11.1642003%2C5.5000003%2011.5000003%2C5.1642103%2011.5000003%2C4.7500003%20C11.5000003%2C4.3357903%2011.1642003%2C4.0000003%2010.7500003%2C4.0000003%20L9.5000003%2C4.0000003%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%20fill-rule%3D%22nonzero%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M5.0000003%2C7.5000003%20C6.3807103%2C7.5000003%207.5000003%2C6.3807003%207.5000003%2C5.0000003%20C7.5000003%2C3.6192903%206.3807103%2C2.5000003%205.0000003%2C2.5000003%20C3.6192903%2C2.5000003%202.5000003%2C3.6192903%202.5000003%2C5.0000003%20C2.5000003%2C6.3807003%203.6192903%2C7.5000003%205.0000003%2C7.5000003%20Z%20M5.0000003%2C6.0000003%20C5.5522803%2C6.0000003%206.0000003%2C5.5522803%206.0000003%2C5.0000003%20C6.0000003%2C4.4477203%205.5522803%2C4.0000003%205.0000003%2C4.0000003%20C4.4477203%2C4.0000003%204.0000003%2C4.4477203%204.0000003%2C5.0000003%20C4.0000003%2C5.5522803%204.4477203%2C6.0000003%205.0000003%2C6.0000003%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M5.5179003%2C0%20L8.4821003%2C0%20C9.2949003%2C0%209.9506003%2C0%2010.4815003%2C0.0433602969%20C11.0281003%2C0.0880302969%2011.5082003%2C0.182390297%2011.9525003%2C0.408730297%20C12.6581003%2C0.768250297%2013.2317003%2C1.3419303%2013.5913003%2C2.0475403%20C13.8176003%2C2.4917603%2013.9120003%2C2.9719003%2013.9566003%2C3.5185303%20C14%2C4.0494403%2014%2C4.7050503%2014%2C5.5178803%20L14%2C6.4821003%20C14%2C7.2950003%2014%2C7.9506003%2013.9566003%2C8.4815003%20C13.9120003%2C9.0281003%2013.8176003%2C9.5082003%2013.5913003%2C9.9525003%20C13.2317003%2C10.6581003%2012.6581003%2C11.2318003%2011.9525003%2C11.5913003%20C11.5082003%2C11.8176003%2011.0281003%2C11.9120003%2010.4815003%2C11.9566003%20C9.9506003%2C12%209.2950003%2C12%208.4821003%2C12%20L5.5178803%2C12%20C4.7050503%2C12%204.0494403%2C12%203.5185303%2C11.9566003%20C2.9719003%2C11.9120003%202.4917503%2C11.8176003%202.0475403%2C11.5913003%20C1.3419303%2C11.2318003%200.768250297%2C10.6581003%200.408730297%2C9.9525003%20C0.182390297%2C9.5082003%200.0880302969%2C9.0281003%200.0433602969%2C8.4815003%20C0%2C7.9506003%200%2C7.2950003%200%2C6.4821003%20L0%2C5.5179003%20C0%2C4.7050603%200%2C4.0494403%200.0433602969%2C3.5185303%20C0.0880302969%2C2.9719003%200.182390297%2C2.4917603%200.408730297%2C2.0475403%20C0.768250297%2C1.3419303%201.3419303%2C0.768250297%202.0475403%2C0.408730297%20C2.4917503%2C0.182390297%202.9719003%2C0.0880302969%203.5185303%2C0.0433602969%20C4.0494403%2C0%204.7050703%2C0%205.5179003%2C0%20Z%20M3.6406803%2C1.5383803%20C3.1868103%2C1.5754703%202.9260403%2C1.6446003%202.7285203%2C1.7452403%20C2.3051603%2C1.9609503%201.9609503%2C2.3051603%201.7452403%2C2.7285203%20C1.6446003%2C2.9260403%201.5754703%2C3.1868103%201.5383803%2C3.6406803%20C1.5005803%2C4.1033103%201.5000003%2C4.6975503%201.5000003%2C5.5500003%20L1.5000003%2C6.4500003%20C1.5000003%2C7.3025003%201.5005803%2C7.8967003%201.5383803%2C8.3593003%20C1.5583303%2C8.6035003%201.5875503%2C8.7917003%201.6259503%2C8.9443003%20C2.0639903%2C8.6783003%202.5333203%2C8.4642003%203.0246203%2C8.3074003%20C3.9503303%2C8.0121003%204.9304603%2C7.9281003%205.8929203%2C8.0616003%20C6.8553703%2C8.1951003%207.7757003%2C8.5427003%208.5861003%2C9.0787003%20C9.1657003%2C9.4621003%209.6785003%2C9.9350003%2010.1063003%2C10.4781003%20C10.1951003%2C10.4736003%2010.2792003%2C10.4682003%2010.3593003%2C10.4616003%20C10.8132003%2C10.4245003%2011.0740003%2C10.3554003%2011.2715003%2C10.2548003%20C11.6948003%2C10.0391003%2012.0390003%2C9.6948003%2012.2548003%2C9.2715003%20C12.3554003%2C9.0740003%2012.4245003%2C8.8132003%2012.4616003%2C8.3593003%20C12.4994003%2C7.8967003%2012.5000003%2C7.3025003%2012.5000003%2C6.4500003%20L12.5000003%2C5.5500003%20C12.5000003%2C4.6975503%2012.4994003%2C4.1033103%2012.4616003%2C3.6406803%20C12.4245003%2C3.1868103%2012.3554003%2C2.9260403%2012.2548003%2C2.7285203%20C12.0390003%2C2.3051603%2011.6948003%2C1.9609503%2011.2715003%2C1.7452403%20C11.0740003%2C1.6446003%2010.8132003%2C1.5754703%2010.3593003%2C1.5383803%20C9.8967003%2C1.5005803%209.3025003%2C1.5000003%208.4500003%2C1.5000003%20L5.5500003%2C1.5000003%20C4.6975503%2C1.5000003%204.1033103%2C1.5005803%203.6406803%2C1.5383803%20Z%20M8.0000003%2C10.5000003%20C7.9213003%2C10.4410003%207.8408003%2C10.3842003%207.7585003%2C10.3298003%20C7.1351003%2C9.9174003%206.4272203%2C9.6501003%205.6868603%2C9.5474003%20C4.9465103%2C9.4447003%204.1925603%2C9.5093003%203.4804803%2C9.7365003%20C3.1527503%2C9.8410003%202.8377203%2C9.9788003%202.5400803%2C10.1470003%20C2.6009203%2C10.1858003%202.6638003%2C10.2218003%202.7285203%2C10.2548003%20C2.9260403%2C10.3554003%203.1868103%2C10.4245003%203.6406803%2C10.4616003%20C4.1033103%2C10.4994003%204.6975503%2C10.5000003%205.5500003%2C10.5000003%20L8.0000003%2C10.5000003%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M5.0000003%2C4.0000003%20C4.4477203%2C4.0000003%204.0000003%2C4.4477203%204.0000003%2C5.0000003%20C4.0000003%2C5.5522803%204.4477203%2C6.0000003%205.0000003%2C6.0000003%20C5.5522803%2C6.0000003%206.0000003%2C5.5522803%206.0000003%2C5.0000003%20C6.0000003%2C4.4477203%205.5522803%2C4.0000003%205.0000003%2C4.0000003%20Z%20M2.5000003%2C5.0000003%20C2.5000003%2C3.6192903%203.6192903%2C2.5000003%205.0000003%2C2.5000003%20C6.3807103%2C2.5000003%207.5000003%2C3.6192903%207.5000003%2C5.0000003%20C7.5000003%2C6.3807003%206.3807103%2C7.5000003%205.0000003%2C7.5000003%20C3.6192903%2C7.5000003%202.5000003%2C6.3807003%202.5000003%2C5.0000003%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavInstitutionAccountInactiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%2011%E5%A4%87%E4%BB%BD%202%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E4%BE%9B%E5%BA%94%E9%93%BE%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-1965%2C%20-76%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-11%E5%A4%87%E4%BB%BD-2%22%20transform%3D%22translate%281965%2C%2076%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22IdentityCardFilled%22%20transform%3D%22translate%283%2C%204%29%22%20fill%3D%22%234A4A4A%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M5.5179003%2C0%20L8.4821003%2C0%20C9.2949003%2C0%209.9506003%2C0%2010.4815003%2C0.0433602969%20C11.0281003%2C0.0880302969%2011.5082003%2C0.182390297%2011.9525003%2C0.408730297%20C12.6581003%2C0.768250297%2013.2317003%2C1.3419303%2013.5913003%2C2.0475403%20C13.8176003%2C2.4917603%2013.9120003%2C2.9719003%2013.9566003%2C3.5185303%20C14%2C4.0494403%2014%2C4.7050503%2014%2C5.5178803%20L14%2C6.4821003%20C14%2C7.2950003%2014%2C7.9506003%2013.9566003%2C8.4815003%20C13.9120003%2C9.0281003%2013.8176003%2C9.5082003%2013.5913003%2C9.9525003%20C13.2317003%2C10.6581003%2012.6581003%2C11.2317003%2011.9525003%2C11.5913003%20C11.5082003%2C11.8176003%2011.0281003%2C11.9120003%2010.4815003%2C11.9566003%20C9.9506003%2C12%209.2950003%2C12%208.4821003%2C12%20L5.5178803%2C12%20C4.7050503%2C12%204.0494403%2C12%203.5185303%2C11.9566003%20C2.9719003%2C11.9120003%202.4917503%2C11.8176003%202.0475403%2C11.5913003%20C1.3419303%2C11.2317003%200.768250297%2C10.6581003%200.408730297%2C9.9525003%20C0.182390297%2C9.5082003%200.0880302969%2C9.0281003%200.0433602969%2C8.4815003%20C0%2C7.9506003%200%2C7.2950003%200%2C6.4821003%20L0%2C5.5179003%20C0%2C4.7050603%200%2C4.0494403%200.0433602969%2C3.5185303%20C0.0880302969%2C2.9719003%200.182390297%2C2.4917603%200.408730297%2C2.0475403%20C0.768250297%2C1.3419303%201.3419303%2C0.768250297%202.0475403%2C0.408730297%20C2.4917503%2C0.182390297%202.9719003%2C0.0880302969%203.5185303%2C0.0433602969%20C4.0494303%2C0%204.7050703%2C0%205.5179003%2C0%20Z%20M2.8750003%2C5.0000003%20C2.8750003%2C3.8264003%203.8264003%2C2.8750003%205.0000003%2C2.8750003%20C6.1736003%2C2.8750003%207.1250003%2C3.8264003%207.1250003%2C5.0000003%20C7.1250003%2C6.1736003%206.1736003%2C7.1250003%205.0000003%2C7.1250003%20C3.8264003%2C7.1250003%202.8750003%2C6.1736003%202.8750003%2C5.0000003%20Z%20M1.7558403%2C9.2842003%20C1.7729303%2C9.3195003%201.7919803%2C9.3566003%201.8131403%2C9.3950003%20C2.0299903%2C9.7618003%202.3471403%2C10.0604003%202.7285203%2C10.2548003%20C2.9260403%2C10.3554003%203.1868003%2C10.4245003%203.6406803%2C10.4616003%20C4.0839903%2C10.4978003%204.6481403%2C10.4999003%205.4445403%2C10.5000003%20L9.4062003%2C10.5000003%20C9.6978003%2C10.5000003%209.8555003%2C10.1687003%209.6518003%2C9.9601003%20C9.3299003%2C9.6303003%208.9729003%2C9.3346003%208.5861003%2C9.0787003%20C7.7757003%2C8.5427003%206.8553703%2C8.1951003%205.8929203%2C8.0616003%20C4.9304603%2C7.9281003%203.9503203%2C8.0121003%203.0246203%2C8.3074003%20C2.6499203%2C8.4270003%202.2880103%2C8.5799003%201.9430003%2C8.7637003%20C1.7511403%2C8.8660003%201.6611603%2C9.0885003%201.7558403%2C9.2842003%20Z%20M8.7500003%2C7.0000003%20C8.7500003%2C6.5858003%209.0858003%2C6.2500003%209.5000003%2C6.2500003%20L10.7500003%2C6.2500003%20C11.1642003%2C6.2500003%2011.5000003%2C6.5858003%2011.5000003%2C7.0000003%20C11.5000003%2C7.4142003%2011.1642003%2C7.7500003%2010.7500003%2C7.7500003%20L9.5000003%2C7.7500003%20C9.0858003%2C7.7500003%208.7500003%2C7.4142003%208.7500003%2C7.0000003%20Z%20M9.5000003%2C4.0000003%20C9.0858003%2C4.0000003%208.7500003%2C4.3357903%208.7500003%2C4.7500003%20C8.7500003%2C5.1642103%209.0858003%2C5.5000003%209.5000003%2C5.5000003%20L10.7500003%2C5.5000003%20C11.1642003%2C5.5000003%2011.5000003%2C5.1642103%2011.5000003%2C4.7500003%20C11.5000003%2C4.3357903%2011.1642003%2C4.0000003%2010.7500003%2C4.0000003%20L9.5000003%2C4.0000003%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var CommentManageIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E4%BE%9B%E5%BA%94%E9%93%BE%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-2113%2C%20-208%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84%22%20transform%3D%22translate%282113%2C%20208%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2-4%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22Icon%22%20transform%3D%22translate%283%2C%200%29%22%20fill%3D%22%234A4A4A%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84%22%20transform%3D%22translate%280%2C%201%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M12.997%2C1.10355%20C13.1923%2C0.908292%2013.5089%2C0.908292%2013.7041%2C1.10355%20L14.7648%2C2.16421%20C14.9601%2C2.35948%2014.9601%2C2.67606%2014.7648%2C2.87132%20L13.8077%2C3.82844%20L12.0399%2C2.06067%20L12.997%2C1.10355%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%20fill-rule%3D%22nonzero%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M11.3328%2C2.76778%20L13.1006%2C4.53554%20L9.26999%2C8.36613%20C8.91115%2C8.72497%208.4293%2C8.93398%207.9221%2C8.95081%20L7.15008%2C8.97642%20C7.00557%2C8.98122%206.88714%2C8.86278%206.89193%2C8.71827%20L6.91754%2C7.94626%20C6.93437%2C7.43905%207.14338%2C6.95721%207.50223%2C6.59836%20L11.3328%2C2.76778%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%20fill-rule%3D%22nonzero%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M3.97553%2C5%20C3.35593%2C5%202.82983%2C5.4539%202.73903%2C6.06681%20L2.15633%2C10%20L4.63962%2C10%20C5.17766%2C10%205.65533%2C10.3443%205.82547%2C10.8547%20L5.98359%2C11.3291%20C6.01762%2C11.4311%206.11315%2C11.5%206.22076%2C11.5%20L7.77924%2C11.5%20C7.88685%2C11.5%207.98238%2C11.4311%208.01641%2C11.3291%20L8.17453%2C10.8547%20C8.34467%2C10.3443%208.82234%2C10%209.36038%2C10%20L11.8437%2C10%20L11.5081%2C7.73491%20C11.4474%2C7.32517%2011.7303%2C6.9438%2012.1401%2C6.8831%20C12.5498%2C6.8224%2012.9312%2C7.10535%2012.9919%2C7.51509%20L13.4055%2C10.3071%20C13.4684%2C10.7316%2013.5%2C11.1602%2013.5%2C11.5894%20L13.5%2C13.25%20C13.5%2C15.0449%2012.0449%2C16.5%2010.25%2C16.5%20L3.75%2C16.5%20C1.95507%2C16.5%200.5%2C15.0449%200.5%2C13.25%20L0.5%2C11.5894%20C0.5%2C11.1602%200.531575%2C10.7316%200.59447%2C10.3071%20L1.25522%2C5.84699%20C1.45499%2C4.49858%202.6124%2C3.5%203.97553%2C3.5%20L7.75%2C3.5%20C8.16421%2C3.5%208.5%2C3.83579%208.5%2C4.25%20C8.5%2C4.66421%208.16421%2C5%207.75%2C5%20L3.97553%2C5%20Z%20M2%2C11.5894%20C2%2C11.5596%202.00018%2C11.5298%202.00055%2C11.5%20L4.45943%2C11.5%20L4.56056%2C11.8034%20C4.79876%2C12.518%205.46751%2C13%206.22076%2C13%20L7.77924%2C13%20C8.53249%2C13%209.20124%2C12.518%209.43944%2C11.8034%20L9.54057%2C11.5%20L11.9994%2C11.5%20C11.9998%2C11.5298%2012%2C11.5596%2012%2C11.5894%20L12%2C13.25%20C12%2C14.2165%2011.2165%2C15%2010.25%2C15%20L3.75%2C15%20C2.7835%2C15%202%2C14.2165%202%2C13.25%20L2%2C11.5894%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var CommentManageFilledIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3EOrderDraftFilled%E5%A4%87%E4%BB%BD%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22OrderDraftFilled%E5%A4%87%E4%BB%BD%22%20stroke%3D%22none%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%20stroke-width%3D%221%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84%22%20fill%3D%22%234A4A4A%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M16.7041%2C1.10355%20C16.5089%2C0.908292%2016.1923%2C0.908292%2015.997%2C1.10355%20L15.0399%2C2.06067%20L16.8077%2C3.82844%20L17.7648%2C2.87132%20C17.9601%2C2.67606%2017.9601%2C2.35948%2017.7648%2C2.16421%20L16.7041%2C1.10355%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%20fill-rule%3D%22nonzero%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M16.1006%2C4.53554%20L14.3328%2C2.76778%20L10.5022%2C6.59836%20C10.1434%2C6.95721%209.93437%2C7.43905%209.91754%2C7.94626%20L9.89193%2C8.71827%20C9.88714%2C8.86278%2010.0056%2C8.98122%2010.1501%2C8.97642%20L10.9221%2C8.95081%20C11.4293%2C8.93398%2011.9111%2C8.72497%2012.27%2C8.36613%20L16.1006%2C4.53554%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%20fill-rule%3D%22nonzero%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M5.73903%2C6.06681%20C5.82983%2C5.4539%206.35593%2C5%206.97553%2C5%20L10.75%2C5%20C11.1642%2C5%2011.5%2C4.66421%2011.5%2C4.25%20C11.5%2C3.83579%2011.1642%2C3.5%2010.75%2C3.5%20L6.97553%2C3.5%20C5.6124%2C3.5%204.45499%2C4.49858%204.25522%2C5.84699%20L3.59447%2C10.3071%20C3.53158%2C10.7316%203.5%2C11.1602%203.5%2C11.5894%20L3.5%2C13.25%20C3.5%2C15.0449%204.95507%2C16.5%206.75%2C16.5%20L13.25%2C16.5%20C15.0449%2C16.5%2016.5%2C15.0449%2016.5%2C13.25%20L16.5%2C11.5894%20C16.5%2C11.1602%2016.4684%2C10.7316%2016.4055%2C10.3071%20L15.9919%2C7.51509%20C15.9312%2C7.10535%2015.5498%2C6.8224%2015.1401%2C6.8831%20C14.7303%2C6.9438%2014.4474%2C7.32517%2014.5081%2C7.73491%20L14.8437%2C10%20L12.3604%2C10%20C11.8223%2C10%2011.3447%2C10.3443%2011.1745%2C10.8547%20L11.0164%2C11.3291%20C10.9824%2C11.4311%2010.8868%2C11.5%2010.7792%2C11.5%20L9.22076%2C11.5%20C9.11315%2C11.5%209.01762%2C11.4311%208.98359%2C11.3291%20L8.82547%2C10.8547%20C8.65533%2C10.3443%208.17766%2C10%207.63962%2C10%20L5.15633%2C10%20L5.73903%2C6.06681%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%20fill-rule%3D%22nonzero%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var MaterialCenterInActiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3EImage%E5%A4%87%E4%BB%BD%202%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22Image%E5%A4%87%E4%BB%BD-2%22%20stroke%3D%22none%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%20stroke-width%3D%221%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84%22%20fill%3D%22%234A4A4A%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M12.9815%2C3.54336%20C13.5281%2C3.58803%2014.0082%2C3.68239%2014.4525%2C3.90873%20C15.1581%2C4.26825%2015.7317%2C4.84193%2016.0913%2C5.54754%20C16.3176%2C5.99175%2016.412%2C6.4719%2016.4566%2C7.01853%20C16.4937997%2C7.47359571%2016.499114%2C8.02027776%2016.4998732%2C8.67905519%20L16.4999997%2C10.9821%20C16.4999997%2C11.795%2016.4999997%2C12.4506%2016.4566%2C12.9815%20C16.412%2C13.5281%2016.3176%2C14.0082%2016.0913%2C14.4525%20C15.7317%2C15.1581%2015.1581%2C15.7317%2014.4525%2C16.0913%20C14.0082%2C16.3176%2013.5281%2C16.412%2012.9815%2C16.4566%20C12.5264429%2C16.4937997%2011.9797694%2C16.499114%2011.3209501%2C16.4998732%20L9.01788%2C16.4999997%20C8.20505%2C16.4999997%207.54944%2C16.4999997%207.01853%2C16.4566%20C6.4719%2C16.412%205.99175%2C16.3176%205.54754%2C16.0913%20C4.84193%2C15.7317%204.26825%2C15.1581%203.90873%2C14.4525%20C3.68239%2C14.0082%203.58803%2C13.5281%203.54336%2C12.9815%20C3.50619403%2C12.5264429%203.50088461%2C11.9796959%203.50012612%2C11.3209187%20L3.4999997%2C9.0179%20C3.4999997%2C8.20506%203.4999997%2C7.54944%203.54336%2C7.01853%20C3.58803%2C6.4719%203.68239%2C5.99175%203.90873%2C5.54754%20C4.26825%2C4.84193%204.84193%2C4.26825%205.54754%2C3.90873%20C5.99175%2C3.68239%206.4719%2C3.58803%207.01853%2C3.54336%20C7.47359571%2C3.50619403%208.0202851%2C3.50088461%208.67907093%2C3.50012612%20L10.9821%2C3.4999997%20C11.7949%2C3.4999997%2012.4506%2C3.4999997%2012.9815%2C3.54336%20Z%20M7.71577334%2C10.2596108%20C7.24156883%2C9.97650562%206.62764805%2C10.1314223%206.34454289%2C10.6056268%20L5.07615%2C12.7302%20L5.03225%2C12.7785%20C5.03417%2C12.8059%205.03622%2C12.8328%205.03838%2C12.8593%20C5.07547%2C13.3132%205.1446%2C13.574%205.24524%2C13.7715%20C5.46095%2C14.1948%205.80516%2C14.539%206.22852%2C14.7548%20C6.42604%2C14.8554%206.68681%2C14.9245%207.14068%2C14.9616%20C7.60331%2C14.9994%208.19755%2C15%209.05%2C15%20L10.95%2C15%20C11.8025%2C15%2012.3967%2C14.9994%2012.8593%2C14.9616%20C13.3132%2C14.9245%2013.574%2C14.8554%2013.7715%2C14.7548%20C14.1948%2C14.539%2014.539%2C14.1948%2014.7548%2C13.7715%20C14.7803%2C13.7214%2014.8037%2C13.6674%2014.8252%2C13.6071%20L14.0506804%2C11.604518%20C14.0251161%2C11.5384194%2013.9857924%2C11.4785106%2013.9353166%2C11.4287637%20C13.7386398%2C11.2349266%2013.4220657%2C11.2372287%2013.2282286%2C11.4339055%20L11.9035153%2C12.7780263%20C11.5248706%2C13.1622181%2010.9097529%2C13.1768591%2010.5132584%2C12.8111171%20L7.88119307%2C10.383197%20C7.83046546%2C10.3364038%207.77503009%2C10.2949877%207.71577334%2C10.2596108%20Z%20M12.5%2C6%20C11.6716%2C6%2011%2C6.67157%2011%2C7.5%20C11%2C8.32843%2011.6716%2C9%2012.5%2C9%20C13.3284%2C9%2014%2C8.32843%2014%2C7.5%20C14%2C6.67157%2013.3284%2C6%2012.5%2C6%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%E7%BB%93%E5%90%88%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var MaterialCenterIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3EImage%E5%A4%87%E4%BB%BD%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22Image%E5%A4%87%E4%BB%BD%22%20stroke%3D%22none%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%20stroke-width%3D%221%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84%22%20fill%3D%22%234A4A4A%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M12.5%2C9%20C13.3284%2C9%2014%2C8.32843%2014%2C7.5%20C14%2C6.67157%2013.3284%2C6%2012.5%2C6%20C11.6716%2C6%2011%2C6.67157%2011%2C7.5%20C11%2C8.32843%2011.6716%2C9%2012.5%2C9%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%20fill-rule%3D%22nonzero%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M9.0179%2C3.4999997%20L10.9821%2C3.4999997%20C11.7949%2C3.4999997%2012.4506%2C3.4999997%2012.9815%2C3.54336%20C13.5281%2C3.58803%2014.0082%2C3.68239%2014.4525%2C3.90873%20C15.1581%2C4.26825%2015.7317%2C4.84193%2016.0913%2C5.54754%20C16.3176%2C5.99175%2016.412%2C6.4719%2016.4566%2C7.01853%20C16.4999997%2C7.54944%2016.4999997%2C8.20505%2016.4999997%2C9.01788%20L16.4999997%2C10.9821%20C16.4999997%2C11.795%2016.4999997%2C12.4506%2016.4566%2C12.9815%20C16.412%2C13.5281%2016.3176%2C14.0082%2016.0913%2C14.4525%20C15.7317%2C15.1581%2015.1581%2C15.7317%2014.4525%2C16.0913%20C14.0082%2C16.3176%2013.5281%2C16.412%2012.9815%2C16.4566%20C12.4506%2C16.4999997%2011.795%2C16.4999997%2010.9821%2C16.4999997%20L9.01788%2C16.4999997%20C8.20505%2C16.4999997%207.54944%2C16.4999997%207.01853%2C16.4566%20C6.4719%2C16.412%205.99175%2C16.3176%205.54754%2C16.0913%20C4.84193%2C15.7317%204.26825%2C15.1581%203.90873%2C14.4525%20C3.68239%2C14.0082%203.58803%2C13.5281%203.54336%2C12.9815%20C3.4999997%2C12.4506%203.4999997%2C11.7949%203.4999997%2C10.9821%20L3.4999997%2C9.0179%20C3.4999997%2C8.20506%203.4999997%2C7.54944%203.54336%2C7.01853%20C3.58803%2C6.4719%203.68239%2C5.99175%203.90873%2C5.54754%20C4.26825%2C4.84193%204.84193%2C4.26825%205.54754%2C3.90873%20C5.99175%2C3.68239%206.4719%2C3.58803%207.01853%2C3.54336%20C7.54944%2C3.4999997%208.20506%2C3.4999997%209.0179%2C3.4999997%20Z%20M7.14068%2C5.03838%20C6.68681%2C5.07547%206.42604%2C5.1446%206.22852%2C5.24524%20C5.80516%2C5.46095%205.46095%2C5.80516%205.24524%2C6.22852%20C5.1446%2C6.42604%205.07547%2C6.68681%205.03838%2C7.14068%20C5.00058%2C7.60331%205%2C8.19755%205%2C9.05%20L5%2C10.4785%20L5.7228%2C9.61114%20C6.38159%2C8.82059%207.57697%2C8.76637%208.30462%2C9.49402%20L11%2C12.1894%20L12.1796%2C11.0097%20C12.9172%2C10.2722%2014.132%2C10.3395%2014.7836%2C11.154%20L14.9997%2C11.424%20C15%2C11.2754%2015%2C11.1177%2015%2C10.95%20L15%2C9.05%20C15%2C8.19755%2014.9994%2C7.60331%2014.9616%2C7.14068%20C14.9245%2C6.68681%2014.8554%2C6.42604%2014.7548%2C6.22852%20C14.539%2C5.80516%2014.1948%2C5.46095%2013.7715%2C5.24524%20C13.574%2C5.1446%2013.3132%2C5.07547%2012.8593%2C5.03838%20C12.3967%2C5.00058%2011.8025%2C5%2010.95%2C5%20L9.05%2C5%20C8.19755%2C5%207.60331%2C5.00058%207.14068%2C5.03838%20Z%20M5.03838%2C12.8593%20C5.03622%2C12.8328%205.03417%2C12.8059%205.03225%2C12.7785%20C5.04743%2C12.7632%205.06209%2C12.7471%205.07615%2C12.7302%20L6.87513%2C10.5714%20C6.96924%2C10.4585%207.14001%2C10.4507%207.24396%2C10.5547%20L10.4697%2C13.7804%20C10.7626%2C14.0733%2011.2374%2C14.0733%2011.5303%2C13.7804%20L13.2403%2C12.0704%20C13.3457%2C11.965%2013.5192%2C11.9747%2013.6123%2C12.091%20L14.8252%2C13.6071%20C14.8037%2C13.6674%2014.7803%2C13.7214%2014.7548%2C13.7715%20C14.539%2C14.1948%2014.1948%2C14.539%2013.7715%2C14.7548%20C13.574%2C14.8554%2013.3132%2C14.9245%2012.8593%2C14.9616%20C12.3967%2C14.9994%2011.8025%2C15%2010.95%2C15%20L9.05%2C15%20C8.19755%2C15%207.60331%2C14.9994%207.14068%2C14.9616%20C6.68681%2C14.9245%206.42604%2C14.8554%206.22852%2C14.7548%20C5.80516%2C14.539%205.46095%2C14.1948%205.24524%2C13.7715%20C5.1446%2C13.574%205.07547%2C13.3132%205.03838%2C12.8593%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var GroupManagementIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3EStore%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22Store%22%20stroke%3D%22none%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M13.257%2C3%20L6.74302%2C3%20C6.35944%2C3%205.9971%2C3.17611%205.76012%2C3.47772%20L3.37394%2C6.51468%20C3.13169%2C6.823%203%2C7.20375%203%2C7.59586%20L3%2C8.25%20C3%2C9.31867%203.60958%2C10.245%204.5%2C10.7001%20L4.5%2C15.25%20C4.5%2C16.2165%205.2835%2C17%206.25%2C17%20L13.75%2C17%20C14.7165%2C17%2015.5%2C16.2165%2015.5%2C15.25%20L15.5%2C10.7001%20C16.3904%2C10.245%2017%2C9.31867%2017%2C8.25%20L17%2C7.76879%20C17%2C7.26465%2016.8307%2C6.77511%2016.5192%2C6.3787%20L14.2399%2C3.47772%20C14.0029%2C3.17611%2013.6406%2C3%2013.257%2C3%20Z%20M13%2C15.5%20L13.75%2C15.5%20C13.8881%2C15.5%2014%2C15.3881%2014%2C15.25%20L14%2C11%20C13.212%2C11%2012.5014%2C10.6686%2012%2C10.1375%20C11.4986%2C10.6686%2010.788%2C11%2010%2C11%20C9.21199%2C11%208.50138%2C10.6686%208%2C10.1375%20C7.49862%2C10.6686%206.78801%2C11%206%2C11%20L6%2C15.25%20C6%2C15.3881%206.11193%2C15.5%206.25%2C15.5%20L10%2C15.5%20L10%2C13%20C10%2C12.4477%2010.4477%2C12%2011%2C12%20L12%2C12%20C12.5523%2C12%2013%2C12.4477%2013%2C13%20L13%2C15.5%20Z%20M6%2C9.5%20L5.75%2C9.5%20C5.05964%2C9.5%204.5%2C8.94036%204.5%2C8.25%20L4.5%2C7.59586%20C4.5%2C7.53985%204.51881%2C7.48545%204.55342%2C7.44141%20L6.86453%2C4.5%20L13.1355%2C4.5%20L15.3397%2C7.30543%20C15.4436%2C7.43757%2015.5%2C7.60075%2015.5%2C7.76879%20L15.5%2C8.25%20C15.5%2C8.94036%2014.9404%2C9.5%2014.25%2C9.5%20L14%2C9.5%20C13.3096%2C9.5%2012.75%2C8.94036%2012.75%2C8.25%20L12.75%2C7.75%20C12.75%2C7.33579%2012.4142%2C7%2012%2C7%20C11.5858%2C7%2011.25%2C7.33579%2011.25%2C7.75%20L11.25%2C8.25%20C11.25%2C8.94036%2010.6904%2C9.5%2010%2C9.5%20C9.30964%2C9.5%208.75%2C8.94036%208.75%2C8.25%20L8.75%2C7.75%20C8.75%2C7.33579%208.41421%2C7%208%2C7%20C7.58579%2C7%207.25%2C7.33579%207.25%2C7.75%20L7.25%2C8.25%20C7.25%2C8.94036%206.69036%2C9.5%206%2C9.5%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%20fill%3D%22%234A4A4A%22%3E%3C%2Fpath%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var GroupManagementInActiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3EStoreFilled%E5%A4%87%E4%BB%BD%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22StoreFilled%E5%A4%87%E4%BB%BD%22%20stroke%3D%22none%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%20stroke-width%3D%221%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84%22%20fill%3D%22%234A4A4A%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M12.2779%2C3%20C13.1218%2C3%2013.919%2C3.38744%2014.4403%2C4.05099%20L16.6003%2C6.80012%20L16.6103%2C6.81407%20C17.3459%2C7.84395%2016.8481%2C9.20962%2015.7787%2C9.62496%20L14.6085%2C9.62496%20C14.1344%2C9.62496%2013.6938%2C9.38073%2013.4425%2C8.97871%20L12.78%2C7.91871%20C12.6675%2C7.73867%2012.4783%2C7.6357%2012.2812%2C7.62568%20C12.2711%2C7.62517%2012.2611%2C7.62491%2012.2511%2C7.62488%20C12.1374%2C7.62458%2012.0223%2C7.65526%2011.9188%2C7.71996%20C11.8314%2C7.77458%2011.7621%2C7.84726%2011.7129%2C7.93014%20C11.6933%2C7.96312%2011.6765%2C7.99828%2011.663%2C8.03537%20C11.6549%2C8.05745%2011.6481%2C8.07995%2011.6426%2C8.10276%20L11.5681%2C8.40069%20C11.3882%2C9.12024%2010.7417%2C9.62503%2010%2C9.62503%20C9.25831%2C9.62503%208.6118%2C9.12024%208.43191%2C8.40069%20L8.35737%2C8.10255%20C8.35218%2C8.08114%208.34586%2C8.06001%208.33844%2C8.03924%20C8.32422%2C7.99938%208.30624%2C7.96173%208.28501%2C7.92659%20C8.23589%2C7.84519%208.1674%2C7.7738%208.08126%2C7.71996%20C7.97774%2C7.65526%207.86261%2C7.62458%207.74893%2C7.62488%20C7.73942%2C7.6249%207.72988%2C7.62515%207.72033%2C7.62561%20C7.52269%2C7.63518%207.33282%2C7.73821%207.22001%2C7.91871%20L6.55751%2C8.97871%20C6.30624%2C9.38073%205.8656%2C9.62496%205.39151%2C9.62496%20L4.22132%2C9.62496%20C3.1519%2C9.20962%202.65408%2C7.84395%203.38971%2C6.81407%20L3.39967%2C6.80012%20L5.5597%2C4.05099%20C6.08106%2C3.38744%206.87821%2C3%207.72208%2C3%20L12.2779%2C3%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%20fill-rule%3D%22nonzero%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M4.50001%2C10.875%20L4.50001%2C15.25%20C4.50001%2C16.2165%205.28351%2C17%206.25001%2C17%20L13.75%2C17%20C14.7165%2C17%2015.50001%2C16.2165%2015.50001%2C15.25%20L15.50001%2C10.875%20L14.6085%2C10.875%20C13.7034%2C10.875%2012.8622%2C10.4087%2012.3825%2C9.64121%20L12.37%2C9.62126%20C11.8478%2C10.3884%2010.9698%2C10.875%2010%2C10.875%20C9.03023%2C10.875%208.15221%2C10.3884%207.62998%2C9.62126%20L7.61751%2C9.64121%20C7.13782%2C10.4087%206.29659%2C10.875%205.39151%2C10.875%20L4.50001%2C10.875%20Z%20M13%2C13.35%20C13%2C12.7977%2012.5523%2C12.35%2012%2C12.35%20L11%2C12.35%20C10.4477%2C12.35%2010%2C12.7977%2010%2C13.35%20L10%2C15.75%20C10%2C15.888%2010.1119%2C16%2010.25%2C16%20L12.75%2C16%20C12.8881%2C16%2013%2C15.888%2013%2C15.75%20L13%2C13.35%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var PointsMallInActiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E2%9A%A0%EF%B8%8FUpdate%20-_%20DynamicSource%E5%A4%87%E4%BB%BD%202%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E2%9A%A0%EF%B8%8FUpdate--_-DynamicSource%E5%A4%87%E4%BB%BD-2%22%20stroke%3D%22none%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%20stroke-width%3D%221%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84%22%20fill%3D%22%23FFFFFF%22%20opacity%3D%220%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20fill-rule%3D%22nonzero%22%20x%3D%220%22%20y%3D%220%22%20width%3D%2220%22%20height%3D%2220%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3Cellipse%20id%3D%22%E6%A4%AD%E5%9C%86%E5%BD%A2%E5%A4%87%E4%BB%BD-2%22%20stroke%3D%22%23EBEBEB%22%20fill%3D%22%23474747%22%20cx%3D%2210.0055069%22%20cy%3D%2212.5068864%22%20rx%3D%227%22%20ry%3D%223.5%22%3E%3C%2Fellipse%3E%20%20%20%20%20%20%20%20%3Cellipse%20id%3D%22%E6%A4%AD%E5%9C%86%E5%BD%A2%E5%A4%87%E4%BB%BD%22%20stroke%3D%22%23EBEBEB%22%20fill%3D%22%23474747%22%20cx%3D%2210.0055069%22%20cy%3D%229.50688645%22%20rx%3D%227%22%20ry%3D%223.5%22%3E%3C%2Fellipse%3E%20%20%20%20%20%20%20%20%3Cellipse%20id%3D%22%E6%A4%AD%E5%9C%86%E5%BD%A2%22%20stroke%3D%22%23EBEBEB%22%20fill%3D%22%23474747%22%20cx%3D%2210.0055069%22%20cy%3D%226.50688645%22%20rx%3D%227%22%20ry%3D%223.5%22%3E%3C%2Fellipse%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var PointsMallIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E2%9A%A0%EF%B8%8FUpdate%20-_%20DynamicSource%E5%A4%87%E4%BB%BD%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E2%9A%A0%EF%B8%8FUpdate--_-DynamicSource%E5%A4%87%E4%BB%BD%22%20stroke%3D%22none%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%20stroke-width%3D%221%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20fill%3D%22%23FFFFFF%22%20fill-rule%3D%22nonzero%22%20opacity%3D%220%22%20x%3D%220%22%20y%3D%220%22%20width%3D%2220%22%20height%3D%2220%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M5.21719%2C6.19965%20C5.01966%2C6.38647%205%2C6.50511%205%2C6.55%20C5%2C6.59489%205.01966%2C6.71353%205.21719%2C6.90035%20C5.41632%2C7.08866%205.7447%2C7.2928%206.21268%2C7.47999%20C7.14447%2C7.85271%208.48317%2C8.1%2010%2C8.1%20C11.5168%2C8.1%2012.8555%2C7.85271%2013.7873%2C7.47999%20C14.2553%2C7.2928%2014.5837%2C7.08866%2014.7828%2C6.90035%20C14.9803%2C6.71353%2015%2C6.59489%2015%2C6.55%20C15%2C6.50511%2014.9803%2C6.38647%2014.7828%2C6.19965%20C14.5837%2C6.01134%2014.2553%2C5.8072%2013.7873%2C5.62001%20C12.8555%2C5.24729%2011.5168%2C5%2010%2C5%20C8.48317%2C5%207.14447%2C5.24729%206.21268%2C5.62001%20C5.7447%2C5.8072%205.41632%2C6.01134%205.21719%2C6.19965%20Z%20M15%2C8.56662%20C14.794%2C8.67903%2014.5739%2C8.78089%2014.3444%2C8.8727%20C13.1951%2C9.33242%2011.6588%2C9.6%2010%2C9.6%20C8.34119%2C9.6%206.80489%2C9.33242%205.65559%2C8.8727%20C5.42605%2C8.78089%205.20598%2C8.67903%205%2C8.56662%20L5%2C9.98%20C5%2C10.0249%205.01966%2C10.1435%205.21719%2C10.3303%20C5.41632%2C10.5187%205.7447%2C10.7228%206.21268%2C10.91%20C7.14447%2C11.2827%208.48317%2C11.53%2010%2C11.53%20C11.5168%2C11.53%2012.8555%2C11.2827%2013.7873%2C10.91%20C14.2553%2C10.7228%2014.5837%2C10.5187%2014.7828%2C10.3303%20C14.9803%2C10.1435%2015%2C10.0249%2015%2C9.98%20L15%2C8.56662%20Z%20M15%2C11.9966%20C14.794%2C12.109%2014.5739%2C12.2109%2014.3444%2C12.3027%20C13.1951%2C12.7624%2011.6588%2C13.03%2010%2C13.03%20C8.34119%2C13.03%206.80489%2C12.7624%205.65559%2C12.3027%20C5.42605%2C12.2109%205.20598%2C12.109%205%2C11.9966%20L5%2C13.45%20C5%2C13.4949%205.01966%2C13.6135%205.21719%2C13.8003%20C5.41632%2C13.9887%205.7447%2C14.1928%206.21268%2C14.38%20C7.14447%2C14.7527%208.48317%2C15%2010%2C15%20C11.5168%2C15%2012.8555%2C14.7527%2013.7873%2C14.38%20C14.2553%2C14.1928%2014.5837%2C13.9887%2014.7828%2C13.8003%20C14.9803%2C13.6135%2015%2C13.4949%2015%2C13.45%20L15%2C11.9966%20Z%20M16.5%2C13.45%20C16.5%2C14.0402%2016.1979%2C14.5267%2015.8135%2C14.8902%20C15.4307%2C15.2522%2014.917%2C15.5437%2014.3444%2C15.7727%20C13.1951%2C16.2324%2011.6588%2C16.5%2010%2C16.5%20C8.34119%2C16.5%206.80489%2C16.2324%205.65559%2C15.7727%20C5.08303%2C15.5437%204.56934%2C15.2522%204.18653%2C14.8902%20C3.80214%2C14.5267%203.5%2C14.0402%203.5%2C13.45%20L3.5%2C6.55%20C3.5%2C5.95976%203.80214%2C5.47334%204.18653%2C5.10982%20C4.56934%2C4.74779%205.08303%2C4.45632%205.65559%2C4.2273%20C6.80489%2C3.76758%208.34119%2C3.5%2010%2C3.5%20C11.6588%2C3.5%2013.1951%2C3.76758%2014.3444%2C4.2273%20C14.917%2C4.45632%2015.4307%2C4.74779%2015.8135%2C5.10982%20C16.1979%2C5.47334%2016.5%2C5.95976%2016.5%2C6.55%20L16.5%2C13.45%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%20fill%3D%22%23474747%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavCardCouponCenterActiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3EGiftCard%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22GiftCard%22%20stroke%3D%22none%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M15.5%2C5.5%20L10%2C5.5%20L10%2C6.25%20C10%2C6.66421%209.66421%2C7%209.25%2C7%20C8.83579%2C7%208.5%2C6.66421%208.5%2C6.25%20L8.5%2C5.5%20L4.5%2C5.5%20C4.22386%2C5.5%204%2C5.72386%204%2C6%20L4%2C9.04268%20C4.2913%2C9.14564%204.5%2C9.42345%204.5%2C9.75%20C4.5%2C10.0766%204.2913%2C10.3544%204%2C10.4573%20L4%2C14%20C4%2C14.2761%204.22386%2C14.5%204.5%2C14.5%20L8.5%2C14.5%20L8.5%2C13.5%20C8.5%2C13.0858%208.83579%2C12.75%209.25%2C12.75%20C9.66421%2C12.75%2010%2C13.0858%2010%2C13.5%20L10%2C14.5%20L15.5%2C14.5%20C15.7761%2C14.5%2016%2C14.2761%2016%2C14%20L16%2C10.5%20L14.75%2C10.5%20C14.3358%2C10.5%2014%2C10.1642%2014%2C9.75%20C14%2C9.33579%2014.3358%2C9%2014.75%2C9%20L16%2C9%20L16%2C6%20C16%2C5.72386%2015.7761%2C5.5%2015.5%2C5.5%20Z%20M17.5%2C9.75%20L17.5%2C6%20C17.5%2C4.89543%2016.6046%2C4%2015.5%2C4%20L9.25%2C4%20L4.5%2C4%20C3.39543%2C4%202.5%2C4.89543%202.5%2C6%20L2.5%2C14%20C2.5%2C15.1046%203.39543%2C16%204.5%2C16%20L15.5%2C16%20C16.6046%2C16%2017.5%2C15.1046%2017.5%2C14%20L17.5%2C9.75%20Z%20M8.79746%2C7.99197%20C8.45366%2C7.23561%207.69951%2C6.75%206.86868%2C6.75%20C5.69632%2C6.75%204.75%2C7.70396%204.75%2C8.87184%20C4.75%2C10.0432%205.69915%2C11%206.875%2C11%20L7.73284%2C11%20C7.13766%2C11.5097%206.47708%2C11.9242%205.89394%2C12.0075%20C5.48389%2C12.0661%205.19896%2C12.446%205.25754%2C12.8561%20C5.31612%2C13.2661%205.69602%2C13.551%206.10607%2C13.4925%20C7.21669%2C13.3338%208.23422%2C12.5733%208.90944%2C11.9624%20C9.03044%2C11.8529%209.14433%2C11.7446%209.25%2C11.6403%20C9.35567%2C11.7446%209.46956%2C11.8529%209.59056%2C11.9624%20C10.2658%2C12.5733%2011.2833%2C13.3338%2012.3939%2C13.4925%20C12.804%2C13.551%2013.1839%2C13.2661%2013.2425%2C12.8561%20C13.301%2C12.446%2013.0161%2C12.0661%2012.6061%2C12.0075%20C12.0229%2C11.9242%2011.3623%2C11.5097%2010.7672%2C11%20L11.625%2C11%20C12.8009%2C11%2013.75%2C10.0432%2013.75%2C8.87184%20C13.75%2C7.70396%2012.8037%2C6.75%2011.6313%2C6.75%20C10.8005%2C6.75%2010.0463%2C7.23561%209.70254%2C7.99197%20L9.25%2C8.98755%20L8.79746%2C7.99197%20Z%20M7.83524%2C9.5%20L6.875%2C9.5%20C6.53206%2C9.5%206.25%2C9.21926%206.25%2C8.87184%20C6.25%2C8.5279%206.52923%2C8.25%206.86868%2C8.25%20C7.1113%2C8.25%207.33152%2C8.3918%207.43191%2C8.61267%20L7.83524%2C9.5%20Z%20M11.625%2C9.5%20L10.6648%2C9.5%20L11.0681%2C8.61267%20C11.1685%2C8.3918%2011.3887%2C8.25%2011.6313%2C8.25%20C11.9708%2C8.25%2012.25%2C8.5279%2012.25%2C8.87184%20C12.25%2C9.21926%2011.9679%2C9.5%2011.625%2C9.5%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%20fill%3D%22%234A4A4A%22%3E%3C%2Fpath%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavCardCouponCenterInactiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%E5%A4%87%E4%BB%BD%203%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E4%BE%9B%E5%BA%94%E9%93%BE%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-2221%2C%20-164%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84%E5%A4%87%E4%BB%BD-3%22%20transform%3D%22translate%282221%2C%20164%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2-3%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22GiftCardFilled%22%20fill%3D%22%234A4A4A%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M8.5%2C4%20L8.5%2C6.25%20C8.5%2C6.66421%208.83579%2C7%209.25%2C7%20C9.66421%2C7%2010%2C6.66421%2010%2C6.25%20L10%2C4%20L15.5%2C4%20C16.6046%2C4%2017.5%2C4.89543%2017.5%2C6%20L17.5%2C9%20L14.75%2C9%20C14.3358%2C9%2014%2C9.33579%2014%2C9.75%20C14%2C10.1642%2014.3358%2C10.5%2014.75%2C10.5%20L17.5%2C10.5%20L17.5%2C14%20C17.5%2C15.1046%2016.6046%2C16%2015.5%2C16%20L10%2C16%20L10%2C13.5%20C10%2C13.0858%209.66421%2C12.75%209.25%2C12.75%20C8.83579%2C12.75%208.5%2C13.0858%208.5%2C13.5%20L8.5%2C16%20L4.5%2C16%20C3.39543%2C16%202.5%2C15.1046%202.5%2C14%20L2.5%2C10.5%20L3.75%2C10.5%20C4.16421%2C10.5%204.5%2C10.1642%204.5%2C9.75%20C4.5%2C9.33579%204.16421%2C9%203.75%2C9%20L2.5%2C9%20L2.5%2C6%20C2.5%2C4.89543%203.39543%2C4%204.5%2C4%20L8.5%2C4%20Z%20M6.86868%2C6.75%20C7.69951%2C6.75%208.45366%2C7.23561%208.79746%2C7.99197%20L9.25%2C8.98755%20L9.70254%2C7.99197%20C10.0463%2C7.23561%2010.8005%2C6.75%2011.6313%2C6.75%20C12.8037%2C6.75%2013.75%2C7.70396%2013.75%2C8.87184%20C13.75%2C10.0432%2012.8009%2C11%2011.625%2C11%20L10.7672%2C11%20C11.3623%2C11.5097%2012.0229%2C11.9242%2012.6061%2C12.0075%20C13.0161%2C12.0661%2013.301%2C12.446%2013.2425%2C12.8561%20C13.1839%2C13.2661%2012.804%2C13.551%2012.3939%2C13.4925%20C11.2833%2C13.3338%2010.2658%2C12.5733%209.59056%2C11.9624%20C9.46956%2C11.8529%209.35567%2C11.7446%209.25%2C11.6403%20C9.14433%2C11.7446%209.03044%2C11.8529%208.90944%2C11.9624%20C8.23422%2C12.5733%207.21669%2C13.3338%206.10607%2C13.4925%20C5.69602%2C13.551%205.31612%2C13.2661%205.25754%2C12.8561%20C5.19896%2C12.446%205.48389%2C12.0661%205.89394%2C12.0075%20C6.47708%2C11.9242%207.13766%2C11.5097%207.73284%2C11%20L6.875%2C11%20C5.69915%2C11%204.75%2C10.0432%204.75%2C8.87184%20C4.75%2C7.70396%205.69632%2C6.75%206.86868%2C6.75%20Z%20M6.875%2C9.5%20L7.83524%2C9.5%20L7.43191%2C8.61267%20C7.33152%2C8.3918%207.1113%2C8.25%206.86868%2C8.25%20C6.52923%2C8.25%206.25%2C8.5279%206.25%2C8.87184%20C6.25%2C9.21926%206.53206%2C9.5%206.875%2C9.5%20Z%20M10.6648%2C9.5%20L11.625%2C9.5%20C11.9679%2C9.5%2012.25%2C9.21926%2012.25%2C8.87184%20C12.25%2C8.5279%2011.9708%2C8.25%2011.6313%2C8.25%20C11.3887%2C8.25%2011.1685%2C8.3918%2011.0681%2C8.61267%20L10.6648%2C9.5%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavSalePromotionActiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3EDiscount%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22Discount%22%20stroke%3D%22none%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%20stroke-width%3D%221%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84%22%20fill%3D%22%234A4A4A%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M12.7803%2C8.28033%20C13.0732%2C7.98744%2013.0732%2C7.51256%2012.7803%2C7.21967%20C12.4874%2C6.92678%2012.0126%2C6.92678%2011.7197%2C7.21967%20L7.21967%2C11.7197%20C6.92678%2C12.0126%206.92678%2C12.4874%207.21967%2C12.7803%20C7.51256%2C13.0732%207.98744%2C13.0732%208.28033%2C12.7803%20L12.7803%2C8.28033%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%20fill-rule%3D%22nonzero%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M9%2C8%20C9%2C8.55228%208.55228%2C9%208%2C9%20C7.44772%2C9%207%2C8.55228%207%2C8%20C7%2C7.44772%207.44772%2C7%208%2C7%20C8.55228%2C7%209%2C7.44772%209%2C8%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%20fill-rule%3D%22nonzero%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M12%2C13%20C12.5523%2C13%2013%2C12.5523%2013%2C12%20C13%2C11.4477%2012.5523%2C11%2012%2C11%20C11.4477%2C11%2011%2C11.4477%2011%2C12%20C11%2C12.5523%2011.4477%2C13%2012%2C13%20Z%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%20fill-rule%3D%22nonzero%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M12.0943%2C3.51441%20C11.2723%2C1.72371%208.72775%2C1.72371%207.90568%2C3.51441%20C7.73011%2C3.89684%207.28948%2C4.07936%206.89491%2C3.93308%20C5.04741%2C3.24816%203.24816%2C5.04741%203.93308%2C6.89491%20C4.07936%2C7.28948%203.89684%2C7.73011%203.51441%2C7.90568%20C1.72371%2C8.72775%201.72371%2C11.2723%203.51441%2C12.0943%20C3.89684%2C12.2699%204.07936%2C12.7105%203.93308%2C13.1051%20C3.24816%2C14.9526%205.04741%2C16.7519%206.89491%2C16.0669%20C7.28948%2C15.9207%207.73011%2C16.1032%207.90568%2C16.4856%20C8.72775%2C18.2763%2011.2723%2C18.2763%2012.0943%2C16.4856%20C12.2699%2C16.1032%2012.7105%2C15.9207%2013.1051%2C16.0669%20C14.9526%2C16.7519%2016.7519%2C14.9526%2016.0669%2C13.1051%20C15.9207%2C12.7105%2016.1032%2C12.2699%2016.4856%2C12.0943%20C18.2763%2C11.2723%2018.2763%2C8.72775%2016.4856%2C7.90568%20C16.1032%2C7.73011%2015.9207%2C7.28948%2016.0669%2C6.89491%20C16.7519%2C5.04741%2014.9526%2C3.24816%2013.1051%2C3.93308%20C12.7105%2C4.07936%2012.2699%2C3.89684%2012.0943%2C3.51441%20Z%20M9.26889%2C4.14023%20C9.55587%2C3.51511%2010.4441%2C3.51511%2010.7311%2C4.14023%20C11.2341%2C5.23573%2012.4963%2C5.75856%2013.6265%2C5.33954%20C14.2715%2C5.10044%2014.8996%2C5.72855%2014.6605%2C6.3735%20C14.2415%2C7.50376%2014.7643%2C8.76597%2015.8598%2C9.26889%20C16.4849%2C9.55587%2016.4849%2C10.4441%2015.8598%2C10.7311%20C14.7643%2C11.2341%2014.2415%2C12.4963%2014.6605%2C13.6265%20C14.8996%2C14.2715%2014.2715%2C14.8996%2013.6265%2C14.6605%20C12.4963%2C14.2415%2011.2341%2C14.7643%2010.7311%2C15.8598%20C10.4441%2C16.4849%209.55587%2C16.4849%209.26889%2C15.8598%20C8.76597%2C14.7643%207.50376%2C14.2415%206.3735%2C14.6605%20C5.72855%2C14.8996%205.10044%2C14.2715%205.33954%2C13.6265%20C5.75856%2C12.4963%205.23573%2C11.2341%204.14023%2C10.7311%20C3.51511%2C10.4441%203.51511%2C9.55587%204.14023%2C9.26889%20C5.23573%2C8.76597%205.75856%2C7.50376%205.33954%2C6.3735%20C5.10044%2C5.72855%205.72855%2C5.10044%206.3735%2C5.33954%20C7.50376%2C5.75856%208.76597%2C5.23573%209.26889%2C4.14023%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var NavSalePromotionInactiveIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2220px%22%20height%3D%2220px%22%20viewBox%3D%220%200%2020%2020%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%E5%A4%87%E4%BB%BD%202%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E4%BE%9B%E5%BA%94%E9%93%BE%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22icon%22%20transform%3D%22translate%28-2285%2C%20-164%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84%E5%A4%87%E4%BB%BD-2%22%20transform%3D%22translate%282285%2C%20164%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2-2%22%20stroke%3D%22%23979797%22%20fill%3D%22%23D8D8D8%22%20opacity%3D%220%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2219%22%20height%3D%2219%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M11.5273%2C3.3268%20C10.9278%2C2.02095%209.07221%2C2.02095%208.47273%2C3.3268%20C8.10598%2C4.12568%207.18552%2C4.50695%206.36129%2C4.20138%20C5.01402%2C3.70191%203.70193%2C5.014%204.2014%2C6.36127%20C4.50697%2C7.1855%204.1257%2C8.10595%203.32682%2C8.47271%20C2.02097%2C9.07219%202.02097%2C10.9278%203.32682%2C11.5272%20C4.1257%2C11.894%204.50697%2C12.8145%204.2014%2C13.6387%20C3.70193%2C14.986%205.01402%2C16.298%206.36129%2C15.7986%20C7.18552%2C15.493%208.10598%2C15.8743%208.47273%2C16.6732%20C9.07221%2C17.979%2010.9278%2C17.979%2011.5273%2C16.6732%20C11.894%2C15.8743%2012.8145%2C15.493%2013.6387%2C15.7986%20C14.986%2C16.298%2016.2981%2C14.986%2015.7986%2C13.6387%20C15.493%2C12.8145%2015.8743%2C11.894%2016.6732%2C11.5272%20C17.979%2C10.9278%2017.979%2C9.07219%2016.6732%2C8.47271%20C15.8743%2C8.10595%2015.493%2C7.1855%2015.7986%2C6.36127%20C16.2981%2C5.014%2014.986%2C3.70191%2013.6387%2C4.20138%20C12.8145%2C4.50695%2011.894%2C4.12568%2011.5273%2C3.3268%20Z%20M9%2C8.24998%20C9%2C8.80226%208.55229%2C9.24998%208%2C9.24998%20C7.44772%2C9.24998%207%2C8.80226%207%2C8.24998%20C7%2C7.69769%207.44772%2C7.24998%208%2C7.24998%20C8.55229%2C7.24998%209%2C7.69769%209%2C8.24998%20Z%20M12.5303%2C8.78031%20L8.53033%2C12.7803%20C8.23744%2C13.0732%207.76256%2C13.0732%207.46967%2C12.7803%20C7.17678%2C12.4874%207.17678%2C12.0125%207.46967%2C11.7196%20L11.4697%2C7.71965%20C11.7626%2C7.42675%2012.2374%2C7.42675%2012.5303%2C7.71965%20C12.8232%2C8.01254%2012.8232%2C8.48741%2012.5303%2C8.78031%20Z%20M13%2C12.25%20C13%2C12.8023%2012.5523%2C13.25%2012%2C13.25%20C11.4477%2C13.25%2011%2C12.8023%2011%2C12.25%20C11%2C11.6977%2011.4477%2C11.25%2012%2C11.25%20C12.5523%2C11.25%2013%2C11.6977%2013%2C12.25%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%20fill%3D%22%234A4A4A%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

var MessageCenterIcon = "data:image/svg+xml,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22UTF-8%22%3F%3E%3Csvg%20width%3D%2216px%22%20height%3D%2216px%22%20viewBox%3D%220%200%2016%2016%22%20version%3D%221.1%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20xmlns%3Axlink%3D%22http%3A%2F%2Fwww.w3.org%2F1999%2Fxlink%22%3E%20%20%20%20%3Ctitle%3E%E7%BC%96%E7%BB%84%3C%2Ftitle%3E%20%20%20%20%3Cg%20id%3D%22%E8%BF%90%E8%90%A5%22%20stroke%3D%22none%22%20stroke-width%3D%221%22%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E6%B6%88%E6%81%AF%E4%B8%AD%E5%BF%83%22%20transform%3D%22translate%28-1216%2C%20-10%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84%22%20transform%3D%22translate%281216%2C%2010%29%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cg%20id%3D%22%E7%BC%96%E7%BB%84-17%E5%A4%87%E4%BB%BD%22%20opacity%3D%220%22%20fill%3D%22%23D8D8D8%22%20stroke%3D%22%23979797%22%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Crect%20id%3D%22%E7%9F%A9%E5%BD%A2%22%20opacity%3D%220.531048387%22%20x%3D%220.5%22%20y%3D%220.5%22%20width%3D%2215%22%20height%3D%2215%22%3E%3C%2Frect%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M5.17684866%2C12.4239%20L2.73084866%2C12.1427%20C0.876248665%2C11.9295%200.350248665%2C9.4844%201.95314866%2C8.5274%20L2.01804866%2C8.4886%20C2.89074866%2C7.96761%203.42524866%2C7.02609%203.42524866%2C6.00969%20L3.42524866%2C5.5%20C3.42524866%2C3.01472%205.43994866%2C1%207.92524866%2C1%20C10.4105487%2C1%2012.4252487%2C3.01472%2012.4252487%2C5.5%20L12.4252487%2C6.00969%20C12.4252487%2C7.02609%2012.9596487%2C7.96761%2013.8323487%2C8.4886%20L13.8973487%2C8.5274%20C15.5002487%2C9.4844%2014.9742487%2C11.9295%2013.1195487%2C12.1427%20L10.5768487%2C12.435%20L10.5768487%2C12.8%20C10.5768487%2C14.2912%209.36794866%2C15.5%207.87684866%2C15.5%20C6.38564866%2C15.5%205.17684866%2C14.2912%205.17684866%2C12.8%20L5.17684866%2C12.4239%20Z%20M9.07684866%2C12.5%20L6.67684866%2C12.5%20L6.67684866%2C12.8%20C6.67684866%2C13.4627%207.21404866%2C14%207.87684866%2C14%20C8.53954866%2C14%209.07684866%2C13.4627%209.07684866%2C12.8%20L9.07684866%2C12.5%20Z%20M5.92524866%2C11%20L9.92524866%2C11%20L12.9483487%2C10.6525%20C13.3777487%2C10.6032%2013.4995487%2C10.0369%2013.1284487%2C9.8153%20L13.0634487%2C9.7766%20C12.9839487%2C9.7291%2012.9062487%2C9.6793%2012.8304487%2C9.6273%20C12.6754487%2C9.521%2012.5283487%2C9.4055%2012.3898487%2C9.2818%20C12.3652487%2C9.2598%2012.3410487%2C9.2377%2012.3169487%2C9.2152%20C11.4391487%2C8.395%2010.9252487%2C7.23833%2010.9252487%2C6.00969%20L10.9252487%2C5.5%20C10.9252487%2C3.84315%209.58204866%2C2.5%207.92524866%2C2.5%20C6.26834866%2C2.5%204.92524866%2C3.84315%204.92524866%2C5.5%20L4.92524866%2C6.00969%20C4.92524866%2C7.23833%204.41124866%2C8.395%203.53344866%2C9.2152%20C3.50944866%2C9.2377%203.48514866%2C9.2598%203.46064866%2C9.2818%20C3.32214866%2C9.4055%203.17504866%2C9.521%203.02004866%2C9.6273%20C2.94424866%2C9.6793%202.86654866%2C9.7291%202.78704866%2C9.7766%20L2.72204866%2C9.8153%20C2.35084866%2C10.0369%202.47264866%2C10.6032%202.90214866%2C10.6525%20L5.92524866%2C11%20Z%22%20id%3D%22%E5%BD%A2%E7%8A%B6%22%20fill%3D%22%233F3F3F%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20d%3D%22M7.73200671%2C4.41789037%20C7.04322318%2C4.66013586%206.69883141%2C5.21258649%206.69883141%2C6.07524228%22%20id%3D%22%E8%B7%AF%E5%BE%84%22%20stroke%3D%22%234973EC%22%20stroke-width%3D%221.5%22%20stroke-linecap%3D%22round%22%3E%3C%2Fpath%3E%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%20%20%20%20%3C%2Fg%3E%20%20%20%20%3C%2Fg%3E%3C%2Fsvg%3E";

const getLocationTypes = () => {
    const arr = [];
    const zone_is = Number(window.getCookie('location_zone_id'));
    const headquarters_is = Number(window.getCookie('headquarters_is'));
    const distribution_center_is = Number(window.getCookie('distribution_center_is'));
    if (zone_is) {
        arr.push('ZONE');
    }
    if (headquarters_is) {
        arr.push('HEAD_QUARTERS');
    }
    if (distribution_center_is) {
        arr.push('DISTRIBUTION_CENTER');
    }
    if (!zone_is && !headquarters_is && !distribution_center_is) {
        arr.push('NORMAL_SHOP');
    }
    return arr;
};
const isCurrentMerchantInKingdeeList = () => {
    const merchantId = window.getCookie('merchant_id') || localCache.getItem('merchant_id');
    const kingdeeCurrentMerchantIs = localCache.getItem('kingdee_current_merchant_is');
    if (!merchantId || !kingdeeCurrentMerchantIs) {
        return false;
    }
    return !!kingdeeCurrentMerchantIs;
};
const isShowNav = (item) => {
    if (item.customRule) {
        if (item.customRule()) {
            return item;
        }
    }
    else {
        if (item.code_key &&
            item.code_key.some((key) => {
                const permission = hasOpteratePermission(key, IPermissonCodeKey.QUERY);
                return permission;
            })) {
            return item;
        }
        if (!item.code_key || item.code_key.length === 0) {
            return item;
        }
    }
    return null;
};
const getPermissionShowNavItems = (navTree, depth = 1, parentMenuType) => 
// 默认考虑菜单数组深度为3
navTree
    .map((ele) => {
    if (ele.children) {
        if (parentMenuType) {
            ele.menuType = parentMenuType;
        }
        ele.children = getPermissionShowNavItems(ele.children, depth + 1, ele.menuType) || [];
        return ele;
    }
    if (parentMenuType) {
        ele.menuType = parentMenuType;
    }
    return isShowNav(ele);
})
    .filter((item) => {
    if (depth <= 2) {
        // 过滤掉没有子菜单的菜单
        if (!item?.isIndependentModule && (!item?.children || !item?.children?.length)) {
            return false;
        }
        return true;
    }
    return !!item;
});
// 运营中心
function navOperationCenter() {
    const merchant_chain_enable = window.getCookie('merchant_chain_enable');
    const isSingleLocation = merchant_chain_enable === 'false';
    const navTree = [
        {
            path: '/home',
            name: $t({ defaultMessage: '主页' }),
            menuType: 'operationCenter-home',
            icon: NavHomeActiveIcon,
            inactiveIcon: NavHomeInactiveIcon,
            isHomePage: true,
            isIndependentModule: true,
            code_key: [
                IPermissonCode.PERMISSION_MAIN_DATA_OVERVIEW,
                IPermissonCode.PERMISSION_MAIN_TO_DO,
                IPermissonCode.PERMISSION_MAIN_PRODUCT_TOP,
            ],
        },
        {
            name: $t({ defaultMessage: '餐厅管理' }),
            menuType: 'operationCenter-restaurant',
            icon: NavRestaurantActiveIcon,
            inactiveIcon: NavRestaurantInactiveIcon,
            children: [
                {
                    path: '/main-operations/stagepositionSettings',
                    name: $t({ defaultMessage: '台位管理' }),
                    children: [
                        {
                            path: '/main-operations/stagepositionSettings',
                            name: $t({ defaultMessage: '台位管理' }),
                            code_key: [IPermissonCode.PERMISSION_SETTING_SHIPPING_TABLE],
                        },
                        {
                            path: '/main-operations/serviceFee',
                            name: $t({ defaultMessage: '服务费' }),
                            code_key: [IPermissonCode.PERMISSION_BUSINESS_SERVICE_FEE],
                        },
                    ],
                },
                {
                    path: '/main-operations/settings/payType',
                    name: $t({ defaultMessage: '支付方式' }),
                    children: [
                        {
                            path: '/main-operations/settings/payType',
                            name: $t({ defaultMessage: '支付方式' }),
                            code_key: [IPermissonCode.PERMISSION_SETTING_PAY],
                        },
                        {
                            path: '/main-operations/settings/vouchers',
                            name: $t({ defaultMessage: '抵用券管理' }),
                            code_key: [IPermissonCode.PERMISSION_SETTING_VOUCHER],
                        },
                    ],
                },
                {
                    path: '/main-operations/goods/mustOrderProduct',
                    name: $t({ defaultMessage: '必点菜管理' }),
                    children: [
                        {
                            path: '/main-operations/goods/mustOrderProduct',
                            name: $t({ defaultMessage: '必点菜' }),
                            code_key: [IPermissonCode.PERMISSION_PRODUCT_MUST_ORDER],
                        },
                    ],
                },
                {
                    path: '/main-operations/posRemark',
                    name: $t({ defaultMessage: '常用备注' }),
                    children: [
                        {
                            path: '/main-operations/posRemark',
                            name: $t({ defaultMessage: '常用备注' }),
                            code_key: [IPermissonCode.PERMISSION_POS_WEB_MEMO],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '打印设置' }),
                    children: [
                        {
                            path: '/main-operations/posPrintSetting',
                            name: $t({ defaultMessage: '票据样式设置' }),
                            code_key: [
                                IPermissonCode.PERMISSION_SETTING_PRINT_MERCHANR,
                                IPermissonCode.PERMISSION_SETTING_PRINT_LOCATION,
                            ],
                            customRule: () => {
                                const locationType = getLocationTypes();
                                if (!hasOpteratePermission([
                                    IPermissonCode.PERMISSION_SETTING_PRINT_MERCHANR,
                                    IPermissonCode.PERMISSION_SETTING_PRINT_LOCATION,
                                ], IPermissonCodeKey.QUERY))
                                    return false;
                                /** 总部区域配送中心可见 */
                                if (locationType.includes('HEAD_QUARTERS') ||
                                    locationType.includes('DISTRIBUTION_CENTER') ||
                                    locationType.includes('ZONE')) {
                                    return true;
                                }
                                return false;
                            },
                        },
                        {
                            path: '/main-operations/cashierRules',
                            name: $t({ defaultMessage: '收银打印配置' }),
                            code_key: [
                                IPermissonCode.PERMISSION_POS_WEB_CASHIER_PRINT_RULE,
                                IPermissonCode.PERMISSION_POS_WEB_CASHIER_PRINT_RULE_BRANCH,
                            ],
                            customRule: () => {
                                // 单店就不显示
                                const merchantChainEnable = window.getCookie('merchant_chain_enable');
                                return ((merchantChainEnable === 'true' &&
                                    hasOpteratePermission([IPermissonCode.PERMISSION_POS_WEB_CASHIER_PRINT_RULE_BRANCH], IPermissonCodeKey.QUERY)) ||
                                    hasOpteratePermission([IPermissonCode.PERMISSION_POS_WEB_CASHIER_PRINT_RULE], IPermissonCodeKey.QUERY));
                            },
                        },
                        {
                            path: '/main-operations/kitchenRules',
                            name: $t({ defaultMessage: '后厨出品档口' }),
                            code_key: [
                                IPermissonCode.PERMISSION_POS_WEB_KITCHEN_PRINT_RULE,
                                IPermissonCode.PERMISSION_POS_WEB_KITCHEN_PRINT_RULE_BRANCH,
                            ],
                            customRule: () => {
                                // 单店就不显示
                                const merchantChainEnable = window.getCookie('merchant_chain_enable');
                                return ((merchantChainEnable === 'true' &&
                                    hasOpteratePermission([IPermissonCode.PERMISSION_POS_WEB_KITCHEN_PRINT_RULE_BRANCH], IPermissonCodeKey.QUERY)) ||
                                    hasOpteratePermission([IPermissonCode.PERMISSION_POS_WEB_KITCHEN_PRINT_RULE], IPermissonCodeKey.QUERY));
                            },
                        },
                        {
                            path: '/main-operations/tagRules',
                            name: $t({ defaultMessage: '标签出品档口' }),
                            code_key: [
                                IPermissonCode.PERMISSION_POS_WEB_LABEL_PRINT_RULE,
                                IPermissonCode.PERMISSION_POS_WEB_LABEL_PRINT_RULE_BRANCH,
                            ],
                            customRule: () => {
                                // 单店就不显示
                                const merchantChainEnable = window.getCookie('merchant_chain_enable');
                                return ((merchantChainEnable === 'true' &&
                                    hasOpteratePermission([IPermissonCode.PERMISSION_POS_WEB_LABEL_PRINT_RULE_BRANCH], IPermissonCodeKey.QUERY)) ||
                                    hasOpteratePermission([IPermissonCode.PERMISSION_POS_WEB_LABEL_PRINT_RULE], IPermissonCodeKey.QUERY));
                            },
                        },
                        {
                            path: '/main-operations/posPrintSort',
                            name: $t({ defaultMessage: '打印顺序' }),
                            code_key: [IPermissonCode.PERMISSION_POS_WEB_PRINT_ORDER],
                        },
                        {
                            path: '/main-operations/printingBusiness',
                            name: $t({ defaultMessage: '打印业务' }),
                            code_key: [IPermissonCode.PERMISSION_SETTING_PRINT_BUSINESS],
                        },
                        {
                            path: '/main-operations/printerManagement',
                            name: $t({ defaultMessage: '打印机管理' }),
                            code_key: [IPermissonCode.PERMISSION_PRINT_DEVICE],
                        },
                    ],
                },
                {
                    path: '/main-operations/kdsSettings',
                    name: $t({ defaultMessage: 'KDS业务设置' }),
                    children: [
                        {
                            path: '/main-operations/kdsSettings',
                            name: $t({ defaultMessage: 'KDS设置' }),
                            code_key: [IPermissonCode.PERMISSION_SETTING_WIN_KDS],
                        },
                        {
                            path: '/main-operations/kdsDeviceManagement',
                            name: $t({ defaultMessage: 'KDS设备管理' }),
                            code_key: [IPermissonCode.PERMISSION_KDS_DEVICE],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '经营设置' }),
                    children: [
                        {
                            path: '/main-operations/operatingParameter',
                            name: $t({ defaultMessage: '业务开关与设置' }),
                            code_key: [IPermissonCode.PERMISSION_SETTING_SHIPPING_COMMON],
                            customRule: () => {
                                if ([IPermissonCode.PERMISSION_SETTING_SHIPPING_COMMON].some((key) => hasOpteratePermission(key, IPermissonCodeKey.EDIT))) {
                                    return true;
                                }
                                return false;
                            },
                        },
                        {
                            path: '/main-operations/screenSetting',
                            name: $t({ defaultMessage: '辅屏设置' }),
                            code_key: [
                                IPermissonCode.PERMISSION_POS_SECOND_DISPLAY_SETTING,
                                IPermissonCode.PERMISSION_POS_WEB_POS_SECOND_DISPLAY_SETTING,
                            ],
                        },
                    ],
                },
                {
                    path: '/main-operations/callNumberSetting',
                    name: $t({ defaultMessage: '叫号设置' }),
                    children: [
                        {
                            path: '/main-operations/callNumberSetting',
                            name: $t({ defaultMessage: '叫号设置' }),
                            code_key: [IPermissonCode.PERMISSION_SETTING_CALL_NUMBER],
                        },
                        {
                            path: '/main-operations/settings/tvSettings',
                            name: $t({ defaultMessage: 'TV屏装修' }),
                            code_key: [IPermissonCode.PERMISSION_SETTING_TAKE_SCREEN],
                        },
                        {
                            path: '/main-operations/tvScreenDevice',
                            name: $t({ defaultMessage: 'TV屏设备' }),
                            code_key: [IPermissonCode.PERMISSION_TV_DEVICE],
                        },
                    ],
                },
                {
                    path: '/main-operations/billScanningCode',
                    name: $t({ defaultMessage: '扫码买单' }),
                    children: [
                        {
                            path: '/main-operations/settings/billScanningCode',
                            name: $t({ defaultMessage: '扫码买单' }),
                            code_key: [IPermissonCode.PERMISSION_SETTING_SCAN_CODE_PAY],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '效期管理' }),
                    children: [
                        {
                            path: '/main-operations/EXPGoods',
                            name: $t({ defaultMessage: '效期商品' }),
                            code_key: [IPermissonCode.PERMISSION_VALIDITY_PRODUCT],
                        },
                        {
                            path: '/main-operations/storageCondition',
                            name: $t({ defaultMessage: '储存条件' }),
                            code_key: [IPermissonCode.PERMISSION_VALIDITY_STORAGE_CONDITION],
                        },
                        {
                            path: '/main-operations/EXPBoard',
                            name: $t({ defaultMessage: '效期看板' }),
                            code_key: [IPermissonCode.PERMISSION_VALIDITY_BOARD],
                        },
                        {
                            path: '/main-operations/EXPCollect',
                            name: $t({ defaultMessage: '效期汇总' }),
                            code_key: [IPermissonCode.PERMISSION_VALIDITY_SUMMARY],
                        },
                    ],
                },
                {
                    path: '/main-operations/settings/cakeDecorator',
                    name: $t({ defaultMessage: '裱花间设置' }),
                    children: [
                        {
                            path: '/main-operations/settings/cakeDecorator',
                            name: $t({ defaultMessage: '裱花网络' }),
                            code_key: [IPermissonCode.PERMISSION_SETTING_CAKE_DECORATOR],
                        },
                    ],
                },
                {
                    path: '/main-operations/equipmentSettings',
                    name: $t({ defaultMessage: '设备管理' }),
                    children: [
                        {
                            path: '/main-operations/equipmentSettings',
                            name: $t({ defaultMessage: '设备管理' }),
                            code_key: [
                                IPermissonCode.PERMISSION_POS_WEB_POS_TERMINAL,
                                IPermissonCode.PERMISSION_POS_WEB_POS_LOCATION_INFO,
                            ],
                        },
                        {
                            path: '/main-operations/cloud-speaker/cloud-speaker-management',
                            name: $t({ defaultMessage: '云音响管理' }),
                            code_key: [IPermissonCode.PERMISSION_CLOUD_SPEAKER],
                        },
                        {
                            path: '/main-operations/cloud-speaker/cloud-speaker-group',
                            name: $t({ defaultMessage: '设备分组管理' }),
                            code_key: [
                                IPermissonCode.PERMISSION_CLOUD_SPEAKER_GROUP,
                                IPermissonCode.PERMISSION_DEVICE_GROUP_DETAIL,
                            ],
                        },
                    ],
                },
                {
                    path: '/main-operations/cloud-speaker/cloud-speaker-rule',
                    name: $t({ defaultMessage: '语音播报策略' }),
                    children: [
                        {
                            path: '/main-operations/cloud-speaker/cloud-speaker-rule',
                            name: $t({ defaultMessage: '语音播报规则' }),
                            code_key: [
                                IPermissonCode.PERMISSION_CLOUD_SPEAKER_RULE,
                                IPermissonCode.PERMISSION_VOICE_BROADCAST_DETAIL,
                            ],
                        },
                    ],
                },
            ],
        },
        {
            name: $t({ defaultMessage: '订单管理' }),
            menuType: 'operationCenter-orders',
            icon: NavOrderActiveIcon,
            inactiveIcon: NavOrderInactiveIcon,
            children: [
                {
                    path: '/main-operations/orders',
                    name: $t({ defaultMessage: '订单管理' }),
                    children: [
                        {
                            path: '/main-operations/sitDownOrder',
                            name: $t({ defaultMessage: '堂食订单' }),
                            code_key: [IPermissonCode.PERMISSION_DINE_IN_ORDER],
                        },
                        {
                            path: '/main-operations/pickUpDistributionOrder',
                            name: $t({ defaultMessage: '自提配送订单' }),
                            code_key: [IPermissonCode.PERMISSION_ORDER_SELF_AND_DELIVER],
                        },
                        {
                            path: '/main-operations/thirdOrder',
                            name: $t({ defaultMessage: '三方平台订单' }),
                            code_key: [IPermissonCode.PERMISSION_ORDER_THIRD_PLATFORM],
                        },
                        {
                            path: '/main-operations/productAppointmentOrder',
                            name: $t({ defaultMessage: '商品预约单' }),
                            code_key: [IPermissonCode.PERMISSION_ORDER_PRODUCT_RESERVE],
                        },
                        {
                            path: '/main-operations/productMallOrder',
                            name: $t({ defaultMessage: '商城订单' }),
                            code_key: [IPermissonCode.PERMISSION_ORDER_SHOP],
                        },
                        {
                            path: '/main-operations/scanOrder',
                            name: $t({ defaultMessage: '买单订单' }),
                            code_key: [IPermissonCode.PERMISSION_ORDER_PRODUCT_SCAN_CODE],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '其他订单' }),
                    children: [
                        {
                            path: '/main-operations/cardorders',
                            name: $t({ defaultMessage: '卡券订单' }),
                            code_key: [IPermissonCode.PERMISSION_CARD_ORDER],
                        },
                        {
                            path: '/main-operations/downPayment',
                            name: $t({ defaultMessage: '订金/押金' }),
                            code_key: [IPermissonCode.PERMISSION_DOWN_PAYMENT],
                        },
                        {
                            path: '/main-operations/receiptOfDepositary',
                            name: $t({ defaultMessage: '寄存单' }),
                            code_key: [IPermissonCode.PERMISSION_STORAGE_ORDER],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '订单操作' }),
                    children: [
                        {
                            path: '/main-operations/afterSaleOrders',
                            name: $t({ defaultMessage: '售后订单' }),
                            code_key: [IPermissonCode.PERMISSION_AFTER_SALE_ORDER],
                        },
                        {
                            path: '/main-operations/cardCouponAfterSalesOrders',
                            name: $t({ defaultMessage: '卡券售后订单' }),
                            code_key: [IPermissonCode.PERMISSION_AFTER_SALE_CARD_ORDER],
                        },
                        {
                            path: '/main-operations/abnormalOrders',
                            name: $t({ defaultMessage: '异常订单' }),
                            code_key: [
                                IPermissonCode.PERMISSION_EXCEPTION_ORDER_ACCEPT,
                                IPermissonCode.PERMISSION_EXCEPTION_ORDER_FULFILL,
                                IPermissonCode.PERMISSION_EXCEPTION_ORDER_PAY,
                                IPermissonCode.PERMISSION_EXCEPTION_DEPOSIT_ORDER,
                            ],
                        },
                        {
                            path: '/main-operations/tableOrder',
                            name: $t({ defaultMessage: '桌台预订' }),
                            code_key: [IPermissonCode.PERMISSION_ORDER_TABLE_RESERVE],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '挂账管理' }),
                    children: [
                        {
                            path: '/main-operations/employeeBillingManagement',
                            name: $t({ defaultMessage: '个人挂账' }),
                            code_key: [IPermissonCode.PERMISSION_PERSON_ARREARS],
                        },
                        {
                            path: '/main-operations/accountManagement',
                            name: $t({ defaultMessage: '企业挂账' }),
                            code_key: [IPermissonCode.PERMISSION_ENTERPRISE],
                        },
                    ],
                },
            ],
        },
        {
            name: $t({ defaultMessage: '菜品管理' }),
            menuType: 'operationCenter-products',
            icon: NavProductActiveIcon,
            inactiveIcon: NavProductInactiveIcon,
            children: [
                {
                    path: '/main-operations/standardProduct',
                    name: $t({ defaultMessage: '基础档案' }),
                    children: [
                        {
                            path: isSingleLocation
                                ? '/product/product-standard-single'
                                : '/product/product-standard',
                            name: $t({ defaultMessage: '菜品库' }),
                            code_key: [IPermissonCode.PERMISSION_PRODUCT],
                        },
                        {
                            path: '/product/product-mall',
                            name: $t({ defaultMessage: '商城商品' }),
                            code_key: [IPermissonCode.PERMISSION_PRODUCT_SHOP],
                        },
                        {
                            path: isSingleLocation
                                ? '/product/product-category-single'
                                : '/product/product-category',
                            name: $t({ defaultMessage: '菜品分类' }),
                            code_key: [IPermissonCode.PERMISSION_PRODUCT_TYPE],
                        },
                        {
                            path: '/main-operations/goods/collections',
                            name: $t({ defaultMessage: '菜品系列' }),
                            code_key: [IPermissonCode.PERMISSION_PRODUCT_COLLECT],
                        },
                        {
                            path: '/main-operations/goods/goodsAttribute',
                            name: $t({ defaultMessage: '菜品属性' }),
                            code_key: [
                                IPermissonCode.PERMISSION_PRODUCT_OPTION_METHOD,
                                IPermissonCode.PERMISSION_PRODUCT_OPTION_ADD,
                                IPermissonCode.PERMISSION_PACKAGE_BOX,
                                IPermissonCode.PERMISSION_PRODUCT_UNIT,
                                IPermissonCode.PERMISSION_PRODUCT_CORNER_MARK,
                            ],
                        },
                        // {
                        //   path: '/main-operations/goods/timePeriodMenu',
                        //   name: $t({ defaultMessage: '时段菜单' }),
                        //   code_key: [IPermissonCode.PERMISSION_PRODUCT_TIME_MENUY],
                        //   customRule: () =>
                        //     getScopeType() === 'HEADQUARTERS' &&
                        //     hasOpteratePermission(
                        //       IPermissonCode.PERMISSION_PRODUCT_TIME_MENUY,
                        //       IPermissonCodeKey.QUERY,
                        //     ),
                        // },
                        {
                            path: '/main-operations/goods/productTimePeriod',
                            name: $t({ defaultMessage: '菜品时段' }),
                            code_key: [IPermissonCode.PERMISSION_PRODUCT_TIME_PERIOD],
                        },
                        {
                            path: '/main-operations/goods/incomeSubject',
                            name: $t({ defaultMessage: '收入科目' }),
                            code_key: [IPermissonCode.PERMISSION_PRODUCT_INCOME_SUBJECT],
                        },
                    ],
                },
                {
                    path: '/main-product/product-release',
                    name: $t({ defaultMessage: '连锁管理' }),
                    children: [
                        {
                            path: '/main-product/product-release',
                            name: $t({ defaultMessage: '菜品下发' }),
                            code_key: [
                                IPermissonCode.PERMISSION_PRODUCT_DISTRIBUTION,
                                IPermissonCode.PERMISSION_PRODUCT_DISTRIBUTION_RECORD,
                            ],
                        },
                        {
                            path: '/main-product/product-template',
                            name: $t({ defaultMessage: '菜谱模板' }),
                            code_key: [IPermissonCode.PERMISSION_PRODUCT_RECIPE_TEMPLATE_CATEGORY],
                        },
                        {
                            path: '/main-product/product-dynamic-price',
                            name: $t({ defaultMessage: '定时变价' }),
                            code_key: [IPermissonCode.PERMISSION_PRODUCT_TIMED_PRICE],
                        },
                    ],
                },
                {
                    path: '/product/product-standard-location',
                    name: $t({ defaultMessage: '门店菜品管理' }),
                    children: [
                        {
                            path: '/product/product-standard-location',
                            name: $t({ defaultMessage: '门店菜品管理' }),
                            code_key: [IPermissonCode.PERMISSION_PRODUCT_LOCATION],
                        },
                        {
                            path: '/product/product-category-location',
                            name: $t({ defaultMessage: '门店菜品分类' }),
                            code_key: [IPermissonCode.PERMISSION_PRODUCT_TYPE_LOCATION],
                        },
                        {
                            path: '/main-operations/goods/storeAttribute',
                            name: $t({ defaultMessage: '门店菜品属性' }),
                            code_key: [
                                IPermissonCode.PERMISSION_PRODUCT_OPTION_LOCATION_METHOD,
                                IPermissonCode.PERMISSION_PRODUCT_OPTION_LOCATION_ADD,
                                IPermissonCode.PERMISSION_PRODUCT_UNIT_LOCATION_UNIT,
                            ],
                        },
                        // {
                        //   path: '/main-operations/goods/storeTimePeriodMenu',
                        //   name: $t({ defaultMessage: '门店时段菜单' }),
                        //   code_key: [IPermissonCode.PERMISSION_PRODUCT_TIME_MENUY_LOCATION],
                        //   customRule: () =>
                        //     ['HEADQUARTERS', 'BRANCH'].includes(getScopeType()) &&
                        //     hasOpteratePermission(
                        //       IPermissonCode.PERMISSION_PRODUCT_TIME_MENUY_LOCATION,
                        //       IPermissonCodeKey.QUERY,
                        //     ),
                        // },
                    ],
                },
                {
                    name: $t({ defaultMessage: '菜品拓展' }),
                    children: [
                        {
                            path: '/main-operations/goods/productionDepartment',
                            name: $t({ defaultMessage: '出品部门' }),
                            code_key: [
                                IPermissonCode.PERMISSION_PRODUCT_TAG_HEADQUARTERS,
                                IPermissonCode.PERMISSION_PRODUCT_TAG_LOCATION,
                            ],
                            customRule: () => {
                                // 单店就不显示
                                const merchantChainEnable = window.getCookie('merchant_chain_enable');
                                return (hasOpteratePermission([IPermissonCode.PERMISSION_PRODUCT_TAG_HEADQUARTERS], IPermissonCodeKey.QUERY) ||
                                    (merchantChainEnable === 'true' &&
                                        hasOpteratePermission([IPermissonCode.PERMISSION_PRODUCT_TAG_LOCATION], IPermissonCodeKey.QUERY)));
                            },
                        },
                        {
                            path: '/main-operations/goods/productPrintConfig',
                            name: $t({ defaultMessage: '菜品打印配置' }),
                            code_key: [IPermissonCode.PERMISSION_PRODUCT_PRINT_CONFIGURATION],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '菜品库存' }),
                    children: [
                        {
                            path: '/main-operations/goods/inventory',
                            name: $t({ defaultMessage: '菜品库存' }),
                            code_key: [IPermissonCode.PERMISSION_PRODUCT_INVENTORY],
                        },
                        {
                            path: '/main-operations/goods/detailLogInventory',
                            name: $t({ defaultMessage: '库存变动' }),
                            code_key: [IPermissonCode.PERMISSION_PRODUCT_INVENTORY_LOG],
                        },
                        {
                            path: '/main-operations/goods/stockSync',
                            name: $t({ defaultMessage: '库存同步' }),
                            code_key: [IPermissonCode.PERMISSION_PRODUCT_INVENTORY_SYNC],
                        },
                        {
                            path: '/main-operations/goods/planInventory',
                            name: $t({ defaultMessage: '计划库存' }),
                            code_key: [IPermissonCode.PERMISSION_PLAN_INVENTORY],
                        },
                    ],
                },
            ],
        },
        {
            name: $t({ defaultMessage: '线上店铺' }),
            menuType: 'operationCenter-weapp',
            icon: NavWeappActiveIcon,
            inactiveIcon: NavWeappInactiveIcon,
            children: [
                {
                    path: '/main-operations/weapp',
                    name: $t({ defaultMessage: '微店铺' }),
                    children: [
                        // {
                        //   path: '/main-operations/weapp/product',
                        //   name: '小程序商品',
                        //   code_key: [IPermissonCode.PERMISSION_WESTORE_PRODUCT],
                        // },
                        {
                            path: '/main-operations/weapp/decorateNavigation',
                            name: $t({ defaultMessage: '小程序管理' }),
                            code_key: [IPermissonCode.PERMISSION_WESTORE_WECHAT_MP_AUTH_SETTING],
                        },
                        {
                            path: '/decorate/shoppage/merchantPage',
                            name: $t({ defaultMessage: '店铺装修' }),
                            customRule: () => {
                                const locationType = getLocationTypes();
                                return (hasOpteratePermission(IPermissonCode.PERMISSION_WESTORE_DECORATION, IPermissonCodeKey.CONFIG) && locationType.includes('HEAD_QUARTERS'));
                            },
                            customClick: () => {
                                navigateToShopDecorationEntry();
                            },
                        },
                        {
                            path: '/main-operations/weapp/popularize',
                            name: $t({ defaultMessage: '小程序推广' }),
                            code_key: [IPermissonCode.PERMISSION_WESTORE_WECHAT_MP_PAGE_QR_CODE],
                        },
                        {
                            path: '/main-operations/weapp/userAgreement',
                            name: $t({ defaultMessage: '用户协议' }),
                            code_key: [IPermissonCode.PERMISSION_SETTING_AGREEMENT],
                        },
                        {
                            path: '/main-operations/weapp/adviceFeedbacks',
                            name: $t({ defaultMessage: '建议反馈' }),
                            code_key: [
                                IPermissonCode.PERMISSION_WESTORE_ADVICE_FEEDBACK_LIST,
                                IPermissonCode.PERMISSION_WESTORE_ADVICE_FEEDBACK_SET,
                            ],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '平台小程序' }),
                    children: [
                        {
                            path: '/main-operations/platform/location',
                            name: $t({ defaultMessage: '门店列表' }),
                            code_key: [IPermissonCode.PERMISSION_PLATFORM_LOCATION],
                        },
                        // {
                        //   path: '/main-operations/platform/product',
                        //   name: '平台小程序商品',
                        //   code_key: [IPermissonCode.PERMISSION_PLATFORM_PRODUCT],
                        // },
                        {
                            path: '/main-operations/platform/examine',
                            name: $t({ defaultMessage: '商品审核' }),
                            code_key: [IPermissonCode.PERMISSION_PLATFORM_PRODUCT_AUDIT],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '其他渠道' }),
                    children: [
                        {
                            path: '/main-operations/weapp/public',
                            name: $t({ defaultMessage: '微信公众号' }),
                            code_key: [IPermissonCode.PERMISSION_WESTORE_WECHAT_MP_OFFICIAL_ACCOUNT],
                        },
                    ],
                },
                {
                    path: '/main-operations/mallSetting',
                    name: $t({ defaultMessage: '快递商城' }),
                    children: [
                        {
                            path: '/main-operations/mallSetting',
                            name: $t({ defaultMessage: '商城设置' }),
                            code_key: [IPermissonCode.PERMISSION_SETTING_EXPRESS_SHOP_SETTING],
                        },
                        {
                            path: '/main-operations/courierTemplate',
                            name: $t({ defaultMessage: '快递模板' }),
                            code_key: [IPermissonCode.PERMISSION_SETTING_EXPRESS_TMPL],
                        },
                    ],
                },
                {
                    path: '/main-operations/businessSettings',
                    name: $t({ defaultMessage: '手机点餐' }),
                    children: [
                        {
                            path: '/main-operations/businessSettings',
                            name: $t({ defaultMessage: '手机点餐设置' }),
                            customRule: () => {
                                if ([
                                    IPermissonCode.PERMISSION_OPERATE_SETTING_DINING_IN,
                                    IPermissonCode.PERMISSION_SETTING_SHIPPING_SELF_AND_TAKE_OUT,
                                    IPermissonCode.PERMISSION_OPERATE_SETTING_TAKE_OUT,
                                    IPermissonCode.PERMISSION_SETTING_SHIPPING_GROUP_MEAL,
                                    IPermissonCode.PERMISSION_OPERATE_SETTING_GROUP_MEAL_TAKE_OUT,
                                ].some((key) => hasOpteratePermission(key, IPermissonCodeKey.EDIT))) {
                                    return true;
                                }
                                return false;
                            },
                        },
                        {
                            path: '/main-operations/aggregatedDelivery',
                            name: $t({ defaultMessage: '聚合配送' }),
                            customRule: () => {
                                if ([
                                    IPermissonCode.PERMISSION_AGGREGATE_TRANSFER_RULE,
                                    IPermissonCode.PERMISSION_AGGREGATE_TRANSFER_CHANNEL,
                                ].some((key) => hasOpteratePermission(key, IPermissonCodeKey.EDIT))) {
                                    return true;
                                }
                                return false;
                            },
                        },
                        {
                            path: '/main-operations/queueSettings',
                            name: $t({ defaultMessage: '排队设置' }),
                            code_key: [IPermissonCode.PERMISSION_SETTING_SHIPPING_TAKE_REQUIRED_QUEUE],
                        },
                        {
                            path: '/main-operations/QRCodeManagement',
                            name: $t({ defaultMessage: '二维码管理' }),
                            code_key: [
                                IPermissonCode.PERMISSION_OPERATE_SETTING_CATEGORY_QR_CODE,
                                IPermissonCode.PERMISSION_OPERATE_SETTING_CATEGORY_QR_CODE_EMPTY,
                            ],
                        },
                    ],
                },
            ],
        },
        // {
        //   name: 'POS销售',
        //   menuType: 'operationCenter-posSale',
        //   icon: NavPOSSaveActiveIcon,
        //   inactiveIcon: NavPOSSaveInactiveIcon,
        //   children: [
        //     {
        //       name: 'POS销售',
        //       children: [
        //         {
        //           path: '/main-operations/pos/product',
        //           name: 'POS门店商品',
        //           code_key: [IPermissonCode.PERMISSION_POS_WEB_PRODUCT],
        //         },
        //       ],
        //     },
        //   ],
        // },
        {
            name: $t({ defaultMessage: '外卖管理' }),
            menuType: 'operationCenter-saleChannels',
            icon: NavChannelActiveIcon,
            inactiveIcon: NavChannelInactiveIcon,
            // menuType: 'operationCenter-takeaway',
            // icon: NavShippingActiveIcon,
            // inactiveIcon: NavShippingInactiveIcon,
            children: [
                {
                    name: $t({ defaultMessage: '外卖菜品下发' }),
                    children: [
                        {
                            path: '/main-operations/oms/takeout/product',
                            name: $t({ defaultMessage: '外卖菜品下发' }),
                            code_key: [IPermissonCode.PERMISSION_TAKEOUT_DISH_ISSUE],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '美团外卖' }),
                    children: [
                        {
                            path: '/main-operations/oms/mt/manager',
                            name: $t({ defaultMessage: '美团门店列表' }),
                            code_key: [IPermissonCode.PERMISSION_OMS_MT_AUTHROIZE_MANAGE],
                        },
                        {
                            path: '/main-operations/oms/mt/product',
                            name: $t({ defaultMessage: '美团门店商品' }),
                            code_key: [IPermissonCode.PERMISSION_OMS_MT_LOCATION_PRODUCT],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '淘宝闪购·餐饮' }),
                    children: [
                        {
                            path: '/main-operations/oms/elm/manager',
                            name: $t({ defaultMessage: '淘宝闪购·餐饮授权' }),
                            code_key: [IPermissonCode.PERMISSION_OMS_EL_AUTHROIZE_MANAGE],
                        },
                        {
                            path: '/main-operations/oms/elm/settings',
                            name: $t({ defaultMessage: '淘宝闪购·餐饮门店列表' }),
                            code_key: [IPermissonCode.PERMISSION_OMS_EL_LOCATION_SET],
                        },
                        {
                            path: '/main-operations/oms/elm/product',
                            name: $t({ defaultMessage: '淘宝闪购·餐饮门店商品' }),
                            code_key: [IPermissonCode.PERMISSION_OMS_EL_LOCATION_PRODUCT],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '淘宝闪购·零售' }),
                    children: [
                        {
                            path: '/main-operations/oms/ebls/manager',
                            name: $t({ defaultMessage: '淘宝闪购·零售授权' }),
                            code_key: [IPermissonCode.PERMISSION_OMS_EB_AUTHROIZE_MANAGE],
                        },
                        {
                            path: '/main-operations/oms/ebls/settings',
                            name: $t({ defaultMessage: '淘宝闪购·零售门店列表' }),
                            code_key: [IPermissonCode.PERMISSION_OMS_EB_LOCATION_SET],
                        },
                        {
                            path: '/main-operations/oms/ebls/product',
                            name: $t({ defaultMessage: '淘宝闪购·零售门店商品' }),
                            code_key: [IPermissonCode.PERMISSION_OMS_EB_LOCATION_PRODUCT],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '抖音外卖' }),
                    children: [
                        {
                            path: '/main-operations/oms/dy/manager',
                            name: $t({ defaultMessage: '抖音外卖授权' }),
                            code_key: [IPermissonCode.PERMISSION_OMS_TIKTOK_AUTHROIZE_MANAGE],
                        },
                        {
                            path: '/main-operations/oms/dy/location',
                            name: $t({ defaultMessage: '抖音门店列表' }),
                            code_key: [IPermissonCode.PERMISSION_OMS_TIKTOK_LOCATION_SET],
                        },
                        {
                            path: '/main-operations/oms/dy/product',
                            name: $t({ defaultMessage: '抖音门店商品' }),
                            code_key: [IPermissonCode.PERMISSION_OMS_TIKTOK_LOCATION_PRODUCT],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '抖音自助核销' }),
                    children: [
                        {
                            path: '/main-operations/oms/tiktokSelf/manager',
                            name: $t({ defaultMessage: '抖音自助核销授权' }),
                            code_key: [IPermissonCode.PERMISSION_OMS_TIKTOK_SELF_CHECK_AUTHROIZE_MANAGE],
                        },
                        {
                            path: '/main-operations/oms/tiktokSelf/location',
                            name: $t({ defaultMessage: '抖音自助核销门店' }),
                            code_key: [IPermissonCode.PERMISSION_OMS_TIKTOK_SELF_CHECK_LOCATION_SET],
                        },
                        {
                            path: '/main-operations/oms/tiktokSelf/product',
                            name: $t({ defaultMessage: '抖音自助核销商品' }),
                            code_key: [IPermissonCode.PERMISSION_OMS_TIKTOK_SELF_CHECK_LOCATION_PRODUCT],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '京东秒送' }),
                    children: [
                        {
                            path: '/main-operations/oms/jd/manager',
                            name: $t({ defaultMessage: '京东秒送授权' }),
                            code_key: [IPermissonCode.PERMISSION_OMS_JD_AUTHROIZE_MANAGE],
                        },
                        {
                            path: '/main-operations/oms/jd/settings',
                            name: $t({ defaultMessage: '京东秒送门店列表' }),
                            code_key: [IPermissonCode.PERMISSION_OMS_JD_LOCATION_SET],
                        },
                        {
                            path: '/main-operations/oms/jd/product',
                            name: $t({ defaultMessage: '京东秒送门店商品' }),
                            code_key: [IPermissonCode.PERMISSION_OMS_JD_LOCATION_PRODUCT],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '美团闪购' }),
                    children: [
                        {
                            path: '/main-operations/oms/mtsg/settings',
                            name: $t({ defaultMessage: '美团闪购门店列表' }),
                            code_key: [IPermissonCode.PERMISSION_OMS_MTSG_LOCATION_SET],
                        },
                        {
                            path: '/main-operations/oms/mtsg/product',
                            name: $t({ defaultMessage: '美团闪购门店商品' }),
                            code_key: [IPermissonCode.PERMISSION_OMS_MTSG_LOCATION_PRODUCT],
                        },
                    ],
                },
            ],
        },
        {
            name: $t({ defaultMessage: '消息渠道' }),
            menuType: 'operationCenter-sms',
            icon: NavSMSActiveIcon,
            inactiveIcon: NavSMSInactiveIcon,
            code_key: [IPermissonCode.PARENT_PERMISSION_MSG_CHANNEL],
            children: [
                {
                    name: $t({ defaultMessage: '消息通知' }),
                    children: [
                        {
                            path: '/main-operations/notifySettings',
                            name: $t({ defaultMessage: '消息通知' }),
                            code_key: [IPermissonCode.PERMISSION_SETTING_NOTIFY],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '短信发送' }),
                    children: [
                        {
                            path: '/main-operations/marketingActivities/smsGroupSend',
                            name: $t({ defaultMessage: '短信群发' }),
                            code_key: [IPermissonCode.PERMISSION_PROMOTION_SMS_GROUP_SEND],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '短信设置' }),
                    children: [
                        {
                            path: '/main-operations/sms/setting/smsTemplate',
                            name: $t({ defaultMessage: '短信模板' }),
                            code_key: [IPermissonCode.PARENT_PERMISSION_SMS_TMPL],
                        },
                        {
                            path: '/main-operations/sms/setting/smsType',
                            name: $t({ defaultMessage: '模板类型' }),
                            code_key: [IPermissonCode.PARENT_PERMISSION_SMS_TMPL_TYPE],
                        },
                    ],
                },
            ],
        },
        {
            name: $t({ defaultMessage: '机构与账号' }),
            menuType: 'operationCenter-institutionAccount',
            icon: NavInstitutionAccountActiveIcon,
            inactiveIcon: NavInstitutionAccountInactiveIcon,
            children: [
                {
                    name: $t({ defaultMessage: '品牌管理' }),
                    children: [
                        {
                            path: '/main-operations/brandName',
                            name: $t({ defaultMessage: '品牌管理' }),
                            code_key: [IPermissonCode.PERMISSION_SETTING_VENDOR_MANAGER],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '门店管理' }),
                    children: [
                        {
                            path: '/main-operations/settings/locations',
                            name: $t({ defaultMessage: '门店管理' }),
                            code_key: [IPermissonCode.PERMISSION_SETTING_LOCATION],
                        },
                        {
                            path: '/main-operations/locationCopy',
                            name: $t({ defaultMessage: '门店复制' }),
                            code_key: [IPermissonCode.PERMISSION_SETTING_LOCATION_DUPLICATE],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '用户管理' }),
                    children: [
                        {
                            path: '/main-operations/userManagement',
                            name: $t({ defaultMessage: '用户管理' }),
                            code_key: [
                                IPermissonCode.PERMISSION_SETTING_ACCOUNT,
                                IPermissonCode.PERMISSION_SETTING_ACCOUNT_PERMISSION,
                            ],
                        },
                        {
                            path: '/main-operations/userRole',
                            name: $t({ defaultMessage: '用户角色' }),
                            code_key: [
                                IPermissonCode.PERMISSION_SETTING_ROLE,
                                IPermissonCode.PERMISSION_SETTING_ACCOUNT_PERMISSION,
                            ],
                        },
                        {
                            path: '/main-operations/staffManagement',
                            name: $t({ defaultMessage: '员工管理' }),
                            code_key: [
                                IPermissonCode.PERMISSION_POS_WEB_STAFF,
                                IPermissonCode.PERMISSION_POS_WEB_STAFF_ROLE,
                            ],
                        },
                        {
                            path: '/main-operations/staffRole',
                            name: $t({ defaultMessage: '员工角色' }),
                            code_key: [
                                IPermissonCode.PERMISSION_POS_WEB_ROLE,
                                IPermissonCode.PERMISSION_POS_WEB_STAFF_ROLE,
                            ],
                        },
                        {
                            path: '/main-operations/salesman',
                            name: $t({ defaultMessage: '销售员' }),
                            code_key: [IPermissonCode.PERMISSION_POS_SALESMAN],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '店铺设置' }),
                    children: [
                        {
                            path: '/main-operations/settings/generalSettings',
                            name: $t({ defaultMessage: '店铺信息' }),
                            code_key: [IPermissonCode.PERMISSION_SETTING_MERCHANT_SETTING],
                        },
                    ],
                },
            ],
        },
        {
            name: $t({ defaultMessage: '系统设置' }),
            menuType: 'operationCenter-systemSettings',
            icon: NavSettingActiveIcon,
            inactiveIcon: NavSettingInactiveIcon,
            children: [
                {
                    name: $t({ defaultMessage: '运维监控' }),
                    path: '/main-operations/systemLog',
                    children: [
                        {
                            path: '/main-operations/systemLog',
                            name: $t({ defaultMessage: '系统日志' }),
                            code_key: [IPermissonCode.PERMISSION_SET_SYSTEM_LOG],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '其他' }),
                    path: '/main-operations/settings/metaField',
                    children: [
                        {
                            path: '/main-operations/settings/metaField',
                            name: $t({ defaultMessage: '元字段' }),
                            code_key: [IPermissonCode.PERMISSION_SETTING_METAFIELD],
                        },
                    ],
                },
            ],
        },
    ];
    return getPermissionShowNavItems(navTree);
}
// 财务中心
function navFinanceCenter() {
    const navTree = [
        {
            name: $t({ defaultMessage: '商户支付' }),
            menuType: 'financeCenter-merchantPayment',
            icon: NavMerchantPayIcon,
            inactiveIcon: NavMerchantPayIcon,
            children: [
                {
                    name: $t({ defaultMessage: '商户支付' }),
                    children: [
                        {
                            path: '/main-finance/dashboard',
                            name: $t({ defaultMessage: '数据概览' }),
                            code_key: [IPermissonCode.PERMISSION_MERCHANT_PAY_DASHBOARD],
                        },
                        {
                            path: '/main-finance/payTrade',
                            name: $t({ defaultMessage: '交易汇总' }),
                            code_key: [IPermissonCode.PERMISSION_MERCHANT_PAY_TRADE],
                        },
                        {
                            path: '/main-finance/payRecord',
                            name: $t({ defaultMessage: '支付记录' }),
                            code_key: [IPermissonCode.PERMISSION_MERCHANT_PAY_RECORD],
                        },
                        {
                            path: '/main-finance/payRefund',
                            name: $t({ defaultMessage: '退款记录' }),
                            code_key: [IPermissonCode.PERMISSION_MERCHANT_PAY_REFUND],
                        },
                        {
                            path: '/main-finance/myApp',
                            name: $t({ defaultMessage: '我的应用' }),
                            code_key: [IPermissonCode.PERMISSION_MERCHANT_PAY_MY_APP],
                        },
                        {
                            path: '/main-finance/myMerchant',
                            name: $t({ defaultMessage: '我的商户' }),
                            code_key: [
                                IPermissonCode.PERMISSION_MERCHANT_PAY_MY_MERCHANT,
                                IPermissonCode.PERMISSION_MERCHANT_PAY_MY_MERCHANT_SUB_MGR,
                                IPermissonCode.PERMISSION_MERCHANT_PAY_MY_MERCHANT_SUB_GROUP,
                            ],
                        },
                        {
                            path: '/main-finance/channelConfig',
                            name: $t({ defaultMessage: '渠道配置' }),
                            code_key: [IPermissonCode.PERMISSION_MERCHANT_PAY_CHANNEL_CONFIG],
                        },
                        {
                            path: '/main-finance/locationSubMerchantConfig',
                            name: $t({ defaultMessage: '门店子商户配置' }),
                            code_key: [IPermissonCode.PERMISSION_MERCHANT_PAY_LOCATION_SUB_APP],
                        },
                    ],
                },
            ],
        },
        {
            name: $t({ defaultMessage: '平台小程序支付' }),
            menuType: 'financeCenter-platformProgramPayment',
            icon: NavMiniProgramActiveIcon,
            inactiveIcon: NavMiniProgramInactiveIcon,
            children: [
                {
                    name: $t({ defaultMessage: '平台小程序支付' }),
                    children: [
                        {
                            path: '/main-finance/appletPayTrade',
                            name: $t({ defaultMessage: '交易汇总' }),
                            code_key: [IPermissonCode.PERMISSION_PLATFORM_PAY_TRADE],
                        },
                        {
                            path: '/main-finance/appletPayRecord',
                            name: $t({ defaultMessage: '支付记录' }),
                            code_key: [IPermissonCode.PERMISSION_PLATFORM_PAY_RECORD],
                        },
                        {
                            path: '/main-finance/appletPayRefund',
                            name: $t({ defaultMessage: '退款记录' }),
                            code_key: [IPermissonCode.PERMISSION_PLATFORM_PAY_REFUND],
                        },
                    ],
                },
            ],
        },
        {
            name: $t({ defaultMessage: '发票管理' }),
            menuType: 'financeCenter-invoice-management',
            icon: NavInvoiceActiveIcon,
            inactiveIcon: NavInvoiceInactiveIcon,
            children: [
                {
                    name: $t({ defaultMessage: '发票管理' }),
                    children: [
                        {
                            path: '/main-base/invoice-management',
                            name: $t({ defaultMessage: '开票商户' }),
                            code_key: [IPermissonCode.PERMISSION_INVOICE_BODY],
                        },
                        {
                            path: '/main-base/invoice-locations',
                            name: $t({ defaultMessage: '开票门店' }),
                            code_key: [IPermissonCode.PERMISSION_INVOICE_LOCATION_SETTING],
                        },
                        {
                            path: '/main-base/invoice-setup',
                            name: $t({ defaultMessage: '开票设置' }),
                            code_key: [IPermissonCode.PERMISSION_INVOICE_SETTING],
                        },
                        {
                            path: '/main-base/invoice-apply',
                            name: $t({ defaultMessage: '开票申请' }),
                            code_key: [IPermissonCode.PERMISSION_INVOICE_APPLY],
                        },
                        {
                            path: '/main-base/invoice-record',
                            name: $t({ defaultMessage: '开票记录' }),
                            code_key: [IPermissonCode.PERMISSION_INVOICE_RECORD],
                        },
                    ],
                },
            ],
        },
        {
            name: $t({ defaultMessage: '加盟分账' }),
            menuType: 'financeCenter-franchiseDistribution',
            icon: NavAddMemberActiveIcon,
            inactiveIcon: NavAddMemberInactiveIcon,
            children: [
                {
                    name: $t({ defaultMessage: '基础信息' }),
                    children: [
                        {
                            path: '/main-finance/financialEntity',
                            name: $t({ defaultMessage: '财务主体' }),
                            code_key: [IPermissonCode.PERMISSION_FRANCHISE_DISTRIBUTION_FINANCIAL_ENTITY],
                        },
                        {
                            path: '/main-finance/locationFinanceSetting',
                            name: $t({ defaultMessage: '门店财务设置' }),
                            code_key: [IPermissonCode.PERMISSION_FINANCE_SUBJECT_LOCATION_CONFIG],
                        },
                        {
                            path: '/main-finance/moneySetting',
                            name: $t({ defaultMessage: '款项设置' }),
                            code_key: [IPermissonCode.PERMISSION_FRANCHISE_DISTRIBUTION_FEE_ITEM],
                        },
                        {
                            path: '/main-finance/financialEntityChangeRecord',
                            name: $t({ defaultMessage: '财务主体变更记录' }),
                            code_key: [
                                IPermissonCode.PERMISSION_FRANCHISE_DISTRIBUTION_FINANCIAL_ENTITY_CHANGE_RECORD,
                            ],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '交易查询' }),
                    children: [
                        {
                            path: '/main-finance/summaryOfFlow',
                            name: $t({ defaultMessage: '流水明细' }),
                            code_key: [IPermissonCode.PERMISSION_FRANCHISE_COMBINE_BALANCE_FLOW],
                        },
                        {
                            path: '/main-finance/capitalFlow',
                            name: $t({ defaultMessage: '资金流水' }),
                            code_key: [IPermissonCode.PERMISSION_FRANCHISE_FUND_ACCOUNT_FLOW],
                        },
                        {
                            path: '/main-finance/recordAccountFlow',
                            name: $t({ defaultMessage: '记账户流水' }),
                            code_key: [IPermissonCode.PERMISSION_FRANCHISE_RECORD_ACCOUNT_FLOW],
                        },
                        {
                            path: '/main-finance/paymentRecords',
                            name: $t({ defaultMessage: '付款记录' }),
                            code_key: [IPermissonCode.PERMISSION_FRANCHISE_DISTRIBUTION_PAYMENT_RECORD],
                        },
                        {
                            path: '/main-finance/withdrawalRecords',
                            name: $t({ defaultMessage: '提现记录' }),
                            code_key: [IPermissonCode.PERMISSION_FRANCHISE_DISTRIBUTION_WITHDRAWAL_RECORD],
                        },
                        {
                            path: '/main-finance/distributionAudit',
                            name: $t({ defaultMessage: '分账稽查' }),
                            code_key: [IPermissonCode.PERMISSION_FRANCHISE_BATCH_SETTLEMENT_ORDER_CHECK],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '分账报表' }),
                    children: [
                        {
                            path: '/main-finance/streamSummary',
                            name: $t({ defaultMessage: '流水汇总' }),
                            code_key: [IPermissonCode.PERMISSION_FRANCHISE_DISTRIBUTION_STREAM_SUMMARY],
                        },
                        {
                            path: '/main-finance/paymentRecordSummary',
                            name: $t({ defaultMessage: '付款记录汇总' }),
                            code_key: [IPermissonCode.PERMISSION_FRANCHISE_DISTRIBUTION_PAYMENT_RECORD_SUMMARY],
                        },
                        {
                            path: '/main-finance/settlementOrder',
                            name: $t({ defaultMessage: '分账明细' }),
                            code_key: [IPermissonCode.PERMISSION_FRANCHISE_DISTRIBUTION_STATISTIC],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '客户储值消费分账' }),
                    children: [
                        {
                            path: '/main-finance/storedValueConsumptionLedger',
                            name: $t({ defaultMessage: '储值消费分账方案' }),
                            code_key: [
                                IPermissonCode.PERMISSION_FRANCHISE_DISTRIBUTION_STORED_VALUE_CONSUMPTION_LEDGER,
                            ],
                        },
                        {
                            path: '/main-finance/storedValueConsumptionSettlementForm',
                            name: $t({ defaultMessage: '储值消费分账结算单' }),
                            code_key: [
                                IPermissonCode.PERMISSION_FRANCHISE_DISTRIBUTION_STORED_VALUE_CONSUMPTION_SETTLEMENT_FORM,
                            ],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '礼品卡消费分账' }),
                    children: [
                        {
                            path: '/main-finance/giftCardConsumptionLedger',
                            name: $t({ defaultMessage: '礼品卡消费分账方案' }),
                            code_key: [
                                IPermissonCode.PERMISSION_FRANCHISE_DISTRIBUTION_GIFT_CARD_CONSUMPTION_LEDGER,
                            ],
                        },
                        {
                            path: '/main-finance/giftCardConsumptionSettlementForm',
                            name: $t({ defaultMessage: '礼品卡消费分账结算单' }),
                            code_key: [
                                IPermissonCode.PERMISSION_FRANCHISE_DISTRIBUTION_GIFT_CARD_CONSUMPTION_SETTLEMENT_FORM,
                            ],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '券核销分账' }),
                    children: [
                        {
                            path: '/main-finance/voucherVerificationLedger',
                            name: $t({ defaultMessage: '券核销分账方案' }),
                            code_key: [
                                IPermissonCode.PERMISSION_FRANCHISE_DISTRIBUTION_VOUCHER_VERIFICATION_LEDGER,
                            ],
                        },
                        {
                            path: '/main-finance/voucherVerificationSettlementForm',
                            name: $t({ defaultMessage: '券核销分账结算单' }),
                            code_key: [
                                IPermissonCode.PERMISSION_FRANCHISE_DISTRIBUTION_VOUCHER_VERIFICATION_SETTLEMENT_FORM,
                            ],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '总部分账' }),
                    children: [
                        {
                            path: '/main-finance/headquartersLedger',
                            name: $t({ defaultMessage: '总部分账方案' }),
                            code_key: [IPermissonCode.PERMISSION_FRANCHISE_DISTRIBUTION_HEADQUARTERS_LEDGER],
                        },
                        {
                            path: '/main-finance/headquartersExpense',
                            name: $t({ defaultMessage: '总部费用单' }),
                            code_key: [IPermissonCode.PERMISSION_FRANCHISE_DISTRIBUTION_HEADQUARTERS_EXPENSE],
                            customRule: () => {
                                const locationType = getLocationTypes();
                                return (hasOpteratePermission(IPermissonCode.PERMISSION_FRANCHISE_DISTRIBUTION_HEADQUARTERS_EXPENSE, IPermissonCodeKey.QUERY) && locationType.includes('HEAD_QUARTERS'));
                            },
                        },
                        {
                            path: '/main-finance/headquartersSettlementForm',
                            name: $t({ defaultMessage: '总部分账结算单' }),
                            code_key: [
                                IPermissonCode.PERMISSION_FRANCHISE_DISTRIBUTION_HEADQUARTERS_SETTLEMENT_FORM,
                            ],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '门店分账' }),
                    children: [
                        {
                            path: '/main-finance/locationLedger',
                            name: $t({ defaultMessage: '门店分账方案' }),
                            code_key: [IPermissonCode.PERMISSION_FRANCHISE_DISTRIBUTION_LOCATION_LEDGER],
                        },
                        {
                            path: '/main-finance/locationExpense',
                            name: $t({ defaultMessage: '门店费用单' }),
                            code_key: [IPermissonCode.PERMISSION_FRANCHISE_DISTRIBUTION_LOCATION_EXPENSE],
                            customRule: () => {
                                const locationType = getLocationTypes();
                                return (hasOpteratePermission(IPermissonCode.PERMISSION_FRANCHISE_DISTRIBUTION_LOCATION_EXPENSE, IPermissonCodeKey.QUERY) && locationType.includes('HEAD_QUARTERS'));
                            },
                        },
                        {
                            path: '/main-finance/locationSettlementForm',
                            name: $t({ defaultMessage: '门店分账结算单' }),
                            code_key: [IPermissonCode.PERMISSION_FRANCHISE_DISTRIBUTION_LOCATION_SETTLEMENT_FORM],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '自定义分账' }),
                    children: [
                        {
                            path: '/main-finance/customLedger',
                            name: $t({ defaultMessage: '自定义分账方案' }),
                            code_key: [IPermissonCode.PERMISSION_FRANCHISE_DISTRIBUTION_CUSTOMIZED_LEDGER],
                        },
                        {
                            path: '/main-finance/customSettlementForm',
                            name: $t({ defaultMessage: '自定义分账结算单' }),
                            code_key: [
                                IPermissonCode.PERMISSION_FRANCHISE_DISTRIBUTION_CUSTOMIZED_SETTLEMENT_FORM,
                            ],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '基础设置' }),
                    children: [
                        {
                            path: '/main-finance/fundParams',
                            name: $t({ defaultMessage: '资金参数' }),
                            code_key: [IPermissonCode.PERMISSION_FRANCHISE_FINANCE_SETTING],
                            customRule: () => {
                                const locationType = getLocationTypes();
                                return (hasOpteratePermission(IPermissonCode.PERMISSION_FRANCHISE_FINANCE_SETTING, IPermissonCodeKey.QUERY) && locationType.includes('HEAD_QUARTERS'));
                            },
                        },
                    ],
                },
            ],
        },
    ];
    return getPermissionShowNavItems(navTree);
}
// 营销中心
function navMarketingCenter() {
    const navTree = [
        {
            name: $t({ defaultMessage: '客户管理' }),
            menuType: 'marketingCenter-customers',
            icon: NavCustomerActiveIcon,
            inactiveIcon: NavCustomerInactiveIcon,
            children: [
                {
                    path: '/main-operations/customers',
                    name: $t({ defaultMessage: '客户信息' }),
                    children: [
                        {
                            path: '/main-operations/customers',
                            name: $t({ defaultMessage: '客户列表' }),
                            code_key: [IPermissonCode.PERMISSION_CUSTOMER],
                        },
                        {
                            path: '/main-operations/clubCard',
                            name: $t({ defaultMessage: '会员卡列表' }),
                            code_key: [IPermissonCode.PERMISSION_CUSTOMER_CARD_LIST],
                        },
                        {
                            path: '/main-operations/cardKind',
                            name: $t({ defaultMessage: '会员卡类型' }),
                            code_key: [IPermissonCode.PERMISSION_CUSTOMER_CARD_TYPE],
                        },
                        {
                            path: '/main-operations/clubCardGroup',
                            name: $t({ defaultMessage: '会员卡共享分组' }),
                            code_key: [IPermissonCode.PERMISSION_CUSTOMER_CARD_SHARE_GROUP],
                        },
                        {
                            path: '/main-operations/cardImport',
                            name: $t({ defaultMessage: 'IC卡导入' }),
                            code_key: [IPermissonCode.PERMISSION_CUSTOMER_IC_CARD],
                            customRule: () => getCardType() &&
                                hasOpteratePermission(IPermissonCode.PERMISSION_CUSTOMER_IC_CARD, IPermissonCodeKey.QUERY),
                        },
                    ],
                },
                {
                    path: '/main-operations/customer-tag',
                    name: $t({ defaultMessage: '客户分层' }),
                    children: [
                        {
                            path: '/main-operations/customer-tag',
                            name: $t({ defaultMessage: '客户标签' }),
                            code_key: [IPermissonCode.PERMISSION_CUSTOMER_MANUAL_TAG],
                        },
                        {
                            path: '/main-operations/customer-group',
                            name: $t({ defaultMessage: '客户群体' }),
                            code_key: [
                                IPermissonCode.PERMISSION_CUSTOMER_SYSTEM_TAG,
                                IPermissonCode.PERMISSION_CUSTOMER_CONDITION_TAG,
                            ],
                        },
                    ],
                },
                {
                    path: '/main-operations/userPortrait',
                    name: $t({ defaultMessage: '客户分析' }),
                    children: [
                        {
                            path: '/main-operations/userPortrait',
                            name: $t({ defaultMessage: '用户画像' }),
                            code_key: [IPermissonCode.PERMISSION_CUSTOMER_PERSONA],
                        },
                        {
                            path: '/main-operations/RFMAnalysis',
                            name: $t({ defaultMessage: 'RFM分析' }),
                            code_key: [IPermissonCode.PERMISSION_CUSTOMER_RFM_REPORT],
                        },
                        {
                            path: '/main-operations/dataDefinition',
                            name: $t({ defaultMessage: '数据定义' }),
                            code_key: [IPermissonCode.PERMISSION_CUSTOMER_CUSTOM_CONDITION],
                        },
                    ],
                },
                {
                    path: '/main-operations/vip',
                    name: $t({ defaultMessage: '客户运营' }),
                    children: [
                        {
                            path: '/main-operations/vip',
                            name: $t({ defaultMessage: '会员体系' }),
                            code_key: [
                                IPermissonCode.PERMISSION_CUSTOMER_LEVEL_MEMBER_CARD,
                                IPermissonCode.PERMISSION_CUSTOMER_MEMBER_RIGHT,
                            ],
                        },
                        {
                            path: '/main-operations/point',
                            name: $t({ defaultMessage: '积分体系' }),
                            code_key: [IPermissonCode.PERMISSION_CUSTOMER_ASSET_POINT_MGR],
                        },
                        {
                            path: '/main-operations/deposit',
                            name: $t({ defaultMessage: '储值体系' }),
                            code_key: [
                                IPermissonCode.PERMISSION_CUSTOMER_ASSET_DEPOSIT_MGR,
                                IPermissonCode.PERMISSION_CUSTOMER_BALANCE_REFUND_ORDER,
                            ],
                        },
                        {
                            path: '/main-operations/preDeposit',
                            name: $t({ defaultMessage: '预储值' }),
                            code_key: [IPermissonCode.PERMISSION_PRE_DEPOSITS],
                        },
                        {
                            path: '/main-operations/member-price',
                            name: $t({ defaultMessage: '会员价' }),
                            code_key: [IPermissonCode.PERMISSION_MEMBER_PRICE],
                        },
                    ],
                },
            ],
        },
        {
            name: $t({ defaultMessage: '卡券中心' }),
            menuType: 'marketingCenter-cardCouponCenter',
            icon: NavCardCouponCenterActiveIcon,
            inactiveIcon: NavCardCouponCenterInactiveIcon,
            children: [
                {
                    path: '/main-operations/marketingActivities/discounts',
                    name: $t({ defaultMessage: '优惠券' }),
                    children: [
                        {
                            name: $t({ defaultMessage: '优惠券' }),
                            path: '/main-operations/marketingActivities/discounts',
                            code_key: [IPermissonCode.PERMISSION_DISCOUNT_TICKET],
                        },
                        {
                            name: $t({ defaultMessage: '优惠券包' }),
                            path: '/main-operations/marketingActivities/discountPackage',
                            code_key: [IPermissonCode.PERMISSION_DISCOUNT_TICKET_PACK],
                        },
                    ],
                },
                {
                    path: '/main-operations/marketingActivities/giftCard',
                    name: $t({ defaultMessage: '礼品卡' }),
                    children: [
                        {
                            name: $t({ defaultMessage: '礼品卡' }),
                            path: '/main-operations/marketingActivities/giftCard',
                            code_key: [
                                IPermissonCode.PERMISSION_PROMOTION_GIFT_CARD,
                                IPermissonCode.PERMISSION_PROMOTION_GIFT_CARD_TMPL,
                                IPermissonCode.PERMISSION_PROMOTION_GIFT_CARD_MAKE_ORDER,
                                IPermissonCode.PERMISSION_PROMOTION_GIFT_CARD_INFO,
                                IPermissonCode.PERMISSION_PROMOTION_GIFT_CARD_THEME,
                            ],
                        },
                    ],
                },
                {
                    path: '/main-operations/marketingActivities/equityCard',
                    name: $t({ defaultMessage: '权益卡' }),
                    children: [
                        {
                            name: $t({ defaultMessage: '权益卡' }),
                            path: '/main-operations/marketingActivities/equityCard',
                            code_key: [IPermissonCode.PERMISSION_PROMOTION_RIGHT_CARD],
                        },
                    ],
                },
            ],
        },
        {
            name: $t({ defaultMessage: '促销活动' }),
            menuType: 'marketingCenter-salesPromotion',
            icon: NavSalePromotionActiveIcon,
            inactiveIcon: NavSalePromotionInactiveIcon,
            children: [
                {
                    path: '/main-operations/promotion',
                    name: $t({ defaultMessage: '促销活动' }),
                    children: [
                        {
                            path: '/main-operations/promotion',
                            name: $t({ defaultMessage: '促销活动中心' }),
                            code_key: [
                                IPermissonCode.PERMISSION_POS_WEB_FAST_DISCOUNT,
                                IPermissonCode.PERMISSION_DISCOUNT_FULL_REDUCE,
                                IPermissonCode.PERMISSION_APP_LIMIT_TIME_DISCOUNT,
                                IPermissonCode.PERMISSION_SECOND_HALF_DISCOUNT,
                                IPermissonCode.PERMISSION_DISCOUNT_FULL_GIFT,
                                IPermissonCode.PERMISSION_DISCOUNT_MARK_UP_PURCHASE,
                                IPermissonCode.PERMISSION_DISCOUNT_FREIGHT_REDUCE,
                                IPermissonCode.PERMISSION_DISCOUNT_ANY_CHOICE,
                                IPermissonCode.PERMISSION_DISCOUNT_RANDOM_REDUCE,
                                IPermissonCode.PERMISSION_DISCOUNT_SCAN_CODE_PAY,
                            ],
                        },
                        {
                            path: '/main-operations/managePromotion',
                            name: $t({ defaultMessage: '促销活动管理' }),
                            code_key: [IPermissonCode.PARENT_PERMISSION_PROMOTION_EVENT_MANAGER],
                        },
                    ],
                },
            ],
        },
        {
            name: $t({ defaultMessage: '营销活动' }),
            menuType: 'marketingCenter-marketing',
            icon: NavMarketingActiveIcon,
            inactiveIcon: NavMarketingInactiveIcon,
            children: [
                {
                    path: '/main-operations/crm',
                    name: $t({ defaultMessage: '营销活动' }),
                    children: [
                        {
                            path: '/main-operations/crm',
                            name: $t({ defaultMessage: '营销活动中心' }),
                            code_key: [
                                IPermissonCode.PERMISSION_APP_GIFT_GET_CARD,
                                IPermissonCode.PERMISSION_APP_GIFT_CONSUME,
                                IPermissonCode.PERMISSION_APP_GIFT_DEPOSIT,
                                IPermissonCode.PERMISSION_APP_GIFT_LEVEL_UP,
                                IPermissonCode.PERMISSION_PROMOTION_POINT_EXCHANGE,
                                IPermissonCode.PERMISSION_APP_GIFT_BIRTHDAY,
                                // IPermissonCode.PERMISSION_APP_SECOND_HALF_DISCOUNT,
                                IPermissonCode.PERMISSION_DISCOUNT_GET_TICKET_ACTIVITY,
                                IPermissonCode.PERMISSION_DISCOUNT_GROUP_RELAY,
                                IPermissonCode.PERMISSION_DISCOUNT_NEW_CUSTOMER_EXCLUSIVE,
                                IPermissonCode.PERMISSION_DISCOUNT_RECOMMEND_PURCHASE,
                                IPermissonCode.PERMISSION_DISCOUNT_TASK_ACTIVITY,
                                // IPermissonCode.PERMISSION_DISCOUNT_TICKET,
                                IPermissonCode.PERMISSION_DISCOUNT_TICKET_PACK,
                                IPermissonCode.PERMISSION_GIFT_LUCKY_DRAW,
                                IPermissonCode.PERMISSION_PROMOTION_AD_MGR,
                                IPermissonCode.PERMISSION_PROMOTION_COLLECT_STAMP,
                                IPermissonCode.PERMISSION_PROMOTION_CUSTOMER_FISSION,
                                IPermissonCode.PERMISSION_PROMOTION_FUN_GUESS,
                                IPermissonCode.PERMISSION_PROMOTION_FUN_MATCH,
                                // IPermissonCode.PERMISSION_PROMOTION_GIFT_CARD,
                                // IPermissonCode.PERMISSION_PROMOTION_GIFT_CARD_INFO,
                                // IPermissonCode.PERMISSION_PROMOTION_GIFT_CARD_MAKE_ORDER,
                                // IPermissonCode.PERMISSION_PROMOTION_GIFT_CARD_THEME,
                                // IPermissonCode.PERMISSION_PROMOTION_GIFT_CARD_TMPL,
                                // IPermissonCode.PERMISSION_PROMOTION_GIFT_SIGN_IN,
                                IPermissonCode.PERMISSION_PROMOTION_GROUP_BUYING,
                                IPermissonCode.PERMISSION_PROMOTION_POINT_EXCHANGE_ACTIVITY,
                                IPermissonCode.PERMISSION_PROMOTION_POINT_EXCHANGE_LOG,
                                IPermissonCode.PERMISSION_PROMOTION_POINT_EXCHANGE_PROPERTY,
                                IPermissonCode.PERMISSION_PROMOTION_POPUP_TICKET_MGR,
                                IPermissonCode.PERMISSION_PROMOTION_QUESTIONNAIRE_SURVEY,
                                IPermissonCode.PERMISSION_PROMOTION_RECOMMEND_PRODUCT_ACTIVITY,
                                // IPermissonCode.PERMISSION_PROMOTION_RIGHT_CARD,
                                IPermissonCode.PERMISSION_PROMOTION_SMS_GROUP_SEND,
                                IPermissonCode.PERMISSION_PROMOTION_TIMED_GIFT_TICKET,
                            ],
                        },
                        {
                            path: '/main-operations/manageCrm',
                            name: $t({ defaultMessage: '营销活动管理' }),
                            code_key: [IPermissonCode.PERMISSION_PROMOTION_ACTIVE_MANAGER],
                        },
                    ],
                },
            ],
        },
        {
            name: $t({ defaultMessage: '评价管理' }),
            menuType: 'marketingCenter-commentManage',
            icon: CommentManageIcon,
            inactiveIcon: CommentManageFilledIcon,
            children: [
                {
                    path: '/main-operations/orderComment',
                    name: $t({ defaultMessage: '评价管理' }),
                    children: [
                        {
                            path: '/main-operations/orderComment',
                            name: $t({ defaultMessage: '订单评价' }),
                            code_key: [IPermissonCode.PERMISSION_ORDER_EVALUATION],
                        },
                        {
                            path: '/main-operations/commentsAnalysis',
                            name: $t({ defaultMessage: '评价分析' }),
                            code_key: [IPermissonCode.PERMISSION_ORDER_EVALUATION_ANALYZE],
                        },
                    ],
                },
                {
                    path: '/main-operations/settings/evaluationSettings',
                    name: $t({ defaultMessage: '评价设置' }),
                    children: [
                        {
                            path: '/main-operations/settings/evaluationSettings',
                            name: $t({ defaultMessage: '评价设置' }),
                            code_key: [IPermissonCode.PERMISSION_SETTING_EVALUATION_SETTING],
                        },
                    ],
                },
            ],
        },
        {
            name: $t({ defaultMessage: '积分商城' }),
            menuType: 'marketingCenter-pointsMall',
            icon: PointsMallIcon,
            inactiveIcon: PointsMallInActiveIcon,
            children: [
                {
                    name: $t({ defaultMessage: '商城活动' }),
                    children: [
                        {
                            path: '/main-operations/marketingActivities/creditExchangeList',
                            name: $t({ defaultMessage: '积分商城' }),
                            code_key: [
                                IPermissonCode.PERMISSION_PROMOTION_POINT_EXCHANGE,
                                IPermissonCode.PERMISSION_PROMOTION_POINT_EXCHANGE_ACTIVITY,
                                IPermissonCode.PERMISSION_PROMOTION_POINT_EXCHANGE_LOG,
                                IPermissonCode.PERMISSION_PROMOTION_POINT_EXCHANGE_PROPERTY,
                            ],
                        },
                    ],
                },
            ],
        },
        {
            name: $t({ defaultMessage: '企业微信' }),
            menuType: 'marketingCenter-weCom',
            icon: NavWechatActiveIcon,
            inactiveIcon: NavWechatInactiveIcon,
            children: [
                {
                    name: $t({ defaultMessage: '企微概览' }),
                    children: [
                        {
                            path: '/main-operations/wecom/overView',
                            name: $t({ defaultMessage: '企微概览' }),
                            code_key: [IPermissonCode.PERMISSION_WECOM_OVERVIEW],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '增长获客' }),
                    children: [
                        {
                            path: '/main-operations/wecom/channelCodeList',
                            name: $t({ defaultMessage: '渠道活码' }),
                            code_key: [IPermissonCode.PERMISSION_WECOM_CUSTOMER_CHANNEL_CODE],
                        },
                        {
                            path: '/main-operations/wecom/groupCodeList',
                            name: $t({ defaultMessage: '社群活码' }),
                            code_key: [IPermissonCode.PERMISSION_WECOM_GROUP_CHANNEL_CODE],
                        },
                        {
                            path: '/main-operations/wecom/lbsFriendList',
                            name: $t({ defaultMessage: 'LBS加好友' }),
                            code_key: [IPermissonCode.PERMISSION_WECOM_LBS_FRIEND],
                        },
                        {
                            path: '/main-operations/wecom/lbsGroupList',
                            name: $t({ defaultMessage: 'LBS引流入群' }),
                            code_key: [IPermissonCode.PERMISSION_WECOM_LBS_GROUP],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '用户触达' }),
                    children: [
                        {
                            path: '/main-operations/wecom/customerGroupSends',
                            name: $t({ defaultMessage: '客户群发' }),
                            code_key: [IPermissonCode.PERMISSION_WECOM_CUSTOMER_GROUP_SEND],
                        },
                        {
                            path: '/main-operations/wecom/massGroupSends',
                            name: $t({ defaultMessage: '社群群发' }),
                            code_key: [IPermissonCode.PERMISSION_WECOM_GROUP_GROUP_SEND],
                        },
                        {
                            path: '/main-operations/wecom/groupSendAssistant',
                            name: $t({ defaultMessage: '群发助手' }),
                            code_key: [IPermissonCode.PERMISSION_WECOM_GROUP_SEND_ASSISANT],
                        },
                        {
                            path: '/main-operations/wecom/materialLibrary',
                            name: $t({ defaultMessage: '素材库' }),
                            code_key: [IPermissonCode.PERMISSION_WECOM_MATERIAL],
                            customRule: () => [IPermissonCodeKey.SAVE, IPermissonCodeKey.EDIT, IPermissonCodeKey.DELETE].some((i) => hasOpteratePermission(IPermissonCode.PERMISSION_WECOM_GROUP_GREET, i)),
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '客户管理' }),
                    children: [
                        {
                            path: '/main-operations/wecom/customerList',
                            name: $t({ defaultMessage: '客户列表' }),
                            code_key: [IPermissonCode.PERMISSION_WECOM_CUSTOMER],
                        },
                        {
                            path: '/main-operations/wecom/customerTag',
                            name: $t({ defaultMessage: '标签管理' }),
                            code_key: [IPermissonCode.PERMISSION_WECOM_CUSTOMER_TAG],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '社群运营' }),
                    children: [
                        {
                            path: '/main-operations/wecom/customerGroupList',
                            name: $t({ defaultMessage: '客户群' }),
                            code_key: [IPermissonCode.PERMISSION_WECOM_CUSTOMER_GROUP],
                        },
                        {
                            path: '/main-operations/wecom/groupOwnerList',
                            name: $t({ defaultMessage: '群主管理' }),
                            code_key: [IPermissonCode.PERMISSION_WECOM_GROUP_OWNER],
                        },
                        {
                            path: '/main-operations/wecom/groupTag',
                            name: $t({ defaultMessage: '社群标签' }),
                            code_key: [IPermissonCode.PERMISSION_WECOM_GROUP_TAG],
                        },
                        {
                            path: '/main-operations/wecom/groupwelcommessage',
                            name: $t({ defaultMessage: '群欢迎语' }),
                            code_key: [IPermissonCode.PERMISSION_WECOM_GROUP_GREET],
                            customRule: () => [IPermissonCodeKey.SAVE, IPermissonCodeKey.EDIT].some((i) => hasOpteratePermission(IPermissonCode.PERMISSION_WECOM_GROUP_GREET, i)),
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '企业管理' }),
                    children: [
                        {
                            path: '/main-operations/wecom/staffList',
                            name: $t({ defaultMessage: '员工管理' }),
                            code_key: [IPermissonCode.PERMISSION_WECOM_STAFF_MGR],
                        },
                        {
                            path: '/main-operations/wecom/roleManage',
                            name: $t({ defaultMessage: '企微角色管理' }),
                            code_key: [IPermissonCode.PERMISSION_WECOM_ROLE_MGR],
                            customRule: () => [IPermissonCodeKey.SAVE, IPermissonCodeKey.EDIT, IPermissonCodeKey.DELETE].some((i) => hasOpteratePermission(IPermissonCode.PERMISSION_WECOM_ROLE_MGR, i)),
                        },
                        {
                            path: '/main-operations/wecom/application',
                            name: $t({ defaultMessage: '应用' }),
                            code_key: [IPermissonCode.PERMISSION_WECOM_APP],
                            customRule: () => [IPermissonCodeKey.SAVE, IPermissonCodeKey.BUY_AND_RENEW].some((i) => hasOpteratePermission(IPermissonCode.PERMISSION_WECOM_APP, i)),
                        },
                        {
                            path: '/main-operations/wecom/accountManage',
                            name: $t({ defaultMessage: '员工接口管理' }),
                            code_key: [IPermissonCode.PERMISSION_WECOM_STAFF_LICENSE_MGR],
                            customRule: () => [
                                IPermissonCodeKey.STAFF_INFO,
                                IPermissonCodeKey.ACCOUNT_INFO,
                                IPermissonCodeKey.APPOINT_STAFF,
                            ].some((i) => hasOpteratePermission(IPermissonCode.PERMISSION_WECOM_STAFF_LICENSE_MGR, i)),
                        },
                    ],
                },
            ],
        },
        {
            name: $t({ defaultMessage: '团购管理' }),
            menuType: 'marketingCenter-groupManagement',
            icon: GroupManagementIcon,
            inactiveIcon: GroupManagementInActiveIcon,
            children: [
                {
                    name: $t({ defaultMessage: '团购管理' }),
                    children: [
                        {
                            path: '/main-operations/coupons/meituanCoupon',
                            name: $t({ defaultMessage: '美团团购券' }),
                            code_key: [IPermissonCode.PERMISSION_PROMOTION_VIRTUAL_TICKET_MT],
                        },
                        {
                            path: '/main-operations/coupons/douyinCoupon',
                            name: $t({ defaultMessage: '抖音团购券' }),
                            code_key: [IPermissonCode.PERMISSION_PROMOTION_VIRTUAL_TICKET_DY],
                        },
                        {
                            path: '/main-operations/coupons/koubeiCoupon',
                            name: $t({ defaultMessage: '口碑团购券' }),
                            code_key: [IPermissonCode.PERMISSION_PROMOTION_VIRTUAL_TICKET_KOU_BEI],
                        },
                        {
                            path: '/main-operations/coupons/kuaishouCoupon',
                            name: $t({ defaultMessage: '快手团购券' }),
                            code_key: [IPermissonCode.PERMISSION_PROMOTION_VIRTUAL_TICKET_KS],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '支付宝' }),
                    children: [
                        {
                            path: '/main-operations/coupons/alipayStore',
                            name: $t({ defaultMessage: '支付宝门店' }),
                            code_key: [IPermissonCode.PERMISSION_PROMOTION_ALIPAY_LOCATION],
                            // 支付宝门店菜单仅总部、区域视角可见，门店视角在神券页内完成绑定。
                            customRule: () => ['HEADQUARTERS', 'ZONE'].includes(getScopeType()) &&
                                hasOpteratePermission(IPermissonCode.PERMISSION_PROMOTION_ALIPAY_LOCATION, IPermissonCodeKey.QUERY),
                        },
                        {
                            path: '/main-operations/coupons/alipayCoupon',
                            name: $t({ defaultMessage: '支付宝神券' }),
                            code_key: [IPermissonCode.PERMISSION_PROMOTION_VIRTUAL_TICKET_ALIPAY],
                        },
                    ],
                },
            ],
        },
        {
            name: $t({ defaultMessage: '渠道流量' }),
            menuType: 'marketingCenter-channelFlow',
            icon: GroupManagementIcon,
            inactiveIcon: GroupManagementInActiveIcon,
            children: [
                {
                    name: $t({ defaultMessage: '乐檬零售' }),
                    children: [
                        {
                            path: '/main-operations/channelFlow/earth/memberPass',
                            name: $t({ defaultMessage: '零售会员通' }),
                            code_key: [IPermissonCode.PERMISSION_RETAIL_MEMBER_PASS],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '抖音' }),
                    children: [
                        {
                            path: '/main-operations/channelFlow/douyin/memberPass',
                            name: $t({ defaultMessage: '抖音会员通' }),
                            code_key: [IPermissonCode.PERMISSION_DY_MEMBER_PASS],
                        },
                    ],
                },
            ],
        },
        {
            name: $t({ defaultMessage: '共享互斥规则' }),
            menuType: 'marketingCenter-sharedMutexesRules',
            icon: NavSharedMutexesRulesActiveIcon,
            inactiveIcon: NavSharedMutexesRulesInactiveIcon,
            children: [
                {
                    path: '/main-operations/sharedMutex',
                    name: $t({ defaultMessage: '共享互斥' }),
                    children: [
                        {
                            path: '/main-operations/sharedMutex',
                            name: $t({ defaultMessage: '共享互斥' }),
                            code_key: [IPermissonCode.PERMISSION_PROMOTION_EXCLUSIVE_SHARE],
                        },
                    ],
                },
            ],
        },
        {
            name: $t({ defaultMessage: '素材中心' }),
            menuType: 'marketingCenter-materialCenter',
            icon: MaterialCenterIcon,
            inactiveIcon: MaterialCenterInActiveIcon,
            children: [
                {
                    path: '/main-operations/fileSettings',
                    name: $t({ defaultMessage: '素材管理' }),
                    children: [
                        {
                            path: '/main-operations/fileSettings',
                            name: $t({ defaultMessage: '素材中心' }),
                            code_key: [IPermissonCode.PERMISSION_SETTING_FILES],
                        },
                    ],
                },
            ],
        },
    ];
    return getPermissionShowNavItems(navTree);
}
// 供应链
function navSupplyChain() {
    const navTree = [
        {
            name: $t({ defaultMessage: '配送管理' }),
            menuType: 'supplyChain-deliveryManagement',
            icon: NavShippingActiveIcon,
            inactiveIcon: NavShippingInactiveIcon,
            isEarthModule: false,
            children: [
                {
                    name: $t({ defaultMessage: '业务操作' }),
                    children: [
                        {
                            path: '/orders/distributionCenter',
                            name: $t({ defaultMessage: '配送中心' }),
                            customRule: () => {
                                const locationType = getLocationTypes();
                                if (!hasOpteratePermission(IPermissonCode.PERMISSION_CHAIN_REQUEST_ORDER_STATISTICS, IPermissonCodeKey.QUERY))
                                    return false;
                                /** 总部、配送中心可见 */
                                if (locationType.includes('HEAD_QUARTERS') ||
                                    locationType.includes('DISTRIBUTION_CENTER')) {
                                    return true;
                                }
                                return false;
                            },
                        },
                        {
                            path: '/orders/requestOrder',
                            name: $t({ defaultMessage: '要货单' }),
                            code_key: [IPermissonCode.PERMISSION_CHAIN_REQUEST_ORDER],
                        },
                        {
                            path: '/orders/transferOutOrder',
                            name: $t({ defaultMessage: '调出单' }),
                            code_key: [IPermissonCode.PERMISSION_CHAIN_TRANSFER_OUT_ORDER],
                        },
                        {
                            path: '/orders/transferInOrder',
                            name: $t({ defaultMessage: '调入单' }),
                            code_key: [IPermissonCode.PERMISSION_CHAIN_TRANSFER_IN_ORDER],
                        },
                        {
                            path: '/orders/innerApplyOrder',
                            name: $t({ defaultMessage: '内部申请单' }),
                            code_key: [IPermissonCode.PERMISSION_CHAIN_INNER_APPLY_ORDER],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '智能补货' }),
                    children: [
                        {
                            path: '/orders/replenishmentGuide',
                            name: $t({ defaultMessage: '补货向导' }),
                            code_key: [IPermissonCode.PERMISSION_DISTRIBUTION_MANAGEMENT_REQUEST_GUIDE],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '基础设置' }),
                    children: [
                        {
                            path: '/orders/shipNet',
                            name: $t({ defaultMessage: '配送网络' }),
                            customRule: () => {
                                const locationType = getLocationTypes();
                                if (!hasOpteratePermission(IPermissonCode.PERMISSION_CHAIN_SHIP_NET, IPermissonCodeKey.QUERY))
                                    return false;
                                /** 总部配送中心可见 */
                                if (locationType.includes('HEAD_QUARTERS') ||
                                    locationType.includes('DISTRIBUTION_CENTER')) {
                                    return true;
                                }
                                return false;
                            },
                        },
                        {
                            path: '/orders/productLocationPrice',
                            name: $t({ defaultMessage: '物品配送价' }),
                            code_key: [IPermissonCode.PERMISSION_CHAIN_PRODUCT_LOCATION_PRICE],
                            customRule: () => {
                                const locationType = getLocationTypes();
                                if (!hasOpteratePermission(IPermissonCode.PERMISSION_CHAIN_PRODUCT_LOCATION_PRICE, IPermissonCodeKey.QUERY))
                                    return false;
                                /** 总部、配送中心可见 */
                                if (locationType.includes('HEAD_QUARTERS') ||
                                    locationType.includes('DISTRIBUTION_CENTER')) {
                                    return true;
                                }
                                return false;
                            },
                        },
                        {
                            path: '/orders/productLocationProp',
                            name: $t({ defaultMessage: '物品配送属性' }),
                            code_key: [IPermissonCode.PERMISSION_CHAIN_PRODUCT_LOCATION_PROP],
                        },
                        // {
                        //   path: '/orders/locationChainPrice',
                        //   name: window.t('location_chain_price'),
                        //   code_key: [IPermissonCode.PERMISSION_CHAIN_PRODUCT_LOCATION],
                        // },
                        {
                            path: '/orders/orderingControl',
                            name: $t({ defaultMessage: '门店订货控制' }),
                            code_key: [IPermissonCode.PERMISSION_CHAIN_LOCATION_REQUEST_CONTROL],
                            customRule: () => {
                                const locationType = getLocationTypes();
                                if (!hasOpteratePermission(IPermissonCode.PERMISSION_CHAIN_LOCATION_REQUEST_CONTROL, IPermissonCodeKey.QUERY))
                                    return false;
                                /** 总部可见 */
                                if (locationType.includes('HEAD_QUARTERS')) {
                                    return true;
                                }
                                return false;
                            },
                        },
                        {
                            path: '/orders/requestTemplate',
                            name: $t({ defaultMessage: '要货模板' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_REQUEST_ORDER_TEMPLATE],
                        },
                        {
                            path: '/orders/innerReturnReason',
                            name: $t({ defaultMessage: '退货申请原因' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_INNER_RETURN_REASON],
                        },
                        {
                            path: '/orders/exceedDiscountOrder',
                            name: $t({ defaultMessage: '超量折扣' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_EXCEED_DISCOUNT_ORDER],
                            customRule: () => {
                                const locationType = getLocationTypes();
                                if (!hasOpteratePermission(IPermissonCode.PERMISSION_STOCK_EXCEED_DISCOUNT_ORDER, IPermissonCodeKey.QUERY))
                                    return false;
                                /** 总部、配送中心可见 */
                                if (locationType.includes('HEAD_QUARTERS') ||
                                    locationType.includes('DISTRIBUTION_CENTER')) {
                                    return true;
                                }
                                return false;
                            },
                        },
                    ],
                },
            ],
        },
        {
            name: $t({ defaultMessage: '配送管理' }),
            menuType: 'supplyChain-deliveryManagement',
            icon: NavShippingActiveIcon,
            inactiveIcon: NavShippingInactiveIcon,
            isEarthModule: true,
            children: [
                {
                    name: $t({ defaultMessage: '业务操作' }),
                    children: [
                        {
                            path: '/earthOrders/earthLocationReplenishment',
                            name: $t({ defaultMessage: '要货单' }),
                            isEarthModule: true,
                            code_key: [IPermissonCode.PERMISSION_CHAIN_REQUEST_ORDER],
                        },
                        {
                            path: '/earthOrders/earthTransferOutOrder',
                            name: $t({ defaultMessage: '调出单' }),
                            isEarthModule: true,
                            code_key: [IPermissonCode.PERMISSION_CHAIN_TRANSFER_OUT_ORDER],
                        },
                        {
                            path: '/earthOrders/earthTransferInOrder',
                            name: $t({ defaultMessage: '调入单' }),
                            isEarthModule: true,
                            code_key: [IPermissonCode.PERMISSION_CHAIN_TRANSFER_IN_ORDER],
                        },
                        {
                            path: '/earthOrders/earthInnerApplyOrder',
                            name: $t({ defaultMessage: '内部申请单' }),
                            isEarthModule: true,
                            code_key: [IPermissonCode.PERMISSION_CHAIN_INNER_APPLY_ORDER],
                        },
                    ],
                },
            ],
        },
        {
            name: $t({ defaultMessage: '采购管理' }),
            menuType: 'supplyChain-purchaseManagement',
            icon: NavPurchaseActiveIcon,
            inactiveIcon: NavPurchaseInactiveIcon,
            children: [
                {
                    name: $t({ defaultMessage: '业务操作' }),
                    children: [
                        {
                            path: '/purchasing/purchaseOrder',
                            name: $t({ defaultMessage: '采购订单' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_PURCHASE_ORDER],
                        },
                        {
                            path: '/purchasing/directOrder',
                            name: $t({ defaultMessage: '供应商直发单' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_SUPPLIER_DIRECT_ORDER],
                            customRule: () => {
                                const locationType = getLocationTypes();
                                if (!hasOpteratePermission(IPermissonCode.PERMISSION_STOCK_SUPPLIER_DIRECT_ORDER, IPermissonCodeKey.QUERY))
                                    return false;
                                /** 总部可见 */
                                if (locationType.includes('HEAD_QUARTERS') ||
                                    locationType.includes('DISTRIBUTION_CENTER')) {
                                    return true;
                                }
                                return false;
                            },
                        },
                        {
                            path: '/purchasing/receiveOrder',
                            name: $t({ defaultMessage: '采购收货' }),
                            code_key: [IPermissonCode.PERMISSION_CHAIN_RECEIVE_ORDER],
                        },
                        {
                            path: '/purchasing/returnOrder',
                            name: $t({ defaultMessage: '退货单' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_RETURN_ORDER],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '智能采购' }),
                    children: [
                        {
                            path: '/purchasing/purchaseGuide',
                            name: $t({ defaultMessage: '采购向导' }),
                            code_key: [IPermissonCode.PERMISSION_PURCHASING_MANAGEMENT_PURCHASE_GUIDE],
                            customRule: () => {
                                const locationType = getLocationTypes();
                                if (!hasOpteratePermission(IPermissonCode.PERMISSION_PURCHASING_MANAGEMENT_PURCHASE_GUIDE, IPermissonCodeKey.QUERY) ||
                                    (locationType.includes('ZONE') && !locationType.includes('HEAD_QUARTERS'))) {
                                    return false;
                                }
                                return true;
                            },
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '基础设置' }),
                    children: [
                        {
                            path: '/purchasing/supply',
                            name: $t({ defaultMessage: '物品供货关系' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_PURCHASE_TMPL],
                        },
                        {
                            path: '/purchasing/productPurchaseProp',
                            name: $t({ defaultMessage: '物品采购属性' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_PRODUCT_LOCATION_PURCHASE],
                        },
                        {
                            path: '/purchasing/purchaseTemplate',
                            name: $t({ defaultMessage: '采购模板' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_PURCHASE_TMPL],
                        },
                    ],
                },
            ],
        },
        {
            name: $t({ defaultMessage: '生产管理' }),
            menuType: 'supplyChain-produceManagement',
            icon: NavProduceActiveIcon,
            inactiveIcon: NavProduceInactiveIcon,
            children: [
                {
                    name: $t({ defaultMessage: '业务操作' }),
                    children: [
                        // {
                        //   path: '/process/produceFastOrder',
                        //   name: window.t('produce_fast_order'),
                        //   code_key: [IPermissonCode.PERMISSION_STOCK_PRODUCE_FAST_ORDER],
                        // },
                        {
                            path: '/process/productionPlan',
                            name: $t({ defaultMessage: '生产计划单' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_PRODUCE_PLAN_ORDER],
                        },
                        {
                            path: '/process/productionBoard',
                            name: $t({ defaultMessage: '生产看板' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_PRODUCE_BOARD],
                        },
                        {
                            path: '/process/productionProcess',
                            name: $t({ defaultMessage: '生产领料单' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_PRODUCE_MATERIAL_ORDER],
                        },
                        {
                            path: '/process/productionStorage',
                            name: $t({ defaultMessage: '生产入库单' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_PRODUCE_STORAGE_ORDER],
                        },
                        {
                            path: '/process/locationProcessOrder',
                            name: $t({ defaultMessage: '门店加工单' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_LOCATION_PRODUCE],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '生产管理' }),
                    children: [
                        {
                            path: '/process/productionOrder',
                            name: $t({ defaultMessage: '生产任务单' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_PRODUCE_TASK],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '数据分析' }),
                    children: [
                        {
                            path: '/process/taskDashBoard',
                            name: $t({ defaultMessage: '加工间任务看板' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_PRODUCE_TASK_BOARD],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '基础设置' }),
                    children: [
                        {
                            path: '/process/processProducts',
                            name: $t({ defaultMessage: '加工品管理' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_PRODUCT_PRODUCE_MGR],
                        },
                        {
                            path: '/process/goodsFormula',
                            name: $t({ defaultMessage: '配方管理' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_PRODUCT_FORMULA],
                        },
                        {
                            path: '/process/template',
                            name: $t({ defaultMessage: '生产模板' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_PRODUCE_TMPL],
                        },
                        {
                            path: '/process/operator',
                            name: $t({ defaultMessage: '作业人员' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_PRODUCE_OPERATOR],
                            customRule: () => {
                                const locationType = getLocationTypes();
                                if (!hasOpteratePermission(IPermissonCode.PERMISSION_STOCK_PRODUCE_OPERATOR, IPermissonCodeKey.QUERY))
                                    return false;
                                /** 总部、配送中心可见 */
                                if (locationType.includes('HEAD_QUARTERS') ||
                                    locationType.includes('DISTRIBUTION_CENTER')) {
                                    return true;
                                }
                                return false;
                            },
                        },
                        {
                            path: '/process/labelPrinting',
                            name: $t({ defaultMessage: '标签打印' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_PRODUCE_TAG_PRINT],
                        },
                    ],
                },
            ],
        },
        {
            name: $t({ defaultMessage: '库存管理' }),
            menuType: 'supplyChain-systemSettings',
            icon: NavStockActiveIcon,
            inactiveIcon: NavStockInactiveIcon,
            children: [
                {
                    name: $t({ defaultMessage: '业务操作' }),
                    children: [
                        {
                            path: '/check/checkOrder',
                            name: $t({ defaultMessage: '库存盘点' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_CHECK_ORDER],
                        },
                        {
                            path: '/check/adjustmentOrder',
                            name: $t({ defaultMessage: '库存调整' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_ADJUSTMENT_ORDER],
                        },
                        {
                            path: '/check/stockTransfer',
                            name: $t({ defaultMessage: '库存转仓' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_TRANSFER_STOREHOUSE],
                        },
                        {
                            path: '/check/inventoryLossOrder',
                            name: $t({ defaultMessage: '商品报损' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_PRODUCT_REPORT_LOSS],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '基础设置' }),
                    children: [
                        {
                            path: '/check/adjustmentReason',
                            name: $t({ defaultMessage: '库存调整原因' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_ADJUSTMENT_REASON],
                        },
                    ],
                },
            ],
        },
        {
            name: $t({ defaultMessage: '销售管理' }),
            menuType: 'supplyChain-saleManagement',
            icon: WholesaleManagerActiveIcon,
            inactiveIcon: WholesaleManagerInactiveIcon,
            children: [
                {
                    name: $t({ defaultMessage: '业务操作' }),
                    children: [
                        {
                            path: '/wholesale-manager/sale-order',
                            name: $t({ defaultMessage: '销售发货单' }),
                            customRule: () => {
                                const locationType = getLocationTypes();
                                if (!hasOpteratePermission(IPermissonCode.PERMISSION_STOCK_WHOLESALE_DELIVERY_ORDER, IPermissonCodeKey.QUERY))
                                    return false;
                                /** 总部、配送中心可见 */
                                if (locationType.includes('HEAD_QUARTERS') ||
                                    locationType.includes('DISTRIBUTION_CENTER')) {
                                    return true;
                                }
                                return false;
                            },
                        },
                        {
                            path: '/wholesale-manager/return-order',
                            name: $t({ defaultMessage: '销售退货单' }),
                            customRule: () => {
                                const locationType = getLocationTypes();
                                if (!hasOpteratePermission(IPermissonCode.PERMISSION_STOCK_WHOLESALE_RETURN_ORDER, IPermissonCodeKey.QUERY))
                                    return false;
                                /** 总部、配送中心可见 */
                                if (locationType.includes('HEAD_QUARTERS') ||
                                    locationType.includes('DISTRIBUTION_CENTER')) {
                                    return true;
                                }
                                return false;
                            },
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '基础设置' }),
                    children: [
                        {
                            path: '/wholesale-manager/quote-order',
                            name: $t({ defaultMessage: '销售报价单' }),
                            customRule: () => {
                                const locationType = getLocationTypes();
                                if (!hasOpteratePermission(IPermissonCode.PERMISSION_STOCK_WHOLESALE_QUOTE_ORDER, IPermissonCodeKey.QUERY))
                                    return false;
                                if (locationType.includes('HEAD_QUARTERS') ||
                                    locationType.includes('DISTRIBUTION_CENTER')) {
                                    return true;
                                }
                                return false;
                            },
                        },
                    ],
                },
            ],
        },
        {
            name: $t({ defaultMessage: '财务管理' }),
            menuType: 'supplyChain-financialManagement',
            icon: NavAccountManagementActiveIcon,
            inactiveIcon: NavAccountManagementInactiveIcon,
            children: [
                {
                    name: $t({ defaultMessage: '成本' }),
                    children: [
                        {
                            path: '/check/stockCostAdjustment',
                            name: $t({ defaultMessage: '库存成本调整' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_INVENTORY_COST_ADJUST],
                        },
                        {
                            path: '/check/stockCostRecalculate',
                            name: $t({ defaultMessage: '库存成本重算' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_INVENTORY_COST_RECAL],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '财务结转' }),
                    children: [
                        {
                            path: '/accounting/periods',
                            name: $t({ defaultMessage: '会计期' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_FINANCIAL_ACCOUNT_PERIOD],
                        },
                        {
                            path: '/accounting/settlementManager',
                            name: $t({ defaultMessage: '结转管理' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_FINANCIAL_ACCOUNT_SETTLEMENT],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '门店结算' }),
                    children: [
                        {
                            path: '/finance/locationPrepayment',
                            name: $t({ defaultMessage: '门店预收' }),
                            customRule: () => {
                                const locationType = getLocationTypes();
                                if (!hasOpteratePermission(IPermissonCode.PERMISSION_STOCK_LOCATION_PRE_RECEIVE, IPermissonCodeKey.QUERY))
                                    return false;
                                /** 总部、配送中心可见 */
                                if (locationType.includes('HEAD_QUARTERS') ||
                                    locationType.includes('DISTRIBUTION_CENTER')) {
                                    return true;
                                }
                                return false;
                            },
                        },
                        {
                            path: '/finance/locationExpense',
                            name: $t({ defaultMessage: '门店费用' }),
                            customRule: () => {
                                const locationType = getLocationTypes();
                                if (!hasOpteratePermission(IPermissonCode.PERMISSION_STOCK_LOCATION_FEE, IPermissonCodeKey.QUERY))
                                    return false;
                                /** 总部、配送中心可见 */
                                if (locationType.includes('HEAD_QUARTERS') ||
                                    locationType.includes('DISTRIBUTION_CENTER')) {
                                    return true;
                                }
                                return false;
                            },
                        },
                        {
                            path: '/finance/distributionSettlement',
                            name: $t({ defaultMessage: '配送结算' }),
                            customRule: () => {
                                const locationType = getLocationTypes();
                                if (!hasOpteratePermission(IPermissonCode.PERMISSION_STOCK_FINANCIAL_DELIVER_SETTLEMENT, IPermissonCodeKey.QUERY))
                                    return false;
                                /** 总部、配送中心可见 */
                                if (locationType.includes('HEAD_QUARTERS') ||
                                    locationType.includes('DISTRIBUTION_CENTER')) {
                                    return true;
                                }
                                return false;
                            },
                        },
                        {
                            path: '/finance/locationDealings',
                            name: $t({ defaultMessage: '门店往来账款' }),
                            customRule: () => {
                                const locationType = getLocationTypes();
                                if (!hasOpteratePermission(IPermissonCode.PERMISSION_STOCK_LOCATION_ACCOUNT_AMOUNT, IPermissonCodeKey.QUERY))
                                    return false;
                                /** 总部、配送中心可见 */
                                if (locationType.includes('HEAD_QUARTERS') ||
                                    locationType.includes('DISTRIBUTION_CENTER')) {
                                    return true;
                                }
                                return false;
                            },
                        },
                        {
                            path: '/finance/locationPrepaymentSum',
                            name: $t({ defaultMessage: '门店预收汇总' }),
                            customRule: () => {
                                const locationType = getLocationTypes();
                                if (!hasOpteratePermission(IPermissonCode.PERMISSION_STOCK_LOCATION_PRE_RECEIVE_SUMMARY, IPermissonCodeKey.QUERY))
                                    return false;
                                /** 总部、配送中心可见 */
                                if (locationType.includes('HEAD_QUARTERS') ||
                                    locationType.includes('DISTRIBUTION_CENTER')) {
                                    return true;
                                }
                                return false;
                            },
                        },
                        {
                            name: $t({ defaultMessage: '门店费用汇总' }),
                            path: '/finance/locationFee',
                            customRule: () => {
                                const locationType = getLocationTypes();
                                if (!hasOpteratePermission(IPermissonCode.PERMISSION_STOCK_LOCATION_FEE_SUMMARY, IPermissonCodeKey.QUERY))
                                    return false;
                                /** 总部、配送中心可见 */
                                if (locationType.includes('HEAD_QUARTERS') ||
                                    locationType.includes('DISTRIBUTION_CENTER')) {
                                    return true;
                                }
                                return false;
                            },
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '供应商结算' }),
                    children: [
                        {
                            path: '/finance/supplierPrepayment',
                            name: $t({ defaultMessage: '供应商预付' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_SUPPLIER_PRE_PAY],
                        },
                        {
                            path: '/finance/supplierExpense',
                            name: $t({ defaultMessage: '供应商费用' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_SUPPLIER_FEE],
                        },
                        {
                            path: '/finance/supplierSettlement',
                            name: $t({ defaultMessage: '供应商结算' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_SUPPLIER_SETTLEMENT_SUB],
                        },
                        {
                            path: '/finance/supplierDealings',
                            name: $t({ defaultMessage: '供应商往来账款' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_SUPPLIER_ACCOUNT_AMOUNT],
                        },
                        {
                            path: '/finance/supplierPrepaymentSum',
                            name: $t({ defaultMessage: '供应商预付汇总' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_SUPPLIER_PRE_PAY_SUMMARY],
                        },
                        {
                            path: '/finance/supplierExpenseSummary',
                            name: $t({ defaultMessage: '供应商费用汇总' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_SUPPLIER_FEE_SUMMARY],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '基础设置' }),
                    children: [
                        {
                            path: '/finance/costProject',
                            name: $t({ defaultMessage: '费用项目' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_FINANCE_FEE_ITEM],
                        },
                    ],
                },
            ],
        },
        {
            name: $t({ defaultMessage: '报表管理' }),
            menuType: 'supplyChain-reportManagement',
            icon: NavWeappActiveIcon,
            inactiveIcon: NavWeappInactiveIcon,
            children: [
                {
                    name: $t({ defaultMessage: '配送报表' }),
                    children: [
                        {
                            path: '/report/orders/transiGoods',
                            name: $t({ defaultMessage: '物品在途' }),
                            code_key: [IPermissonCode.PERMISSION_CHAIN_PRODUCT_IN_TRANSIT],
                        },
                        {
                            path: '/report/orders/requestOrderStatusList',
                            name: $t({ defaultMessage: '要货单提交状态表' }),
                            code_key: [IPermissonCode.PERMISSION_CHAIN_REQUEST_ORDER_COMMIT_STATE],
                            customRule: () => {
                                const locationType = getLocationTypes();
                                if (!hasOpteratePermission(IPermissonCode.PERMISSION_CHAIN_REQUEST_ORDER_COMMIT_STATE, IPermissonCodeKey.QUERY))
                                    return false;
                                /** 总部、配送中心可见 */
                                if (locationType.includes('HEAD_QUARTERS') ||
                                    locationType.includes('DISTRIBUTION_CENTER')) {
                                    return true;
                                }
                                return false;
                            },
                        },
                        {
                            path: '/report/orders/transferReportsTrack',
                            name: $t({ defaultMessage: '配送跟踪' }),
                            code_key: [IPermissonCode.PERMISSION_TRANSFER_TRACK],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '采购报表' }),
                    children: [
                        {
                            path: '/report/purchasing/summaryDetails',
                            name: $t({ defaultMessage: '采购汇总明细' }),
                            code_key: [IPermissonCode.PERMISSION_PURCHASE_SUMMARY_DETAIL],
                        },
                        {
                            path: '/report/purchasing/monitor',
                            name: $t({ defaultMessage: '采购订单监控' }),
                            code_key: [IPermissonCode.PERMISSION_PURCHASE_ORDER_MONITOR],
                        },
                        {
                            path: '/report/purchasing/directOrderDetails',
                            name: $t({ defaultMessage: '供应商直发明细表' }),
                            code_key: [IPermissonCode.PERMISSION_PURCHASE_ORDER_SUPPLIER_SHIPMENT_DETAIL],
                            customRule: () => {
                                const locationType = getLocationTypes();
                                if (!hasOpteratePermission(IPermissonCode.PERMISSION_PURCHASE_ORDER_SUPPLIER_SHIPMENT_DETAIL, IPermissonCodeKey.QUERY))
                                    return false;
                                /** 总部可见 */
                                if (locationType.includes('HEAD_QUARTERS') ||
                                    locationType.includes('DISTRIBUTION_CENTER')) {
                                    return true;
                                }
                                return false;
                            },
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '生产报表' }),
                    children: [
                        // {
                        //   path: '/report/process/finishedRateReport',
                        //   name: window.t('finished_rate_report'),
                        //   code_key: [IPermissonCode.PERMISSION_STOCK_PRODUCE_PROCESS_RATE],
                        // },
                        {
                            path: '/report/process/materialAnalysisReport',
                            name: $t({ defaultMessage: '加工品原料耗用分析表' }),
                            code_key: [IPermissonCode.PERMISSION_PRODUCE_MATERIAL_USE_ANALYZE],
                        },
                        {
                            path: '/report/process/expectedYieldReport',
                            name: $t({ defaultMessage: '应产率报表' }),
                            code_key: [IPermissonCode.PERMISSION_SHOULD_PRODUCE_RATE_ANALYZE],
                        },
                        {
                            path: '/report/process/processSummaryReport',
                            name: $t({ defaultMessage: '生产汇总表' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_PRODUCE_PROCESS_RATE],
                        },
                        {
                            path: '/report/process/processStaffReport',
                            name: $t({ defaultMessage: '生产人员生产汇总表' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_PRODUCE_OPERATOR_PRODUCE_SUMMARY],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '库存报表' }),
                    children: [
                        {
                            path: '/report/check/goodsInventory',
                            name: $t({ defaultMessage: '物品库存' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_PRODUCT_STOCK],
                        },
                        {
                            path: '/report/check/entryExitDetails',
                            name: $t({ defaultMessage: '库存进出明细' }),
                            code_key: [IPermissonCode.PERMISSION_FILES_PRODUCT_IN_OUT_DETAIL],
                        },
                        {
                            path: '/report/check/entryStockSummary',
                            name: $t({ defaultMessage: '入库汇总' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_INVENTORY_IN_SUMMARY],
                        },
                        {
                            path: '/report/check/exitStockSummary',
                            name: $t({ defaultMessage: '出库汇总' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_INVENTORY_OUT_SUMMARY],
                        },
                        {
                            path: '/report/check/entryExitSummary',
                            name: $t({ defaultMessage: '库存进出汇总' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_INVENTORY_IN_OUT_SUMMARY],
                        },
                        {
                            path: '/report/check/statisticsOfChange',
                            name: $t({ defaultMessage: '进销存变动统计' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_STATISTICS_OF_CHANGE],
                        },
                        {
                            path: '/report/check/profitLossReport',
                            name: $t({ defaultMessage: '损益统计表' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_GAIN_LOSS_REPORT],
                        },
                        {
                            path: '/report/check/stockInterrupt',
                            name: $t({ defaultMessage: '断货分析' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_INTERRUPT_INVENTORY_REPORT],
                        },
                        {
                            path: '/report/check/inventoryWarn',
                            name: $t({ defaultMessage: '库存预警' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_WARN_INVENTORY_REPORT],
                        },
                        {
                            path: '/report/check/inventoryTurnoverRate',
                            name: $t({ defaultMessage: '库存周转率' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_INVENTORY_TURNOVER_RATE],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '财务报表' }),
                    children: [
                        {
                            path: '/report/finance/distributionGrossProfit',
                            name: $t({ defaultMessage: '配送毛利分析表' }),
                            code_key: [IPermissonCode.PERMISSION_CHAIN_DISTRIBUTION_GROSS_PROFIT],
                        },
                        {
                            path: '/report/finance/locationGrossProfit',
                            name: $t({ defaultMessage: '门店毛利分析表' }),
                            code_key: [IPermissonCode.PERMISSION_LOCATION_DISTRIBUTION_GROSS_PROFIT],
                        },
                        {
                            path: '/report/finance/productGrossProfit',
                            name: $t({ defaultMessage: '商品毛利分析表' }),
                            code_key: [IPermissonCode.PERMISSION_PRODUCT_DISTRIBUTION_GROSS_PROFIT],
                        },
                        {
                            path: '/report/finance/locationGrossProfitDiff',
                            name: $t({ defaultMessage: '门店商品毛利对比表' }),
                            code_key: [IPermissonCode.PERMISSION_LOCATION_PRODUCT_GROSS_CONTRAST],
                        },
                        {
                            path: '/report/finance/productCostDetail',
                            name: $t({ defaultMessage: '商品成本明细表' }),
                            code_key: [IPermissonCode.PERMISSION_ITEM_COST_DETAIL],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '客户销售报表' }),
                    children: [
                        {
                            path: '/report/customerSales/itemsSalesQuery',
                            name: $t({ defaultMessage: '客户物品销售查询' }),
                            customRule: () => {
                                const locationType = getLocationTypes();
                                if (!hasOpteratePermission(IPermissonCode.PERMISSION_WHOLESALE_REPORT, IPermissonCodeKey.QUERY))
                                    return false;
                                /** 总部、配送中心可见 */
                                if (locationType.includes('HEAD_QUARTERS') ||
                                    locationType.includes('DISTRIBUTION_CENTER')) {
                                    return true;
                                }
                                return false;
                            },
                        },
                    ],
                },
            ],
        },
        {
            name: $t({ defaultMessage: '档案管理' }),
            menuType: 'supplyChain-achiveManagement',
            icon: NavArchiveActiveIcon,
            inactiveIcon: NavArchiveInactiveIcon,
            children: [
                {
                    name: $t({ defaultMessage: '物品' }),
                    children: [
                        {
                            path: '/document/product',
                            name: $t({ defaultMessage: '物品档案' }),
                            code_key: [IPermissonCode.PERMISSION_FILES_RPODUCT_FILES],
                        },
                        {
                            path: '/document/virtualItem',
                            name: $t({ defaultMessage: '虚拟物品' }),
                            code_key: [IPermissonCode.PERMISSION_FILES_VIRTUAL_ITEM],
                        },
                        {
                            path: '/document/completeItem',
                            name: $t({ defaultMessage: '成套件管理' }),
                            code_key: [IPermissonCode.PERMISSION_FILES_PRODUCT_VARIANT_MATERIAL],
                        },
                        {
                            path: '/document/priceLogs',
                            name: $t({ defaultMessage: '物品改价记录' }),
                            code_key: [IPermissonCode.PERMISSION_FILES_ITEM_PRICE_LOG],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '供应商' }),
                    children: [
                        {
                            path: '/purchasing/supplier',
                            name: $t({ defaultMessage: '供应商档案' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_SUPPLIER],
                        },
                        {
                            path: '/purchasing/supplierUser',
                            name: $t({ defaultMessage: '供应商用户' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_SUPPLIER_USER],
                        },
                        {
                            path: '/purchasing/supplierUserRole',
                            name: $t({ defaultMessage: '供应商用户角色' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_SUPPLIER_ROLE],
                        },
                        {
                            path: '/purchasing/supplierWechatUnbind',
                            name: $t({ defaultMessage: '供应商微信解绑' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_SUPPLIER_WECHAT_UNBIND],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '销售客户' }),
                    children: [
                        {
                            path: '/wholesale-customer/customer',
                            name: $t({ defaultMessage: '客户档案' }),
                            customRule: () => {
                                const locationType = getLocationTypes();
                                const customPermission = hasOpteratePermission(IPermissonCode.PERMISSION_WHOLESALE_CUSTOMER_FILE, IPermissonCodeKey.QUERY);
                                const customerCategoryPermission = hasOpteratePermission(IPermissonCode.PERMISSION_WHOLESALE_CUSTOMER_CATEGORY, IPermissonCodeKey.QUERY);
                                if (locationType.includes('HEAD_QUARTERS')) {
                                    return customPermission || customerCategoryPermission;
                                }
                                return customPermission;
                            },
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '成本卡' }),
                    children: [
                        {
                            path: '/document/costCard',
                            name: $t({ defaultMessage: '成本卡管理' }),
                            code_key: [IPermissonCode.PERMISSION_FILES_COST_CARD],
                        },
                        {
                            path: '/document/itemReplace',
                            name: $t({ defaultMessage: '替代品管理' }),
                            customRule: () => {
                                const locationType = getLocationTypes();
                                const customPermission = hasOpteratePermission(IPermissonCode.PERMISSION_FILES_REPLACE_ITEM, IPermissonCodeKey.QUERY);
                                return locationType.includes('HEAD_QUARTERS') && customPermission;
                            },
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '仓库' }),
                    children: [
                        {
                            path: '/document/store',
                            name: $t({ defaultMessage: '仓库档案' }),
                            code_key: [IPermissonCode.PERMISSION_FILES_WAREHOUSE_FILES],
                        },
                        {
                            path: '/document/stockItemRule',
                            name: $t({ defaultMessage: '仓库物品规则' }),
                            code_key: [IPermissonCode.PERMISSION_WAREHOUSE_ITEM_RULES],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '基础设置' }),
                    children: [
                        {
                            path: '/document/productQueryTemplate',
                            name: $t({ defaultMessage: '查询模板' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_ITEM_QUERY_TMPL],
                            // customRule: () => {
                            //   const locationType = getLocationTypes()
                            //   if (!hasOpteratePermission(IPermissonCode.PERMISSION_STOCK_ITEM_QUERY_TMPL, IPermissonCodeKey.QUERY)) return false
                            //   /** 总部区域可见 */
                            //   if (locationType.includes('HEAD_QUARTERS')) {
                            //     return true
                            //   } else {
                            //     return false
                            //   }
                            // },
                        },
                        {
                            path: '/document/itemCornerLabel',
                            name: $t({ defaultMessage: '物品角标' }),
                            code_key: [IPermissonCode.PERMISSION_ITEM_CORNER_MARK],
                        },
                    ],
                },
            ],
        },
        {
            name: $t({ defaultMessage: '系统设置' }),
            menuType: 'supplyChain-stockManagement',
            icon: NavSettingActiveIcon,
            inactiveIcon: NavSettingInactiveIcon,
            children: [
                {
                    name: $t({ defaultMessage: '基础设置' }),
                    children: [
                        {
                            path: '/settings/base',
                            name: $t({ defaultMessage: '供应链参数' }),
                            code_key: [IPermissonCode.PARENT_PERMISSION_STOCK_SETTING],
                        },
                        {
                            path: '/settings/printTemplate',
                            name: $t({ defaultMessage: '打印模板设置' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_SETTING_PRINT_TMPL],
                        },
                        {
                            path: '/settings/appProductSetting',
                            name: $t({ defaultMessage: 'APP物品列表配置' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_APP_ITEM_LIST_SETTING],
                        },
                        {
                            path: '/settings/locationCopy',
                            name: $t({ defaultMessage: '供应链门店复制' }),
                            code_key: [IPermissonCode.PERMISSION_STOCK_LOCATION_SYNC],
                            customRule: () => {
                                const locationType = getLocationTypes();
                                return (hasOpteratePermission(IPermissonCode.PERMISSION_STOCK_LOCATION_SYNC, IPermissonCodeKey.QUERY) && locationType.includes('HEAD_QUARTERS'));
                            },
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '零售供应链' }),
                    children: [
                        {
                            path: '/settings/couplerSettings/productSync',
                            name: $t({ defaultMessage: '商品同步' }),
                            code_key: [IPermissonCode.PERMISSION_APP_EARTH_BRIDGE],
                            customRule: () => {
                                const locationType = getLocationTypes();
                                return (hasOpteratePermission(IPermissonCode.PERMISSION_APP_EARTH_BRIDGE, IPermissonCodeKey.QUERY) && locationType.includes('HEAD_QUARTERS'));
                            },
                        },
                        {
                            path: '/settings/couplerSettings/earthLocation',
                            name: $t({ defaultMessage: '门店列表' }),
                            code_key: [IPermissonCode.PERMISSION_APP_EARTH_BRIDGE],
                            customRule: () => {
                                const locationType = getLocationTypes();
                                return (hasOpteratePermission(IPermissonCode.PERMISSION_APP_EARTH_BRIDGE_LOCATION, IPermissonCodeKey.QUERY) && locationType.includes('HEAD_QUARTERS'));
                            },
                        },
                        {
                            path: '/settings/couplerSettings/transferOrder',
                            name: $t({ defaultMessage: '单据日志' }),
                            code_key: [IPermissonCode.PERMISSION_APP_EARTH_BRIDGE],
                            customRule: () => {
                                const locationType = getLocationTypes();
                                return (hasOpteratePermission(IPermissonCode.PERMISSION_APP_EARTH_BRIDGE_ORDER_LOG, IPermissonCodeKey.QUERY) && locationType.includes('HEAD_QUARTERS'));
                            },
                        },
                        {
                            path: '/settings/couplerSettings/authorization',
                            name: $t({ defaultMessage: '授权设置' }),
                            code_key: [IPermissonCode.PERMISSION_APP_EARTH_BRIDGE],
                            customRule: () => {
                                const locationType = getLocationTypes();
                                return (hasOpteratePermission(IPermissonCode.PERMISSION_APP_EARTH_BRIDGE_AUTH, IPermissonCodeKey.QUERY) && locationType.includes('HEAD_QUARTERS'));
                            },
                        },
                        {
                            path: '/settings/couplerSettings/retailSupplyChainSetting',
                            name: $t({ defaultMessage: '零售供应链设置' }),
                            code_key: [IPermissonCode.PERMISSION_APP_EARTH_BRIDGE],
                            customRule: () => {
                                const locationType = getLocationTypes();
                                return (hasOpteratePermission(IPermissonCode.PERMISSION_EARTH_CHAIN_SETTING, IPermissonCodeKey.QUERY) && locationType.includes('HEAD_QUARTERS'));
                            },
                        },
                    ],
                },
            ],
        },
        {
            name: $t({ defaultMessage: '金蝶对接' }),
            menuType: 'supplyChain-kingdee',
            icon: NavSettingActiveIcon,
            inactiveIcon: NavSettingInactiveIcon,
            children: [
                {
                    name: $t({ defaultMessage: '金蝶对接' }),
                    children: [
                        {
                            path: '/kingdee/branchBind',
                            name: $t({ defaultMessage: '门店绑定' }),
                            customRule: () => {
                                const locationType = getLocationTypes();
                                return locationType.includes('HEAD_QUARTERS') && isCurrentMerchantInKingdeeList();
                            },
                        },
                        {
                            path: '/kingdee/productSync',
                            name: $t({ defaultMessage: '商品同步' }),
                            customRule: () => {
                                return isCurrentMerchantInKingdeeList();
                            },
                        },
                    ],
                },
            ],
        },
    ];
    return getPermissionShowNavItems(navTree.filter((nav) => {
        if (nav.isEarthModule !== undefined) {
            return getEarthAuth(nav.isEarthModule);
        }
        return true;
    }));
}
// 报表中心
function navReportCenter() {
    const navTree = [
        {
            name: $t({ defaultMessage: '营业报表' }),
            menuType: 'reportCenter-business',
            icon: NavWeappActiveIcon,
            inactiveIcon: NavWeappInactiveIcon,
            children: [
                {
                    name: $t({ defaultMessage: '营业' }),
                    children: [
                        {
                            path: '/main-report/busincess-analysis-report',
                            name: $t({ defaultMessage: '营业概览' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_BUSINESS_OVERVIEW],
                        },
                        {
                            path: '/main-report/business-statistics-report',
                            name: $t({ defaultMessage: '营业统计' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_BUSINESS_STATISTICS],
                        },
                        {
                            path: '/main-report/business-beyond-statistic',
                            name: $t({ defaultMessage: '营业外统计' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_BUSINESS_BEYOND_STATISTICS],
                        },
                        {
                            path: '/main-report/business-detail-report',
                            name: $t({ defaultMessage: '营业明细' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_BUSINESS_DETAIL],
                        },
                        {
                            path: '/main-report/business-income-discount-report',
                            name: $t({ defaultMessage: '收入优惠统计' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_BUSINESS_INCOME_DISCOUNT],
                        },
                        {
                            path: '/main-report/business-payment-report',
                            name: $t({ defaultMessage: '营业收入构成' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_BUSINESS_PAYMENT],
                        },
                        {
                            path: '/main-report/business-discount-report',
                            name: $t({ defaultMessage: '营业优惠分析' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_BUSINESS_DISCOUNT],
                        },
                        {
                            path: '/main-report/business-refund-statistic',
                            name: $t({ defaultMessage: '营业退款统计' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_BUSINESS_REFUND_STATISTICS],
                        },
                        {
                            path: '/main-report/channel-business-statistics-report',
                            name: $t({ defaultMessage: '渠道营业统计' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_ORDER_BUSINESS_REPORT_ANALYSIS],
                        },
                        {
                            path: '/main-report/session-business-statistics-report',
                            name: $t({ defaultMessage: '餐/时段营业统计' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_SESSION_BUSINESS_STATISTICS],
                        },
                        {
                            path: '/main-report/ticket-income-statistics-report',
                            name: $t({ defaultMessage: '券收入统计' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_TICKET_INCOME_STATISTICS],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '堂食' }),
                    children: [
                        {
                            path: '/main-report/table-business-statistics-report',
                            name: $t({ defaultMessage: '餐区/台位营业统计' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_TABLE_BUSINESS_STATISTICS],
                        },
                        {
                            path: '/main-report/service-fee-statistics',
                            name: $t({ defaultMessage: '服务费统计' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_SERVICE_FEE_ITEM],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '商城' }),
                    children: [
                        {
                            path: '/main-report/express-mall-statistics',
                            name: $t({ defaultMessage: '快递商城统计' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_EXPRESS_MALL_STATISTICS],
                        },
                    ],
                },
            ],
        },
        {
            name: $t({ defaultMessage: '销售报表' }),
            menuType: 'reportCenter-sale',
            icon: NavChannelActiveIcon,
            inactiveIcon: NavChannelInactiveIcon,
            children: [
                {
                    name: $t({ defaultMessage: '商品套餐' }),
                    children: [
                        {
                            path: '/main-report/product-overview-report',
                            name: $t({ defaultMessage: '商品概览' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_PRODUCT_OVERVIEW_REPORT],
                        },
                        {
                            path: '/main-report/product-summary-statistics',
                            name: $t({ defaultMessage: '商品汇总' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_PRODUCT_SUMMARY],
                        },
                        {
                            path: '/main-report/product-sales-summary-report',
                            name: $t({ defaultMessage: '商品销售汇总' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_PRODUCT_SALES_SUMMARY],
                        },
                        {
                            path: '/main-report/product-sales-details-report',
                            name: $t({ defaultMessage: '商品销售明细' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_PRODUCT_SALES_DETAIL],
                        },
                        {
                            path: '/main-report/product-sales-ranking-report',
                            name: $t({ defaultMessage: '商品销售排行' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_PRODUCT_SALES_RANKING],
                        },
                        {
                            path: '/main-report/category-sales-ranking-report',
                            name: $t({ defaultMessage: '类别销售排行' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_CATEGORY_SALES_RANKING],
                        },
                        {
                            path: '/main-report/product-location-sales-report',
                            name: $t({ defaultMessage: '商品门店销售汇总' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_PRODUCT_LOCATION_SALES],
                        },
                        {
                            path: '/main-report/kit-sales-ranking-report',
                            name: $t({ defaultMessage: '套餐销售统计' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_KIT_SALE_STATISTICS],
                        },
                        {
                            path: '/main-report/session-product-statistics-report',
                            name: $t({ defaultMessage: '餐/时段商品统计' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_SESSION_PRODUCT_STATISTICS],
                        },
                        {
                            path: '/main-report/return-product-statistics-report',
                            name: $t({ defaultMessage: '退菜/退货统计' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_SESSION_PRODUCT_REFUND_STATISTICS],
                        },
                        {
                            path: '/main-report/gift-statistics-report',
                            name: $t({ defaultMessage: '赠品统计' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_PRODUCT_GIFT_STATISTICS],
                        },
                        {
                            path: '/main-report/feed-sale-statistics-report',
                            name: $t({ defaultMessage: '加料销售统计' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_PROPERTY_SALE_STATISTICS],
                        },
                        {
                            path: '/main-report/method-statistics-report',
                            name: $t({ defaultMessage: '做法统计' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_MAKE_METHOD_STATISTICS],
                        },
                        {
                            path: '/main-report/product-marketing-report',
                            name: $t({ defaultMessage: '商品营销统计' }),
                            code_key: [IPermissonCode.PERMISSION_PRODUCT_MARKETING_REPORT],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '其他销售' }),
                    children: [
                        {
                            path: '/main-report/platform-order-report',
                            name: $t({ defaultMessage: '三方订单分析' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_CUSTOMER_PHONE_ANALYSIS],
                        },
                        {
                            path: '/main-report/card-sales-detail',
                            name: $t({ defaultMessage: '卡券销售明细' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_BUSINESS_CARD_ORDER_DETAIL_ANALYSIS],
                        },
                        {
                            path: '/main-report/sales-consumption',
                            name: $t({ defaultMessage: '原材料销售消耗报表' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_SALES_CONSUMPTION],
                        },
                    ],
                },
            ],
        },
        {
            name: $t({ defaultMessage: '会员报表' }),
            menuType: 'reportCenter-vip',
            icon: NavCustomerActiveIcon,
            inactiveIcon: NavCustomerInactiveIcon,
            children: [
                {
                    name: $t({ defaultMessage: '储值' }),
                    children: [
                        {
                            path: '/main-report/deposit-report',
                            name: $t({ defaultMessage: '储值分析' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_DEPOSIT],
                        },
                        {
                            path: '/main-report/vip-data-report',
                            name: $t({ defaultMessage: '会员概览' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_CUSTOMER_MEMBER_DATA],
                        },
                        {
                            path: '/main-report/customer-card-trade-log',
                            name: $t({ defaultMessage: '会员卡交易流水' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_CUSTOMER_CARD_TRADE_LOG],
                        },
                        {
                            path: '/main-report/customer-stored-value-report',
                            name: $t({ defaultMessage: '会员储值明细' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_CUSTOMER_DEPOSIT_DETAIL_ANALYSIS],
                        },
                        {
                            path: '/main-report/deposite-payment-method-detail',
                            name: $t({ defaultMessage: '储值支付方式明细' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_DEPOSIT_PAYMENT_TYPE_DETAIL_ANALYSIS],
                        },
                        {
                            path: '/main-report/customer-deduction-report',
                            name: $t({ defaultMessage: '会员扣款记录' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_CUSTOMER_CONSUME_DETAIL_ANALYSIS],
                        },
                        {
                            path: '/main-report/customer-consume-analysis',
                            name: $t({ defaultMessage: '储值消费店间结算' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_CROSS_LOCATION_CONSUME],
                        },
                        {
                            path: '/main-report/vip-card-operate-analysis',
                            name: $t({ defaultMessage: '会员卡操作明细' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_CUSTOMER_CARD_OPERATE_LOG],
                        },
                        {
                            path: '/main-report/deposit-activity-effect-analysis',
                            name: $t({ defaultMessage: '储值活动效果分析' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_GIFT_DEPOSIT],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '卡券资产' }),
                    children: [
                        {
                            path: '/main-report/coupon-template-analysis',
                            name: $t({ defaultMessage: '券模板分析' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_TICKET],
                        },
                        {
                            path: '/main-report/gift-card-analysis',
                            name: $t({ defaultMessage: '卡模板分析' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_GIFT_CARD],
                        },
                        {
                            path: '/main-report/gift-card-balance-log',
                            name: $t({ defaultMessage: '礼品卡明细' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_GIFT_CARD_BALANCE_LOG],
                        },
                        {
                            path: '/main-report/equity-card-stat',
                            name: $t({ defaultMessage: '权益卡分析' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_RIGHT_CARD],
                        },
                        {
                            path: '/main-report/ticket-package-sale-statistics',
                            name: $t({ defaultMessage: '券包销售统计' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_TICKET_PACKAGE_SALE],
                        },
                        {
                            path: '/main-report/point-analysis-report',
                            name: $t({ defaultMessage: '积分分析' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_POINT],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '会员运营' }),
                    children: [
                        {
                            path: '/main-report/customer-performance',
                            name: $t({ defaultMessage: '用户业绩' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_CUSTOMER_PERFORMANCE],
                        },
                        {
                            path: '/main-report/customer-repurchase',
                            name: $t({ defaultMessage: '会员复购汇总' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_CUSTOMER_REPURCHASE],
                        },
                        {
                            path: '/main-report/customer-repurchase-product',
                            name: $t({ defaultMessage: '会员商品复购' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_CUSTOMER_REPURCHASE_PRODUCT_DETAIL],
                        },
                        {
                            path: '/main-report/customer-consume-ratio',
                            name: $t({ defaultMessage: '会员消费占比' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_CUSTOMER_CONSUME_RATE],
                        },
                    ],
                },
            ],
        },
        {
            name: $t({ defaultMessage: '财务报表' }),
            menuType: 'reportCenter-finance',
            icon: NavSettlementActiveIcon,
            inactiveIcon: NavSettlementInactiveIcon,
            children: [
                {
                    name: $t({ defaultMessage: '对账' }),
                    children: [
                        {
                            path: '/main-report/business-receipt-statistics',
                            name: $t({ defaultMessage: '营业收款统计' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_BUSINESS_RECEIPT_STATISTICS],
                        },
                        {
                            path: '/main-report/business-receipt-detail',
                            name: $t({ defaultMessage: '营业收款明细' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_BUSINESS_RECEIPT_DETAIL],
                        },
                        {
                            path: '/main-report/shift-handover-statistics',
                            name: $t({ defaultMessage: '交班统计' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_SHIFT_HANDOVER_STATISTICS],
                        },
                        {
                            path: '/main-report/cashier-statistics',
                            name: $t({ defaultMessage: '收银员统计' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_CASHIER_STATISTICS],
                        },
                        {
                            path: '/main-report/sign-payment-detail',
                            name: $t({ defaultMessage: '挂账明细' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_SIGN_PAYMENT_DETAIL],
                        },
                        {
                            path: '/main-report/deposit-pledge-statistics',
                            name: $t({ defaultMessage: '订金/押金统计' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_DOWN_PAYMENT],
                        },
                        {
                            path: '/main-report/product-cost-gross',
                            name: $t({ defaultMessage: '商品成本毛利' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_PRODUCT_COST_GROSS],
                        },
                        {
                            path: '/main-report/group-purchase-coupon-record',
                            name: $t({ defaultMessage: '团购券核销记录' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_PLATFORM_TICKET_VERIFY_LOG],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '三方对账' }),
                    children: [
                        {
                            path: '/main-report/third-party-reconciliation-calendar',
                            name: $t({ defaultMessage: '对账日历' }),
                            code_key: [IPermissonCode.PERMISSION_OMS_RECONCILE_CALENDAR],
                        },
                        {
                            path: '/main-report/third-party-reconciliation-result-summary',
                            name: $t({ defaultMessage: '对账结果汇总' }),
                            code_key: [IPermissonCode.PERMISSION_OMS_RECONCILE_SUMMARY],
                        },
                        {
                            path: '/main-report/third-party-reconciliation-result-detail',
                            name: $t({ defaultMessage: '对账结果明细' }),
                            code_key: [IPermissonCode.PERMISSION_OMS_RECONCILE_DETAIL],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '财务稽查' }),
                    children: [
                        {
                            path: '/main-report/business-collection-reconciliation',
                            name: $t({ defaultMessage: '营业收款稽核' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_BUSINESS_PAY_VERIFY],
                        },
                        {
                            path: '/main-report/sensitive-operation-report',
                            name: $t({ defaultMessage: '敏感操作统计' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_SENSITIVE_OPERATION],
                        },
                    ],
                },
            ],
        },
        {
            name: $t({ defaultMessage: '运营报表' }),
            menuType: 'reportCenter-operation',
            icon: NavOperationActiveIcon,
            inactiveIcon: NavOperationInactiveIcon,
            children: [
                {
                    name: $t({ defaultMessage: '运营分析' }),
                    children: [
                        {
                            path: '/main-report/business-target-report',
                            name: $t({ defaultMessage: '营业指标同环比' }),
                            code_key: [IPermissonCode.PERMISSION_REPORT_BUSINESS_CYCLE_COMPARE],
                        },
                        {
                            path: '/main-report/salesperson-statistics-report',
                            name: $t({ defaultMessage: '销售员统计' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_POS_STAFF],
                        },
                        {
                            path: '/main-report/customer-price-report',
                            name: $t({ defaultMessage: '客单分析' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_CUSTOMER_ORDER_PRICE],
                        },
                        {
                            path: '/main-report/zero-sales-product-report',
                            name: $t({ defaultMessage: '零销量菜品统计' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_PRODUCT_ZERO_SALE],
                        },
                        {
                            path: '/main-report/product-association-recommendation',
                            name: $t({ defaultMessage: '菜品关联推荐' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_PRODUCT_RELATE_SUGGEST],
                        },
                        {
                            path: '/main-report/clear-sale-report',
                            name: $t({ defaultMessage: '商品沽清统计' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_PRODUCT_CLEARANCE_LOG],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '营销' }),
                    children: [
                        {
                            path: '/main-report/promotion-effect-analysis',
                            name: $t({ defaultMessage: '促销活动效果分析' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_DISCOUNT_EFFECT],
                        },
                        {
                            path: '/main-report/marketing-activity-effect-analysis',
                            name: $t({ defaultMessage: '营销活动效果分析' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_DISCOUNT_PROMOTION],
                        },
                    ],
                },
                {
                    name: $t({ defaultMessage: '线上店铺' }),
                    children: [
                        {
                            path: '/main-report/mall-traffic-overview-report',
                            name: $t({ defaultMessage: '商城流量概况' }),
                            code_key: [IPermissonCode.PERMISSION_WESTORE_TRAFFIC_OVERVIEW],
                        },
                        {
                            path: '/main-report/consumption-analysis',
                            name: $t({ defaultMessage: '小程序分析' }),
                            code_key: [IPermissonCode.PERMISSION_ANALYZE_CONSUMPTION_ANALYSIS],
                        },
                    ],
                },
            ],
        },
    ];
    return getPermissionShowNavItems(navTree);
}
// 服务市场
function navServiceMarket() {
    const navTree = [
        {
            name: $t({ defaultMessage: '乐檬餐饮服务' }),
            menuType: 'serviceMarket-lezhiService',
            icon: NavServiceActiveIcon,
            inactiveIcon: NavServiceInactiveIcon,
            children: [
                {
                    name: $t({ defaultMessage: '乐檬餐饮服务' }),
                    children: [
                        {
                            path: '/main-serviceMarket/settings/lemonServices',
                            name: $t({ defaultMessage: '服务市场' }),
                            code_key: [IPermissonCode.PERMISSION_SERVICE_MARKET],
                        },
                        {
                            path: '/main-serviceMarket/settings/lemonServices/temporaryPosPoint',
                            name: $t({ defaultMessage: '授权点数管理' }),
                            code_key: [IPermissonCode.PERMISSION_SERVICE_TEMP_POS_LIMIT],
                        },
                        {
                            path: '/main-serviceMarket/expense/invoice',
                            name: $t({ defaultMessage: '发票管理' }),
                            code_key: [IPermissonCode.PERMISSION_INVOICE_MANAGEMENT],
                        },
                        {
                            path: '/main-serviceMarket/expense/order',
                            name: $t({ defaultMessage: '费用订单' }),
                            code_key: [IPermissonCode.PERMISSION_SERVICE_FINANCE],
                        },
                    ],
                },
            ],
        },
        {
            name: $t({ defaultMessage: '增值服务' }),
            menuType: 'serviceMarket-valueAdd',
            icon: NavAddServiceActiveIcon,
            inactiveIcon: NavAddServiceInactiveIcon,
            children: [
                {
                    name: $t({ defaultMessage: '增值服务' }),
                    children: [
                        {
                            path: '/main-serviceMarket/equityAccountList',
                            name: $t({ defaultMessage: '权益账户' }),
                            code_key: [IPermissonCode.PERMISSION_SETTING_VALIE_ADD],
                        },
                        {
                            path: '/main-serviceMarket/settings/valueAdd/platformProgramCall',
                            name: $t({ defaultMessage: '平台小程序调用' }),
                            code_key: [IPermissonCode.PERMISSION_PLATFORM_APPLET_CALL],
                        },
                        {
                            path: '/main-serviceMarket/storeAccountSettings',
                            name: $t({ defaultMessage: '门店账户设置' }),
                            code_key: [IPermissonCode.PERMISSION_SETTING_LOCATION_ACCOUNT_SETTING],
                        },
                    ],
                },
            ],
        },
    ];
    return getPermissionShowNavItems(navTree);
}
// 右上角下拉->管理乐檬餐饮账号
function navAccountManagement() {
    const navTree = [
        {
            name: $t({ defaultMessage: '个人信息' }),
            menuType: 'accountManagement-account',
            icon: NavAccountActiveIcon,
            inactiveIcon: NavAccountInactiveIcon,
            isIndependentModule: true,
            path: '/main-account/account',
        },
        {
            name: $t({ defaultMessage: '安全性' }),
            menuType: 'accountManagement-security',
            icon: NavSecurityActiveIcon,
            inactiveIcon: NavSecurityInactiveIcon,
            isIndependentModule: true,
            path: '/main-account/security',
        },
    ];
    return getPermissionShowNavItems(navTree);
}
function navMessageCenter() {
    const navTree = [
        {
            name: $t({ defaultMessage: '消息中心' }),
            menuType: 'main-message',
            icon: MessageCenterIcon,
            inactiveIcon: MessageCenterIcon,
            children: [
                {
                    name: $t({ defaultMessage: '消息中心' }),
                    children: [
                        {
                            path: '/main-message/message-list',
                            name: $t({ defaultMessage: '消息中心' }),
                            code_key: [IPermissonCode.PERMISSION_NOTICE_CENTER_SEND_MSG],
                            customRule: () => {
                                return true;
                            },
                        },
                    ],
                },
            ],
        },
    ];
    return getPermissionShowNavItems(navTree);
}
/**
 * 获取菜单列表
 * @returns
 */
const getBaseMenus = () => ({
    OPERATION_CENTER: navOperationCenter(),
    FINANCE_CENTER: navFinanceCenter(),
    MARKETING_CENTER: navMarketingCenter(),
    SUPPLY_CHAIN: navSupplyChain(),
    REPORT_CENTER: navReportCenter(),
    SERVICE_MARKET: navServiceMarket(),
    ACCOUNT: navAccountManagement(),
    MESSAGE_CENTER: navMessageCenter(),
});

/** 门店时区分组选项（与产品原型一致） */
const getStoreTimezoneOptionGroups = () => [
    {
        label: $t({ defaultMessage: '默认与常用' }),
        options: [
            // { value: 'AUTO', label: '自动检测(跟随设备/浏览器)', offset: 'AUTO' },
            {
                value: 'Shanghai1',
                label: $t({ defaultMessage: 'Shanghai(UTC+08:00) 中国标准时间' }),
                offset: 'UTC+08:00',
            },
            {
                value: 'UTC',
                label: $t({ defaultMessage: 'UTC (UTC+00:00) 协调世界时' }),
                offset: 'UTC+00:00',
            },
        ],
    },
    {
        label: $t({ defaultMessage: '亚洲 (Asia)' }),
        options: [
            {
                value: 'Shanghai',
                label: $t({ defaultMessage: 'Shanghai(UTC+08:00) 中国标准时间' }),
                offset: 'UTC+08:00',
            },
            {
                value: 'Hong_Kong',
                label: $t({ defaultMessage: 'Hong_Kong(UTC+08:00) 香港时间' }),
                offset: 'UTC+08:00',
            },
            {
                value: 'Macau',
                label: $t({ defaultMessage: 'Macau(UTC+08:00) 澳门时间' }),
                offset: 'UTC+08:00',
            },
            {
                value: 'Taipei',
                label: $t({ defaultMessage: 'Taipei(UTC+08:00) 台北时间' }),
                offset: 'UTC+08:00',
            },
            {
                value: 'Tokyo',
                label: $t({ defaultMessage: 'Tokyo(UTC+09:00) 日本标准时间' }),
                offset: 'UTC+09:00',
            },
            {
                value: 'Seoul',
                label: $t({ defaultMessage: 'Seoul(UTC+09:00) 韩国标准时间' }),
                offset: 'UTC+09:00',
            },
            {
                value: 'Singapore',
                label: $t({ defaultMessage: 'Singapore(UTC+08:00) 新加坡时间' }),
                offset: 'UTC+08:00',
            },
            {
                value: 'Kuala_Lumpur',
                label: $t({ defaultMessage: 'Kuala_Lumpur(UTC+08:00) 吉隆坡时间' }),
                offset: 'UTC+08:00',
            },
            {
                value: 'Bangkok',
                label: $t({ defaultMessage: 'Bangkok(UTC+07:00) 曼谷时间' }),
                offset: 'UTC+07:00',
            },
            {
                value: 'Manila',
                label: $t({ defaultMessage: 'Manila(UTC+08:00) 马尼拉时间' }),
                offset: 'UTC+08:00',
            },
            {
                value: 'Jakarta',
                label: $t({ defaultMessage: 'Jakarta(UTC+07:00) 雅加达时间 (西部)' }),
                offset: 'UTC+07:00',
            },
            {
                value: 'Ho_Chi_Minh',
                label: $t({ defaultMessage: 'Ho_Chi_Minh(UTC+07:00) 胡志明市时间' }),
                offset: 'UTC+07:00',
            },
            {
                value: 'Dubai',
                label: $t({ defaultMessage: 'Dubai(UTC+04:00) 迪拜时间' }),
                offset: 'UTC+04:00',
            },
            {
                value: 'Kolkata',
                label: $t({ defaultMessage: 'Kolkata(UTC+05:30) 印度标准时间' }),
                offset: 'UTC+05:30',
            },
            {
                value: 'Karachi',
                label: $t({ defaultMessage: 'Karachi(UTC+05:00) 巴基斯坦时间' }),
                offset: 'UTC+05:00',
            },
            {
                value: 'Riyadh',
                label: $t({ defaultMessage: 'Riyadh(UTC+03:00) 利雅得时间' }),
                offset: 'UTC+03:00',
            },
            {
                value: 'Jerusalem',
                label: $t({ defaultMessage: 'Jerusalem(UTC+02:00/+03:00) 以色列时间（含夏令时）' }),
                offset: 'UTC+02:00/+03:00',
            },
            {
                value: 'Beirut',
                label: $t({ defaultMessage: 'Beirut(UTC+02:00/+03:00) 贝鲁特时间（含夏令时）' }),
                offset: 'UTC+02:00/+03:00',
            },
        ],
    },
    {
        label: $t({ defaultMessage: '美洲 (America)' }),
        options: [
            {
                value: 'New_York',
                label: $t({ defaultMessage: 'New_York(UTC-05:00/-04:00) 美国东部时间（含夏令时）' }),
                offset: 'UTC-05:00/-04:00',
            },
            {
                value: 'Chicago',
                label: $t({ defaultMessage: 'Chicago(UTC-06:00/-05:00) 美国中部时间（含夏令时）' }),
                offset: 'UTC-06:00/-05:00',
            },
            {
                value: 'Denver',
                label: $t({ defaultMessage: 'Denver(UTC-07:00/-06:00) 美国山地时间（含夏令时）' }),
                offset: 'UTC-07:00/-06:00',
            },
            {
                value: 'Los_Angeles',
                label: $t({ defaultMessage: 'Los_Angeles(UTC-08:00/-07:00) 美国太平洋时间（含夏令时）' }),
                offset: 'UTC-08:00/-07:00',
            },
            {
                value: 'Toronto',
                label: $t({ defaultMessage: 'Toronto(UTC-05:00/-04:00) 多伦多时间（含夏令时）' }),
                offset: 'UTC-05:00/-04:00',
            },
            {
                value: 'Vancouver',
                label: $t({ defaultMessage: 'Vancouver(UTC-08:00/-07:00) 温哥华时间（含夏令时）' }),
                offset: 'UTC-08:00/-07:00',
            },
            {
                value: 'Mexico_City',
                label: $t({ defaultMessage: 'Mexico_City(UTC-06:00) 墨西哥城时间' }),
                offset: 'UTC-06:00',
            },
            {
                value: 'Sao_Paulo',
                label: $t({ defaultMessage: 'Sao_Paulo(UTC-03:00) 圣保罗时间' }),
                offset: 'UTC-03:00',
            },
            {
                value: 'Buenos_Aires',
                label: $t({ defaultMessage: 'Buenos_Aires(UTC-03:00) 布宜诺斯艾利斯时间' }),
                offset: 'UTC-03:00',
            },
            {
                value: 'Lima',
                label: $t({ defaultMessage: 'Lima(UTC-05:00) 利马时间' }),
                offset: 'UTC-05:00',
            },
        ],
    },
    {
        label: $t({ defaultMessage: '欧洲 (Europe)' }),
        options: [
            {
                value: 'London',
                label: $t({ defaultMessage: 'London(UTC+00:00/+01:00) 伦敦时间（含夏令时）' }),
                offset: 'UTC+00:00/+01:00',
            },
            {
                value: 'Paris',
                label: $t({ defaultMessage: 'Paris(UTC+01:00/+02:00) 巴黎时间（含夏令时）' }),
                offset: 'UTC+01:00/+02:00',
            },
            {
                value: 'Berlin',
                label: $t({ defaultMessage: 'Berlin(UTC+01:00/+02:00) 柏林时间（含夏令时）' }),
                offset: 'UTC+01:00/+02:00',
            },
            {
                value: 'Madrid',
                label: $t({ defaultMessage: 'Madrid(UTC+01:00/+02:00) 马德里时间（含夏令时）' }),
                offset: 'UTC+01:00/+02:00',
            },
            {
                value: 'Rome',
                label: $t({ defaultMessage: 'Rome(UTC+01:00/+02:00) 罗马时间（含夏令时）' }),
                offset: 'UTC+01:00/+02:00',
            },
            {
                value: 'Moscow',
                label: $t({ defaultMessage: 'Moscow(UTC+03:00) 莫斯科时间' }),
                offset: 'UTC+03:00',
            },
            {
                value: 'Istanbul',
                label: $t({ defaultMessage: 'Istanbul(UTC+03:00) 伊斯坦布尔时间' }),
                offset: 'UTC+03:00',
            },
        ],
    },
    {
        label: $t({ defaultMessage: '大洋洲 (Oceania)' }),
        options: [
            {
                value: 'Sydney',
                label: $t({ defaultMessage: 'Sydney(UTC+10:00/+11:00) 悉尼时间（含夏令时）' }),
                offset: 'UTC+10:00/+11:00',
            },
            {
                value: 'Melbourne',
                label: $t({ defaultMessage: 'Melbourne(UTC+10:00/+11:00) 墨尔本时间（含夏令时）' }),
                offset: 'UTC+10:00/+11:00',
            },
            {
                value: 'Perth',
                label: $t({ defaultMessage: 'Perth(UTC+08:00) 珀斯时间' }),
                offset: 'UTC+08:00',
            },
            {
                value: 'Auckland',
                label: $t({ defaultMessage: 'Auckland(UTC+12:00/+13:00) 奥克兰时间（含夏令时）' }),
                offset: 'UTC+12:00/+13:00',
            },
            {
                value: 'Honolulu',
                label: $t({ defaultMessage: 'Honolulu(UTC-10:00) 檀香山时间' }),
                offset: 'UTC-10:00',
            },
        ],
    },
    {
        label: $t({ defaultMessage: '非洲 (Africa)' }),
        options: [
            {
                value: 'Cairo',
                label: $t({ defaultMessage: 'Cairo(UTC+02:00) 开罗时间' }),
                offset: 'UTC+02:00',
            },
            {
                value: 'Johannesburg',
                label: $t({ defaultMessage: 'Johannesburg(UTC+02:00) 约翰内斯堡时间' }),
                offset: 'UTC+02:00',
            },
            {
                value: 'Lagos',
                label: $t({ defaultMessage: 'Lagos(UTC+01:00) 拉各斯时间' }),
                offset: 'UTC+01:00',
            },
            {
                value: 'Nairobi',
                label: $t({ defaultMessage: 'Nairobi(UTC+03:00) 内罗毕时间' }),
                offset: 'UTC+03:00',
            },
        ],
    },
];
/** 保留常量导出兼容旧调用；需要随语言环境重新翻译时请使用 getStoreTimezoneOptionGroups */
const STORE_TIMEZONE_OPTION_GROUPS = getStoreTimezoneOptionGroups();
const DEFAULT_STORE_TIMEZONE = 'Shanghai';
/** 下拉选项 value 与接口时区值的别名映射 */
const TIMEZONE_SELECT_ALIAS = {
    Shanghai1: 'Shanghai',
};

/*
 * @Author: 王仁涛 wangrt@cnlemon.net
 * @Date: 2026-06-04 11:20:06
 * @LastEditors: 王仁涛 wangrt@cnlemon.net
 * @LastEditTime: 2026-06-16 15:40:22
 * @FilePath: /mars-web/packages/utils/src/utils/time-zone-conversion.ts
 * @Description: 时区转换
 */
const [TO_DAY_FORMAT, TO_SECOND_FORMAT] = [
    'YYYY-MM-DD',
    'YYYY-MM-DD HH:mm:ss',
    'YYYY-MM-DD 00:00:00',
    'YYYY-MM-DD 23:59:59',
];
const DEFAULT_TIME_ZONE_OFFSET = 'UTC+08:00';
const SERVER_TIME_ZONE_OFFSET_MINUTES = 8 * 60;
const UTC_OFFSET_REGEXP = /^UTC([+-])(\d{2}):(\d{2})$/;
let timezoneOffsetMap = null;
let cachedTimezoneConfig;
/** 夏令时时区取标准时偏移（/ 前面的值），供固定 offset 查找 */
const resolveTimezoneOffset = (option) => {
    if (option.offset.includes('/')) {
        return option.offset.split('/')[0];
    }
    return option.offset;
};
const getMarsTimezone = () => {
    if (typeof localStorage === 'undefined') {
        return undefined;
    }
    return localStorage.getItem('MarsTimezone') || undefined;
};
const getTimezoneOffsetMap = () => {
    if (timezoneOffsetMap) {
        return timezoneOffsetMap;
    }
    timezoneOffsetMap = new Map();
    STORE_TIMEZONE_OPTION_GROUPS.forEach((group) => {
        group.options.forEach((option) => {
            const offset = resolveTimezoneOffset(option);
            timezoneOffsetMap?.set(option.value, offset);
            const alias = TIMEZONE_SELECT_ALIAS[option.value];
            if (alias) {
                timezoneOffsetMap?.set(alias, offset);
            }
        });
    });
    return timezoneOffsetMap;
};
const parseUtcOffsetMinutes = (offset) => {
    const stableOffset = offset.includes('/') ? offset.split('/')[0] : offset;
    const matched = UTC_OFFSET_REGEXP.exec(stableOffset);
    if (!matched) {
        return undefined;
    }
    const [, sign, hour, minute] = matched;
    const offsetMinutes = Number(hour) * 60 + Number(minute);
    return sign === '-' ? -offsetMinutes : offsetMinutes;
};
const getTimezoneConfig = () => {
    const marsTimeZone = getMarsTimezone();
    const cacheKey = marsTimeZone || '';
    if (cachedTimezoneConfig?.key === cacheKey) {
        return cachedTimezoneConfig.config;
    }
    const offset = marsTimeZone
        ? getTimezoneOffsetMap().get(marsTimeZone) || DEFAULT_TIME_ZONE_OFFSET
        : DEFAULT_TIME_ZONE_OFFSET;
    const offsetMinutes = parseUtcOffsetMinutes(offset);
    const config = {
        offset,
        offsetMinutes,
        shouldConvert: offsetMinutes !== undefined && offsetMinutes !== SERVER_TIME_ZONE_OFFSET_MINUTES,
    };
    cachedTimezoneConfig = {
        key: cacheKey,
        config,
    };
    return config;
};
// 识别仅包含时分秒的时段格式，纯时段没有日期，不能按完整日期时间解析。
const isTimeOnlyFormat = (format) => {
    return !/[YMD]/.test(format) && /H/.test(format);
};
// 纯时间值在做时区偏移计算时需要补一个稳定的日期，避免 moment-timezone 无法正确换算。
// 最终格式化回 HH:mm:ss / HH:mm 时会丢弃这个补齐的日期，只保留时间部分。
const TIME_ONLY_ANCHOR_DATE = '2000-01-01';
const getTimeOnlyDateTimeFormat = (format) => `YYYY-MM-DD ${format}`;
// 将 HH:mm:ss / HH:mm 按“补齐日期后的完整时间”解析，避免直接转换时出现 invalid date。
const parseMomentByFormat = (time, format) => {
    if (isTimeOnlyFormat(format)) {
        return moment(`${TIME_ONLY_ANCHOR_DATE} ${time}`, getTimeOnlyDateTimeFormat(format));
    }
    return moment(time, format);
};
const convertByOffset = (time, fromOffsetMinutes, toOffsetMinutes, format) => {
    const parsedTime = parseMomentByFormat(time, format);
    const formattedTime = parsedTime.isValid() ? parsedTime : moment(time);
    return formattedTime.add(toOffsetMinutes - fromOffsetMinutes, 'minutes').format(format);
};
const formatTimeToServer = (userTime, format = TO_SECOND_FORMAT) => {
    // 没有设置时区、东八区、时间为空直接返回传入值 不做偏移
    const timezoneConfig = getTimezoneConfig();
    if (!userTime) {
        return userTime;
    }
    if (!timezoneConfig.offset) {
        throw new Error($t({ defaultMessage: 'time_zone_offset缺失' }));
    }
    if (timezoneConfig.offsetMinutes === undefined) {
        throw new Error($t({ defaultMessage: '[{userOffset}]不支持' }, { userOffset: timezoneConfig.offset }));
    }
    if (!timezoneConfig.shouldConvert) {
        return userTime;
    }
    const formatOneTime = (time) => {
        return convertByOffset(time, timezoneConfig.offsetMinutes, SERVER_TIME_ZONE_OFFSET_MINUTES, format);
    };
    if (typeof userTime === 'string') {
        return formatOneTime(userTime);
    }
    if (Array.isArray(userTime)) {
        return userTime.map((time) => formatOneTime(time));
    }
    throw new Error($t({ defaultMessage: '转换值为字符串或者字符串数组' }));
};
const parseTimeFromServer = (serverTime, format = TO_SECOND_FORMAT) => {
    if (!serverTime) {
        return serverTime;
    }
    const timezoneConfig = getTimezoneConfig();
    // 没有设置时区、东八区、时间为空 不做偏移
    if (timezoneConfig.offsetMinutes === undefined || !timezoneConfig.shouldConvert) {
        return parseMomentByFormat(serverTime, format).format(format);
    }
    try {
        return convertByOffset(serverTime, SERVER_TIME_ZONE_OFFSET_MINUTES, timezoneConfig.offsetMinutes, format);
    }
    catch (error) {
        return parseMomentByFormat(serverTime, format).format(format);
    }
};
/** 批量将服务端东八区时间字段转换为当前用户时区展示时间，不会修改原对象。 */
const parseTimeFieldsFromServer = (data, fields, format = TO_SECOND_FORMAT) => {
    if (!data) {
        return data;
    }
    const result = { ...data };
    fields.forEach((field) => {
        result[field] = parseTimeFromServer(result[field], format);
    });
    return result;
};
/** 批量将当前用户时区的时间字段转换为服务端东八区时间，不会修改原对象。 */
const formatTimeFieldsToServer = (data, fields, format = TO_SECOND_FORMAT) => {
    if (!data) {
        return data;
    }
    const result = { ...data };
    fields.forEach((field) => {
        result[field] = formatTimeToServer(result[field], format);
    });
    return result;
};
/** 将日期范围补齐为起止秒并按需转换为服务端东八区时间，供查询、导出参数复用。 */
const formatDateRangeToServer = (dates, options = {}) => {
    const { needFormatTime = true, startTime = '00:00:00', endTime = '23:59:59', format = TO_SECOND_FORMAT, } = options;
    const date_min = dates?.[0] ? `${dates[0]} ${startTime}` : undefined;
    const date_max = dates?.[1] ? `${dates[1]} ${endTime}` : undefined;
    return {
        date_min: needFormatTime ? formatTimeToServer(date_min, format) : date_min,
        date_max: needFormatTime ? formatTimeToServer(date_max, format) : date_max,
    };
};
/** 根据时区 value 从 STORE_TIMEZONE_OPTION_GROUPS 中匹配展示文案 */
const getStoreTimezoneLabel = (timezone, isNeedTimezone) => {
    /** isNeedTimezone 为 true 时，timezone 为传入的时区,为了解决AUTO下获取浏览器时区后显示，否则为 localStorage.getItem('MarsTimezone') */
    const timeZone = isNeedTimezone ? timezone : getMarsTimezone() || timezone;
    if (!timeZone) {
        return null;
    }
    for (const group of getStoreTimezoneOptionGroups()) {
        for (const option of group.options) {
            const alias = TIMEZONE_SELECT_ALIAS[option.value];
            if (option.value === timeZone || alias === timeZone) {
                return {
                    label: option.label,
                    offset: resolveTimezoneOffset(option),
                };
            }
        }
    }
    return {
        label: timezone,
        offset: '',
    };
};
/** 获取当前用户时区的时间并用东八区显示后，转换成对应设置时区的时间 */
const getCurrentUserTimeByTimezone = (format = TO_SECOND_FORMAT) => {
    return parseTimeFromServer(moment().utcOffset(8).format('YYYY-MM-DD HH:mm:ss'), format);
};
// 判断当前时区是否是包含夏令时的时区
const isContainsSummerTimezone = () => {
    return getStoreTimezoneLabel()?.label?.includes('夏令时') ?? false;
};

export { ArrayUtil, DECORATION_SCHEME_REQUIRED_ALI_MP_VERSION, DECORATION_SCHEME_REQUIRED_WECHAT_MP_VERSION, DEFAULT_STORE_TIMEZONE, OSS_DOMAIN, OSS_ORIGIN, SHOP_DECORATION_ENTRY_PATH, SHOP_DECORATION_MP_MANAGE_PATH, STORE_TIMEZONE_OPTION_GROUPS, TIMEZONE_SELECT_ALIAS, UNITTYPE, ValidateTypes, WEIGHTUNITEN, WEIGHTUNITZN, analyzeStorageSize, app, arrayToTree, assembleProductOption, autoConvertLongNumberToStringJsonParse, batchSystemLog, bignumber, buildShopDecorationEntryPath, buildShopDecorationEntryUrl, calcMoney, calculateProductMetafieldApply_list, canEditMarketing, canRead, changeDateFilter, checkIsLocationView, checkRepeatProduct, checkSelectTimeOverLimit, checkUseNewPromotionCalRule, commonOrderJump, compareDate, compareHours, compareMiniProgramVersion, computeUseUnitPrice, computeWinLossMoneyCount, convertToTree, copyToClipboard, dataURLtoBlob, decodeDots$1 as decodeDots, delay, delayeringFn, diffLabel, downloadImageByURL, downloadImageByURLForWx, downloadToCanvas, dynamicAddOrDelete, earthAuthis, emptyProductData, encodeDots, filterProductModifiedUnit, filterSelect, filterSelectCaseSensitive, findNodeInTree, findPathbyId, formatArray, formatCustomColumnsExport, formatCustomColumnsExportV2, formatDateRangeToServer, formatExportColumns, formatExportColumnsWithNestedFields, formatMapKey, formatTimeFieldsToServer, formatTimeToServer, getAvailableQueryYear, getBaseMenus, getBrowserType, getCardType, getChinese, getContextMenuItems, getCurrencyPrecision, getCurrentColumnItem, getCurrentMonth, getCurrentQuarter, getCurrentUserTimeByTimezone, getCurrentWeek, getCurrentYear, getDateHandler, getDiffKey, getEarthAuth, getEditPageIdParam, getFilteredChildren, getLastMonth, getLastWeek, getLocationTypes$1 as getLocationTypes, getMoneyMaxValue, getMoneyMinValue, getNameById, getOrderPermission, getPageQuery, getProductChainUnitRate, getProductChainUnitType, getProductKeyAttr, getProductKitItem, getProductRateByUnit, getProductTransferUnitKey, getProductUnitRate, getProductUnitRateWithAssistIs, getPurchaseBaseUnitPrice, getPurchaseUseUnitPrice, getRangeOptions, getRealWindow, getReportRangePickerDisabledDate, getScopeType, getSevenDays, getShopType, getSize, getStoreTimezoneLabel, getStoreTimezoneOptionGroups, getTableMaxHeight, getTableMaxHeightForHandle, getTableScroll, getThirtyDays, getTitle$1 as getTitle, getToday, getTodayTime, getTodayTimeNew, getTreeData, getUuid, getYesterday, goStockPage, hasOpteratePermission, hexToRgb, hexToRgba, inSpecialCharacters, includeSpecial, inputFormatterRule, inputNumberParser, isBranch, isCentralizedStore, isCloudUrl, isContainsSummerTimezone, isDateIntersection, isDecentralizedStore, isEmail, isFunction, isHeadquarters, isInt, isMerchantPhone, isMiniProgramVersionBelow, isMoney, isNewMerchant, isNewPage, isNewPhoneOrEmail, isNumberStr, isPhone, isPhoneOrEmail, isZero, isZone, judgeSortOrder, localCache, makePosPin, makeTreeZIndex, managementStyleProductEditState, maskSensitiveInformation, mixedStringComparator, moneyFormat, moneyFormatter, moneyFormatterOperator, moneyFormatterWithCurrencyPrecision, moveIndexColumnToFirst, multiCartesian, navigateToShopDecorationEntry, numberFixed, numberFormat, numberOperator, numberToLocaleString$1 as numberToLocaleString, openNewWindowWithStorageViaBackend, openShopDecorationEntry, openShopDecorationMpManage, oss_image_path, parseMapKey, parsePayOrderNoFromURL, parseTimeFieldsFromServer, parseTimeFromServer, preDaysHandler, precisionLimit, priceQuantityFormatter, priceQuantityFormatterOperator, processError, processInfo, randomString, rateFormatter, rateFormatterOperator, removeDate, restoreStorageFromBackend, returnText, rgbAddOpacity, rgbToHex, rgbToRgba, rgbaToRgb, routeCheck, saveSystemLog, scrollToFirstError, sessionCache, setCardType, setEarthAuth, setPermission, showOperationOfAttr, simpleHash, sort, splitBusinessScenarioParam, systemFormulaLog, systemLog, tableAdd, tableCostCardDelete, tableDelete, tableSort, tableSortHandler, tableUpdate, timeFormat, toChinesNum, toFixedNumber, transUnitPriceDecimal, transferCustomColumn, transferCustomFilter, transformToArray, treeParentSelectOnSearchMode, treeParentSelectOnSearchModeNew, treeTraverse, updateOrderAttr, useAntPagination, useBaseAppState, useCacheHandler, useCopyText, useCostCardPermission, useCurrency, useCurrencyByCode, useDebounce, useDescriptionModal, useFetchOptions, useGetLocationList, useGetVoucherIdAndNameList, useGetVoucherList, usePageUnload, useRemberListIndex, useSafeEffect, useTableHeight, useTimeCountDown, validate, validateTypes, verifyCostCardMoney, verifyCostCardQty, verifyCostCardUnit, verifyDataValidity, verifyMateriaQty, verifyMinValue, verifyMoney, verifyMoneyWithRound, verifyProductUnit, verifyQty, verifyQtyAndGiftQty, weedOutQuery };
//# sourceMappingURL=index.esm.js.map
